import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';

// ---- Overlay layout math (mirrors app/backend/lib/overlayMath.js) ----
// We inline this tiny module rather than import across app boundaries so Vite
// doesn't need a CommonJS interop shim. If you change the formula here, also
// change it in overlayMath.js — both sides MUST agree or the preview drifts
// from the exported MP4 frame. See lib/overlayMath.js for the authoritative doc.
const OVERLAY_REFERENCE_WIDTH = 1080;
function computeOverlayLayout({
  containerWidthPx,
  fontSize = 72,
  borderWidth = 8,
  verticalPositionPct = 75,
  alignment = 'center',
}) {
  if (!containerWidthPx || containerWidthPx <= 0) {
    containerWidthPx = OVERLAY_REFERENCE_WIDTH; // safe fallback
  }
  const justifyContent = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
  return {
    fontSizePx: Math.round((fontSize / OVERLAY_REFERENCE_WIDTH) * containerWidthPx),
    borderWidthPx: Math.max(1, Math.round((borderWidth / OVERLAY_REFERENCE_WIDTH) * containerWidthPx)),
    paddingPx: Math.max(16, Math.round(containerWidthPx * 0.04)),
    topPct: verticalPositionPct,
    translateY: 'translateY(-50%)',
    textAlign: alignment,
    justifyContent,
  };
}
import { Download, Zap, Scissors, Info, ArrowRight, CheckCircle2, Loader2, Play, Pause, Volume2, VolumeX, Maximize, Minimize, AlertCircle, FileVideo, Save, Plus, Copy, Settings, Terminal, Globe, ChevronLeft, Moon, Sun, X, Activity, Search, Mic, LayoutGrid, Home, FileText, HelpCircle, Image as ImageIcon, ChevronDown, ChevronUp, Sparkles, Briefcase, Layers, Clock, Share2, Type, Trash2, Pencil, RotateCcw, Upload, Calendar, Square, Key, Check, FolderOpen, Monitor, Smartphone, Palette, Eye, Link2, Stamp, Eraser } from 'lucide-react';

import { QRCodeSVG } from 'qrcode.react';

// Import Format Assets
import thumbOriginal from './assets/formats/thumb-original.png';
import thumbStacked from './assets/formats/thumb-stacked.png';
import thumbVertZoom from './assets/formats/thumb-vert-zoom.png';
import thumbSqBlur from './assets/formats/thumb-sq-blur.png';
import thumbSqZoom from './assets/formats/thumb-sq-zoom.png';
import logoDark from './assets/qlipper-icon-192.png';
import logoLight from './assets/qlipper-icon-192.png';
// Brand icon set — royal-gold (light) / dark-fire (dark)
const brandIcon = (name, dark = false) => `/brand/${dark ? 'dark-fire' : 'royal-gold'}/${dark ? 'df' : 'rg'}-${name}.png`;
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import StyleSettingsModal from './components/StyleSettingsModal';
import ErrorBanner from './components/ErrorBanner';
import FormatPicker from './components/FormatPicker';
// Router / OpenAI-compatible analysis (OmniRoute etc.) — the SAME settings panel and
// chat client Qlipper Lite (lite.html) uses; settings are shared via localStorage.
import LiteLlmSettings from './lite/ui/LiteLlmSettings.jsx';
import { PRESETS } from './lib/llm/registry.js';
import { ANALYZE_MODEL } from './lite/analyzeProxy.js';
import {
  loadLlmCfg, saveLlmCfg, loadFullAppProvider, saveFullAppProvider, resolveLlm, llmMissing,
} from './lib/llm/savedConfig.js';

// License Integration
import { useLicense } from './hooks/useLicense.js';
import LicenseLogin from './components/LicenseLogin.jsx';
import licenseService from './services/licenseService.js';

const API_BASE = '';

// Friendly error parser — converts raw errors into user-readable messages (Bahasa Indonesia)
// Errors are attributed to the actual service that failed (Gemini, YouTube, FFmpeg) — not Qlipper.
const friendlyError = (err, action = '') => {
  const raw = err.response?.data?.error || err.message || 'Terjadi kesalahan';
  const status = err.response?.status;
  const lowerRaw = raw.toLowerCase();

  // --- Gemini / AI errors ---
  if (status === 429 && (lowerRaw.includes('gemini') || lowerRaw.includes('rate limit') || lowerRaw.includes('quota') || action.includes('AI') || action.includes('Analysis'))) {
    return `⏳ Gemini Rate Limit\n\nGoogle Gemini membatasi jumlah request kamu. Ini batas dari Google, bukan Qlipper.\n\n💡 Coba langkah berikut:\n1. Tunggu 1-2 menit lalu coba lagi\n2. Ganti ke model lain di Settings (misal: Gemini 1.5 Flash gratis)\n3. Kalau sering terjadi, pertimbangkan upgrade API key di Google AI Studio`;
  }
  if (lowerRaw.includes('503') && (lowerRaw.includes('gemini') || lowerRaw.includes('overloaded') || lowerRaw.includes('high demand'))) {
    return `🔥 Gemini Sedang Overload\n\nServer Google Gemini sedang kelebihan beban. Ini masalah di sisi Google, bukan Qlipper.\n\n💡 Coba langkah berikut:\n1. Tunggu beberapa menit lalu coba lagi\n2. Ganti model di Settings (misal: Gemini 2.0 Flash atau 1.5 Flash)\n3. Cek status Google AI di status.cloud.google.com`;
  }
  if (lowerRaw.includes('api key') || lowerRaw.includes('api_key_invalid') || (status === 401 && action.includes('AI'))) {
    return `🔑 API Key Gemini Bermasalah\n\n${raw}\n\n💡 Coba langkah berikut:\n1. Buka Settings dan periksa API key kamu\n2. Pastikan key sudah di-copy lengkap dari Google AI Studio\n3. Cek apakah key masih aktif di aistudio.google.com/apikey`;
  }

  // --- YouTube / yt-dlp errors ---
  if (status === 429 && (lowerRaw.includes('youtube') || lowerRaw.includes('yt-dlp') || action.includes('Download') || action.includes('Metadata'))) {
    return `⏳ YouTube Rate Limit\n\nYouTube membatasi jumlah download kamu. Ini batas dari YouTube, bukan Qlipper.\n\n💡 Coba langkah berikut:\n1. Tunggu 5-10 menit lalu coba lagi\n2. Coba video yang berbeda\n3. Update yt-dlp di Settings`;
  }
  if (lowerRaw.includes('403') && (action.includes('Download') || action.includes('Metadata'))) {
    return `🚫 YouTube Memblokir Request\n\nYouTube menolak request download. Ini biasanya sementara.\n\n💡 Coba langkah berikut:\n1. Update yt-dlp di Settings (paling sering berhasil)\n2. Tunggu 5-10 menit lalu coba lagi\n3. Aktifkan YouTube Authentication di Settings`;
  }

  // --- FFmpeg errors ---
  if (lowerRaw.includes('ffmpeg') || lowerRaw.includes('filter') || lowerRaw.includes('codec') || lowerRaw.includes('encoder')) {
    return `🎬 FFmpeg Error\n\nTerjadi masalah saat memproses video dengan FFmpeg. Ini error dari video processor, bukan Qlipper.\n\n${raw}\n\n💡 Coba langkah berikut:\n1. Coba render ulang clip ini\n2. Hindari karakter spesial di teks hook ([ ] : ; ')\n3. Cek System Log Stream untuk detail FFmpeg command`;
  }

  // --- Network / connection (Qlipper internal) ---
  if (err.code === 'ECONNABORTED' || lowerRaw.includes('timeout')) {
    return `⏱️ Waktu Habis\n\n${action || 'Proses'} memakan waktu terlalu lama.\n\n💡 Coba langkah berikut:\n1. Tunggu sebentar lalu coba lagi\n2. Pastikan backend sedang berjalan\n3. Coba video yang lebih pendek`;
  }
  if (err.code === 'ERR_NETWORK' || lowerRaw.includes('network error')) {
    return `🌐 Koneksi Gagal\n\nTidak dapat terhubung ke server Qlipper.\n\n💡 Coba langkah berikut:\n1. Pastikan backend berjalan (port 3001)\n2. Periksa koneksi internet kamu\n3. Restart aplikasi`;
  }

  // --- Generic rate limit (fallback) ---
  if (status === 429 || lowerRaw.includes('rate limit') || lowerRaw.includes('quota')) {
    return `⏳ Rate Limit dari Layanan Eksternal\n\nLayanan pihak ketiga membatasi jumlah request. Ini bukan masalah Qlipper.\n\n💡 Coba langkah berikut:\n1. Tunggu 1-2 menit lalu coba lagi\n2. Hindari klik berulang terlalu cepat`;
  }

  // --- Generic server error ---
  if (status >= 500) {
    return `⚠️ Kesalahan Server\n\n${raw}\n\n💡 Coba langkah berikut:\n1. Coba lagi sebentar\n2. Restart backend server\n3. Cek System Log Stream untuk detail`;
  }

  // Already formatted (has suggestions) — pass through
  if (raw.includes('💡 What to try:') || raw.includes('💡 Coba')) return raw;

  // Generic — wrap with context
  if (action) {
    return `❌ ${action} Gagal\n\n${raw}`;
  }
  return raw;
};

// License headers for server-side verification on critical endpoints
const getLicenseHeaders = () => ({
  'X-License-Key': licenseService.getStoredLicenseKey() || '',
  'X-Device-Id': licenseService.getDeviceId() || ''
});

// ============================================================
// GUARDRAIL: YouTube Thumbnail Component
// DO NOT MODIFY without testing all pipeline views!
// This component is critical for user experience during processing.
// Last verified working: 2026-01-28
// ============================================================
const YouTubeThumbnail = ({ videoUrl, fallbackThumbnail, title, className = '', overlay = null }) => {
  const [imgSrc, setImgSrc] = React.useState(null);
  const [fallbackLevel, setFallbackLevel] = React.useState(0);
  const [isPlaceholder, setIsPlaceholder] = React.useState(false);

  // Extract video ID from YouTube URL
  const getVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getVideoId(videoUrl);

  // Thumbnail sources in priority order (hq → mq → default, skip maxres as it often returns placeholder)
  const thumbnailSources = React.useMemo(() => {
    const sources = [];
    if (videoId) {
      // Start with hqdefault - most reliable, almost always exists
      sources.push(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
      sources.push(`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`);
      sources.push(`https://img.youtube.com/vi/${videoId}/sddefault.jpg`);
      sources.push(`https://img.youtube.com/vi/${videoId}/default.jpg`);
    }
    // Add fallback thumbnail last (often broken URLs from API)
    if (fallbackThumbnail && fallbackThumbnail.startsWith('http')) {
      sources.push(fallbackThumbnail);
    }
    return sources;
  }, [videoId, fallbackThumbnail]);

  // Set initial source
  React.useEffect(() => {
    if (thumbnailSources.length > 0) {
      setImgSrc(thumbnailSources[0]);
      setFallbackLevel(0);
      setIsPlaceholder(false);
    }
  }, [thumbnailSources]);

  // Try next fallback
  const tryNextFallback = () => {
    const nextLevel = fallbackLevel + 1;
    if (nextLevel < thumbnailSources.length) {
      setImgSrc(thumbnailSources[nextLevel]);
      setFallbackLevel(nextLevel);
    } else {
      setIsPlaceholder(true);
    }
  };

  // Handle image load error - try next fallback
  const handleError = () => tryNextFallback();

  // Handle image load - check if it's a YouTube placeholder (120x90)
  const handleLoad = (e) => {
    const img = e.target;
    // YouTube returns 120x90 placeholder for missing thumbnails
    if (img.naturalWidth === 120 && img.naturalHeight === 90) {
      tryNextFallback();
    }
  };

  // Placeholder when no valid source or all sources exhausted
  if (!imgSrc || thumbnailSources.length === 0 || isPlaceholder) {
    return (
      <div className={`bg-gradient-to-br from-dark-300 to-dark-500 flex items-center justify-center ${className}`}>
        <div className="text-center p-4">
          <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-red-600/20 flex items-center justify-center border border-red-500/30">
            <Play size={28} className="text-red-400 ml-1" fill="currentColor" />
          </div>
          <p className="text-cream-400 text-sm font-medium">{title || 'Video Preview'}</p>
        </div>
        {overlay}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <img
        src={imgSrc}
        onError={handleError}
        onLoad={handleLoad}
        className="w-full h-full object-cover"
        alt={title || 'YouTube Video'}
        loading="eager"
      />
      {overlay}
    </div>
  );
};
// ============================================================
// END GUARDRAIL: YouTube Thumbnail Component
// ============================================================

const ArticleCard = ({ item, idx }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.1 }}
      className="group dark:bg-dark-400/40 bg-white rounded-[2rem] border dark:border-dark-300 border-cream-200 overflow-hidden hover:border-gold-500/50 transition-all flex flex-col"
    >
      <div className="aspect-[2/1] bg-dark-300 overflow-hidden relative">
        {item.thumbnail && !imgError ? (
          <img
            src={item.thumbnail}
            alt=""
            onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center dark:bg-dark-300 bg-cream-100 opacity-20">
            <FileText size={40} className="text-cream-500" />
          </div>
        )}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-md text-[11px] font-bold text-white border border-white/10 uppercase tracking-widest">Article</span>
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h4 className="font-bold text-sm leading-tight mb-3 line-clamp-2 group-hover:text-gold-400 transition-colors uppercase tracking-tight italic">{item.title}</h4>
        <p className="text-[11px] dark:text-cream-400 text-cream-400 line-clamp-2 mb-4 leading-relaxed font-medium">{item.contentSnippet}</p>
        <div className="mt-auto flex items-center justify-between pt-4 border-t dark:border-dark-300/50">
          <span className="text-[11px] font-bold text-cream-500">{new Date(item.pubDate).toLocaleDateString()}</span>
          <div className="flex items-center gap-3">
            {item.creator && (
              <span className="px-3 py-1 rounded-full bg-gradient-to-r from-gold-600 to-amber-600 text-white text-[11px] font-bold lowercase tracking-tight shadow-gold-lg/20 border border-white/10 opacity-90">by : {item.creator}</span>
            )}
            <ArrowRight size={14} className="text-gold-500 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
          </div>
        </div>
      </div>
    </motion.a>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b dark:border-dark-300 border-cream-200 py-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left group"
      >
        <h4 className="text-lg font-bold dark:text-white text-dark-200 group-hover:text-gold-500 transition-colors uppercase tracking-tight italic">{question}</h4>
        {isOpen ? <ChevronUp size={20} className="text-gold-500" /> : <ChevronDown size={20} className="text-cream-400" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="mt-4 text-sm leading-relaxed dark:text-cream-400 text-cream-500 font-medium whitespace-pre-line">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const faqData = [
    // ── Getting Started ──
    {
      question: "Apa itu Qlipper?",
      answer: "Qlipper adalah desktop tool berbasis AI untuk kreator konten. Kamu bisa potong video panjang (YouTube atau file lokal) jadi klip-klip pendek siap posting ke TikTok, Reels, atau Shorts — lengkap dengan subtitle, hook text, dan logo overlay. Semua diproses 100% di komputer kamu, jadi privasi video terjaga."
    },
    {
      question: "Bagaimana cara paling cepat pakai Qlipper?",
      answer: "Cara tercepat pakai Pipeline (dari halaman utama):\n1. Paste link YouTube di search bar, lalu klik Generate.\n2. Qlipper akan otomatis download, analisis dengan AI, dan potong video sekaligus.\n3. Hasilnya langsung muncul di My Files — tinggal download.\n\nKalau mau kontrol lebih, pakai menu DOWNLOAD, AI ANALYSIS, dan CLIPPER satu per satu."
    },
    {
      question: "Apakah saya butuh Gemini API Key?",
      answer: "Ya, untuk fitur analisis AI (Get Viral Moment, auto-subtitle, dsb), kamu butuh API Key dari Google Gemini. Gratis untuk penggunaan standar.\n\nCara setup:\n1. Klik tombol Settings di kanan atas.\n2. Masukkan API Key di kolom Gemini API Key.\n3. Klik Check untuk verifikasi, lalu Save.\n\nDapatkan key gratis di aistudio.google.com/apikey"
    },
    // ── Input Sources ──
    {
      question: "Bisa pakai video lokal selain YouTube?",
      answer: "Bisa! Ada 3 cara input video:\n\n1. YouTube — Paste link di search bar.\n2. Local File — Klik chip 'Local File' di bawah search bar, lalu pilih file dari komputer.\n3. Asset Library — Upload file ke Asset Library, lalu pilih dari sana.\n\nUntuk file lokal, Qlipper otomatis transcribe audio menggunakan Whisper supaya AI bisa menganalisis kontennya."
    },
    {
      question: "Download YouTube gagal / error, harus bagaimana?",
      answer: "Beberapa solusi:\n\n1. Update yt-dlp — Buka Settings, klik Update yt-dlp. Ini paling sering berhasil.\n2. Aktifkan Browser Cookies — Di Settings, pilih browser yang sudah login YouTube (Edge/Chrome). Ini membantu untuk video yang di-restrict.\n3. Coba kualitas lebih rendah — Pilih 720p atau 480p.\n4. Upload manual — Kalau semua gagal, download video pakai cara lain, lalu upload ke Qlipper via Local File."
    },
    // ── AI Analysis & Prompts ──
    {
      question: "Apa itu AI Analysis / Get Viral Moment?",
      answer: "Ini fitur inti Qlipper. AI (Google Gemini) membaca transkrip/caption video kamu, lalu menyarankan bagian-bagian mana yang paling menarik untuk dijadikan klip pendek. Hasilnya berisi timestamp (mulai-selesai), topik, dan alasan kenapa momen itu menarik.\n\nDi Pipeline mode, analisis ini berjalan otomatis. Di tab AI ANALYSIS, kamu bisa menjalankannya manual."
    },
    {
      question: "Bisa pakai custom prompt untuk AI?",
      answer: "Bisa! Di Pipeline Configuration, ada Prompt Preset yang bisa dipilih sebelum AI menganalisis video. Kamu juga bisa tulis instruksi custom sendiri — misalnya 'cari momen lucu saja' atau 'fokus ke bagian tutorial'.\n\nPrompt presets bisa dikelola di Settings."
    },
    {
      question: "Error 'Gemini Rate Limit' atau '503 Overloaded'?",
      answer: "Ini batas dari Google, bukan Qlipper.\n\n1. Tunggu 1-2 menit lalu coba lagi.\n2. Ganti model AI di Settings — coba Gemini 2.0 Flash atau 1.5 Flash.\n3. Kalau sering terjadi, pertimbangkan upgrade API key (paket berbayar) di Google AI Studio."
    },
    // ── Transcription ──
    {
      question: "Bagaimana cara transcribe audio / subtitle?",
      answer: "Qlipper punya 2 jalur subtitle:\n\n1. YouTube captions — Otomatis diambil saat download video YouTube. Langsung tersedia untuk subtitle.\n2. Whisper transcription — Untuk video lokal atau YouTube tanpa caption. Pilih file, lalu Qlipper otomatis transcribe menggunakan Whisper AI.\n\nKamu bisa edit transkripsi di tab SUBTITLE — perbaiki teks, timing, atau hapus bagian yang salah."
    },
    {
      question: "Whisper engine apa yang tersedia?",
      answer: "Ada 3 pilihan di Settings:\n\n1. whisper-cpp — Engine default, cepat dan ringan. Berjalan offline di komputer kamu.\n2. Gemini AI — Menggunakan Gemini API untuk transcribe. Lebih akurat untuk bahasa Indonesia, tapi butuh internet + API key.\n3. whisper-wasm — Fallback browser-based. Paling lambat tapi paling kompatibel.\n\nUntuk bahasa Indonesia, Gemini biasanya memberikan hasil terbaik."
    },
    // ── Edit Video / Clip Settings ──
    {
      question: "Bagaimana cara edit klip sebelum export?",
      answer: "Klik klip yang sudah jadi di My Files, lalu pilih Edit Video. Di modal edit kamu bisa:\n\n• Hook Overlay — Tambah teks headline di atas video (font, warna, posisi semua bisa diatur).\n• Subtitle — Aktifkan subtitle karaoke word-by-word dengan warna highlight.\n• Logo — Upload dan posisikan logo/watermark di video.\n• Video Style — Ganti format (Blur, Zoom, Split, dll.) dan resolusi.\n\nSetelah edit, klik 'Apply & Re-render' untuk memproses ulang."
    },
    {
      question: "Format / style video apa saja yang tersedia?",
      answer: "Qlipper menyediakan banyak format:\n\n9:16 Vertical (TikTok/Reels/Shorts):\n• Blur (Fast) — Video di tengah, background blur.\n• Blur (Motion) — Blur background mengikuti gerakan video.\n• Zoom Crop — Crop langsung ke 9:16 tanpa background.\n• Square — Video 1:1 di atas background blur.\n• Split — Dua bagian video (speaker kiri/kanan).\n• Face Track Zoom — Auto-zoom mengikuti wajah pembicara.\n\n3:4 Portrait (IG Post), 1:1 Square (IG Feed), dan Original juga tersedia.\n\nSemua bisa dikombinasikan dengan resolusi 360p sampai 4K."
    },
    {
      question: "Apa itu Multi-Format export?",
      answer: "Dengan Multi-Format, kamu bisa render satu klip ke beberapa format sekaligus — misalnya Blur 9:16 + Square 1:1 + Original, semua dalam satu klik. Hasilnya muncul sebagai variasi di bawah klip utama di My Files.\n\nCara pakai: Klik klip → Edit Video → tab Video Style → aktifkan Multi-Format → centang format yang diinginkan → Apply."
    },
    // ── Subtitle & Hook ──
    {
      question: "Bagaimana cara tambah subtitle ke video?",
      answer: "Di modal Edit Video, buka tab Subtitle lalu aktifkan toggle. Pilih gaya subtitle:\n\n• Karaoke — Teks muncul word-by-word dengan warna highlight (paling populer untuk Shorts/Reels).\n• Full Line — Seluruh kalimat tampil sekaligus.\n\nKamu bisa atur font, ukuran, warna teks, warna highlight, posisi, dan outline. Preview langsung terlihat di panel kanan."
    },
    {
      question: "Apa itu Hook Overlay?",
      answer: "Hook Overlay adalah teks headline besar yang muncul di atas video — biasa dipakai untuk menarik perhatian viewer di detik pertama (contoh: 'INI RAHASIA TRADING!' atau 'WAJIB NONTON').\n\nFitur:\n• Auto — AI otomatis buat teks hook dari topik klip.\n• Use Title — Pakai judul video asli.\n• Manual — Tulis sendiri.\n• Kustomisasi font, warna, outline, shadow, dan posisi."
    },
    {
      question: "Bisa tambah logo / watermark?",
      answer: "Bisa! Di modal Edit Video, buka tab Logo/Sticker:\n\n1. Upload logo (PNG/JPG/SVG) atau pilih dari Logo Library.\n2. Klik di preview video untuk posisikan logo.\n3. Atur scale (ukuran) dan opacity (transparansi) dengan slider.\n\nLogo akan di-burn ke video saat render — tidak bisa dihapus dari hasil akhir."
    },
    // ── Face Detection ──
    {
      question: "Apa itu Face Detection / Face Track Zoom?",
      answer: "Face Track Zoom otomatis mendeteksi wajah pembicara dan crop video mengikuti posisi wajah. Cocok untuk podcast, interview, atau presentasi.\n\nAda 2 mode tracking:\n• Track Sekali — Deteksi wajah di frame pertama, lalu crop tetap di posisi itu.\n• Track Sepanjang — Deteksi wajah sepanjang video dan crop mengikuti gerakan (smooth atau snap).\n\nPilih format 'Zoom (Face)' atau 'Split (Face)' di Video Style untuk mengaktifkannya."
    },
    // ── Asset & File Management ──
    {
      question: "Apa itu Asset Library?",
      answer: "Asset Library adalah tempat penyimpanan file di Qlipper. Kamu bisa upload video, dan file akan tersimpan secara lokal. Video di Asset Library bisa langsung dipakai di Pipeline tanpa upload ulang.\n\nBuka via tab ASSETS atau chip 'Asset Library' di halaman utama."
    },
    {
      question: "Di mana hasil klip tersimpan?",
      answer: "Semua hasil klip tersimpan di tab My Files (ikon Briefcase di navigasi). Setiap project menampilkan:\n\n• Daftar klip yang sudah jadi.\n• Status processing (kalau masih berjalan).\n• Tombol download, edit, dan delete per klip.\n\nFile fisiknya tersimpan di folder backend/db/ di komputer kamu."
    },
    // ── Settings & Troubleshooting ──
    {
      question: "Apa saja yang bisa diatur di Settings?",
      answer: "Di Settings (klik tombol Settings di kanan atas) kamu bisa atur:\n\n• Gemini API Key — Untuk fitur AI.\n• Model AI — Pilih versi Gemini (Flash, Pro, dll.).\n• Whisper Engine — Pilih engine transcription.\n• YouTube Browser Cookies — Untuk video yang di-restrict.\n• Update yt-dlp — Update downloader ke versi terbaru.\n• System Log Stream — Tampilkan/sembunyikan console log.\n• Prompt Presets — Kelola template instruksi AI."
    },
    {
      question: "Video diproses di mana? Apakah aman?",
      answer: "100% di komputer kamu sendiri. Qlipper menggunakan FFmpeg engine yang berjalan secara lokal — video tidak pernah dikirim ke server manapun.\n\nYang dikirim ke internet hanya:\n• Link YouTube (untuk download).\n• Transkrip teks (untuk analisis AI via Gemini).\n\nVideo asli dan hasil klip tetap di komputer kamu."
    },
    {
      question: "Bisa akses Qlipper dari HP?",
      answer: "Bisa! Klik ikon phone di kanan atas untuk menampilkan QR Code. Scan dari HP dan buka di browser — asalkan HP dan komputer terhubung ke WiFi yang sama.\n\nLisensi yang aktif di PC otomatis berlaku untuk semua device yang akses via WiFi yang sama (HP, tablet, laptop). Tidak perlu beli lisensi tambahan."
    },
    {
      question: "Qlipper mendukung platform apa saja?",
      answer: "Qlipper berjalan di macOS dan Windows. Cukup extract ZIP, jalankan launcher (run-mac.sh atau run-windows.bat), dan buka di browser.\n\nTidak perlu install software tambahan — semua dependensi (FFmpeg, yt-dlp, Node.js modules) sudah termasuk dalam paket."
    }
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto py-12 px-6"
    >
      <div className="flex items-center gap-4 mb-12">
        <div className="p-4 rounded-2xl bg-gold-500/10 text-gold-500">
          <HelpCircle size={32} />
        </div>
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight italic uppercase">FAQ & Bantuan</h2>
          <p className="text-sm dark:text-cream-400 text-cream-400 font-bold uppercase tracking-widest">Semua jawaban yang kamu butuhkan</p>
        </div>
      </div>

      <div className="dark:bg-dark-400/40 bg-white rounded-[2rem] border dark:border-dark-300 border-cream-200 p-8 md:p-12 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
          <HelpCircle size={200} className="text-gold-500" />
        </div>
        <div className="relative z-10">
          {faqData.map((item, i) => (
            <FAQItem key={i} question={item.question} answer={item.answer} />
          ))}
        </div>

        <div className="mt-12 p-8 rounded-3xl dark:bg-gold-500/5 bg-gold-50 flex flex-col items-center text-center">
          <h5 className="font-bold text-lg mb-2 italic uppercase tracking-tight">Masih bingung?</h5>
          <p className="text-xs dark:text-cream-400 text-cream-500 mb-6 font-medium">Buka dokumentasi atau tanya admin via Medium.</p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <button onClick={() => window.open('https://medium.com/@qlipper.ai', '_blank')} className="px-8 py-3 rounded-2xl bg-gold-600 text-white font-bold text-xs uppercase tracking-widest shadow-lg hover:bg-gold-500 transition-all active:scale-95">
              Baca Dokumentasi Lainnya
            </button>
            <button onClick={() => window.open('https://instagram.com/qlipper.ai', '_blank')} className="px-8 py-3 rounded-2xl bg-pink-600 text-white font-bold text-xs uppercase tracking-widest shadow-lg hover:bg-pink-500 transition-all active:scale-95 flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-white flex items-center justify-center p-[2px]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-pink-600 w-full h-full"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg></span>
              DM Instagram
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// --- API KEY PROMPT MODAL (Phase 19) ---
// --- REUSABLE API KEY INPUT (Phase 25 - Selectable Models) ---
/**
 * SX-1 + SX-2 (v2.4.0): simplified ApiKeyInput.
 *
 * Behavior changes from v2.3.0:
 * - Auto-save: typing immediately commits via parent's onChange (App-level
 *   useEffect persists to localStorage). No "Save" button — would be
 *   redundant double-save.
 * - Single-purpose button: "Verify" only. Fires API key validation; on
 *   success reports back to parent via `onModelsAvailable(models[])` so the
 *   curated variant radio list can mark each tile with availability badges.
 * - No internal `selectedVariant` state. Variant choice lives in parent's
 *   `geminiVariant` (or equivalent) — single source of truth. Eliminates
 *   the silent-drift bug where ApiKeyInput's dropdown wrote to a different
 *   localStorage key than what backend actually read.
 */
const ApiKeyInput = ({ id, label, value, onChange, placeholder, onModelsAvailable }) => {
  const [status, setStatus] = useState('idle'); // idle, checking, valid, error
  const [msg, setMsg] = useState('');

  // Reset status when user types
  useEffect(() => {
    if (status !== 'idle' && status !== 'checking') {
      setStatus('idle');
      setMsg('');
    }
  }, [value]);

  const handleVerify = async () => {
    if (!value) return;
    setStatus('checking');
    try {
      const res = await axios.post(`${API_BASE}/api/system/check-model`, { model: id, apiKey: value });
      setStatus('valid');
      const models = res.data.models || [];
      const count = models.length;
      setMsg(count > 0 ? `Valid · ${count} models accessible` : 'Key valid');
      // Bubble model list up so the curated variant radio list (in parent)
      // can show availability badges. Parent decides what to do.
      if (onModelsAvailable) onModelsAvailable(models);
    } catch (err) {
      setStatus('error');
      setMsg(err.response?.data?.message || 'Invalid key');
      if (onModelsAvailable) onModelsAvailable([]); // clear stale list
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-[11px] font-bold uppercase tracking-widest dark:text-cream-400 text-cream-500">{label}</label>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 relative">
          <input
            type="password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full dark:bg-dark-600 bg-white border dark:border-dark-300 border-cream-200 rounded-2xl px-5 py-4 text-sm dark:text-white text-dark-200 placeholder-cream-400 focus:ring-2 focus:ring-gold-500/50 outline-none transition-all shadow-inner"
            placeholder={placeholder}
          />
          <button
            onClick={handleVerify}
            disabled={status === 'checking' || !value}
            className={`px-4 rounded-xl font-bold text-xs uppercase tracking-widest transition-all min-w-[100px] flex items-center justify-center gap-2 ${
              status === 'valid'
                ? 'bg-green-600/80 text-white'
                : status === 'error'
                  ? 'bg-red-500 text-white'
                  : 'bg-dark-200 hover:bg-dark-100 text-white'
            } ${(!value || status === 'checking') ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {status === 'checking' ? <Loader2 size={16} className="animate-spin" /> :
              status === 'valid' ? <Check size={16} /> :
                status === 'error' ? <AlertCircle size={16} /> :
                  <Check size={16} />}
            {status === 'checking' ? 'Verifying' :
              status === 'valid' ? 'Verified' :
                status === 'error' ? 'Retry' :
                  'Verify'}
          </button>
        </div>

        {msg && (
          <p className={`text-[11px] font-bold ${status === 'error' ? 'text-red-500' : 'text-green-500'} animate-in fade-in slide-in-from-top-1`}>
            {msg}
          </p>
        )}
      </div>
    </div>
  );
};

const ApiKeyPrompt = ({ isOpen, onClose, onSave }) => {
  const [keyInput, setKeyInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleVerify = async () => {
    if (!keyInput.trim()) {
      setError("Please enter an API Key");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/api/system/check-gemini`, { apiKey: keyInput });
      // Success
      onSave(keyInput);
    } catch (err) {
      setError(err.response?.data?.message || "Verification Failed. Key invalid.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md bg-white dark:bg-dark-400 rounded-3xl border border-cream-200 dark:border-dark-300 shadow-2xl overflow-hidden"
      >
        <div className="p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-gold-500/10 flex items-center justify-center mb-6 text-gold-500">
            <Key size={32} />
          </div>
          <h2 className="text-2xl font-bold dark:text-white text-dark-200 mb-2">Gemini API Required</h2>
          <p className="text-sm text-cream-500 dark:text-cream-400 mb-8 leading-relaxed">
            To use the AI features (Viral Moments, Analysis), you need a Google Gemini API Key. It's free and takes 1 minute to get.
          </p>

          <div className="w-full space-y-4">
            <div className="relative">
              <input
                type="text"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Paste your API Key here..."
                className="w-full px-4 py-4 rounded-xl bg-cream-100 dark:bg-dark-400 border border-cream-200 dark:border-dark-200 focus:ring-2 focus:ring-gold-500 outline-none font-mono text-sm dark:text-white"
              />
            </div>
            {error && (
              <div className="text-xs font-bold text-red-500 flex items-center justify-center gap-1 bg-red-500/10 py-2 rounded-lg">
                <AlertCircle size={12} /> {error}
              </div>
            )}

            <button
              onClick={handleVerify}
              disabled={verifying}
              className="w-full py-4 rounded-xl bg-gold-600 hover:bg-gold-500 text-white font-bold shadow-gold-lg/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              {verifying ? <Loader2 size={20} className="animate-spin" /> : <>Verify & Save <ArrowRight size={18} /></>}
            </button>

            <button onClick={onClose} className="w-full py-3 text-xs font-bold text-cream-400 hover:text-cream-500 dark:hover:text-cream-100 transition-colors uppercase tracking-widest">
              Cancel
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-cream-200 dark:border-dark-300 w-full">
            <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-gold-500 hover:underline flex items-center justify-center gap-1">
              Get a free API Key <Globe size={12} />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- COLOR PICKER (stable component, outside modal to prevent re-creation on render) ---
const ColorPicker = React.memo(({ label, color, onChange, showBgToggle, bgEnabled, onBgToggle }) => {
  const [showPicker, setShowPicker] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(color);

  React.useEffect(() => {
    setInputValue(color);
  }, [color]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      onChange(val);
    }
  };

  const handleRgbChange = (channel, value) => {
    const hex = (color || '#000000').replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;

    const newR = channel === 'r' ? value : r;
    const newG = channel === 'g' ? value : g;
    const newB = channel === 'b' ? value : b;

    const newHex = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`.toUpperCase();
    onChange(newHex);
    setInputValue(newHex);
  };

  const hexToRgb = (hex) => {
    const h = (hex || '#000000').replace('#', '');
    return {
      r: parseInt(h.substring(0, 2), 16) || 0,
      g: parseInt(h.substring(2, 4), 16) || 0,
      b: parseInt(h.substring(4, 6), 16) || 0
    };
  };

  const rgb = hexToRgb(color || '#00FF00');
  const presetColors = ['#00FF00', '#FFD700', '#FFFFFF', '#FF4444', '#00BFFF', '#FF00FF', '#000000', '#1a1a2e'];

  return (
    <div className="space-y-2 relative z-10">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">{label}</label>
        {showBgToggle && (
          <button
            onClick={(e) => { e.stopPropagation(); onBgToggle(); }}
            className={`text-[11px] px-2 py-0.5 rounded relative z-10 ${bgEnabled ? 'bg-green-500/20 text-green-400' : 'bg-cream-200 dark:bg-dark-200 text-cream-500 dark:text-cream-400'}`}
          >
            {bgEnabled ? 'ON' : 'OFF'}
          </button>
        )}
      </div>

      <div className="flex gap-2 items-center">
        <button
          onClick={(e) => { e.stopPropagation(); setShowPicker(!showPicker); }}
          className="w-8 h-8 min-w-[32px] min-h-[32px] aspect-square flex-shrink-0 rounded-lg border-2 border-cream-300 dark:border-dark-100 hover:border-gold-500 transition-all shadow-inner flex items-center justify-center relative z-10 hover:z-20"
          style={{ backgroundColor: color === 'transparent' ? '#1a1a1a' : color }}
        >
          {color === 'transparent' && <span className="text-[7px] text-cream-500">-</span>}
        </button>

        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onClick={(e) => e.stopPropagation()}
          className="w-24 px-2 py-1.5 text-xs font-mono bg-white dark:bg-dark-300 border border-cream-300 dark:border-dark-200 rounded-lg text-dark-200 dark:text-white focus:outline-none focus:border-gold-500 relative z-10 focus:z-20"
          placeholder="#00FF00"
        />
      </div>

      {showPicker && (
        <div className="bg-dark-300/80 rounded-xl p-3 space-y-3 border border-dark-200 relative z-50 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1.5 flex-wrap">
            {presetColors.map((c) => (
              <button
                key={c}
                onClick={() => { onChange(c); setInputValue(c); }}
                className={`w-6 h-6 min-w-[24px] min-h-[24px] aspect-square flex-shrink-0 rounded-full border-2 transition-all ${color === c ? 'border-gold-500 scale-110' : 'border-dark-100 hover:border-cream-400'}`}
                style={{ backgroundColor: c }}
              />
            ))}
            {showBgToggle && (
              <button
                onClick={() => { onChange('transparent'); setInputValue('transparent'); }}
                className={`w-6 h-6 min-w-[24px] min-h-[24px] aspect-square flex-shrink-0 rounded-full border-2 transition-all bg-dark-400 text-[11px] text-cream-500 ${color === 'transparent' ? 'border-gold-500' : 'border-dark-100'}`}
              >
                X
              </button>
            )}
          </div>

          <div className="space-y-2 relative z-10 pointer-events-auto">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-red-400 w-4">R</span>
              <input type="range" min="0" max="255" value={rgb.r} onChange={(e) => handleRgbChange('r', parseInt(e.target.value))} className="flex-1 h-2 accent-red-500 relative z-10 cursor-pointer" />
              <span className="text-[11px] font-mono text-cream-400 w-8">{rgb.r}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-green-400 w-4">G</span>
              <input type="range" min="0" max="255" value={rgb.g} onChange={(e) => handleRgbChange('g', parseInt(e.target.value))} className="flex-1 h-2 accent-green-500 relative z-10 cursor-pointer" />
              <span className="text-[11px] font-mono text-cream-400 w-8">{rgb.g}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-blue-400 w-4">B</span>
              <input type="range" min="0" max="255" value={rgb.b} onChange={(e) => handleRgbChange('b', parseInt(e.target.value))} className="flex-1 h-2 accent-blue-500 relative z-10 cursor-pointer" />
              <span className="text-[11px] font-mono text-cream-400 w-8">{rgb.b}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 relative z-10 pointer-events-auto">
            <input
              type="color"
              value={color === 'transparent' ? '#000000' : color}
              onChange={(e) => { onChange(e.target.value.toUpperCase()); setInputValue(e.target.value.toUpperCase()); }}
              className="w-8 h-8 min-w-[32px] min-h-[32px] aspect-square flex-shrink-0 rounded cursor-pointer relative z-10"
            />
            <span className="text-[11px] text-cream-500">Click to open system color picker</span>
          </div>
        </div>
      )}
    </div>
  );
});

// --- CLIP EDIT MODAL (Hook, Subtitle, Style) ---
const ClipEditModal = ({
  isOpen,
  type,
  clipIdx,
  clip,
  projectId,
  editState,
  videoThumbnail,
  setEditState,
  onClose,
  onApply,
  generateAutoHook,
  isReprocessing,
  formatThumbnails,
  captionEnhanceStatus,
  captionStatus,
  enhanceCaptions,
  logos,
  logoOverlay,
  setLogoOverlay,
  fetchLogos,
  styleSettings,
  setStyleSettings,
  multiFormatSelections,
  onMultiFormatToggle,
  onMultiFormatApply,
  videoTitle
}) => {
  const [activeTab, setActiveTab] = React.useState('hook');

  const isPresetMode = clipIdx === 'preset';

  React.useEffect(() => {
    if (type === 'multi-format') setActiveTab('style');
    else if (type !== 'full') setActiveTab(type);
    else setActiveTab('hook');
  }, [type, isOpen]);

  // Measured width of the hook preview container.
  // Drives the shared overlayMath so preview pixel sizes match the Puppeteer
  // PNG export's scale, eliminating the "preview ≠ exported MP4" drift.
  const hookPreviewRef = useRef(null);
  const [hookPreviewWidth, setHookPreviewWidth] = useState(288); // w-72 default
  useLayoutEffect(() => {
    if (!hookPreviewRef.current) return;
    const el = hookPreviewRef.current;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setHookPreviewWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, activeTab]);

  if (!isOpen || (!clip && !isPresetMode)) return null;

  // In preset mode, use a dummy clip object
  const effectiveClip = isPresetMode ? { topic: 'Style Preset', customHook: '', customSubtitle: '', outputFormat: 'stacked-blur', resolution: '720p' } : clip;

  const currentHookText = editState?.hookText ?? effectiveClip.customHook ?? '';
  const currentSubtitleText = editState?.subtitleText ?? effectiveClip.customSubtitle ?? '';
  const currentFormat = editState?.format ?? effectiveClip.outputFormat ?? 'stacked-blur';

  // Custom FFmpeg Style Defaults (neon-green as default)
  const hookTextColor = editState?.hookTextColor ?? '#00FF00';
  const hookBorderColor = editState?.hookBorderColor ?? '#000000';
  const hookBgColor = editState?.hookBgColor ?? 'transparent';
  const hookBorderWidth = editState?.hookBorderWidth ?? 8;
  const hookFontFamily = editState?.hookFontFamily ?? 'Sans-Bold';
  const hookFontSize = editState?.hookFontSize ?? 72;
  const hookBgEnabled = editState?.hookBgEnabled ?? false;
  const hookOutlineEnabled = editState?.hookOutlineEnabled ?? true;
  const hookShadowEnabled = editState?.hookShadowEnabled ?? true;

  // Subtitle/Caption Style Defaults
  const subtitleTextColor = editState?.subtitleTextColor ?? '#FFFFFF';
  const subtitleHighlightColor = editState?.subtitleHighlightColor ?? '#00FF66';
  const subtitleOutlineColor = editState?.subtitleOutlineColor ?? '#000000';
  const subtitleBgColor = editState?.subtitleBgColor ?? 'transparent';
  const subtitleFontFamily = editState?.subtitleFontFamily ?? 'Sans-Bold';
  const subtitleFontSize = editState?.subtitleFontSize ?? 48;
  const subtitleOutlineWidth = editState?.subtitleOutlineWidth ?? 3;
  const subtitlePosition = editState?.subtitlePosition ?? 85;
  const subtitleAlignment = editState?.subtitleAlignment ?? 'center';
  const subtitleWordByWord = editState?.subtitleWordByWord ?? true;
  const subtitleBgEnabled = editState?.subtitleBgEnabled ?? false;
  const subtitleShadowEnabled = editState?.subtitleShadowEnabled ?? true;
  const subtitleScaleEffect = editState?.subtitleScaleEffect ?? false;
  const subtitleGlowEffect = editState?.subtitleGlowEffect ?? true;
  const subtitleMaxWordsPerLine = editState?.subtitleMaxWordsPerLine ?? 4;
  const subtitleUppercase = editState?.subtitleUppercase ?? true;
  const subtitleMinDisplaySec = editState?.subtitleMinDisplaySec ?? 0.8;
  const subtitleHighlightScale = editState?.subtitleHighlightScale ?? 115;
  const hookUppercase = editState?.hookUppercase ?? true;

  // Available fonts
  const FONT_OPTIONS = [
    { id: 'Sans-Bold', label: 'Sans Bold' },
    { id: 'Poppins-Black', label: 'Poppins Black' },
    { id: 'Poppins-Bold', label: 'Poppins Bold' },
    { id: 'Arial-Bold', label: 'Arial Bold' },
    { id: 'Helvetica-Bold', label: 'Helvetica Bold' },
    { id: 'Impact', label: 'Impact' },
    { id: 'Roboto-Bold', label: 'Roboto Bold' },
    { id: 'Roboto-Black', label: 'Roboto Black' },
    { id: 'Montserrat-Black', label: 'Montserrat Black' },
    { id: 'Montserrat-ExtraBold', label: 'Montserrat ExtraBold' },
    { id: 'Oswald-Bold', label: 'Oswald Bold' },
    { id: 'Bebas-Neue', label: 'Bebas Neue' },
    { id: 'Bangers', label: 'Bangers' },
    { id: 'Komika-Axis', label: 'Komika Axis' },
    { id: 'The-Bold-Font', label: 'The Bold Font (Hormozi Style)' },
    { id: 'Anton', label: 'Anton' },
    { id: 'Permanent-Marker', label: 'Permanent Marker' },
    { id: 'Luckiest-Guy', label: 'Luckiest Guy' },
  ];

  const formatOptions = [
    { id: 'raw-cuts', label: 'Original', desc: 'No changes' },
    { id: 'stacked-blur', label: '9:16 Stacked', desc: 'Blur background' },
    { id: 'center-crop', label: '9:16 Zoomed', desc: 'Center crop' },
    { id: 'portrait-square', label: '9:16 Square', desc: '1:1 center + blur' },
    { id: 'square-blur', label: '1:1 Square', desc: 'Blur background' },
    { id: 'square-zoom', label: '1:1 Zoomed', desc: 'Center crop' }
  ];

  const showHook = type === 'hook' || type === 'full';
  const showSubtitle = type === 'subtitle' || type === 'full';
  const showStyle = type === 'style' || type === 'full' || type === 'multi-format';
  const showLogo = type === 'logo' || type === 'full';
  const currentLogoState = logoOverlay?.[clipIdx] || null;

  return (
    <div className={`fixed inset-0 ${isPresetMode ? 'z-[120]' : 'z-[100]'} flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm`} onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className={`w-full ${type === 'full' ? 'max-w-5xl' : 'max-w-lg'} bg-white dark:bg-dark-400 rounded-3xl border border-cream-200 dark:border-dark-300 shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b dark:border-dark-300">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type === 'full' ? 'bg-gradient-to-br from-gold-500/20 to-purple-500/20 text-gold-500' :
                type === 'hook' ? 'bg-gold-500/10 text-gold-500' :
                  type === 'subtitle' ? 'bg-blue-500/10 text-blue-500' :
                    type === 'multi-format' ? 'bg-indigo-500/10 text-indigo-500' :
                    'bg-purple-500/10 text-purple-500'
                }`}>
                {type === 'full' && <Settings size={20} />}
                {type === 'hook' && <Type size={20} />}
                {type === 'subtitle' && <FileText size={20} />}
                {type === 'style' && <Palette size={20} />}
                {type === 'multi-format' && <Copy size={20} />}
              </div>
              <div>
                <h3 className="text-lg font-bold dark:text-white">
                  {type === 'full' && (isPresetMode ? 'Style Defaults' : 'Edit Video')}
                  {type === 'hook' && 'Add Hook Overlay'}
                  {type === 'subtitle' && 'Add Subtitle'}
                  {type === 'style' && 'Change Video Style'}
                  {type === 'multi-format' && 'Multi-Format Export'}
                </h3>
                <p className="text-xs text-cream-500">{isPresetMode ? 'Configure default style for all clips' : `Clip #${clipIdx + 1}: ${effectiveClip.topic}`}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isPresetMode && videoThumbnail && (
                <img src={videoThumbnail} alt="Video preview" className="h-10 w-16 rounded-lg object-cover border border-dark-200/50" />
              )}
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-cream-100 dark:hover:bg-dark-300 text-cream-400 hover:text-cream-500 transition-all">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Tabs for Full Mode */}
          {type === 'full' && (
            <div className="flex gap-1 bg-cream-100 dark:bg-dark-300/50 p-1 rounded-xl">
              {[
                { id: 'hook', label: 'Hook Overlay', icon: Type, enabled: true },
                { id: 'subtitle', label: 'Subtitle', icon: FileText, enabled: true },
                { id: 'logo', label: 'Logo/Sticker', icon: Stamp, enabled: true },
                { id: 'style', label: 'Video Style', icon: Palette, enabled: true }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => tab.enabled && setActiveTab(tab.id)}
                  disabled={!tab.enabled}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all ${activeTab === tab.id
                    ? 'bg-white dark:bg-dark-400 text-gold-600 shadow-sm'
                    : tab.enabled
                      ? 'text-cream-500 hover:text-dark-200 dark:hover:text-cream-200'
                      : 'text-cream-400 cursor-not-allowed opacity-60'
                    }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                  {tab.soon && <span className="px-1 py-0.5 bg-amber-500/20 text-amber-500 text-[11px] font-bold rounded">SOON</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content - Two Column Layout for Hook/Subtitle/Logo Mode */}
        <div className={`${(type === 'full' && (activeTab === 'hook' || activeTab === 'subtitle' || activeTab === 'logo')) ? 'flex gap-6' : ''} p-6 max-h-[70vh] overflow-hidden`}>
          {/* HOOK MODE - Left Column: Controls */}
          {(showHook && (type !== 'full' || activeTab === 'hook')) && (
            <div className="flex-1 space-y-5 overflow-y-auto pr-2" style={{ maxHeight: '65vh' }}>
              {/* Hook Source Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Hook Text Source</label>
                <div className="flex gap-1 bg-cream-100 dark:bg-dark-300/50 p-1 rounded-xl">
                  {[
                    { id: 'none', label: 'Manual' },
                    { id: 'auto', label: 'Auto' },
                    { id: 'title', label: 'Use Title' }
                  ].map(src => (
                    <button
                      key={src.id}
                      onClick={() => {
                        if (setStyleSettings) setStyleSettings(prev => ({ ...prev, hookSource: src.id }));
                        // Auto-fill hook text based on source
                        if (src.id === 'auto') {
                          const autoHook = generateAutoHook(effectiveClip.topic);
                          setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookText: autoHook } }));
                        } else if (src.id === 'title') {
                          const titleText = videoTitle || '[Video Title]';
                          setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookText: titleText } }));
                        }
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                        styleSettings?.hookSource === src.id
                          ? 'bg-white dark:bg-dark-400 text-gold-600 shadow-sm'
                          : 'text-cream-500 hover:text-dark-200 dark:hover:text-cream-200'
                      }`}
                    >
                      {src.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hook Input */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Hook Text</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={styleSettings?.hookSource === 'title' ? (videoTitle || '[Video Title]') : 'Enter hook text...'}
                    value={currentHookText}
                    onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookText: e.target.value } }))}
                    className="flex-1 px-3 py-2 text-sm rounded-xl bg-cream-100 dark:bg-dark-400 border border-cream-200 dark:border-dark-200 focus:ring-2 focus:ring-gold-500 outline-none dark:text-white"
                  />
                  <button
                    onClick={() => {
                      const autoHook = generateAutoHook(effectiveClip.topic);
                      setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookText: autoHook } }));
                    }}
                    className="px-3 py-2 bg-gold-500/10 hover:bg-gold-500/20 text-gold-600 dark:text-gold-400 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all whitespace-nowrap"
                  >
                    <Sparkles size={14} /> Auto
                  </button>
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookUppercase: !hookUppercase } }))}
                    className={`px-3 py-2 rounded-xl text-xs font-black transition-all whitespace-nowrap border ${hookUppercase
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-dark-300 border-dark-200 text-cream-400 hover:bg-dark-200'}`}
                    title="Force UPPERCASE hook text"
                  >
                    AA
                  </button>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Quick Presets</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'neon-green', label: 'Neon Green', color: '#00FF00', border: '#000000', bg: 'transparent' },
                    { id: 'bold-gold', label: 'Bold Gold', color: '#FFD700', border: '#000000', bg: 'transparent' },
                    { id: 'clean-white', label: 'Clean White', color: '#FFFFFF', border: '#000000', bg: 'transparent' },
                    { id: 'fire-red', label: 'Fire Red', color: '#FF4444', border: '#000000', bg: 'transparent' },
                    { id: 'ocean-blue', label: 'Ocean Blue', color: '#00BFFF', border: '#000000', bg: 'transparent' },
                    { id: 'box-white', label: 'Box White', color: '#000000', border: '#000000', bg: '#FFFFFF', bgOn: true },
                    { id: 'box-gold', label: 'Box Gold', color: '#000000', border: '#000000', bg: '#FFD700', bgOn: true }
                  ].map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => setEditState(prev => ({
                        ...prev,
                        [clipIdx]: {
                          ...prev[clipIdx],
                          hookTextColor: preset.color,
                          hookBorderColor: preset.border,
                          hookBgColor: preset.bg,
                          hookBgEnabled: preset.bgOn || false
                        }
                      }))}
                      className="px-3 py-1.5 rounded-lg border border-cream-300 dark:border-dark-200 bg-neutral-800 hover:border-gold-500 transition-all shadow-sm"
                      title={`Preview: ${preset.label} on video`}
                    >
                      <span
                        className="text-[11px] font-bold"
                        style={{
                          color: preset.color,
                          backgroundColor: preset.bgOn ? preset.bg : 'transparent',
                          textShadow: preset.bgOn ? 'none' : `1px 1px 0 ${preset.border}, -1px -1px 0 ${preset.border}`,
                          padding: preset.bgOn ? '2px 6px' : '0',
                          borderRadius: '4px'
                        }}
                      >
                        {preset.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* FFmpeg Style Controls */}
              <div className="bg-cream-100 dark:bg-dark-300/30 rounded-xl p-4 space-y-4 border border-cream-300 dark:border-dark-200/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-amber-500">Advanced Styling</span>
                  <span className="text-[11px] text-cream-500 bg-cream-200 dark:bg-dark-200/50 px-2 py-0.5 rounded">Advanced</span>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="relative z-20">
                    <ColorPicker
                      label="Text Color"
                      color={hookTextColor}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookTextColor: c } }))}
                    />
                  </div>
                  <div className="relative z-20">
                    <ColorPicker
                      label="Outline Color"
                      color={hookOutlineEnabled ? hookBorderColor : 'transparent'}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookBorderColor: c } }))}
                      showBgToggle={true}
                      bgEnabled={hookOutlineEnabled}
                      onBgToggle={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookOutlineEnabled: !hookOutlineEnabled } }))}
                    />
                  </div>
                  <div className="relative z-20">
                    <ColorPicker
                      label="Background"
                      color={hookBgEnabled ? hookBgColor : 'transparent'}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookBgColor: c, hookBgEnabled: c !== 'transparent' } }))}
                      showBgToggle={true}
                      bgEnabled={hookBgEnabled}
                      onBgToggle={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookBgEnabled: !hookBgEnabled } }))}
                    />
                  </div>
                </div>

                {/* Font & Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Font Family</label>
                    <select
                      value={hookFontFamily}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookFontFamily: e.target.value } }))}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-white dark:bg-dark-300 border border-cream-300 dark:border-dark-200 text-dark-200 dark:text-white focus:outline-none focus:border-gold-500"
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.id} value={font.id}>{font.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Font Size</label>
                      <span className="text-xs text-cream-500">{hookFontSize}px</span>
                    </div>
                    <input
                      type="range"
                      min="24"
                      max="120"
                      value={hookFontSize}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookFontSize: parseInt(e.target.value) } }))}
                      className="w-full accent-gold-500"
                    />
                  </div>
                </div>

                {/* Outline Width & Shadow Toggle */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Outline Width</label>
                      <span className="text-xs text-cream-500">{hookBorderWidth}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={hookBorderWidth}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookBorderWidth: parseInt(e.target.value) } }))}
                      className="w-full accent-gold-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Drop Shadow</label>
                    <button
                      onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookShadowEnabled: !hookShadowEnabled } }))}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-all ${hookShadowEnabled ? 'bg-gold-600 border-gold-500 text-white' : 'bg-white dark:bg-dark-300 border-cream-300 dark:border-dark-200 text-cream-500 dark:text-cream-400'}`}
                    >
                      {hookShadowEnabled ? 'Shadow ON' : 'Shadow OFF'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Position Controls */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Alignment</label>
                  <div className="flex gap-1">
                    {['left', 'center', 'right'].map((align) => (
                      <button
                        key={align}
                        onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookAlignment: align } }))}
                        className={`flex-1 py-2 rounded-lg border text-xs font-bold capitalize transition-all ${(editState?.hookAlignment ?? 'center') === align
                          ? 'bg-gold-500/20 border-gold-500 text-gold-400'
                          : 'bg-white dark:bg-dark-300/50 border-cream-300 dark:border-dark-200 text-cream-500 dark:text-cream-400 hover:border-gold-300 dark:hover:border-dark-100'
                          }`}
                      >
                        {align}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Y Position</label>
                    <span className="text-xs text-cream-500">{editState?.hookVerticalPosition ?? 75}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="95"
                    value={editState?.hookVerticalPosition ?? 75}
                    onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], hookVerticalPosition: parseInt(e.target.value) } }))}
                    className="w-full accent-gold-500"
                  />
                </div>
              </div>

              {/* Sticker Headline — disabled, kept for future use */}
            </div>
          )}

          {/* HOOK MODE - Right Column: Live Preview */}
          {(showHook && (type !== 'full' || activeTab === 'hook')) && (
            <div className="w-72 flex-shrink-0 bg-dark-300/30 rounded-2xl p-4 border border-dark-200/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold uppercase text-gold-400">Live Preview</span>
                <span className="text-[11px] text-cream-500">Pixel-perfect output</span>
              </div>
              <div ref={hookPreviewRef} className="aspect-[9/16] bg-gradient-to-b from-dark-200 to-dark-500 rounded-xl overflow-hidden relative shadow-2xl border border-dark-100/30">
                {/* Video thumbnail as background */}
                {clip?.url && (
                  <video
                    src={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}#t=0.5`}
                    className="absolute inset-0 w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                {currentHookText && (() => {
                  const layout = computeOverlayLayout({
                    containerWidthPx: hookPreviewWidth,
                    containerHeightPx: hookPreviewWidth * (16 / 9),
                    fontSize: hookFontSize,
                    borderWidth: hookBorderWidth,
                    verticalPositionPct: editState?.hookVerticalPosition ?? 75,
                    alignment: editState?.hookAlignment ?? 'center',
                  });
                  return (
                    <div
                      className="absolute"
                      style={{
                        top: `${layout.topPct}%`,
                        left: `${layout.paddingPx}px`,
                        right: `${layout.paddingPx}px`,
                        transform: layout.translateY,
                        textAlign: layout.textAlign,
                      }}
                    >
                      <p
                        className="leading-tight"
                        style={{
                          color: hookTextColor,
                          fontSize: `${layout.fontSizePx}px`,
                          fontFamily: hookFontFamily.includes('Poppins') ? 'Poppins, sans-serif' : hookFontFamily.includes('Komika') ? '"Komika Axis", sans-serif' : hookFontFamily.includes('Bold-Font') ? '"The Bold Font", sans-serif' : 'system-ui, sans-serif',
                          fontWeight: 900,
                          WebkitTextStroke: (hookBgEnabled || !hookOutlineEnabled) ? 'none' : `${layout.borderWidthPx}px ${hookBorderColor}`,
                          paintOrder: 'stroke fill',
                          textShadow: (hookBgEnabled || !hookShadowEnabled) ? 'none' : '2px 2px 4px rgba(0,0,0,0.5)',
                          backgroundColor: hookBgEnabled && hookBgColor !== 'transparent' ? hookBgColor : 'transparent',
                          padding: hookBgEnabled ? '6px 12px' : '0',
                          borderRadius: hookBgEnabled ? '6px' : '0',
                          display: 'inline-block',
                        }}
                      >
                        {hookUppercase ? currentHookText.toUpperCase() : currentHookText}
                      </p>
                    </div>
                  );
                })()}
                {!currentHookText && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-cream-500 text-sm">Enter hook text</p>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-cream-500 text-center mt-3">Preview matches final video output</p>
            </div>
          )}

          {/* SUBTITLE MODE - Left Column: Controls */}
          {(showSubtitle && (type !== 'full' || activeTab === 'subtitle')) && (
            <div className="flex-1 space-y-5 overflow-y-auto pr-2" style={{ maxHeight: '65vh' }}>
              {/* Subtitle Toggle — always visible at top */}
              <div className={`p-3 rounded-xl border space-y-2.5 ${currentFormat === 'raw-cuts' ? 'bg-dark-300/30 border-dark-200/50 opacity-60' : 'bg-dark-300/50 border-dark-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">Enable Subtitles</p>
                    <p className="text-[11px] text-cream-400">
                      {currentFormat === 'raw-cuts'
                        ? 'Not available with Original (raw-cuts) format'
                        : 'Burn captions into exported video'}
                    </p>
                    {currentFormat !== 'raw-cuts' && !(editState?.useVtt ?? styleSettings.subtitleEnabled ?? false) && (
                      <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        captionStatus?.type === 'whisper' ? 'bg-blue-500/20 text-blue-400' :
                        captionStatus?.type === 'vtt' ? 'bg-yellow-500/20 text-yellow-400' :
                        captionStatus?.type === 'edited' ? 'bg-green-500/20 text-green-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {captionStatus?.type === 'whisper' ? 'Whisper Ready' : captionStatus?.type === 'vtt' ? 'YouTube Caption' : captionStatus?.type === 'edited' ? 'Edited' : 'No Captions'}
                      </span>
                    )}
                  </div>
                  <button
                    disabled={currentFormat === 'raw-cuts'}
                    onClick={() => {
                      const wasEnabled = editState?.useVtt ?? styleSettings.subtitleEnabled ?? false;
                      setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], useVtt: !wasEnabled } }));
                      // Toggle is now instant — no auto-Whisper on enable.
                      // Whisper runs lazily during render (see reprocessClip line ~3559)
                      // so user doesn't see a "Syncing..." spinner here. The status
                      // badge below reflects whichever caption source is currently
                      // cached; render-time auto-upgrade swaps it for word-level
                      // when Whisper finishes in the background.
                    }}
                    className={`w-12 h-6 rounded-full transition-all ${currentFormat === 'raw-cuts' ? 'bg-dark-200 cursor-not-allowed' : (editState?.useVtt ?? styleSettings.subtitleEnabled ?? false) ? 'bg-blue-500' : 'bg-dark-100'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-all ${(editState?.useVtt ?? styleSettings.subtitleEnabled ?? false) && currentFormat !== 'raw-cuts' ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Caption Source Indicator — only when enabled */}
                {(editState?.useVtt ?? styleSettings.subtitleEnabled ?? false) && currentFormat !== 'raw-cuts' && (
                  <div className="flex items-center justify-between pt-1 border-t border-dark-200/50">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                        captionEnhanceStatus === 'enhancing' ? 'bg-gold-500/20 text-gold-400 ring-1 ring-gold-500/30' :
                        captionStatus?.type === 'edited' ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30' :
                        captionStatus?.type === 'whisper' ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30' :
                        captionStatus?.type === 'vtt' && captionStatus?.wordLevel ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30' :
                        captionStatus?.type === 'vtt' ? 'bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-500/30' :
                        'bg-red-500/20 text-red-400 ring-1 ring-red-500/30'
                      }`}>
                        {captionEnhanceStatus === 'enhancing' && <><Loader2 size={9} className="animate-spin" /> Syncing...</>}
                        {captionEnhanceStatus !== 'enhancing' && captionStatus?.type === 'edited' && <><CheckCircle2 size={9} /> Edited Transcript</>}
                        {captionEnhanceStatus !== 'enhancing' && captionStatus?.type === 'whisper' && <><CheckCircle2 size={9} /> AI Word Sync</>}
                        {captionEnhanceStatus !== 'enhancing' && captionStatus?.type === 'vtt' && captionStatus?.wordLevel && <><CheckCircle2 size={9} /> Caption Ready</>}
                        {captionEnhanceStatus !== 'enhancing' && captionStatus?.type === 'vtt' && !captionStatus?.wordLevel && <><AlertCircle size={9} /> Estimated Timing</>}
                        {captionEnhanceStatus !== 'enhancing' && (!captionStatus || captionStatus.type === 'none') && <><AlertCircle size={9} /> No Captions</>}
                      </span>
                    </div>
                    {captionEnhanceStatus !== 'enhancing' && captionStatus?.type !== 'edited' && captionStatus?.type !== 'none' && (
                      <button onClick={enhanceCaptions} className="px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-gold-600/20 text-gold-400 hover:bg-gold-600/30 ring-1 ring-gold-500/30 transition-all">
                        <span className="flex items-center gap-1"><Sparkles size={9} /> Whisper AI Sync</span>
                      </button>
                    )}
                    {captionEnhanceStatus !== 'enhancing' && (!captionStatus || captionStatus.type === 'none') && (
                      <button onClick={enhanceCaptions} className="px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 ring-1 ring-blue-500/30 transition-all">
                        <span className="flex items-center gap-1"><Sparkles size={9} /> Generate Captions</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Subtitle customization controls — only visible when enabled */}
              {(editState?.useVtt ?? styleSettings.subtitleEnabled ?? false) && currentFormat !== 'raw-cuts' && (<>
              {/* Caption Style Presets — Vizard-style visual cards */}
              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Caption Style</label>
                <p className="text-[11px] text-cream-500 dark:text-cream-400 -mt-1">Choose a style preset to get started</p>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {[
                    { id: 'karaoke', label: 'Karaoke', preview: ['TO GET', 'STARTED'], textColor: '#FFFFFF', highlightColor: '#00FF66', outline: '#000000', glow: true, wordByWord: true, font: 'Sans Bold' },
                    { id: 'beasty', label: 'Beasty', preview: ['CHOOSE A', 'STYLE'], textColor: '#FFFFFF', highlightColor: '#FFB800', outline: '#000000', scale: true, wordByWord: true, font: 'Impact' },
                    { id: 'deep-diver', label: 'Deep Diver', preview: ['to get', 'started'], textColor: '#CCCCCC', highlightColor: '#FFFFFF', outline: 'transparent', bg: 'rgba(30,30,30,0.85)', wordByWord: false, font: 'Sans Bold' },
                    { id: 'youshaei', label: 'Youshaei', preview: ['TO GET', 'STARTED'], textColor: '#FFFFFF', highlightColor: '#E040FB', outline: '#000000', glow: false, wordByWord: true, font: 'Poppins Black' },
                    { id: 'pod-p', label: 'Pod P', preview: ['GET', 'STARTED'], textColor: '#FFFFFF', highlightColor: '#FF4444', outline: '#000000', glow: false, wordByWord: true, font: 'The Bold Font' },
                    { id: 'mozi', label: 'Mozi', preview: ['CHOOSE A', 'STYLE'], textColor: '#FFFFFF', highlightColor: '#00BFFF', outline: '#000000', glow: false, wordByWord: true, font: 'Roboto Bold' },
                    { id: 'popline', label: 'Popline', preview: ['TO GET', 'STARTED'], textColor: '#FFFFFF', highlightColor: '#FFFFFF', outline: '#222222', bg: 'transparent', glow: false, wordByWord: true, font: 'Arial Bold' },
                    { id: 'simple', label: 'Simple', preview: ['CHOOSE A', 'STYLE'], textColor: '#FFFFFF', highlightColor: '#FFFFFF', outline: '#000000', bg: 'rgba(0,0,0,0.7)', wordByWord: false, font: 'Sans Bold' },
                    { id: 'think-media', label: 'Think Media', preview: ['TO GET', 'STARTED'], textColor: '#00FF00', highlightColor: '#FFFF00', outline: '#000000', glow: true, wordByWord: true, font: 'Komika Axis' },
                  ].map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => setEditState(prev => ({
                        ...prev,
                        [clipIdx]: {
                          ...prev[clipIdx],
                          subtitlePreset: preset.id,
                          subtitleTextColor: preset.textColor,
                          subtitleHighlightColor: preset.highlightColor,
                          subtitleOutlineColor: preset.outline,
                          subtitleBgColor: preset.bg || 'transparent',
                          subtitleBgEnabled: !!preset.bg,
                          subtitleGlowEffect: preset.glow || false,
                          subtitleScaleEffect: preset.scale || false,
                          subtitleWordByWord: preset.wordByWord !== false,
                          ...(preset.font && { subtitleFont: preset.font }),
                        }
                      }))}
                      className={`p-3 rounded-xl border transition-all text-center ${
                        (editState?.subtitlePreset || 'karaoke') === preset.id
                          ? 'border-gold-500 bg-gold-500/10 ring-1 ring-gold-500/30'
                          : 'border-dark-200/50 dark:bg-dark-400/50 bg-cream-100 hover:border-cream-400 dark:hover:border-dark-100'
                      }`}
                    >
                      <div className="h-12 flex flex-col items-center justify-center rounded-lg mb-2 bg-dark-600/80 dark:bg-black/40 overflow-hidden px-2">
                        <span className="text-[11px] font-bold leading-tight" style={{ color: preset.textColor, textShadow: preset.outline !== 'transparent' ? `-1px -1px 0 ${preset.outline}, 1px -1px 0 ${preset.outline}, -1px 1px 0 ${preset.outline}, 1px 1px 0 ${preset.outline}` : 'none' }}>
                          {preset.preview[0]} <span style={{ color: preset.highlightColor, textShadow: preset.glow ? `0 0 8px ${preset.highlightColor}, -1px -1px 0 ${preset.outline}, 1px -1px 0 ${preset.outline}, -1px 1px 0 ${preset.outline}, 1px 1px 0 ${preset.outline}` : preset.outline !== 'transparent' ? `-1px -1px 0 ${preset.outline}, 1px -1px 0 ${preset.outline}, -1px 1px 0 ${preset.outline}, 1px 1px 0 ${preset.outline}` : 'none', ...(preset.bg ? { backgroundColor: preset.bg, padding: '1px 4px', borderRadius: '2px' } : {}) }}>{preset.preview[1]}</span>
                        </span>
                      </div>
                      <p className="text-[10px] font-bold text-dark-200 dark:text-cream-300 truncate">{preset.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode Toggle: Word-by-Word vs Phrase */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Caption Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleWordByWord: true } }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${subtitleWordByWord
                      ? 'bg-blue-500 text-white'
                      : 'bg-dark-300 text-cream-400 hover:bg-dark-200'
                      }`}
                  >
                    Word-by-Word
                  </button>
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleWordByWord: false } }))}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${!subtitleWordByWord
                      ? 'bg-blue-500 text-white'
                      : 'bg-dark-300 text-cream-400 hover:bg-dark-200'
                      }`}
                  >
                    Full Line
                  </button>
                </div>
              </div>

              {/* Style Controls */}
              <div className="bg-dark-300/30 rounded-xl p-4 space-y-4 border border-dark-200/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-blue-500">Style Controls</span>
                  <span className="text-[11px] text-cream-500 bg-dark-200/50 px-2 py-0.5 rounded">Advanced</span>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative z-20">
                    <ColorPicker
                      label="Text Color"
                      color={subtitleTextColor}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleTextColor: c } }))}
                    />
                  </div>
                  <div className="relative z-20">
                    <ColorPicker
                      label={subtitleWordByWord ? "Highlight Color" : "Text Color"}
                      color={subtitleHighlightColor}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleHighlightColor: c } }))}
                    />
                  </div>
                  <div className="relative z-20">
                    <ColorPicker
                      label="Outline Color"
                      color={subtitleOutlineColor}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleOutlineColor: c } }))}
                    />
                  </div>
                  <div className="relative z-20">
                    <ColorPicker
                      label="Background"
                      color={subtitleBgEnabled ? subtitleBgColor : 'transparent'}
                      onChange={(c) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleBgColor: c, subtitleBgEnabled: c !== 'transparent' } }))}
                      showBgToggle={true}
                      bgEnabled={subtitleBgEnabled}
                      onBgToggle={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleBgEnabled: !subtitleBgEnabled } }))}
                    />
                  </div>
                </div>

                {/* Font & Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Font Family</label>
                    <select
                      value={subtitleFontFamily}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleFontFamily: e.target.value } }))}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-dark-300 border border-dark-200 text-white focus:outline-none focus:border-blue-500"
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.id} value={font.id}>{font.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Font Size</label>
                      <span className="text-xs text-cream-500">{subtitleFontSize}px</span>
                    </div>
                    <input
                      type="range"
                      min="24"
                      max="96"
                      value={subtitleFontSize}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleFontSize: parseInt(e.target.value) } }))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                </div>

                {/* Outline Width & Max Words Per Line */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Outline Width</label>
                      <span className="text-xs text-cream-500">{subtitleOutlineWidth}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={subtitleOutlineWidth}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleOutlineWidth: parseInt(e.target.value) } }))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Max Words/Line</label>
                      <span className="text-xs text-cream-500">{subtitleMaxWordsPerLine}</span>
                    </div>
                    <input
                      type="range"
                      min="2"
                      max="8"
                      value={subtitleMaxWordsPerLine}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleMaxWordsPerLine: parseInt(e.target.value) } }))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Display Speed</label>
                      <span className="text-xs text-cream-500">{subtitleMinDisplaySec}s</span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="3.0"
                      step="0.1"
                      value={subtitleMinDisplaySec}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleMinDisplaySec: parseFloat(e.target.value) } }))}
                      className="w-full accent-blue-500"
                    />
                    <div className="flex justify-between text-[11px] text-cream-500">
                      <span>Fast (0.5s)</span>
                      <span>Slow (3.0s)</span>
                    </div>
                  </div>
                </div>

                {/* Effect Toggles */}
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleUppercase: !subtitleUppercase } }))}
                    className={`px-3 py-2 text-xs font-black rounded-lg border transition-all ${subtitleUppercase
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-dark-300 border-dark-200 text-cream-400'}`}
                    title="Force UPPERCASE subtitles"
                  >
                    AA
                  </button>
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleShadowEnabled: !subtitleShadowEnabled } }))}
                    className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all ${subtitleShadowEnabled
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-dark-300 border-dark-200 text-cream-400'}`}
                  >
                    Shadow
                  </button>
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleGlowEffect: !subtitleGlowEffect } }))}
                    className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all ${subtitleGlowEffect
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-dark-300 border-dark-200 text-cream-400'}`}
                  >
                    Glow
                  </button>
                  <button
                    onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleScaleEffect: !subtitleScaleEffect } }))}
                    className={`px-3 py-2 text-xs font-bold rounded-lg border transition-all ${subtitleScaleEffect
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-dark-300 border-dark-200 text-cream-400'}`}
                  >
                    Scale
                  </button>
                </div>
                {subtitleScaleEffect && (
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Scale Amount</label>
                      <span className="text-xs text-cream-500">{subtitleHighlightScale}%</span>
                    </div>
                    <input
                      type="range"
                      min="100"
                      max="150"
                      step="5"
                      value={subtitleHighlightScale}
                      onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleHighlightScale: parseInt(e.target.value) } }))}
                      className="w-full accent-blue-500"
                    />
                    <div className="flex justify-between text-[11px] text-cream-500">
                      <span>Subtle</span>
                      <span>Dramatic</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Position Controls */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Alignment</label>
                  <div className="flex gap-1">
                    {['left', 'center', 'right'].map((align) => (
                      <button
                        key={align}
                        onClick={() => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitleAlignment: align } }))}
                        className={`flex-1 px-2 py-2 rounded-lg text-xs font-bold transition-all ${subtitleAlignment === align
                          ? 'bg-blue-500 text-white'
                          : 'bg-dark-300 text-cream-400 hover:bg-dark-200'
                          }`}
                      >
                        {align === 'left' ? '◀' : align === 'center' ? '◆' : '▶'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Y Position</label>
                    <span className="text-xs text-cream-500">{subtitlePosition}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="95"
                    value={subtitlePosition}
                    onChange={(e) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], subtitlePosition: parseInt(e.target.value) } }))}
                    className="w-full accent-blue-500"
                  />
                </div>
              </div>

              </>)}
            </div>
          )}

          {/* SUBTITLE MODE - Right Column: Live Preview */}
          {(showSubtitle && (type !== 'full' || activeTab === 'subtitle')) && (
            <div className="w-72 flex-shrink-0 bg-dark-300/30 rounded-2xl p-4 border border-dark-200/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold uppercase text-blue-400">Live Preview</span>
                <span className="text-[11px] text-cream-500">Hook + Caption</span>
              </div>
              <div className="aspect-[9/16] bg-gradient-to-b from-dark-200 to-dark-500 rounded-xl overflow-hidden relative shadow-2xl border border-dark-100/30">
                {/* Video thumbnail as background */}
                {clip?.url && (
                  <video
                    src={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}#t=0.5`}
                    className="absolute inset-0 w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                {/* Hook Preview (if enabled) */}
                {(editState?.hookText || clip?.customHook) && (
                  <div
                    className="absolute left-3 right-3"
                    style={{
                      top: `${editState?.hookVerticalPosition ?? 75}%`,
                      transform: 'translateY(-50%)',
                      textAlign: (editState?.hookAlignment ?? 'center')
                    }}
                  >
                    <p
                      className="leading-tight"
                      style={{
                        color: hookTextColor,
                        fontSize: `${Math.max(hookFontSize / 5, 12)}px`,
                        fontFamily: hookFontFamily.includes('Poppins') ? 'Poppins, sans-serif' : hookFontFamily.includes('Komika') ? '"Komika Axis", sans-serif' : hookFontFamily.includes('Bold-Font') ? '"The Bold Font", sans-serif' : 'system-ui, sans-serif',
                        fontWeight: 900,
                        WebkitTextStroke: (hookBgEnabled || !hookOutlineEnabled) ? 'none' : `${Math.max(hookBorderWidth / 5, 1)}px ${hookBorderColor}`,
                        paintOrder: 'stroke fill',
                        textShadow: (hookBgEnabled || !hookShadowEnabled) ? 'none' : '2px 2px 4px rgba(0,0,0,0.5)',
                        backgroundColor: hookBgEnabled && hookBgColor !== 'transparent' ? hookBgColor : 'transparent',
                        padding: hookBgEnabled ? '4px 8px' : '0',
                        borderRadius: hookBgEnabled ? '4px' : '0',
                        display: 'inline-block',
                        opacity: 0.6
                      }}
                    >
                      {(() => {
                        const raw = editState?.hookText || clip?.customHook || 'Hook Text';
                        return hookUppercase ? raw.toUpperCase() : raw;
                      })()}
                    </p>
                  </div>
                )}

                {/* Subtitle Preview */}
                <div
                  className="absolute left-3 right-3"
                  style={{
                    top: `${subtitlePosition}%`,
                    transform: 'translateY(-50%)',
                    textAlign: subtitleAlignment
                  }}
                >
                  <p
                    className="leading-tight"
                    style={{
                      fontSize: `${Math.max(subtitleFontSize / 5, 10)}px`,
                      fontFamily: subtitleFontFamily.includes('Poppins') ? 'Poppins, sans-serif' : subtitleFontFamily.includes('Komika') ? '"Komika Axis", sans-serif' : subtitleFontFamily.includes('Bold-Font') ? '"The Bold Font", sans-serif' : 'system-ui, sans-serif',
                      fontWeight: 700,
                      WebkitTextStroke: subtitleBgEnabled ? 'none' : `${Math.max(subtitleOutlineWidth / 5, 0.5)}px ${subtitleOutlineColor}`,
                      paintOrder: 'stroke fill',
                      backgroundColor: subtitleBgEnabled && subtitleBgColor !== 'transparent' ? subtitleBgColor : 'transparent',
                      padding: subtitleBgEnabled ? '4px 8px' : '0',
                      borderRadius: subtitleBgEnabled ? '4px' : '0',
                      display: 'inline-block'
                    }}
                  >
                    {subtitleWordByWord ? (
                      <>
                        <span style={{
                          color: subtitleTextColor,
                          textShadow: subtitleShadowEnabled && !subtitleBgEnabled ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none'
                        }}>{subtitleUppercase ? 'TO ' : 'to '}</span>
                        <span style={{
                          color: subtitleHighlightColor,
                          transform: subtitleScaleEffect ? 'scale(1.15)' : 'none',
                          display: 'inline-block',
                          textShadow: subtitleGlowEffect
                            ? `0 0 8px ${subtitleHighlightColor}, 0 0 16px ${subtitleHighlightColor}40${subtitleShadowEnabled ? ', 2px 2px 4px rgba(0,0,0,0.5)' : ''}`
                            : (subtitleShadowEnabled && !subtitleBgEnabled ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none')
                        }}>{subtitleUppercase ? 'GET ' : 'get '}</span>
                        <span style={{
                          color: subtitleTextColor,
                          textShadow: subtitleShadowEnabled && !subtitleBgEnabled ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none'
                        }}>{subtitleUppercase ? 'STARTED' : 'started'}</span>
                      </>
                    ) : (
                      <span style={{
                        color: subtitleTextColor,
                        textShadow: subtitleShadowEnabled && !subtitleBgEnabled ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none'
                      }}>{subtitleUppercase ? 'TO GET STARTED' : 'to get started'}</span>
                    )}
                  </p>
                </div>

                {/* Overlap Warning */}
                {(() => {
                  const hookY = editState?.hookVerticalPosition ?? 75;
                  const hasOverlap = Math.abs(hookY - subtitlePosition) < 15;
                  return hasOverlap && (editState?.hookText || clip?.customHook) ? (
                    <div className="absolute bottom-2 left-2 right-2 bg-amber-500/90 text-black text-[11px] font-bold px-2 py-1 rounded text-center">
                      ⚠️ Elements may overlap!
                    </div>
                  ) : null;
                })()}
              </div>
              <p className="text-[11px] text-cream-500 text-center mt-3">Preview shows hook + caption placement</p>
            </div>
          )}

          {/* STYLE MODE - Using FormatPicker component */}
          {(showStyle && (type !== 'full' || activeTab === 'style')) && (
            <FormatPicker
              selectedFormat={currentFormat}
              onFormatChange={(formatId) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], format: formatId } }))}
              selectedResolution={editState?.resolution || effectiveClip?.resolution || '720p'}
              onResolutionChange={(resId) => setEditState(prev => ({ ...prev, [clipIdx]: { ...prev[clipIdx], resolution: resId } }))}
              compact={true}
              multiSelect={type === 'multi-format'}
              selectedFormats={multiFormatSelections || []}
              onMultiFormatToggle={onMultiFormatToggle}
              faceDetectionEnabled={editState?.liveFaceDetection || false}
              faceDetectionMode={editState?.faceDetectionMode || 'sekali'}
              trackingStyle={editState?.trackingStyle || 'smooth'}
              onFaceDetectionChange={(changes) => {
                setEditState(prev => ({
                  ...prev,
                  [clipIdx]: {
                    ...prev[clipIdx],
                    ...(changes.enabled !== undefined && { liveFaceDetection: changes.enabled }),
                    ...(changes.mode && { faceDetectionMode: changes.mode }),
                    ...(changes.trackingStyle && { trackingStyle: changes.trackingStyle }),
                  }
                }));
              }}
            />
          )}

          {/* Multi-format selection summary */}
          {type === 'multi-format' && multiFormatSelections && multiFormatSelections.length > 0 && (
            <div className="mt-3 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                {multiFormatSelections.length} format{multiFormatSelections.length > 1 ? 's' : ''} selected
              </p>
            </div>
          )}

          {/* LOGO/STICKER MODE */}
          {(showLogo && (type !== 'full' || activeTab === 'logo')) && (
            <div className="flex-1 space-y-5 overflow-y-auto pr-2" style={{ maxHeight: '65vh' }}>
              {/* Upload New Logo */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Upload Logo</label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    id={`logo-upload-${clipIdx}`}
                    className="hidden"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('logo', file);
                      formData.append('name', file.name.replace(/\.[^.]+$/, ''));
                      try {
                        const res = await axios.post(`${API_BASE}/api/logos/upload?type=logo`, formData);
                        if (fetchLogos) fetchLogos();
                        // Auto-select uploaded logo
                        setLogoOverlay(prev => ({
                          ...prev,
                          [clipIdx]: {
                            ...prev[clipIdx],
                            logoId: res.data.id,
                            serverPath: res.data.path,
                            x: prev[clipIdx]?.x ?? 5,
                            y: prev[clipIdx]?.y ?? 5,
                            scale: prev[clipIdx]?.scale ?? 25,
                            opacity: prev[clipIdx]?.opacity ?? 1.0
                          }
                        }));
                      } catch (err) {
                        console.error('Logo upload failed:', err.message);
                      }
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor={`logo-upload-${clipIdx}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all bg-gold-500/10 border-2 border-dashed border-gold-500/30 hover:border-gold-500 text-gold-500 text-xs font-bold"
                  >
                    <Upload size={14} /> Upload PNG / JPG / SVG
                  </label>
                </div>
              </div>

              {/* Logo Library */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">
                  Logo Library {logos.length > 0 && <span className="text-gold-500">({logos.length})</span>}
                </label>
                {logos.length === 0 ? (
                  <div className="p-4 text-center border-2 border-dashed dark:border-dark-300 rounded-xl">
                    <Stamp className="w-6 h-6 mx-auto mb-2 text-cream-500" />
                    <p className="text-[11px] text-cream-500">No logos uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2 max-h-[120px] overflow-y-auto custom-scrollbar">
                    {logos.map(logo => (
                      <div
                        key={logo.id}
                        onClick={() => setLogoOverlay(prev => ({
                          ...prev,
                          [clipIdx]: {
                            ...prev[clipIdx],
                            logoId: logo.id,
                            serverPath: logo.path,
                            x: prev[clipIdx]?.x ?? 5,
                            y: prev[clipIdx]?.y ?? 5,
                            scale: prev[clipIdx]?.scale ?? 25,
                            opacity: prev[clipIdx]?.opacity ?? 1.0
                          }
                        }))}
                        className={`relative group rounded-lg border-2 p-1 cursor-pointer transition-all ${currentLogoState?.logoId === logo.id
                          ? 'border-gold-500 bg-gold-500/10'
                          : 'border-transparent hover:border-dark-100 bg-cream-100 dark:bg-dark-400'
                          }`}
                      >
                        <img
                          src={`${API_BASE}/${logo.path}`}
                          alt={logo.name}
                          className="w-full h-12 object-contain"
                        />
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.delete(`${API_BASE}/api/logos/${logo.id}`);
                              if (fetchLogos) fetchLogos();
                              if (currentLogoState?.logoId === logo.id) {
                                setLogoOverlay(prev => {
                                  const next = { ...prev };
                                  delete next[clipIdx];
                                  return next;
                                });
                              }
                            } catch (err) {
                              console.error('Logo delete failed:', err.message);
                            }
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={8} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Remove Logo Button */}
              {currentLogoState && (
                <button
                  onClick={() => setLogoOverlay(prev => {
                    const next = { ...prev };
                    delete next[clipIdx];
                    return next;
                  })}
                  className="w-full py-2 rounded-xl bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <Trash2 size={12} /> Remove Logo
                </button>
              )}

              {/* Scale & Opacity Controls */}
              {currentLogoState && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Scale</label>
                      <span className="text-xs text-gold-500 font-bold">{currentLogoState.scale ?? 25}%</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="80"
                      value={currentLogoState.scale ?? 25}
                      onChange={(e) => setLogoOverlay(prev => ({
                        ...prev,
                        [clipIdx]: { ...prev[clipIdx], scale: parseInt(e.target.value) }
                      }))}
                      className="w-full accent-gold-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Opacity</label>
                      <span className="text-xs text-gold-500 font-bold">{Math.round((currentLogoState.opacity ?? 1.0) * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={Math.round((currentLogoState.opacity ?? 1.0) * 100)}
                      onChange={(e) => setLogoOverlay(prev => ({
                        ...prev,
                        [clipIdx]: { ...prev[clipIdx], opacity: parseInt(e.target.value) / 100 }
                      }))}
                      className="w-full accent-gold-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LOGO PREVIEW - Right Column (when in logo tab) */}
          {(showLogo && (type !== 'full' || activeTab === 'logo')) && currentLogoState && (
            <div className="w-[240px] flex-shrink-0">
              <div
                className="relative bg-black rounded-xl overflow-hidden cursor-crosshair select-none"
                style={{ aspectRatio: '9/16' }}
                onMouseDown={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const updatePos = (ev) => {
                    const x = Math.max(0, Math.min(100, ((ev.clientX - rect.left) / rect.width) * 100));
                    const y = Math.max(0, Math.min(100, ((ev.clientY - rect.top) / rect.height) * 100));
                    setLogoOverlay(prev => ({
                      ...prev,
                      [clipIdx]: { ...prev[clipIdx], x: Math.round(x), y: Math.round(y) }
                    }));
                  };
                  updatePos(e);
                  const onMove = (ev) => updatePos(ev);
                  const onUp = () => {
                    window.removeEventListener('mousemove', onMove);
                    window.removeEventListener('mouseup', onUp);
                  };
                  window.addEventListener('mousemove', onMove);
                  window.addEventListener('mouseup', onUp);
                }}
              >
                {/* Video thumbnail background */}
                {clip?.url && (
                  <video
                    src={`${API_BASE}/${clip.url}#t=1`}
                    className="w-full h-full object-cover pointer-events-none absolute inset-0"
                  />
                )}
                {!clip?.url && (
                  <div className="w-full h-full bg-gradient-to-b from-dark-300 to-dark-500 absolute inset-0" />
                )}

                {/* Logo overlay positioned */}
                <img
                  src={`${API_BASE}/${currentLogoState.serverPath}`}
                  alt="Logo"
                  className="absolute pointer-events-none"
                  style={{
                    left: `${currentLogoState.x ?? 5}%`,
                    top: `${currentLogoState.y ?? 5}%`,
                    width: `${currentLogoState.scale ?? 25}%`,
                    opacity: currentLogoState.opacity ?? 1.0,
                    transform: 'translate(-50%, -50%)',
                  }}
                />

                {/* Crosshair indicator */}
                <div
                  className="absolute w-3 h-3 border-2 border-gold-400 rounded-full pointer-events-none"
                  style={{
                    left: `${currentLogoState.x ?? 5}%`,
                    top: `${currentLogoState.y ?? 5}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              </div>
              <p className="text-[11px] text-cream-500 text-center mt-3">Click or drag to position logo</p>
              <p className="text-[11px] text-cream-400 text-center">
                Position: ({currentLogoState.x ?? 5}%, {currentLogoState.y ?? 5}%)
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t dark:border-dark-300 flex items-center justify-between gap-3">
          <button onClick={onClose} className="px-6 py-3 text-sm font-bold text-cream-500 hover:text-dark-200 dark:hover:text-cream-200 transition-all">
            Cancel
          </button>
          <button
            onClick={() => {
              if (type === 'multi-format' && onMultiFormatApply) {
                onMultiFormatApply(editState?.resolution || effectiveClip?.resolution || '720p');
                return;
              }
              onApply(currentFormat, currentHookText, currentSubtitleText);
            }}
            disabled={isReprocessing || (type === 'multi-format' && (!multiFormatSelections || multiFormatSelections.length === 0))}
            className="px-6 py-3 bg-gradient-to-r from-gold-600 to-amber-500 hover:from-gold-500 hover:to-amber-400 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg transition-all disabled:opacity-50"
          >
            {isReprocessing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {isPresetMode ? 'Save Defaults' : type === 'multi-format' ? `Render ${multiFormatSelections?.length || 0} Formats` : (isReprocessing ? 'Processing...' : 'Apply & Re-render')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// --- REVISED PLAYER COMPONENT (Phase 17) ---
// --- REVISED PLAYER COMPONENT (Phase 17 - Enhanced) ---
const FeedClipPlayer = ({ url, poster }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0 to 100
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  }, [speed]);

  const togglePlay = (e) => {
    e?.stopPropagation();
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleFullscreen = (e) => {
    e?.stopPropagation();
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => console.error(err));
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const curr = videoRef.current.currentTime;
    const dur = videoRef.current.duration;
    setCurrentTime(curr);
    setProgress((curr / dur) * 100);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      // If url has #t=0.1, it starts paused at 0.1
      if (videoRef.current.paused) setIsPlaying(false);
    }
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const newPct = parseFloat(e.target.value);
    const newTime = (newPct / 100) * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setProgress(newPct);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div ref={containerRef} className="w-full h-full relative group bg-black rounded-xl overflow-hidden shadow-lg select-none">
      {loadError ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-cream-500 gap-2 p-4">
          <FileVideo size={32} className="opacity-30" />
          <span className="text-[11px] font-bold uppercase tracking-widest opacity-50">Source video removed</span>
          <span className="text-[10px] text-cream-500 opacity-60">Your exported clips are still available</span>
          <button onClick={() => { setLoadError(false); if (videoRef.current) videoRef.current.load(); }} className="text-[11px] text-gold-500 hover:text-gold-400 font-bold uppercase">Retry</button>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={url}
          className="w-full h-full object-contain"
          preload="metadata"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={() => setLoadError(true)}
          onClick={togglePlay}
          playsInline
        />
      )}

      {/* Center Play Overlay (Only when Paused) */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-all cursor-pointer z-10" onClick={togglePlay}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/40 shadow-xl text-white transition-all transform hover:scale-105"
          >
            <Play size={24} fill="currentColor" className="ml-1" />
          </motion.button>
        </div>
      )}

      {/* Control Bar (Visible on Hover or Paused) */}
      <div className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 flex flex-col gap-2 z-20 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>

        {/* Timeline */}
        <div className="w-full h-1 bg-white/30 rounded-full cursor-pointer relative group/timeline" onClick={(e) => e.stopPropagation()}>
          <input
            type="range"
            min="0" max="100"
            value={progress || 0}
            onChange={handleSeek}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
          />
          <div className="h-full bg-gold-500 rounded-full relative" style={{ width: `${progress}%` }}>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md scale-0 group-hover/timeline:scale-100 transition-transform" />
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between text-white" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="hover:text-gold-400 transition-colors">
              {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
            </button>

            <div className="flex items-center gap-2 group/vol">
              <button onClick={toggleMute} className="hover:text-gold-400 transition-colors">
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>

            <div className="text-[11px] font-mono font-medium opacity-80 select-none">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Speed Toggle */}
            <div className="relative">
              <button onClick={() => setShowSpeedMenu(!showSpeedMenu)} className="text-[11px] font-bold hover:text-gold-400 border border-white/20 px-1.5 py-0.5 rounded transition-all w-8 text-center bg-black/20 backdrop-blur-sm">
                {speed}x
              </button>
              {showSpeedMenu && (
                <div className="absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-md rounded-lg overflow-hidden border border-white/10 flex flex-col shadow-xl min-w-[60px]">
                  {[0.5, 1, 1.5, 2].map(r => (
                    <button
                      key={r}
                      onClick={() => { setSpeed(r); setShowSpeedMenu(false); }}
                      className={`px-3 py-2 text-[11px] hover:bg-white/10 text-left ${speed === r ? 'text-gold-400 font-bold' : 'text-white'}`}
                    >
                      {r}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={toggleFullscreen} className="hover:text-gold-400 transition-colors">
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// --- TIMELINE TRANSCRIPT COMPONENT (Updated) ---
const TimelineTranscript = ({ segments }) => {
  if (!segments || segments.length === 0) return <div className="p-4 text-center text-cream-500 text-xs">No transcript data available</div>;

  // Clean VTT timing tags from text (removes <00:00:00.000><c> tags and </c>)
  const cleanVttText = (text) => {
    if (!text) return '';
    return text
      .replace(/<\d{2}:\d{2}:\d{2}\.\d{3}>/g, '') // Remove timing tags like <00:00:00.480>
      .replace(/<\/?c>/g, '') // Remove <c> and </c> tags
      .replace(/<[^>]+>/g, '') // Remove any other HTML-like tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  };

  return (
    <div className="mt-4 p-6 rounded-2xl bg-dark-400/70 border border-dark-200/50 max-h-[400px] overflow-y-auto space-y-4 custom-scrollbar">
      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-dark-200/50">
        <div className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center">
          <FileText size={16} className="text-gold-500" />
        </div>
        <div>
          <h4 className="text-sm font-bold text-white">Transcript</h4>
          <p className="text-[11px] text-cream-500">{segments.length} segments</p>
        </div>
      </div>
      {segments.map((seg, idx) => (
        <div key={idx} className="flex gap-4 group hover:bg-dark-300/40 p-3 rounded-xl transition-colors border border-transparent hover:border-dark-200/50">
          <div className="w-20 flex-shrink-0 text-xs font-mono text-gold-500/80 bg-gold-500/10 px-2 py-1 rounded-lg text-center">
            {typeof seg.start === 'number' ? new Date(seg.start * 1000).toISOString().substr(11, 8) : (String(seg.start || '').split('.')[0] || '00:00:00')}
          </div>
          <div className="flex-1 text-sm text-cream-200 group-hover:text-white transition-colors leading-relaxed">
            {cleanVttText(seg.text)}
          </div>
        </div>
      ))}
    </div>
  );
};

// --- EDITABLE TRANSCRIPT COMPONENT ---
const EditableTranscript = ({ segments, onSave, onSegmentEdit, isSaving, whisperEngine, setWhisperEngine, onRetranscribe, transcriptMethod }) => {
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [localSegments, setLocalSegments] = useState(segments || []);
  const [hasChanges, setHasChanges] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalSegments(segments || []);
    setHasChanges(false);
  }, [segments]);

  useEffect(() => {
    if (editingIdx !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIdx]);

  // Clean VTT timing tags from text
  const cleanVttText = (text) => {
    if (!text) return '';
    return text
      .replace(/<\d{2}:\d{2}:\d{2}\.\d{3}>/g, '')
      .replace(/<\/?c>/g, '')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleStartEdit = (idx, text) => {
    setEditingIdx(idx);
    setEditValue(cleanVttText(text));
  };

  const handleSaveEdit = () => {
    if (editingIdx === null) return;

    const newSegments = [...localSegments];
    newSegments[editingIdx] = {
      ...newSegments[editingIdx],
      text: editValue
    };
    setLocalSegments(newSegments);
    setEditingIdx(null);
    setHasChanges(true);

    if (onSegmentEdit) {
      onSegmentEdit(editingIdx, editValue);
    }
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleSaveAll = () => {
    if (onSave) {
      onSave(localSegments);
      setHasChanges(false);
    }
  };

  const formatTimestamp = (timeStr) => {
    if (timeStr === null || timeStr === undefined) return '00:00:00';
    // Handle numeric timestamps (seconds) from Whisper
    if (typeof timeStr === 'number') {
      return new Date(timeStr * 1000).toISOString().substr(11, 8);
    }
    // Handle both "00:00:00.000" and "00:00:00" string formats
    return String(timeStr).split('.')[0];
  };

  if (!localSegments || localSegments.length === 0) {
    return (
      <div className="p-6 text-center text-cream-500 text-sm">
        <FileText size={32} className="mx-auto mb-3 opacity-30" />
        <p>No transcript data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-300 dark:border-dark-200/50 bg-cream-100 dark:bg-dark-400/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center">
            <FileText size={16} className="text-gold-500" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-dark-200 dark:text-white">Transcript</h4>
            <p className="text-[11px] text-cream-500">{localSegments.length} segments</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {transcriptMethod && (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
              transcriptMethod.includes('gemini') ? 'bg-gold-500/20 text-gold-400' :
              transcriptMethod.includes('whisper-cpp') ? 'bg-cyan-500/20 text-cyan-400' :
              transcriptMethod.includes('whisper-wasm') ? 'bg-emerald-500/20 text-emerald-400' :
              'bg-cream-1000/20 text-cream-400'
            }`}>{transcriptMethod}</span>
          )}
          {hasChanges && (
            <button
              onClick={handleSaveAll}
              disabled={isSaving}
              className="px-3 py-1.5 bg-gold-600 hover:bg-gold-500 text-white rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          )}
        </div>
      </div>

      {/* Engine selector toolbar */}
      {setWhisperEngine && onRetranscribe && (
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cream-300 dark:border-dark-200/50 bg-cream-50 dark:bg-slate-950/30">
          <select
            value={whisperEngine}
            onChange={(e) => setWhisperEngine(e.target.value)}
            className="flex-1 px-3 py-1.5 rounded-lg bg-white dark:bg-dark-400 border border-cream-300 dark:border-dark-200 text-xs text-dark-200 dark:text-cream-300 focus:ring-1 focus:ring-gold-500 outline-none"
          >
            <option value="whisper-cpp">Whisper.cpp (recommended, timestamps)</option>
            <option value="whisper-wasm">Whisper WASM (no install needed)</option>
            <option value="gemini">Gemini AI (cloud, no timestamps)</option>
          </select>
          <button
            onClick={onRetranscribe}
            className="px-3 py-1.5 bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-200 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all whitespace-nowrap"
          >
            <RotateCcw size={12} /> Re-transcribe
          </button>
        </div>
      )}

      {/* Segments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar bg-white dark:bg-transparent">
        {localSegments.map((seg, idx) => (
          <div
            key={idx}
            className={`flex gap-3 p-3 rounded-xl transition-all border ${editingIdx === idx
              ? 'bg-gold-500/10 border-gold-500/30'
              : 'bg-cream-50 dark:bg-dark-300/30 border-cream-200 dark:border-transparent hover:bg-cream-100 dark:hover:bg-dark-300/50 hover:border-cream-300 dark:hover:border-dark-200/50'
              }`}
          >
            {/* Timestamp */}
            <div className="w-20 flex-shrink-0">
              <div className="text-xs font-mono text-gold-500/80 bg-gold-500/10 px-2 py-1.5 rounded-lg text-center">
                {formatTimestamp(seg.start)}
              </div>
            </div>

            {/* Text Content */}
            <div className="flex-1 min-w-0">
              {editingIdx === idx ? (
                <div className="space-y-2">
                  <textarea
                    ref={inputRef}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-dark-400 border border-cream-300 dark:border-dark-200 rounded-lg text-dark-200 dark:text-white resize-none focus:ring-2 focus:ring-gold-500 focus:border-transparent outline-none"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-3 py-1 bg-gold-600 hover:bg-gold-500 text-white rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      <Check size={12} /> Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1 bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-300 rounded-lg text-xs font-bold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => handleStartEdit(idx, seg.text)}
                  className="text-sm text-dark-200 dark:text-cream-200 hover:text-dark-400 dark:hover:text-white cursor-pointer leading-relaxed group/text"
                >
                  <span>{cleanVttText(seg.text)}</span>
                  <Pencil size={12} className="inline-block ml-2 opacity-0 group-hover/text:opacity-50 transition-opacity text-cream-400" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-dark-200/50 bg-dark-400/30">
        <p className="text-[11px] text-cream-500 text-center">
          Click on any segment to edit • Press Enter to save, Esc to cancel
        </p>
      </div>
    </div>
  );
};

// ============================================================
// LICENSE LOADING SCREEN
// Full-screen spinner shown while verifying the license on startup.
// ============================================================
const LicenseLoadingScreen = () => (
  <div className="min-h-screen bg-dark-500 flex flex-col items-center justify-center gap-6">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
      <Zap size={32} className="text-white" fill="currentColor" />
    </div>
    <div className="flex flex-col items-center gap-3">
      <Loader2 size={24} className="animate-spin text-amber-400" />
      <p className="text-cream-400 text-sm font-medium">Verifying license…</p>
    </div>
  </div>
);
// ============================================================
// END LICENSE LOADING SCREEN
// ============================================================

const QlipperAppInner = () => {
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'tool-a' | 'tool-b' | 'tool-c' | 'projects' | 'assets'
  const [feedItems, setFeedItems] = useState([]);
  const [viewTranscriptId, setViewTranscriptId] = useState(null);
  const [cachedTranscripts, setCachedTranscripts] = useState({});

  // Theme & UI State
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  const [showSettings, setShowSettings] = useState(false);

  // Update-available banner — /api/update-check compares the installed version
  // against latest.json on the download host (server caches the check 6h).
  // Dismissal is stored per-version so the next release re-surfaces the banner.
  const [updateInfo, setUpdateInfo] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/api/update-check`).then(({ data }) => {
      if (data?.updateAvailable && localStorage.getItem('qlipper_update_dismissed') !== data.latest) {
        setUpdateInfo(data);
      }
    }).catch(() => { /* offline / old backend without the endpoint = no banner */ });
  }, []);

  const [showConsole, setShowConsole] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('showConsole') === 'true';
    }
    return false;
  });
  const [isConsoleMinimized, setIsConsoleMinimized] = useState(false);

  // Hoisted: addLog is referenced as a dep by callbacks declared earlier in
  // this function than its old definition site, which threw a TDZ error on
  // mount under React.StrictMode ("Cannot access 'L' before initialization").
  const [logs, setLogs] = useState([
    { time: new Date().toLocaleTimeString(), msg: 'SYSTEM: Core Services Initialized', type: 'info' }
  ]);
  const addLog = useCallback((msg, type = 'info') => {
    setLogs(prev => [...prev.slice(-200), {
      time: new Date().toLocaleTimeString(),
      msg,
      type
    }]);
  }, []);

  // Backend log stream over WebSocket. The backend wraps console.log/warn/error
  // and broadcasts to /ws/logs (server.js:24-33). Without this client, retry
  // messages, ffmpeg progress, and other backend signals only land in terminal
  // — invisible to the user. Reconnects with backoff if the server restarts.
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let reconnectDelay = 1000;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // API_BASE is `http://hostname:3001` on the deployed app and `''` in
      // Vite dev mode (port 5173). In dev, point the WS at backend explicitly.
      const wsHost = API_BASE
        ? API_BASE.replace(/^https?:\/\//, '')
        : `${window.location.hostname}:3001`;
      try {
        ws = new WebSocket(`${proto}//${wsHost}/ws/logs`);
      } catch {
        reconnectTimer = setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30000);
        return;
      }
      ws.onopen = () => { reconnectDelay = 1000; };
      ws.onmessage = (event) => {
        try {
          const m = JSON.parse(event.data);
          if (m && m.type === 'LOG' && typeof m.msg === 'string') {
            const uiType = m.level === 'error' ? 'error' : 'info';
            addLog(m.msg, uiType);
          }
        } catch { /* ignore non-JSON frames */ }
      };
      ws.onclose = () => {
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) try { ws.close(); } catch {}
    };
  }, [addLog]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('showConsole', showConsole);
  }, [showConsole]);

  // Fetch prompt presets on mount
  useEffect(() => {
    axios.get(`${API_BASE}/api/prompts`).then(res => {
      if (Array.isArray(res.data)) setPromptPresets(res.data);
    }).catch(() => { /* ignore — prompts are optional */ });
  }, []);

  // App Shared State
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [error, setError] = useState(null);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('gemini_api_key') || '');
  const [openaiKey, setOpenaiKey] = useState(() => localStorage.getItem('openai_api_key') || '');
  const [claudeKey, setClaudeKey] = useState(() => localStorage.getItem('claude_api_key') || '');
  // Groq removed in v2.4.0 QW-1 (xAI Grok superseded; user didn't use Groq).
  const [mistralKey, setMistralKey] = useState(() => localStorage.getItem('mistral_api_key') || '');
  const [deepseekKey, setDeepseekKey] = useState(() => localStorage.getItem('deepseek_api_key') || '');
  const [openrouterKey, setOpenrouterKey] = useState(() => localStorage.getItem('openrouter_api_key') || '');
  const [minimaxKey, setMinimaxKey] = useState(() => localStorage.getItem('minimax_api_key') || '');
  // PR 4f: xAI Grok key (Elon's AI — distinct from Groq above)
  const [xaiKey, setXaiKey] = useState(() => localStorage.getItem('xai_api_key') || '');
  const [openrouterModel, setOpenrouterModel] = useState(() => localStorage.getItem('openrouter_model') || 'google/gemma-3-27b-it');

  // Gemini Model Variant State
  // Migrate away from deprecated/unavailable models: *-latest (unpredictable routing),
  // 2.0 (shut down Jun 1 2026), 1.5 (retired), 2.5 flash/lite (retiring ~Oct 2026,
  // new free AI Studio keys already 404 on them).
  const deprecatedGeminiVariants = [
    'gemini-flash-latest', 'gemini-pro-latest',
    'gemini-2.0-flash', 'gemini-2.0-flash-lite',
    'gemini-1.5-flash', 'gemini-1.5-pro',
    'gemini-2.5-flash', 'gemini-2.5-flash-lite',
  ];
  const [geminiVariant, setGeminiVariant] = useState(() => {
    const stored = localStorage.getItem('gemini_variant');
    if (!stored || deprecatedGeminiVariants.includes(stored)) {
      const pinned = 'gemini-3.1-flash-lite';
      localStorage.setItem('gemini_variant', pinned);
      return pinned;
    }
    return stored;
  });
  const [availableGeminiModels, setAvailableGeminiModels] = useState([]);
  const [fetchingModels, setFetchingModels] = useState(false);
  // SX-1: derived from session, not persisted. On app reload, isKeyVerified
  // resets to false — user clicks Verify once per session if they want the
  // accessibility badges to populate. Pipeline runs validate independently.
  const [isKeyVerified, setIsKeyVerified] = useState(false);
  // Auto-fetch model list when key is verified or Settings opened
  useEffect(() => {
    if (isKeyVerified && availableGeminiModels.length === 0 && apiKey) {
      axios.get(`${API_BASE}/api/gemini/models`, { headers: { 'x-gemini-key': apiKey } })
        .then(res => { if (res.data.models?.length) setAvailableGeminiModels(res.data.models); })
        .catch(() => {});
    }
  }, [isKeyVerified, showSettings]);
  // (apiKeyBtnSaved was dead state after SX-1 — Verified label replaces "Saved!" toast)
  const [sysCheckLoading, setSysCheckLoading] = useState(false);
  const [sysStatusInfo, setSysStatusInfo] = useState({ text: 'Click "Check" to verify', className: 'text-[11px] text-cream-400' });
  const [ytdlpUpdateState, setYtdlpUpdateState] = useState('idle');
  const [ytdlpProgress, setYtdlpProgress] = useState(null);
  // yt-dlp release channel — 'stable' (default) or 'nightly' (latest YouTube extractor fixes).
  // Persisted to localStorage so the choice survives reloads.
  const [ytdlpChannel, setYtdlpChannel] = useState(() => {
    try { return localStorage.getItem('qlipper.ytdlpChannel') === 'nightly' ? 'nightly' : 'stable'; }
    catch { return 'stable'; }
  });
  useEffect(() => {
    try { localStorage.setItem('qlipper.ytdlpChannel', ytdlpChannel); } catch {}
  }, [ytdlpChannel]);
  const [whisperStatus, setWhisperStatus] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [networkUrl, setNetworkUrl] = useState(null); // { installed, availableModels, engine }
  const [whisperUpdateState, setWhisperUpdateState] = useState('idle');
  const [whisperProgress, setWhisperProgress] = useState(null);
  const [whisperEngine, setWhisperEngine] = useState(() => localStorage.getItem('whisper_engine') || 'whisper-cpp');
  const [whisperWasmFallback, setWhisperWasmFallback] = useState(() => localStorage.getItem('whisper_wasm_fallback') !== 'false'); // default: enabled
  const [whisperModel, setWhisperModel] = useState(() => localStorage.getItem('whisper_model') || 'base');
  const [getClipsBtnText, setGetClipsBtnText] = useState('Get AI Clips');
  const [geminiCopyDone, setGeminiCopyDone] = useState(false);

  // YouTube Cookie Browser State
  const [cookieBrowser, setCookieBrowser] = useState(() => localStorage.getItem('youtube_cookie_browser') || 'none');

  // Error Reporting Opt-in (default: enabled)
  const [errorReporting, setErrorReporting] = useState(() => localStorage.getItem('error_reporting') !== 'false');

  // v2.3.0 jobs system: resumable, structured-error pipeline for download →
  // transcribe → analyze. Render still bridges to legacy /api/video/process —
  // full port to a jobs-system render phase is v2.4.0 scope. Default-on so
  // the foundation reaches all users; explicit 'false' in localStorage flips
  // back to the legacy linear path (toggle in Settings).
  const [useJobsSystem, setUseJobsSystem] = useState(() => localStorage.getItem('use_jobs_system') !== 'false');

  // Pipeline-via-jobs runtime state. Mirrors the manifest shape from
  // /api/jobs/<id> so existing UI elements (progress bar, status text) can
  // read the same fields. activeJobId persists across reloads so a "Resume"
  // button can pick up where the user left off.
  const [activeJobId, setActiveJobId] = useState(() => localStorage.getItem('active_job_id') || null);
  const [activeJobManifest, setActiveJobManifest] = useState(null);

  // PR 4a: structured error info alongside the legacy plain-string pipelineError.
  // QlipperError shape: { code, ui_message, category, retryable, remediations[] }.
  // When set, ErrorBanner renders rich error UI; legacy callers without
  // structured info fall back to the plain banner.
  const [pipelineErrorInfo, setPipelineErrorInfo] = useState(null);

  // PR 4g: render engine profile (declared early — referenced by the
  // localStorage-save useEffect below; must be in scope before that runs).
  // 'hw' = hardware encoder; 'cpu-fast' = libx264 ultrafast; 'cpu-quality' =
  // libx264 slow. Default seeded by /api/system/encoder-info probe on mount.
  const [renderProfile, setRenderProfile] = useState(() => localStorage.getItem('render_profile') || 'hw');
  const [hwEncoderAvailable, setHwEncoderAvailable] = useState(true);
  // SX-1: persist via localStorage so picker choice survives reload. Declared
  // here (not next to other Tool B state below) because the auto-save useEffect
  // a few lines down references it in its deps array — `const` is not hoisted,
  // so it must exist before the useEffect runs to avoid TDZ ReferenceError.
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem('selectedModel') || 'gemini');
  // "Router / API lain" tile (selectedModel === 'router'): which preset + its saved
  // {baseUrl, apiKey, model}. The per-preset map is shared with Lite (lite_llm_cfg).
  const [routerProvider, setRouterProvider] = useState(() => loadFullAppProvider());
  const [llmCfg, setLlmCfg] = useState(() => loadLlmCfg());
  const routerLlm = resolveLlm(routerProvider, llmCfg, { openrouterModel: ANALYZE_MODEL });
  const routerMissing = llmMissing(routerLlm);
  const onRouterProviderChange = (id) => {
    if (id === 'gemini') { setSelectedModel('gemini'); return; } // "Gemini" in the panel = the Gemini tile
    setRouterProvider(id); saveFullAppProvider(id);
  };
  // Settings → AI Model: the router tile lives in a collapsed "Lanjutan" section (San,
  // 27 Sep 2026) so it never distracts normal users. Each time Settings opens it starts
  // collapsed, unless the router is the saved choice (then its tile must stay visible).
  const [showAdvancedModels, setShowAdvancedModels] = useState(false);
  useEffect(() => {
    if (showSettings) setShowAdvancedModels(selectedModel === 'router');
  }, [showSettings]); // eslint-disable-line react-hooks/exhaustive-deps
  const onRouterCfgChange = (presetId, patch) => {
    setLlmCfg((prev) => {
      const next = { ...prev, [presetId]: { ...(prev[presetId] || {}), ...patch } };
      saveLlmCfg(next);
      return next;
    });
  };

  // PR 4e: License server status check. User can self-test from Settings
  // whether license.qlipper.id is reachable, so when they hit a 403
  // "Valid license required" banner they can distinguish "their device
  // de-activated" from "Qlipper's server is down right now."
  // 'idle' | 'checking' | 'up' | 'down'
  const [licenseServerStatus, setLicenseServerStatus] = useState('idle');
  const [licenseServerLatency, setLicenseServerLatency] = useState(null);

  useEffect(() => {
    localStorage.setItem('gemini_api_key', apiKey);
    localStorage.setItem('openai_api_key', openaiKey);
    localStorage.setItem('claude_api_key', claudeKey);
    localStorage.setItem('mistral_api_key', mistralKey);
    localStorage.setItem('deepseek_api_key', deepseekKey);
    localStorage.setItem('minimax_api_key', minimaxKey);
    localStorage.setItem('xai_api_key', xaiKey);
    localStorage.setItem('gemini_variant', geminiVariant);
    localStorage.setItem('youtube_cookie_browser', cookieBrowser);
    localStorage.setItem('error_reporting', errorReporting ? 'true' : 'false');
    localStorage.setItem('whisper_engine', whisperEngine);
    localStorage.setItem('whisper_wasm_fallback', whisperWasmFallback ? 'true' : 'false');
    localStorage.setItem('whisper_model', whisperModel);
    localStorage.setItem('use_jobs_system', useJobsSystem ? 'true' : 'false');
    localStorage.setItem('render_profile', renderProfile);
    localStorage.setItem('selectedModel', selectedModel);
    localStorage.setItem('openrouter_model', openrouterModel);
    if (activeJobId) localStorage.setItem('active_job_id', activeJobId);
    else localStorage.removeItem('active_job_id');
  }, [apiKey, openaiKey, claudeKey, mistralKey, deepseekKey, minimaxKey, xaiKey, geminiVariant, cookieBrowser, errorReporting, whisperEngine, whisperWasmFallback, whisperModel, useJobsSystem, activeJobId, renderProfile, selectedModel, openrouterModel]);

  // PR 4g: probe backend on mount for HW encoder availability. If user hasn't
  // explicitly picked a profile (no localStorage entry), seed from server's
  // recommendedProfile so first-time users on machines without a HW encoder
  // start on cpu-fast instead of failing at first render attempt.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/system/encoder-info`, { timeout: 8000 });
        if (cancelled) return;
        setHwEncoderAvailable(!!res.data?.hwEncoderAvailable);
        // Only override profile if user hadn't explicitly picked one
        if (!localStorage.getItem('render_profile') && res.data?.recommendedProfile) {
          setRenderProfile(res.data.recommendedProfile);
        }
      } catch {
        // Backend not yet up or probe failed — keep optimistic defaults
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Tool A State
  const [metadata, setMetadata] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [downloadResult, setDownloadResult] = useState(null);
  const [toolAProgress, setToolAProgress] = useState(0);
  const [toolAStatus, setToolAStatus] = useState('');
  const [toolATranscribing, setToolATranscribing] = useState(false);
  const [toolASaveToAssets, setToolASaveToAssets] = useState(true); // Default: save to library

  // Tool B State
  const [toolBUrl, setToolBUrl] = useState('');
  const [toolBMetadata, setToolBMetadata] = useState(null); // { title, thumbnail, duration, channel }
  const [toolBLoadingMeta, setToolBLoadingMeta] = useState(false);
  const [toolBSourceMode, setToolBSourceMode] = useState('url'); // 'url' | 'asset'
  const [toolBSelectedAsset, setToolBSelectedAsset] = useState(null);
  const [instruction, setInstruction] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeMode, setAnalyzeMode] = useState('auto'); // 'auto' | 'advanced'
  const [advNumCuts, setAdvNumCuts] = useState(5);
  const [advMaxDuration, setAdvMaxDuration] = useState(10);
  const [advFocus, setAdvFocus] = useState('Viral Hooks');
  // selectedModel declared earlier (next to renderProfile) for TDZ-safe ordering
  const [availableModels, setAvailableModels] = useState([]);
  const [advVibe, setAdvVibe] = useState('Dynamic & Fast');

  // Tool C State
  const [videoFile, setVideoFile] = useState(null);
  const [toolCSourceMode, setToolCSourceMode] = useState('asset'); // 'asset' | 'upload'
  const [toolCSelectedAsset, setToolCSelectedAsset] = useState(null);
  const [pastedJson, setPastedJson] = useState('');
  const [useTransitions, setUseTransitions] = useState(true);
  // (renderProfile + hwEncoderAvailable declared earlier — see PR 4g block above
  // useJobsSystem useEffect — TDZ-safe ordering)
  const [outputFormat, setOutputFormat] = useState('stacked-blur');
  const [outputResolution, setOutputResolution] = useState('720p'); // Default 720p HD
  const [pipelineQuality, setPipelineQuality] = useState('720'); // Quality selection for downloads
  const [multiFormatSelections, setMultiFormatSelections] = useState([]); // format IDs for multi-format export
  const [multiRenderProgress, setMultiRenderProgress] = useState(null); // {current, total, clipIdx}
  const [multiFormatResults, setMultiFormatResults] = useState({}); // {`${projectId}_${clipIdx}`: [{format, url}]}
  const [pipelineInstruction, setPipelineInstruction] = useState(''); // Custom prompt for pipeline analysis
  const [selectedPromptIdx, setSelectedPromptIdx] = useState(0); // 0=Default, 1-N=presets, -1=Custom
  const [promptPresets, setPromptPresets] = useState([]); // Prompt presets from /api/prompts
  const [pipelineVideoLang, setPipelineVideoLang] = useState(null); // Detected video language from metadata
  const [availableQualities, setAvailableQualities] = useState(null); // { tiers: [], maxHeight: null }
  const [qualityProbing, setQualityProbing] = useState(false); // Loading state for quality probe
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [finalClips, setFinalClips] = useState([]);
  const [showClipperStyleModal, setShowClipperStyleModal] = useState(false);

  // Saved Analyses (persisted to localStorage)
  const [savedAnalyses, setSavedAnalyses] = useState(() => {
    try {
      const saved = localStorage.getItem('qlipper_saved_analyses');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  // --- CAPTION & STYLE STATE ---
  const [captionText, setCaptionText] = useState('');
  const [captionStyle, setCaptionStyle] = useState('simple');
  const [burnSubtitles, setBurnSubtitles] = useState(false);
  const [subtitleStyle, setSubtitleStyle] = useState('default');

  // --- STYLE SETTINGS MODAL STATE ---
  const [showStyleModal, setShowStyleModal] = useState(false);
  // styleSettings — initialised from a user-saved default (Style Defaults modal
  // "Save Defaults" / Style Settings modal "Save Settings") persisted in
  // localStorage, falling back to the hardcoded defaults below. Without this
  // load, customised defaults silently reverted every time a new video was
  // processed (beta feedback, v2.3).
  const [styleSettings, setStyleSettings] = useState(() => {
    const defaults = {
      // Hook Overlay (per-element customization)
      hookEnabled: false,
      hookSource: 'none', // 'none' | 'auto' | 'title' — persistent hook text source
      hookBrand: { text: '', color: '#04FF00', font: 'Inter' },
      hookHeadline: { text: '', color: '#04FF00', font: 'Inter Bold' },
      hookSubtitle: { text: '', color: '#04FF00', font: 'Inter' },
      hookAlignment: 'center', // 'left' | 'center' | 'right' (default: center for better aesthetics)
      hookVerticalPosition: 75, // 0-100% from top (default: 75% = lower third area)
      hookStroke: true, // Enable text outline/stroke
      hookStrokeColor: '#000000', // Stroke color (default black)

      // Sticker Headline (below hook overlay)
      stickerEnabled: false,
      stickerText: '',
      stickerShape: 'pill', // pill, comment-bubble, arrow-badge, star-burst, tape-strip
      stickerBgColor: '#FF3B30',
      stickerTextColor: '#FFFFFF',
      stickerImagePath: null, // Custom uploaded image path

      // Subtitles — ON by default with the karaoke preset, so the batch pipeline
      // auto-captions all clips and the editor preview matches the export byte-for-byte.
      // These values are kept in sync with the ClipEditModal fallbacks (App.jsx ~834-851)
      // and the reprocessClip merge (~3546-3561) so batch == per-clip == preview.
      subtitleEnabled: true,
      subtitlePreset: 'karaoke',
      subtitleFont: 'Sans-Bold',
      subtitleTextColor: '#FFFFFF',
      subtitleOutlineColor: '#000000',
      subtitleBgColor: 'transparent',
      subtitleBgEnabled: false,
      subtitleOpacity: 0.65,
      subtitlePosition: 85,
      subtitleStroke: false,
      subtitleWordByWord: true,           // karaoke word-by-word
      subtitleHighlightColor: '#00FF66',  // active-word color (green)
      subtitleUppercase: true,
      subtitleGlowEffect: true,
      subtitleShadowEnabled: true,
      subtitleMaxWordsPerLine: 4,
      subtitleMinDisplaySec: 0.8
    };
    // Versioned migration: a one-time bump so EXISTING users (who have
    // subtitleEnabled:false persisted) adopt the new on-by-default karaoke
    // subtitles once, while keeping any other tweaks they saved.
    const DEFAULTS_VERSION = 3;
    try {
      const saved = localStorage.getItem('qlipper_style_defaults');
      if (saved) {
        const parsed = JSON.parse(saved);
        if ((parsed.__v ?? 0) < DEFAULTS_VERSION) {
          const migrated = {
            ...defaults, ...parsed,
            subtitleEnabled: true,
            subtitlePreset: parsed.subtitlePreset || 'karaoke',
            subtitleWordByWord: parsed.subtitleWordByWord ?? true,
            subtitleHighlightColor: parsed.subtitleHighlightColor ?? '#00FF66',
            __v: DEFAULTS_VERSION
          };
          try { localStorage.setItem('qlipper_style_defaults', JSON.stringify(migrated)); } catch { /* ignore */ }
          return migrated;
        }
        return { ...defaults, ...parsed };
      }
    } catch { /* corrupt entry — fall back to defaults */ }
    return { ...defaults, __v: DEFAULTS_VERSION };
  });

  // Persist the user's style settings as their default. Called from the two
  // explicit "save" sites only (preset modal apply, Style Settings modal save)
  // so transient UI tweaks don't silently become the default.
  const persistStyleDefaults = (settings) => {
    try { localStorage.setItem('qlipper_style_defaults', JSON.stringify(settings)); }
    catch { /* storage full / disabled — non-fatal */ }
  };

  // --- GRAND FEATURE: PIPELINE STATE ---
  const [isPipelineRunning, setIsPipelineRunning] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState({
    step1: 'idle', // 'idle' | 'loading' | 'success' | 'error'
    step2: 'idle',
    step3: 'idle'
  });
  const [pipelineMetadata, setPipelineMetadata] = useState(null);
  const [pipelineAnalysis, setPipelineAnalysis] = useState(null);
  const [pipelineError, setPipelineError] = useState(null);
  // Phase Transition State
  const [pipelineAwaitingFormat, setPipelineAwaitingFormat] = useState(false);
  const [pipelineReadyData, setPipelineReadyData] = useState(null);
  const [downloadFallbackEnabled, setDownloadFallbackEnabled] = useState(false);

  // Thank You Modal State
  const [showThankYouModal, setShowThankYouModal] = useState(() => {
    return !localStorage.getItem('qlipper_thank_you_dismissed');
  });
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const [onboardingSlide, setOnboardingSlide] = useState(0);
  const [onboardingPlaying, setOnboardingPlaying] = useState(true);

  // Project History State (Phase 7)
  const [historyProjects, setHistoryProjects] = useState([]);
  const [selectedHistoryProject, setSelectedHistoryProject] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedProjectClip, setSelectedProjectClip] = useState(null); // For Player Modal

  // Pipeline Setup State (Phase 2 Refactor)
  const [pipelineSetup, setPipelineSetup] = useState(false);
  const [setupUrl, setSetupUrl] = useState('');
  const [clipLength, setClipLength] = useState('Any length');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [pipelineMode, setPipelineMode] = useState('youtube'); // 'youtube' | 'upload'
  const [showTranscribingModal, setShowTranscribingModal] = useState(false);
  const [transcribingProgress, setTranscribingProgress] = useState({ status: 'uploading', message: 'Uploading file…' });
  const [pipelineUploadedAsset, setPipelineUploadedAsset] = useState(null); // asset obj when mode=upload
  const [showAssetPicker, setShowAssetPicker] = useState(false); // asset library picker dropdown

  // Assets State (Phase 16)
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const fileInputRef = useRef(null);
  // Dashboard State
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, assetId: null });
  const [projectToDelete, setProjectToDelete] = useState(null); // { id, title }
  const [transcribingAssets, setTranscribingAssets] = useState({}); // { [assetId]: { status, jobId, logs } }

  // --- WATERMARK REMOVER STATE (Experimental) ---
  const [wmSource, setWmSource] = useState(null);        // { type: 'asset'|'clip', path, name }
  const [wmFrameUrl, setWmFrameUrl] = useState(null);    // URL of extracted frame
  const [wmVideoDims, setWmVideoDims] = useState(null);  // { width, height }
  const [wmTimestamp, setWmTimestamp] = useState(3);      // current scrub position (seconds)
  const [wmDuration, setWmDuration] = useState(0);       // video duration
  const [wmScrubbing, setWmScrubbing] = useState(false); // debounce flag
  const [wmRegion, setWmRegion] = useState(null);         // { x, y, w, h } in percentage
  const [wmDrawing, setWmDrawing] = useState(false);      // is user currently drawing
  const [wmDrawStart, setWmDrawStart] = useState(null);   // drag start point
  const [wmProcessing, setWmProcessing] = useState(false);
  const [wmResult, setWmResult] = useState(null);         // { outputUrl, outputFilename }
  const [wmError, setWmError] = useState(null);
  const wmImgRef = useRef(null);
  // Polling interval refs (cleaned up on reset/unmount)
  const wmPollRef = useRef(null);
  const silPollRef = useRef(null);
  const brollPollRef = useRef(null);

  // --- SILENCE REMOVER STATE (Experimental) ---
  const [silSource, setSilSource] = useState(null);       // { type, path, name }
  const [silThreshold, setSilThreshold] = useState(-30);  // dB
  const [silMinDuration, setSilMinDuration] = useState(0.5); // seconds
  const [silMode, setSilMode] = useState('duplicate');     // 'duplicate' | 'replace'
  const [silProcessing, setSilProcessing] = useState(false);
  const [silResult, setSilResult] = useState(null);        // { outputUrl, outputFilename, replaced }
  const [silError, setSilError] = useState(null);

  // --- B-ROLL INSERTION STATE (Experimental) ---
  const [brollSource, setBrollSource] = useState(null);
  const [brollMoments, setBrollMoments] = useState([]);
  const [brollAnalyzing, setBrollAnalyzing] = useState(false);
  const [brollItems, setBrollItems] = useState([]);
  const [brollProcessing, setBrollProcessing] = useState(false);
  const [brollResult, setBrollResult] = useState(null);
  const [brollError, setBrollError] = useState(null);
  const [brollImageSearch, setBrollImageSearch] = useState({});

  // --- TRANSCRIPT PANEL STATE ---
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [assetTranscriptData, setAssetTranscriptData] = useState(null); // { text, segments, language }
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [savingTranscript, setSavingTranscript] = useState(false);

  // --- CLIP EDITING STATE ---
  const [clipEditState, setClipEditState] = useState({}); // { [clipIdx]: { hookText, format, isReprocessing } }
  const [reprocessingClips, setReprocessingClips] = useState({}); // { [clipIdx]: true/false }
  const [clipEditModal, setClipEditModal] = useState({ isOpen: false, type: null, clipIdx: null }); // type: 'hook' | 'subtitle' | 'style'

  // --- LOGO OVERLAY STATE ---
  const [logos, setLogos] = useState([]); // uploaded logo library from backend
  const [logoOverlay, setLogoOverlay] = useState({ // current logo config per clip
    // { [clipIdx]: { logoId, serverPath, x, y, scale, opacity } }
  });

  // --- WHISPER CAPTION ENHANCEMENT STATE ---
  // Tracks whether AI-enhanced word-level captions are available or being generated
  // 'idle' | 'enhancing' | 'enhanced' | 'failed'
  const [captionEnhanceStatus, setCaptionEnhanceStatus] = useState('idle');
  const [captionStatus, setCaptionStatus] = useState(null); // { type, wordLevel, path }

  // --- GEMINI VALIDATION STATE ---
  const [showApiKeyValidation, setShowApiKeyValidation] = useState(false);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);

  const validateApiKey = () => {
    if (selectedModel === 'router') {
      if (routerMissing) { setShowApiKeyValidation(true); return false; }
      return true;
    }
    const keyMap = {
      'gemini': apiKey,
      'gpt-4o': openaiKey,
      'claude': claudeKey,
      'mistral': mistralKey,
      'deepseek': deepseekKey,
      'openrouter': openrouterKey,
      'minimax': minimaxKey,
      'xai': xaiKey
    };
    const key = keyMap[selectedModel] || apiKey;
    if (!key || key.trim() === '') {
      setShowApiKeyValidation(true);
      return false;
    }
    return true;
  };

  // ... (existing logging code)

  // Auto-minimize console when any modal is open
  useEffect(() => {
    if (pipelineSetup || showSettings || deleteConfirmation.isOpen || projectToDelete) {
      setIsConsoleMinimized(true);
    }
  }, [pipelineSetup, showSettings, deleteConfirmation, projectToDelete]);

  // Cleanup tool polling intervals on unmount
  useEffect(() => {
    return () => {
      if (wmPollRef.current) clearInterval(wmPollRef.current);
      if (silPollRef.current) clearInterval(silPollRef.current);
      if (brollPollRef.current) clearInterval(brollPollRef.current);
    };
  }, []);

  // Quality Probing Effect - Fetch available formats when YouTube URL changes
  useEffect(() => {
    // Only probe if URL looks like a valid YouTube link
    const isSupportedUrl = (url) => {
      if (!url) return false;
      // YouTube
      if (url.match(/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)) return true;
      // TikTok
      if (url.match(/tiktok\.com\//)) return true;
      // Instagram Reels
      if (url.match(/instagram\.com\/(reel|p)\//)) return true;
      // Twitter/X
      if (url.match(/(twitter\.com|x\.com)\/.*\/status\//)) return true;
      // Facebook video
      if (url.match(/facebook\.com\/.*\/videos?\//)) return true;
      return false;
    };

    if (!isSupportedUrl(youtubeUrl)) {
      setAvailableQualities(null);
      setQualityProbing(false);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setQualityProbing(true);
      try {
        const res = await axios.get(`${API_BASE}/api/youtube/metadata`, {
          params: { url: youtubeUrl }
        });
        const { formats, maxHeight, language } = res.data;
        setAvailableQualities({ tiers: formats || [], maxHeight: maxHeight || 720 });
        setPipelineVideoLang(language || null);
        // Auto-select highest available that makes sense (but don't override "Best")
        if (pipelineQuality !== 'best') {
          if (maxHeight >= 1080) setPipelineQuality('1080');
          else if (maxHeight >= 720) setPipelineQuality('720');
          else setPipelineQuality('480');
        }
      } catch (err) {
        console.warn('[QUALITY-PROBE] Failed to fetch available qualities:', err.message);
        setAvailableQualities(null); // Fallback to static options
      } finally {
        setQualityProbing(false);
      }
    }, 1200); // 1.2s debounce

    return () => clearTimeout(debounceTimer);
  }, [youtubeUrl]);

  // Auto-detect Whisper caption enhancement status when project changes.
  // Uses the captions API to check if Whisper word-level data exists for any clip.
  useEffect(() => {
    if (!selectedHistoryProject?.sourceVideo) return;
    if (captionEnhanceStatus !== 'idle') return; // Don't interrupt active enhancement

    const checkWhisperExists = async () => {
      try {
        // Find an asset matching this project's source video
        const assetsRes = await axios.get(`${API_BASE}/api/assets`);
        const sourceFilename = String(selectedHistoryProject.sourceVideo || '').split('/').pop();
        const asset = assetsRes.data.find(a => a.filename === sourceFilename);
        if (!asset) return;

        // Check captions endpoint — it returns source type
        const captionsRes = await axios.get(`${API_BASE}/api/captions/${asset.id}`, {
          params: { preferWhisper: 'true' }
        });
        if (captionsRes.data.source === 'whisper' || captionsRes.data.hasWordTimestamps) {
          setCaptionEnhanceStatus('enhanced');
        }
      } catch (e) {
        // Silently ignore — Whisper data doesn't exist yet
      }
    };

    checkWhisperExists();
  }, [selectedHistoryProject?.sourceVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch caption status from backend when project changes OR when its clip
  // count changes. Reason for the second trigger: when a new project is
  // optimistically created, the fetch runs once before any transcript exists →
  // returns `none`. After the pipeline completes (clips populate), the cached
  // `none` was sticking around forever, making the "Generate Captions" button
  // appear permanently even though Whisper had already produced a transcript.
  // Re-fetching when clips arrive forces a fresh truth.
  useEffect(() => {
    if (!selectedHistoryProject?.id) {
      setCaptionStatus(null);
      return;
    }
    setReprocessingClips({});
    axios.get(`${API_BASE}/api/video/caption-status/${selectedHistoryProject.id}`)
      .then(res => setCaptionStatus(res.data))
      .catch(() => setCaptionStatus({ type: 'none', wordLevel: false, path: null }));
  }, [selectedHistoryProject?.id, selectedHistoryProject?.clips?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also reset stale enhancement status when switching to a new project, so
  // a `failed` from one session doesn't leak into the next and disable the
  // Generate Captions button.
  useEffect(() => {
    setCaptionEnhanceStatus('idle');
  }, [selectedHistoryProject?.id]);

  // Polling Effect for Transcriptions
  useEffect(() => {
    const interval = setInterval(() => {
      Object.entries(transcribingAssets).forEach(async ([assetId, job]) => {
        if (job.status === 'processing' || job.status === 'queued') {
          try {
            const res = await axios.get(`${API_BASE}/api/transcribe/status/${job.jobId}`);
            const updatedJob = res.data;

            setTranscribingAssets(prev => ({
              ...prev,
              [assetId]: { ...prev[assetId], status: updatedJob.status, logs: updatedJob.logs }
            }));

            if (updatedJob.status === 'completed') {
              addLog(`SUCCESS: Transcription complete for asset ${assetId}`, 'success');
              fetchAssets(); // Refresh grid
              // If this asset is selected, update it
              if (selectedAsset?.id === assetId) {
                setSelectedAsset(prev => ({ ...prev, hasSubtitle: true, hasAudio: true, transcriptionPath: res.data.path /* or fetch fresh */ }));
                // Better: fetch fresh asset
                const fresh = await axios.get(`${API_BASE}/api/assets`);
                const current = fresh.data.find(a => a.id === assetId);
                if (current) setSelectedAsset(current);
              }
            } else if (updatedJob.status === 'failed') {
              addLog(`ERROR: Transcription failed for asset ${assetId}`, 'error');
            }
          } catch (err) {
            console.error("Polling error", err);
          }
        }
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [transcribingAssets, selectedAsset]);

  const handleOpenFile = async (clipUrl) => {
    try {
      await axios.post(`${API_BASE}/api/system/open-file`, { filePath: clipUrl });
      addLog('System: Opening file location...', 'success');
    } catch (err) {
      addLog('Error opening file location. Try "Save to Files" instead.', 'error');
    }
  };

  const handleSaveToFiles = async (clipUrl, clipTitle) => {
    try {
      addLog('System: Preparing download...', 'info');
      // Trigger browser download via the new endpoint
      const downloadUrl = `${API_BASE}/api/system/download-file?filePath=${encodeURIComponent(clipUrl)}`;

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = clipTitle ? `${clipTitle.replace(/[^a-z0-9]/gi, '_')}.mp4` : 'clip.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addLog('System: Download started!', 'success');
    } catch (err) {
      addLog('Error saving file: ' + err.message, 'error');
    }
  };

  const requestDeleteAsset = (id) => {
    setDeleteConfirmation({ isOpen: true, assetId: id });
  };

  const handleStartTranscribe = async (asset) => {
    try {
      // Optimistic update
      setTranscribingAssets(prev => ({
        ...prev,
        [asset.id]: { status: 'processing', jobId: null, logs: ['Initiating...'] }
      }));

      // Pass Gemini API key and variant for reliable transcription fallback
      const res = await axios.post(`${API_BASE}/api/transcribe/${asset.id}`, {
        whisperEngine,
        whisperModel,
        whisperWasmFallback
      }, {
        headers: apiKey ? {
          'x-gemini-key': apiKey,
          'x-gemini-variant': geminiVariant
        } : {}
      });
      if (res.data.success) {
        setTranscribingAssets(prev => ({
          ...prev,
          [asset.id]: { status: 'processing', jobId: res.data.jobId, logs: ['Job Started...'] }
        }));
        addLog('Job started in background', 'info');
      }
    } catch (err) {
      addLog('Failed to start transcription', 'error');
      setTranscribingAssets(prev => {
        const next = { ...prev };
        delete next[asset.id];
        return next;
      });
    }
  };

  const executeDeleteAsset = async () => {
    const id = deleteConfirmation.assetId;
    if (!id) return;

    try {
      await axios.delete(`${API_BASE}/api/assets/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
      addLog('SUCCESS: Asset deleted', 'success');
      setDeleteConfirmation({ isOpen: false, assetId: null });
      if (selectedAsset?.id === id) setSelectedAsset(null);
    } catch (err) {
      addLog('ERROR: Failed to delete asset', 'error');
    }
  };

  // Auto-load transcript when videoFile selected in Subtitle Editor tab
  useEffect(() => {
    if (activeTab === 'tool-d' && videoFile?.hasSubtitle) {
      loadTranscriptForAsset(videoFile);
    }
  }, [videoFile, activeTab]);

  // --- TRANSCRIPT HANDLERS ---
  const loadTranscriptForAsset = async (asset) => {
    if (!asset?.transcriptionPath) return;

    setLoadingTranscript(true);
    try {
      const path = asset.transcriptionPath.startsWith('http')
        ? asset.transcriptionPath
        : `${API_BASE}/${asset.transcriptionPath}`;
      const res = await fetch(path);
      const data = await res.json();
      setAssetTranscriptData(data);
      setShowTranscriptPanel(true);
      addLog('Transcript loaded', 'info');
    } catch (err) {
      addLog('Failed to load transcript', 'error');
      setAssetTranscriptData(null);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const saveTranscriptForAsset = async (assetId, segments) => {
    setSavingTranscript(true);
    try {
      // Rebuild full text from segments
      const fullText = segments.map(s => s.text).join(' ');

      await axios.patch(`${API_BASE}/api/assets/${assetId}/transcript`, {
        text: fullText,
        segments: segments,
        language: assetTranscriptData?.language || 'en'
      });

      // Update local state
      setAssetTranscriptData(prev => ({
        ...prev,
        text: fullText,
        segments: segments
      }));

      addLog('Transcript saved successfully', 'success');
    } catch (err) {
      addLog('Failed to save transcript', 'error');
    } finally {
      setSavingTranscript(false);
    }
  };

  const toggleTranscriptPanel = () => {
    if (!showTranscriptPanel && selectedAsset?.hasSubtitle) {
      loadTranscriptForAsset(selectedAsset);
    } else {
      setShowTranscriptPanel(!showTranscriptPanel);
    }
  };

  // --- CLIP EDITING HANDLERS ---
  const updateClipMetadata = async (projectId, clipIdx, updates) => {
    try {
      await axios.patch(`${API_BASE}/api/history/${projectId}/clips/${clipIdx}`, updates);
      // Update local state
      if (selectedHistoryProject?.id === projectId) {
        setSelectedHistoryProject(prev => ({
          ...prev,
          clips: prev.clips.map((c, i) => i === clipIdx ? { ...c, ...updates } : c)
        }));
      }
      addLog('Clip metadata saved', 'success');
    } catch (err) {
      addLog('Failed to save clip metadata', 'error');
    }
  };

  const generateAutoHook = (clipTopic) => {
    // Simple polishing: capitalize, add energy
    const polished = String(clipTopic || '')
      .replace(/[_-]/g, ' ')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
    return polished;
  };

  // Trigger Whisper word-level transcription for the current project's source video.
  // Called from the subtitle settings "Enhance Accuracy" button.
  const enhanceCaptions = async () => {
    if (!selectedHistoryProject?.sourceVideo) {
      addLog('No source video available for caption enhancement.', 'warning');
      return;
    }
    setCaptionEnhanceStatus('enhancing');
    try {
      const whisperRes = await axios.post(`${API_BASE}/api/youtube/transcribe`, {
        videoPath: selectedHistoryProject.sourceVideo,
        whisperModel,
        language: pipelineVideoLang || ''
      });

      if (whisperRes.data.cached) {
        setCaptionEnhanceStatus('enhanced');
        addLog('AI-enhanced captions already available (cached).', 'success');
        if (selectedHistoryProject?.id) {
          axios.get(`${API_BASE}/api/video/caption-status/${selectedHistoryProject.id}`)
            .then(res => setCaptionStatus(res.data))
            .catch(() => {});
        }
        return;
      }

      if (whisperRes.data.success) {
        const whisperJobId = whisperRes.data.jobId;
        // Poll with 5-minute timeout
        await new Promise((resolve) => {
          const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
          const startTime = Date.now();
          const wInterval = setInterval(async () => {
            if (Date.now() - startTime > TIMEOUT_MS) {
              clearInterval(wInterval);
              setCaptionEnhanceStatus('failed');
              addLog('Caption enhancement timed out. Export will use YouTube captions.', 'warning');
              resolve();
              return;
            }
            try {
              const wStatus = await axios.get(`${API_BASE}/api/transcribe/status/${whisperJobId}`);
              if (wStatus.data.status === 'completed') {
                clearInterval(wInterval);
                setCaptionEnhanceStatus('enhanced');
                addLog('AI-enhanced word-level captions ready!', 'success');
                // Refresh caption status
                if (selectedHistoryProject?.id) {
                  axios.get(`${API_BASE}/api/video/caption-status/${selectedHistoryProject.id}`)
                    .then(res => setCaptionStatus(res.data))
                    .catch(() => {});
                }
                resolve();
              } else if (wStatus.data.status === 'failed') {
                clearInterval(wInterval);
                setCaptionEnhanceStatus('failed');
                addLog('Caption enhancement failed. Export will use YouTube captions.', 'warning');
                resolve();
              }
            } catch (e) { /* continue polling */ }
          }, 2000);
        });
      }
    } catch (err) {
      setCaptionEnhanceStatus('failed');
      addLog(`Caption enhancement error: ${err.message}`, 'warning');
    }
  };

  const reprocessClip = async (projectId, clipIdx, newFormat, subtitleSettings = null) => {
    if (!selectedHistoryProject) return;

    setReprocessingClips(prev => ({ ...prev, [clipIdx]: true }));

    try {
      const clip = selectedHistoryProject.clips[clipIdx];
      const editState = clipEditState[clipIdx] || {};
      // Resolve hook text based on hookSource setting
      // Use ?? so empty string (user cleared the hook) is respected, not skipped
      let hookText = editState.hookText !== undefined ? editState.hookText : (clip.customHook ?? '');
      if (!hookText && styleSettings.hookSource === 'title') {
        hookText = selectedHistoryProject.title || '';
      } else if (!hookText && styleSettings.hookSource === 'auto') {
        hookText = clip.topic ? clip.topic.replace(/[_-]/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';
      }

      // IMPORTANT: Use the format passed in, or fall back to clip's current format, or finally 'raw-cuts'
      const actualFormat = newFormat || clip.outputFormat || 'raw-cuts';

      addLog(`Re-rendering clip #${clipIdx + 1} with format: ${actualFormat}`, 'info');

      // Build merged styleSettings using custom FFmpeg parameters from edit state
      // Default to neon-green style if no custom values set
      const customTextColor = editState.hookTextColor || '#00FF00'; // neon-green default
      const customBorderColor = editState.hookBorderColor || '#000000';
      const customBgColor = editState.hookBgColor || 'transparent';
      const customBgEnabled = editState.hookBgEnabled || false;
      const customOutlineEnabled = editState.hookOutlineEnabled ?? true; // Outline enabled by default
      const customShadowEnabled = editState.hookShadowEnabled ?? true; // Shadow enabled by default
      const customBorderWidth = editState.hookBorderWidth ?? 8;
      const customFontFamily = editState.hookFontFamily || 'Sans-Bold';
      const customFontSize = editState.hookFontSize ?? 72;

      // Merge global styleSettings with clip-specific custom FFmpeg overrides
      const mergedStyleSettings = {
        // Use ALL global settings from StyleSettingsModal as base
        ...styleSettings,

        // Hook is strictly per-clip: it renders only when THIS clip has a
        // resolved hookText, OR the user explicitly enabled it for this clip
        // via editState.hookEnabled. We intentionally do NOT fall back to
        // styleSettings.hookEnabled or styleSettings.hookHeadline.text here —
        // that fallback caused global style state to leak into clips the user
        // never edited.
        hookEnabled: editState.hookEnabled !== undefined
          ? !!editState.hookEnabled
          : !!hookText,
        hookHeadline: hookText
          ? {
            ...styleSettings.hookHeadline,
            text: hookText,
            color: customTextColor
          }
          : { ...styleSettings.hookHeadline, text: '' },

        // Custom FFmpeg style parameters
        hookTextColor: customTextColor,
        hookStroke: customOutlineEnabled && !customBgEnabled, // Disable stroke when background is enabled or outline is off
        hookStrokeColor: customBorderColor,
        hookBorderWidth: customBorderWidth,
        hookBackground: customBgEnabled ? customBgColor : null,
        hookBgEnabled: customBgEnabled,
        hookOutlineEnabled: customOutlineEnabled,
        hookShadowEnabled: customShadowEnabled,
        hookFontFamily: customFontFamily,
        hookFontSize: customFontSize,
        hookGlow: false, // Can be extended later

        // Override alignment and position with clip-specific values
        hookAlignment: editState.hookAlignment || styleSettings.hookAlignment || 'center',
        hookVerticalPosition: editState.hookVerticalPosition ?? styleSettings.hookVerticalPosition ?? 75,

        // Override subtitle settings with clip-specific values
        subtitleEnabled: editState.useVtt ?? styleSettings.subtitleEnabled ?? false,
        subtitlePreset: editState.subtitlePreset || styleSettings.subtitlePreset || 'karaoke',
        subtitleTextColor: editState.subtitleTextColor || '#FFFFFF',
        subtitleHighlightColor: editState.subtitleHighlightColor || '#00FF66',
        subtitleOutlineColor: editState.subtitleOutlineColor || '#000000',
        subtitleBgColor: editState.subtitleBgColor || 'transparent',
        subtitleBgEnabled: editState.subtitleBgEnabled ?? false,
        subtitleFontFamily: editState.subtitleFontFamily || 'Sans-Bold',
        subtitleFontSize: editState.subtitleFontSize ?? 48,
        subtitleOutlineWidth: editState.subtitleOutlineWidth ?? 3,
        subtitlePosition: editState.subtitlePosition ?? 85,
        subtitleAlignment: editState.subtitleAlignment || 'center',
        subtitleWordByWord: editState.subtitleWordByWord ?? true,
        subtitleShadowEnabled: editState.subtitleShadowEnabled ?? true,
        subtitleGlowEffect: editState.subtitleGlowEffect ?? true,
        subtitleScaleEffect: editState.subtitleScaleEffect ?? false,
        subtitleMaxWordsPerLine: editState.subtitleMaxWordsPerLine ?? 4,
        subtitleUppercase: editState.subtitleUppercase ?? true,
        subtitleMinDisplaySec: editState.subtitleMinDisplaySec ?? 0.8,
        subtitleHighlightScale: editState.subtitleHighlightScale ?? 115,
        hookUppercase: editState.hookUppercase ?? true,

        // Logo overlay settings
        logoEnabled: !!logoOverlay[clipIdx],
        logoPath: logoOverlay[clipIdx]?.serverPath || null,
        logoX: logoOverlay[clipIdx]?.x ?? 5,
        logoY: logoOverlay[clipIdx]?.y ?? 5,
        logoScale: logoOverlay[clipIdx]?.scale ?? 25,
        logoOpacity: logoOverlay[clipIdx]?.opacity ?? 1.0,

        liveFaceDetection: editState.liveFaceDetection || false,
        faceDetectionMode: editState.faceDetectionMode || 'sekali',
        trackingStyle: editState.trackingStyle || 'smooth',
      };

      // --- LAZY WHISPER: Trigger word-level transcription only when subtitles are enabled ---
      // This avoids running Whisper on every pipeline run (saves CPU/RAM on lower-end devices).
      // The backend caches the result, so subsequent exports are instant.
      if (mergedStyleSettings.subtitleEnabled && selectedHistoryProject?.sourceVideo) {
        try {
          addLog('Enhancing captions for word-level accuracy...', 'info');
          const whisperRes = await axios.post(`${API_BASE}/api/youtube/transcribe`, {
            videoPath: selectedHistoryProject.sourceVideo,
            whisperModel,
            language: pipelineVideoLang || ''
          });

          if (whisperRes.data.success && !whisperRes.data.cached) {
            const whisperJobId = whisperRes.data.jobId;
            // Poll Whisper with 5-minute timeout — user is waiting for export
            await new Promise((resolve) => {
              const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
              const startTime = Date.now();
              const wInterval = setInterval(async () => {
                if (Date.now() - startTime > TIMEOUT_MS) {
                  clearInterval(wInterval);
                  addLog('Word-level enhancement timed out — using YouTube captions (still accurate).', 'warning');
                  resolve();
                  return;
                }
                try {
                  const wStatus = await axios.get(`${API_BASE}/api/transcribe/status/${whisperJobId}`);
                  if (wStatus.data.status === 'completed') {
                    clearInterval(wInterval);
                    addLog('Word-level captions ready!', 'success');
                    resolve();
                  } else if (wStatus.data.status === 'failed') {
                    clearInterval(wInterval);
                    addLog('Word-level enhancement unavailable — using YouTube captions (still accurate).', 'warning');
                    resolve();
                  }
                } catch (e) { /* continue polling */ }
              }, 2000);
            });
          } else if (whisperRes.data.cached) {
            addLog('Using cached word-level captions.', 'success');
          }
        } catch (err) {
          // Silently fall back to VTT — don't block the export
          addLog('Word-level enhancement skipped — using YouTube captions.', 'warning');
        }
      }
      // If no clip-specific logo overlay exists, check if we have default logo settings from pipeline
      let logoPayload = logoOverlay[clipIdx];
      if (!logoPayload && styleSettings.defaultLogoPath) {
        logoPayload = {
          serverPath: styleSettings.defaultLogoPath,
          x: styleSettings.defaultLogoX,
          y: styleSettings.defaultLogoY,
          scale: styleSettings.defaultLogoScale,
          opacity: styleSettings.defaultLogoOpacity
        };
      }

      await axios.post(`${API_BASE}/api/video/reprocess-single`, {
        projectId,
        clipIdx,
        outputFormat: actualFormat,
        outputResolution: editState.resolution || clip.resolution || '720p',
        containerFormat: editState.containerFormat || 'mp4',
        styleSettings: mergedStyleSettings,
        logoOverlay: logoPayload,
      }, { headers: getLicenseHeaders() });

      // Store the start time to compare against reprocessedAt
      const reprocessStartTime = Date.now();

      // Poll for completion - check reprocessStatus and reprocessedAt timestamp
      const pollStatus = async () => {
        try {
          const res = await axios.get(`${API_BASE}/api/video/reprocess-status/${projectId}/${clipIdx}`);
          const { reprocessStatus, reprocessError, reprocessedAt, subtitleSkipReason } = res.data;

          console.log(`[POLL] clipIdx=${clipIdx} status=${reprocessStatus} reprocessedAt=${reprocessedAt}`);

          // Check if completed via status OR if reprocessedAt is newer than when we started
          const isCompleted = reprocessStatus === 'completed' ||
            (reprocessedAt && new Date(reprocessedAt).getTime() > reprocessStartTime);

          if (isCompleted) {
            // Refresh project data
            const historyRes = await axios.get(`${API_BASE}/api/history`);
            const updatedProject = historyRes.data.find(p => p.id === projectId);
            if (updatedProject) {
              setSelectedHistoryProject(updatedProject);
              setHistoryProjects((Array.isArray(historyRes.data) ? historyRes.data : []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            }
            setReprocessingClips(prev => ({ ...prev, [clipIdx]: false }));
            // Clear local edit state to prevent n-1 bug
            setClipEditState(prev => {
              const next = { ...prev };
              delete next[clipIdx];
              return next;
            });
            addLog(`Clip #${clipIdx + 1} re-rendered successfully!`, 'success');
            // Warn user if subtitles were enabled but skipped
            if (subtitleSkipReason === 'no_vtt_file' || subtitleSkipReason === 'no_caption_source') {
              addLog(`Subtitles skipped: No caption source found. Try "Enhance Accuracy" in subtitle settings to generate AI captions.`, 'warn');
            } else if (subtitleSkipReason === 'no_captions_in_range') {
              addLog(`Subtitles skipped: No captions found in this clip's time range.`, 'warn');
            }
            return;
          } else if (reprocessStatus === 'failed') {
            setReprocessingClips(prev => ({ ...prev, [clipIdx]: false }));
            addLog(`Clip #${clipIdx + 1} re-render failed: ${reprocessError || 'Unknown error'}`, 'error');
            return;
          }
          // Still processing, continue polling
          setTimeout(pollStatus, 2000);
        } catch (err) {
          console.error('[POLL] Error:', err.message);
          setTimeout(pollStatus, 2000);
        }
      };
      // Start polling after 2 seconds (reduced from 3)
      setTimeout(pollStatus, 2000);

    } catch (err) {
      setReprocessingClips(prev => ({ ...prev, [clipIdx]: false }));
      addLog(`Failed to re-render clip: ${err.message}`, 'error');
    }
  };

  // ── MULTI-FORMAT BATCH RENDER ──

  const reprocessClipAndWait = (projectId, clipIdx, format, resolution) => {
    return new Promise(async (resolve, reject) => {
      try {
        const clip = selectedHistoryProject.clips[clipIdx];
        const editState = clipEditState[clipIdx] || {};

        // Resolve hook text based on hookSource setting
        let hookText = editState.hookText !== undefined ? editState.hookText : (clip.customHook ?? '');
        if (!hookText && styleSettings.hookSource === 'title') {
          hookText = selectedHistoryProject.title || '';
        } else if (!hookText && styleSettings.hookSource === 'auto') {
          hookText = clip.topic ? clip.topic.replace(/[_-]/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';
        }

        // Build the same mergedStyleSettings as reprocessClip
        const mergedStyleSettings = {
          ...styleSettings,
          hookEnabled: styleSettings.hookEnabled && !!hookText,
          hookHeadline: hookText
            ? { ...styleSettings.hookHeadline, text: hookText, color: editState.hookTextColor || '#00FF00' }
            : styleSettings.hookHeadline,
          hookTextColor: editState.hookTextColor || '#00FF00',
          hookStroke: (editState.hookOutlineEnabled ?? true) && !(editState.hookBgEnabled ?? false),
          hookStrokeColor: editState.hookBorderColor || '#000000',
          hookBorderWidth: editState.hookBorderWidth ?? 8,
          hookBackground: (editState.hookBgEnabled ?? false) ? (editState.hookBgColor || 'transparent') : null,
          hookBgEnabled: editState.hookBgEnabled ?? false,
          hookOutlineEnabled: editState.hookOutlineEnabled ?? true,
          hookShadowEnabled: editState.hookShadowEnabled ?? true,
          hookFontFamily: editState.hookFontFamily || 'Sans-Bold',
          hookFontSize: editState.hookFontSize ?? 72,
          hookGlow: false,
          hookAlignment: editState.hookAlignment || styleSettings.hookAlignment || 'center',
          hookVerticalPosition: editState.hookVerticalPosition ?? styleSettings.hookVerticalPosition ?? 75,
          hookUppercase: editState.hookUppercase ?? true,
          subtitleEnabled: editState.useVtt ?? styleSettings.subtitleEnabled ?? false,
          subtitlePreset: editState.subtitlePreset || styleSettings.subtitlePreset || 'karaoke',
          subtitleTextColor: editState.subtitleTextColor || '#FFFFFF',
          subtitleHighlightColor: editState.subtitleHighlightColor || '#00FF66',
          subtitleOutlineColor: editState.subtitleOutlineColor || '#000000',
          subtitleBgColor: editState.subtitleBgColor || 'transparent',
          subtitleBgEnabled: editState.subtitleBgEnabled ?? false,
          subtitleFontFamily: editState.subtitleFontFamily || 'Sans-Bold',
          subtitleFontSize: editState.subtitleFontSize ?? 48,
          subtitleOutlineWidth: editState.subtitleOutlineWidth ?? 3,
          subtitlePosition: editState.subtitlePosition ?? 85,
          subtitleAlignment: editState.subtitleAlignment || 'center',
          subtitleWordByWord: editState.subtitleWordByWord ?? true,
          subtitleShadowEnabled: editState.subtitleShadowEnabled ?? true,
          subtitleGlowEffect: editState.subtitleGlowEffect ?? true,
          subtitleScaleEffect: editState.subtitleScaleEffect ?? false,
          subtitleMaxWordsPerLine: editState.subtitleMaxWordsPerLine ?? 4,
          subtitleUppercase: editState.subtitleUppercase ?? true,
          subtitleMinDisplaySec: editState.subtitleMinDisplaySec ?? 0.8,
          subtitleHighlightScale: editState.subtitleHighlightScale ?? 115,
          logoEnabled: !!logoOverlay[clipIdx],
          logoPath: logoOverlay[clipIdx]?.serverPath || null,
          logoX: logoOverlay[clipIdx]?.x ?? 5,
          logoY: logoOverlay[clipIdx]?.y ?? 5,
          logoScale: logoOverlay[clipIdx]?.scale ?? 25,
          logoOpacity: logoOverlay[clipIdx]?.opacity ?? 1.0,

          liveFaceDetection: editState.liveFaceDetection || false,
          faceDetectionMode: editState.faceDetectionMode || 'sekali',
          trackingStyle: editState.trackingStyle || 'smooth',
        };

        await axios.post(`${API_BASE}/api/video/reprocess-single`, {
          projectId,
          clipIdx,
          outputFormat: format,
          outputResolution: resolution,
          styleSettings: mergedStyleSettings,
        }, { headers: getLicenseHeaders() });

        const reprocessStartTime = Date.now();

        const pollStatus = () => {
          axios.get(`${API_BASE}/api/video/reprocess-status/${projectId}/${clipIdx}`)
            .then(res => {
              const { reprocessStatus, reprocessError, reprocessedAt } = res.data;
              const isCompleted = reprocessStatus === 'completed' ||
                (reprocessedAt && new Date(reprocessedAt).getTime() > reprocessStartTime);

              if (isCompleted) {
                resolve();
              } else if (reprocessStatus === 'failed') {
                reject(new Error(reprocessError || 'Render failed'));
              } else {
                setTimeout(pollStatus, 2000);
              }
            })
            .catch(() => setTimeout(pollStatus, 2000));
        };
        setTimeout(pollStatus, 2000);
      } catch (err) {
        reject(err);
      }
    });
  };

  const reprocessClipMultiFormat = async (projectId, clipIdx, formats, resolution) => {
    setReprocessingClips(prev => ({ ...prev, [clipIdx]: true }));
    setMultiRenderProgress({ current: 0, total: formats.length, clipIdx });
    const key = `${projectId}_${clipIdx}`;
    const collectedResults = [];

    for (let i = 0; i < formats.length; i++) {
      setMultiRenderProgress({ current: i + 1, total: formats.length, clipIdx });
      addLog(`Multi-format: Rendering ${i + 1}/${formats.length} (${formats[i]})...`, 'info');

      try {
        await reprocessClipAndWait(projectId, clipIdx, formats[i], resolution);
        // Capture the rendered URL immediately after this format completes
        const snap = await axios.get(`${API_BASE}/api/history`);
        const proj = snap.data.find(p => p.id === projectId);
        if (proj?.clips?.[clipIdx]?.url) {
          collectedResults.push({ format: formats[i], url: proj.clips[clipIdx].url });
        }
        addLog(`Multi-format: ${formats[i]} complete!`, 'success');
      } catch (err) {
        addLog(`Multi-format: ${formats[i]} failed: ${err.message}`, 'error');
      }
    }

    // Store all collected variant URLs
    setMultiFormatResults(prev => ({ ...prev, [key]: collectedResults }));

    // Refresh project data after all renders
    try {
      const historyRes = await axios.get(`${API_BASE}/api/history`);
      const updatedProject = historyRes.data.find(p => p.id === projectId);
      if (updatedProject) {
        setSelectedHistoryProject(updatedProject);
        setHistoryProjects((Array.isArray(historyRes.data) ? historyRes.data : []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      }
    } catch (e) { /* ignore refresh error */ }

    setReprocessingClips(prev => ({ ...prev, [clipIdx]: false }));
    setMultiRenderProgress(null);
    addLog(`All ${formats.length} formats rendered! See clip variants below.`, 'success');
  };

  // ── MODAL MUTUAL EXCLUSION ──

  const dismissAllModals = useCallback(() => {
    setShowApiKeyValidation(false);
    setShowApiKeyPrompt(false);
    setShowSettings(false);
    setShowByokModal(false);
    setShowStyleModal(false);
    setClipEditModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // ── WATERMARK REMOVER HANDLERS ──

  const handleWmExtractFrame = async (source, ts) => {
    const timestamp = ts ?? 3.0;
    if (!ts) {
      // Fresh source selection — reset everything
      setWmSource(source);
      setWmRegion(null);
      setWmResult(null);
      setWmError(null);
      setWmTimestamp(3);
      // Try to get duration from asset metadata
      const asset = assets.find(a => a.path === source.path);
      setWmDuration(asset?.duration || source.duration || 0);
    }
    setWmFrameUrl(null);
    try {
      const res = await axios.post(`${API_BASE}/api/video/extract-frame`, { videoPath: source.path, timestamp });
      setWmFrameUrl(`${API_BASE}${res.data.frameUrl}`);
      setWmVideoDims({ width: res.data.width, height: res.data.height });
      if (res.data.duration && (!wmDuration || wmDuration === 0)) setWmDuration(res.data.duration);
    } catch (err) {
      setWmError('Failed to extract frame: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleWmRemove = async () => {
    if (!wmSource || !wmRegion || !wmVideoDims) return;
    setWmProcessing(true);
    setWmError(null);
    setWmResult(null);

    const pixelRegion = {
      x: Math.round(wmRegion.x / 100 * wmVideoDims.width),
      y: Math.round(wmRegion.y / 100 * wmVideoDims.height),
      w: Math.round(wmRegion.w / 100 * wmVideoDims.width),
      h: Math.round(wmRegion.h / 100 * wmVideoDims.height)
    };

    try {
      const res = await axios.post(`${API_BASE}/api/video/remove-watermark`, {
        videoPath: wmSource.path,
        region: pixelRegion,
        sourceType: wmSource.type
      }, { headers: getLicenseHeaders() });

      const { jobId } = res.data;
      addLog(`WATERMARK: Job started (${jobId})`, 'info');

      // Poll for status (store interval ref for cleanup)
      if (wmPollRef.current) clearInterval(wmPollRef.current);
      wmPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/api/job/${jobId}`);
          const job = statusRes.data;
          if (job.status === 'completed') {
            clearInterval(wmPollRef.current); wmPollRef.current = null;
            setWmResult({ outputUrl: job.outputUrl, outputFilename: job.outputFilename });
            setWmProcessing(false);
            addLog(`WATERMARK: Done! Saved as "${job.outputFilename}"`, 'success');
            fetchAssets(); // refresh asset library
          } else if (job.status === 'failed') {
            clearInterval(wmPollRef.current); wmPollRef.current = null;
            setWmError(job.error || 'Watermark removal failed');
            setWmProcessing(false);
            addLog(`WATERMARK: Failed — ${job.error}`, 'error');
          }
        } catch {}
      }, 2000);
    } catch (err) {
      setWmError(err.response?.data?.error || err.message);
      setWmProcessing(false);
    }
  };

  const handleWmReset = () => {
    if (wmPollRef.current) { clearInterval(wmPollRef.current); wmPollRef.current = null; }
    setWmSource(null);
    setWmFrameUrl(null);
    setWmVideoDims(null);
    setWmRegion(null);
    setWmProcessing(false);
    setWmResult(null);
    setWmError(null);
  };

  // ── SILENCE REMOVER HANDLERS ──

  const handleSilRemove = async () => {
    if (!silSource) return;
    setSilProcessing(true);
    setSilError(null);
    setSilResult(null);

    try {
      const res = await axios.post(`${API_BASE}/api/video/remove-silence`, {
        videoPath: silSource.path,
        threshold: silThreshold,
        minDuration: silMinDuration,
        mode: silMode
      }, { headers: getLicenseHeaders() });

      const { jobId } = res.data;
      addLog(`SILENCE: Job started (${jobId}) — mode: ${silMode}`, 'info');

      if (silPollRef.current) clearInterval(silPollRef.current);
      silPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/api/job/${jobId}`);
          const job = statusRes.data;
          if (job.status === 'completed') {
            clearInterval(silPollRef.current); silPollRef.current = null;
            setSilResult({ outputUrl: job.outputUrl, outputFilename: job.outputFilename, replaced: job.replaced });
            setSilProcessing(false);
            addLog(`SILENCE: Done! ${job.replaced ? 'Replaced original' : `Saved as "${job.outputFilename}"`}`, 'success');
            fetchAssets();
          } else if (job.status === 'failed') {
            clearInterval(silPollRef.current); silPollRef.current = null;
            setSilError(job.error || 'Silence removal failed');
            setSilProcessing(false);
            addLog(`SILENCE: Failed — ${job.error}`, 'error');
          }
        } catch {}
      }, 2000);
    } catch (err) {
      setSilError(err.response?.data?.error || err.message);
      setSilProcessing(false);
    }
  };

  const handleSilReset = () => {
    if (silPollRef.current) { clearInterval(silPollRef.current); silPollRef.current = null; }
    setSilSource(null);
    setSilProcessing(false);
    setSilResult(null);
    setSilError(null);
    setSilThreshold(-30);
    setSilMinDuration(0.5);
    setSilMode('duplicate');
  };

  // ── B-ROLL INSERTION HANDLERS ──

  const handleBrollAnalyze = async () => {
    if (!brollSource) return;
    setBrollAnalyzing(true);
    setBrollError(null);
    setBrollMoments([]);
    setBrollItems([]);

    try {
      // Get transcript — try from asset or fetch from clip
      let transcript = '';
      if (brollSource.transcript) {
        transcript = brollSource.transcript;
      } else {
        // Try to load transcript from the source
        try {
          const res = await axios.get(`${API_BASE}/api/assets`);
          const asset = res.data.find(a => a.path === brollSource.path);
          if (asset?.transcriptionPath) {
            const tRes = await axios.get(`${API_BASE}/${asset.transcriptionPath}`);
            transcript = typeof tRes.data === 'string' ? tRes.data : JSON.stringify(tRes.data);
          }
        } catch {}
      }

      if (!transcript) {
        setBrollError('No transcript available. Go to Asset Library, select this video, and click Transcribe Audio.');
        setBrollAnalyzing(false);
        return;
      }

      const keyMap = { 'gemini': 'x-gemini-key', 'gpt-4o': 'x-openai-key', 'claude': 'x-anthropic-key', 'mistral': 'x-mistral-key', 'deepseek': 'x-deepseek-key', 'openrouter': 'x-openrouter-key' };
      const headers = { [keyMap[selectedModel] || 'x-gemini-key']: apiKey, ...getLicenseHeaders() };
      if (selectedModel === 'gemini') headers['x-gemini-variant'] = geminiVariant;

      // Use pipeline language setting; fallback to 'id'. Never use 'orig' (breaks backend).
      const brollLang = (pipelineVideoLang && pipelineVideoLang !== 'orig') ? pipelineVideoLang : 'id';
      const res = await axios.post(`${API_BASE}/api/video/analyze-broll`, {
        transcript: transcript.substring(0, 8000),
        duration: brollSource.duration || 60,
        language: brollLang,
        clipTopic: brollSource.name,
        model: selectedModel
      }, { headers });

      const moments = res.data.broll_moments || [];
      setBrollMoments(moments);
      setBrollItems(moments.map(m => ({ ...m, imagePath: null, imageUrl: null, source: 'pexels', effect: 'kenburns' })));

      // Auto-search Pexels for each moment
      moments.forEach((m, idx) => {
        axios.post(`${API_BASE}/api/video/search-broll-image`, { keyword: m.keyword }).then(r => {
          setBrollImageSearch(prev => ({ ...prev, [idx]: r.data.images || [] }));
          // Auto-select first image
          if (r.data.images?.length > 0) {
            setBrollItems(prev => {
              const next = [...prev];
              next[idx] = { ...next[idx], imageUrl: r.data.images[0].url, thumbnail: r.data.images[0].thumbnail };
              return next;
            });
          }
        }).catch(() => {});
      });

      addLog(`B-ROLL: Found ${moments.length} moments for B-roll insertion`, 'success');
    } catch (err) {
      setBrollError(err.response?.data?.error || err.message);
    } finally {
      setBrollAnalyzing(false);
    }
  };

  const handleBrollApply = async () => {
    const validItems = brollItems.filter(item => item.imageUrl || item.imagePath);
    if (validItems.length === 0 || !brollSource) return;
    setBrollProcessing(true);
    setBrollError(null);

    try {
      // Download remote images to server first
      const resolvedItems = await Promise.all(validItems.map(async (item) => {
        if (item.imagePath) return item; // Already local
        // Download from URL
        const dlRes = await axios.post(`${API_BASE}/api/video/download-broll-image`, {
          imageUrl: item.imageUrl, keyword: item.keyword
        });
        return { ...item, imagePath: dlRes.data.path };
      }));

      // Parse timestamps to seconds
      const parseTime = (t) => {
        if (typeof t === 'number') return t;
        const parts = t.split(':').map(Number);
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
        if (parts.length === 2) return parts[0] * 60 + parts[1];
        return parseFloat(t) || 0;
      };

      const brollPayload = resolvedItems.map(item => ({
        start: parseTime(item.start),
        end: parseTime(item.end),
        imagePath: item.imagePath,
        effect: item.effect || 'kenburns'
      }));

      const res = await axios.post(`${API_BASE}/api/video/apply-broll`, {
        videoPath: brollSource.path,
        brollItems: brollPayload
      }, { headers: getLicenseHeaders() });

      const { jobId } = res.data;
      addLog(`B-ROLL: Composition started (${jobId})`, 'info');

      if (brollPollRef.current) clearInterval(brollPollRef.current);
      brollPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/api/job/${jobId}`);
          const job = statusRes.data;
          if (job.status === 'completed') {
            clearInterval(brollPollRef.current); brollPollRef.current = null;
            setBrollResult({ outputUrl: job.outputUrl, outputFilename: job.outputFilename });
            setBrollProcessing(false);
            addLog(`B-ROLL: Done! Saved as "${job.outputFilename}"`, 'success');
            fetchAssets();
          } else if (job.status === 'failed') {
            clearInterval(brollPollRef.current); brollPollRef.current = null;
            setBrollError(job.error || 'B-roll composition failed');
            setBrollProcessing(false);
          }
        } catch {}
      }, 2500);
    } catch (err) {
      setBrollError(err.response?.data?.error || err.message);
      setBrollProcessing(false);
    }
  };

  const handleBrollReset = () => {
    if (brollPollRef.current) { clearInterval(brollPollRef.current); brollPollRef.current = null; }
    setBrollSource(null);
    setBrollMoments([]);
    setBrollItems([]);
    setBrollProcessing(false);
    setBrollResult(null);
    setBrollError(null);
    setBrollImageSearch({});
    setBrollAnalyzing(false);
  };

  const consoleEndRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytPlayerContainerRef = useRef(null);

  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Refs to break dependency cycle in fetchHistory — prevents re-render loops
  const selectedHistoryProjectRef = useRef(selectedHistoryProject);
  const selectedProjectClipRef = useRef(selectedProjectClip);
  useEffect(() => { selectedHistoryProjectRef.current = selectedHistoryProject; }, [selectedHistoryProject]);
  useEffect(() => { selectedProjectClipRef.current = selectedProjectClip; }, [selectedProjectClip]);

  const fetchHistory = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_BASE}/api/history`);
      // Sort by timestamp desc
      const sorted = (Array.isArray(res.data) ? res.data : []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setHistoryProjects(sorted);

      const currentProject = selectedHistoryProjectRef.current;
      const currentClip = selectedProjectClipRef.current;

      if (currentProject) {
        // Refresh selected project with latest data from server
        const updated = sorted.find(p => p.id === currentProject.id);
        if (updated) {
          setSelectedHistoryProject(updated);
          // Also refresh selected clip if the clips have changed
          if (updated.clips && updated.clips.length > 0 && !currentClip) {
            setSelectedProjectClip(updated.clips[0]);
          }
        }
      } else if (sorted.length > 0) {
        // Auto-select first if none selected
        setSelectedHistoryProject(sorted[0]);
        if (sorted[0].clips && sorted[0].clips.length > 0) {
          setSelectedProjectClip(sorted[0].clips[0]);
        }
      }
    } catch (err) {
      addLog('ERROR: Failed to load project history', 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const res = await axios.get(`${API_BASE}/api/assets`);
      setAssets((Array.isArray(res.data) ? res.data : []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    } catch (err) {
      addLog('ERROR: Failed to load assets', 'error');
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  const fetchLogos = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/logos`);
      setLogos(res.data);
    } catch (err) {
      console.error('Failed to load logos:', err.message);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'projects') fetchHistory();
    if (['dashboard', 'tool-a', 'tool-b', 'tool-c', 'tool-d', 'tool-f', 'tool-g', 'tool-h', 'assets'].includes(activeTab)) fetchAssets();
    if (activeTab === 'projects' || activeTab === 'tool-e') fetchLogos();
    if (['tool-f', 'tool-g', 'tool-h'].includes(activeTab)) fetchHistory();
  }, [activeTab, fetchHistory, fetchAssets, fetchLogos]);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/rss`);
        setFeedItems(res.data);
      } catch (err) {
        console.error("Feed error:", err);
      }
    };
    fetchFeed();
  }, []);

  // YouTube IFrame Player API Logic
  useEffect(() => {
    // 1. Load the script if not already present
    if (!window.YT && !document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }

    // 2. Define global callback for when API is ready
    window.onYouTubeIframeAPIReady = () => {
      initPlayer();
    };

    const initPlayer = () => {
      const videoId = getYoutubeVideoId(youtubeUrl);
      if (videoId && ytPlayerContainerRef.current) {
        // If player already exists, just load the new video
        if (ytPlayerRef.current && typeof ytPlayerRef.current.loadVideoById === 'function') {
          try {
            ytPlayerRef.current.loadVideoById(videoId);
          } catch (e) {
            console.error("YT Player load error:", e);
          }
        } else {
          // Create new player
          ytPlayerRef.current = new window.YT.Player(ytPlayerContainerRef.current, {
            videoId: videoId,
            height: '100%',
            width: '100%',
            playerVars: {
              'origin': window.location.origin,
              'modestbranding': 1,
              'rel': 0
            },
            events: {
              'onReady': (event) => {
                // event.target.playVideo();
              },
              'onError': (e) => {
                addLog(`YT_PLAYER: Error code ${e.data}`, 'error');
              }
            }
          });
        }
      }
    };

    // If already loaded, just re-init or update
    if (window.YT && window.YT.Player) {
      initPlayer();
    }

    // Cleanup (optional, but keep for stability)
    return () => {
      // We don't necessarily want to destroy the player on every re-render
      // but if the URL changes, loadVideoById handles it.
    };
  }, [youtubeUrl, activeTab, metadata]); // Re-run when URL, tab, or metadata changes to ensure container ref is valid

  const getYoutubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const fetchMetadata = async (url) => {
    const targetUrl = url || youtubeUrl;
    if (!targetUrl) return;
    setLoadingMetadata(true);
    setError(null);
    setMetadata(null);
    addLog(`YOUTUBE: Fetching metadata for ${targetUrl}`);
    try {
      const response = await axios.get(`${API_BASE}/api/youtube/metadata`, {
        params: { url: targetUrl },
        timeout: 120000
      });
      setMetadata(response.data);
      if (response.data.formats?.length > 0) setSelectedFormat(response.data.formats[0].format_id);
      setPipelineVideoLang(response.data.language || null);
      addLog(`SUCCESS: Metadata extracted - "${response.data.title}"${response.data.language ? ` (lang: ${response.data.language})` : ''}`, 'success');
      if (activeTab === 'dashboard') setActiveTab('tool-a');
    } catch (err) {
      const msg = friendlyError(err, 'Metadata Fetch');
      setError(msg);
      addLog(`ERROR: Metadata fetch failed - ${err.response?.data?.error || err.message}`, 'error');
    } finally {
      setLoadingMetadata(false);
    }
  };

  const handleViewTranscript = async (asset) => {
    if (viewTranscriptId === asset.id) {
      setViewTranscriptId(null);
      return;
    }

    if (cachedTranscripts[asset.id]) {
      setViewTranscriptId(asset.id);
      return;
    }

    try {
      const path = asset.transcriptionPath.startsWith('http') ? asset.transcriptionPath : `${API_BASE}/${asset.transcriptionPath}`;
      const res = await fetch(path);
      const data = await res.json();

      setCachedTranscripts(prev => ({ ...prev, [asset.id]: data }));
      setViewTranscriptId(asset.id);
    } catch (err) {
      addLog('Failed to load transcript', 'error');
    }
  };

  const handleDownload = async () => {
    if (!metadata) return;

    if (!toolASaveToAssets) {
      // Legacy: Direct browser download (stream to downloads folder)
      setDownloading(true);
      setError(null);
      addLog(`YOUTUBE: Starting browser download...`);
      try {
        const response = await axios.post(`${API_BASE}/api/youtube/download-stream`, {
          url: youtubeUrl,
          title: metadata.title,
          cookieBrowser
        }, {
          responseType: 'blob',
          timeout: 0
        });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'video/mp4' }));
        const link = document.createElement('a');
        link.href = blobUrl;
        const safeTitle = (metadata.title || 'video').replace(/[^a-z0-9]/gi, '_');
        link.setAttribute('download', `${safeTitle}.mp4`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        addLog(`SUCCESS: Download started for "${metadata.title}"`, 'success');
      } catch (err) {
        console.error(err);
        addLog('ERROR: Stream download failed', 'error');
      } finally {
        setDownloading(false);
      }
      return;
    }

    // New: Save to Asset Library (same as main pipeline)
    setDownloading(true);
    setToolAProgress(0);
    setToolAStatus('Initializing download...');
    setDownloadResult(null);
    setError(null);
    addLog(`YOUTUBE: Starting download to Asset Library...`);

    try {
      // Step 1: Start download job (pass selected quality format)
      const startRes = await axios.post(`${API_BASE}/api/youtube/download`, { url: youtubeUrl, format_id: selectedFormat || undefined, preferredLang: pipelineVideoLang, cookieBrowser });
      const { downloadId, title } = startRes.data;
      addLog(`YOUTUBE: Download job started - ${title}`);

      // Step 2: Poll for progress
      const downloadData = await new Promise((resolve, reject) => {
        const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
        const startTime = Date.now();

        const interval = setInterval(async () => {
          if (Date.now() - startTime > TIMEOUT_MS) {
            clearInterval(interval);
            reject(new Error("Download timeout - took longer than 30 minutes. Try a shorter video or check your connection."));
            return;
          }

          try {
            const statusRes = await axios.get(`${API_BASE}/api/youtube/download/${downloadId}`);
            const { status, progress, path, filename, error: dlError, canRetry } = statusRes.data;

            if (progress) {
              setToolAProgress(Math.min(progress, 100));
              setToolAStatus(`Downloading... ${progress}%`);
            }

            if (status === 'completed') {
              clearInterval(interval);
              resolve({ path, filename, title });
            } else if (status === 'failed') {
              clearInterval(interval);
              // Use the detailed error from backend if available
              const errorMsg = dlError || "Download failed. Please try again.";
              reject(new Error(errorMsg));
            }
          } catch (e) {
            console.warn("Download poll error:", e.message);
          }
        }, 1500);
      });

      addLog(`SUCCESS: Downloaded "${downloadData.title}" to Asset Library`, 'success');
      setDownloadResult(downloadData);
      setToolAProgress(100);
      setToolAStatus('Download complete!');

      // Step 3: Auto-transcribe
      setToolATranscribing(true);
      setToolAStatus('Starting transcription...');
      addLog(`TRANSCRIBE: Looking for asset to transcribe...`);

      // Find asset ID
      let assetId = null;
      let attempts = 0;
      while (!assetId && attempts < 5) {
        const assetsRes = await axios.get(`${API_BASE}/api/assets`);
        const match = assetsRes.data.find(a => a.filename === downloadData.filename);
        if (match) assetId = match.id;
        else await new Promise(r => setTimeout(r, 1000));
        attempts++;
      }

      if (assetId) {
        addLog(`TRANSCRIBE: Found asset ${assetId}, starting transcription...`);
        const transcribeRes = await axios.post(`${API_BASE}/api/transcribe/${assetId}`, {}, {
          headers: apiKey ? { 'x-gemini-key': apiKey, 'x-gemini-variant': geminiVariant } : {}
        });

        if (transcribeRes.data.success) {
          const jobId = transcribeRes.data.jobId;
          // Poll transcription
          await new Promise((resolve) => {
            const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
            const startTime = Date.now();

            const tInterval = setInterval(async () => {
              if (Date.now() - startTime > TIMEOUT_MS) {
                clearInterval(tInterval);
                addLog('WARNING: Transcription timeout', 'warning');
                resolve();
                return;
              }

              try {
                const tStatus = await axios.get(`${API_BASE}/api/transcribe/status/${jobId}`);
                const { status } = tStatus.data;
                setToolAStatus(`Transcribing... (${status})`);

                if (status === 'completed') {
                  clearInterval(tInterval);
                  addLog(`SUCCESS: Transcription complete!`, 'success');
                  resolve();
                } else if (status === 'failed') {
                  clearInterval(tInterval);
                  addLog('WARNING: Transcription failed', 'warning');
                  resolve();
                }
              } catch (e) {
                console.warn("Transcription poll error:", e.message);
              }
            }, 2000);
          });
        }
      } else {
        addLog('WARNING: Could not find asset for transcription', 'warning');
      }

      setToolAStatus('Ready for AI Analysis!');
      addLog(`COMPLETE: Video ready in Asset Library. You can now use AI Analysis!`, 'success');

      // Refresh assets list
      fetchAssets();

    } catch (err) {
      console.error(err);
      const msg = friendlyError(err, 'Download');
      setError(msg);
      addLog(`ERROR: ${err.response?.data?.error || err.message || 'Download failed'}`, 'error');
      setToolAStatus('');
    } finally {
      setDownloading(false);
      setToolATranscribing(false);
    }
  };


  // Auto-fetch metadata when Tool B URL changes (debounced)
  useEffect(() => {
    if (!toolBUrl || toolBSourceMode !== 'url') {
      setToolBMetadata(null);
      return;
    }
    // Basic YouTube URL check
    const isYouTube = /youtu\.?be/.test(toolBUrl);
    if (!isYouTube) return;

    const timer = setTimeout(async () => {
      setToolBLoadingMeta(true);
      try {
        const res = await axios.get(`${API_BASE}/api/youtube/metadata`, {
          params: { url: toolBUrl },
          timeout: 15000
        });
        setToolBMetadata({
          title: res.data.title,
          thumbnail: res.data.thumbnail,
          duration: res.data.duration,
          channel: res.data.channel || res.data.uploader
        });
        addLog(`TOOL-B: Metadata loaded — "${res.data.title}"`, 'success');
      } catch {
        // Silent fail — metadata is optional for Tool B
        setToolBMetadata(null);
      } finally {
        setToolBLoadingMeta(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [toolBUrl, toolBSourceMode]);

  const handleAnalyze = async () => {
    if (!validateApiKey()) return;

    // Determine source: URL or Asset
    const useAsset = toolBSourceMode === 'asset' && toolBSelectedAsset;
    const targetUrl = useAsset ? `local://${toolBSelectedAsset.filename}` : (toolBUrl || youtubeUrl);

    if (!useAsset && !targetUrl) {
      setError('Provide a YouTube URL or select an asset for analysis.');
      addLog('ERROR: Analysis aborted - source missing', 'error');
      return;
    }

    if (useAsset && !toolBSelectedAsset.transcriptionPath) {
      setError('Selected asset has no transcript. Please transcribe it first in the Asset Library.');
      addLog('ERROR: Analysis aborted - asset has no transcript', 'error');
      return;
    }

    addLog(`AI: Initializing analysis with ${selectedModel}...`);
    setAnalyzing(true);

    try {
      // If using asset, fetch its transcript first
      let assetTranscript = null;
      let assetLanguage = null;
      if (useAsset && toolBSelectedAsset.transcriptionPath) {
        try {
          const transcriptPath = toolBSelectedAsset.transcriptionPath.startsWith('http')
            ? toolBSelectedAsset.transcriptionPath
            : `${API_BASE}/${toolBSelectedAsset.transcriptionPath}`;
          const transcriptRes = await fetch(transcriptPath);
          const transcriptData = await transcriptRes.json();
          // Extract detected language from transcript
          assetLanguage = transcriptData.language || null;
          // Convert transcript to text format
          if (transcriptData.segments) {
            assetTranscript = transcriptData.segments.map(s => `[${s.start}] ${s.text}`).join('\n');
          } else if (typeof transcriptData === 'string') {
            assetTranscript = transcriptData;
          } else if (transcriptData.text) {
            assetTranscript = transcriptData.text;
          }
          addLog(`AI: Loaded transcript for "${toolBSelectedAsset.filename}"${assetLanguage ? ` (lang: ${assetLanguage})` : ''}`, 'success');
        } catch (e) {
          addLog('WARNING: Could not load asset transcript, using metadata only', 'warning');
        }
      }

      // Build headers based on selected model
      const keyMap = {
        'gemini': { header: 'x-gemini-key', key: apiKey },
        'gpt-4o': { header: 'x-openai-key', key: openaiKey },
        'claude': { header: 'x-anthropic-key', key: claudeKey },
        'mistral': { header: 'x-mistral-key', key: mistralKey },
        'deepseek': { header: 'x-deepseek-key', key: deepseekKey },
        'openrouter': { header: 'x-openrouter-key', key: openrouterKey },
        'minimax': { header: 'x-minimax-key', key: minimaxKey },
        'xai': { header: 'x-xai-key', key: xaiKey }
      };
      const modelConfig = keyMap[selectedModel] || keyMap['gemini'];

      // Per-provider variant headers. After SX-2 (efe0685) the per-provider
      // variant dropdown was removed and `${model}_api_key_variant` localStorage
      // keys are no longer written by anyone. The previous code still tried to
      // read them — always returned null for non-gemini, but the formula
      // looked load-bearing and confused readers. Now: gemini sends
      // x-gemini-variant from its dedicated state; openrouter sends
      // x-model-variant from its dedicated input. No other provider routes a
      // variant today.
      const headers = {
        [modelConfig.header]: modelConfig.key,
        ...(selectedModel === 'gemini' ? { 'x-gemini-variant': geminiVariant } : {}),
        ...(selectedModel === 'openrouter' ? { 'x-model-variant': openrouterModel } : {})
      };

      // Build request body
      const requestBody = {
        url: useAsset ? (toolBSelectedAsset.sourceUrl || `file://${toolBSelectedAsset.path}`) : targetUrl,
        instruction: instruction,
        mode: analyzeMode,
        model: selectedModel,
        advanced: analyzeMode === 'advanced' ? {
          numCuts: advNumCuts,
          maxDuration: advMaxDuration,
          focus: advFocus,
          vibe: advVibe
        } : null
      };

      // Add transcript if available from asset
      if (assetTranscript) {
        requestBody.transcript = assetTranscript;
        requestBody.language = assetLanguage;
        // Also add asset metadata for context
        requestBody.assetMetadata = {
          filename: toolBSelectedAsset.filename,
          duration: toolBSelectedAsset.duration,
          title: toolBSelectedAsset.title || toolBSelectedAsset.filename
        };
      }

      const response = await axios.post(`${API_BASE}/api/analyze`, requestBody, { headers: { ...headers, ...getLicenseHeaders() } });
      setAnalysisResult(response.data);
      setPastedJson(JSON.stringify(response.data, null, 2));

      // If using asset, also store the asset path for Tool C
      if (useAsset) {
        setVideoFile(toolBSelectedAsset);
      }

      // Pre-fill hook text from analysis result (gives user a starting point)
      const sourceInfo = response.data.source_info;
      if (sourceInfo) {
        setStyleSettings(prev => ({
          ...prev,
          hookHeadline: { ...prev.hookHeadline, text: prev.hookHeadline.text || sourceInfo.video_title || '' },
          hookBrand: { ...prev.hookBrand, text: prev.hookBrand.text || sourceInfo.channel_name || '' }
        }));
      }

      // Save analysis to history
      const newAnalysis = {
        id: `analysis_${Date.now()}`,
        timestamp: new Date().toISOString(),
        title: response.data.source_info?.video_title || toolBSelectedAsset?.title || 'Untitled Analysis',
        clipCount: response.data.video_clips?.length || 0,
        model: selectedModel,
        sourceType: useAsset ? 'asset' : 'url',
        sourceUrl: useAsset ? null : targetUrl,
        assetId: useAsset ? toolBSelectedAsset?.id : null,
        assetFilename: useAsset ? toolBSelectedAsset?.filename : null,
        data: response.data
      };

      const updatedAnalyses = [newAnalysis, ...savedAnalyses].slice(0, 20); // Keep last 20
      setSavedAnalyses(updatedAnalyses);
      localStorage.setItem('qlipper_saved_analyses', JSON.stringify(updatedAnalyses));
      setSelectedAnalysis(newAnalysis);

      addLog('SUCCESS: Analysis complete. Found ' + response.data.video_clips.length + ' viral segments.', 'success');
      addLog('SAVED: Analysis saved to history. You can select it in Clipper.', 'success');
    } catch (err) {
      const msg = friendlyError(err, 'AI Analysis');
      setError(msg);
      addLog(`ERROR: Analysis failed - ${err.message || err.response?.data?.error}`, 'error');
      // If it was the specific connection error, show API key validation modal
      if (msg.includes("Gemini API Connection Failed") || msg.includes("API") || msg.includes("key")) {
        setShowApiKeyValidation(true);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDeleteProject = (e, projectId, projectTitle) => {
    e.stopPropagation();
    setProjectToDelete({ id: projectId, title: projectTitle });
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    const { id: projectId } = projectToDelete;

    try {
      addLog(`SYSTEM: Deleting project ${projectId}...`);
      await axios.delete(`${API_BASE}/api/history/${projectId}`);

      // Update State
      const newHistory = historyProjects.filter(p => p.id !== projectId);
      setHistoryProjects(newHistory);

      // If deleted project was selected, deselect or select another
      if (selectedHistoryProject?.id === projectId) {
        if (newHistory.length > 0) {
          setSelectedHistoryProject(newHistory[0]);
          if (newHistory[0].clips?.length > 0) setSelectedProjectClip(newHistory[0].clips[0]);
          else setSelectedProjectClip(null);
        } else {
          setSelectedHistoryProject(null);
          setSelectedProjectClip(null);
        }
      }
      addLog(`SUCCESS: Project deleted.`, 'success');
      setProjectToDelete(null);
    } catch (err) {
      addLog(`ERROR: Delete failed - ${err.message}`, 'error');
    }
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAsset(true);
    addLog(`ASSETS: Uploading ${file.name}...`);

    const formData = new FormData();
    formData.append('asset', file);

    try {
      const res = await axios.post(`${API_BASE}/api/assets/upload?type=asset`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' } // browser sets boundary auto
      });
      setAssets(prev => [res.data, ...prev]);
      addLog(`SUCCESS: Asset uploaded - ${res.data.name}`, 'success');
    } catch (err) {
      addLog(`ERROR: Upload failed - ${err.message}`, 'error');
    } finally {
      setUploadingAsset(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };



  const faqItems = [
    {
      q: "Where are my clips stored?",
      a: "For privacy and speed, all clips are stored LOCALLY on your machine. You can find them in the `backend/clips` folder inside the project directory. Qlipper AI does not upload your content to any cloud server for storage."
    },
    {
      q: "How does Turbo Mode work?",
      a: "Turbo Mode uses your Mac's dedicated VideoToolbox hardware acceleration to render video up to 5x faster. It might result in slightly larger file sizes but significantly reduces waiting time."
    },
    {
      q: "Can I resume a previous project?",
      a: "Yes! Your project history is saved automatically. We are currently rolling out the 'History' tab where you can see past sessions and re-download clips without re-processing."
    },
    {
      q: "Where is my API key stored?",
      a: "Your Gemini API key is stored locally in your browser's local storage. It is never sent to our servers or shared with third parties."
    },
    {
      q: "Is my console log history saved?",
      a: "No, the console log history is temporary and resets when you close or refresh the application. Only the last 200 messages are kept for performance."
    }
  ];

  const handleProcessClips = async () => {
    let localVideoPath = (downloadResult && downloadResult.path) ? downloadResult.path : null;

    // --- PRIORITY 1: Use Tool C selected asset if available ---
    if (!localVideoPath && toolCSelectedAsset) {
      localVideoPath = toolCSelectedAsset.path;
      addLog(`CLIPPER: Using asset from library: ${toolCSelectedAsset.filename}`, 'success');
    }

    // --- PRIORITY 2: Use videoFile if it's an asset object (from Tool B) ---
    if (!localVideoPath && videoFile && videoFile.path) {
      localVideoPath = videoFile.path;
      addLog(`CLIPPER: Using linked asset: ${videoFile.filename || videoFile.name}`, 'success');
    }

    // --- PRIORITY 3: UPLOAD LOCAL FILE IF NEEDED ---
    if (!localVideoPath && videoFile && videoFile instanceof File) {
      setProcessing(true);
      setProcessingStatus('Uploading local video to server...');
      setProgress(5); // Show some progress for upload start
      addLog(`SYSTEM: Uploading ${videoFile.name} to server for processing...`);

      const formData = new FormData();
      formData.append('video', videoFile);

      try {
        const uploadRes = await axios.post(`${API_BASE}/api/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(Math.round(percentCompleted * 0.8)); // 80% of total progress for upload
            setProcessingStatus(`Uploading: ${percentCompleted}%`);
          }
        });
        localVideoPath = uploadRes.data.path;
        addLog(`SUCCESS: Upload complete. Server path stored.`, 'success');
      } catch (err) {
        addLog(`ERROR: Upload failed - ${err.message}`, 'error');
        setProcessing(false);
        return;
      }
    }

    if (!localVideoPath) {
      addLog('ERROR: Processing aborted - No video source. Select an asset or upload a file.', 'error');
      setError('No video source selected. Please select an asset from the library or upload a file.');
      return;
    }

    if (!pastedJson) return;

    setProcessing(true);
    setProgress(0);
    setFinalClips([]);
    setSelectedProjectClip(null);
    setActiveTab('projects');
    setProcessingStatus('Initializing processing...');
    addLog(`CLIPMAN: Starting SERVER-SIDE processing sequence...`);

    try {
      const config = JSON.parse(pastedJson);

      // Manual paste-JSON path: respect exactly what's in the JSON. Do NOT
      // synthesize hookText from clip.topic/title even when global hookEnabled
      // is on — users bringing their own JSON aren't expecting `topic` (a
      // categorization label like "Token War: GPT-5.5") to be silently burned
      // in as a headline overlay. If they want a hook, they put it in the
      // JSON's `hookText` field, or set it per-clip in the edit UI before
      // re-rendering. Reported by user: "if use the manual process just
      // pasting the json, it already assume some burn in text?"

      // --- BACKEND PROCESSING PATH (Streaming/Polling) ---
      const profileLabel = renderProfile === 'hw' ? 'Hardware' : renderProfile === 'cpu-quality' ? 'CPU Quality' : 'CPU Fast';
      setProcessingStatus(`Requesting server-side processing (${profileLabel} engine)...`);
      addLog(`SERVER: Sending job to native FFmpeg engine [profile: ${renderProfile}]...`);

      // 1. Start Job
      const initResponse = await axios.post(`${API_BASE}/api/video/process`, {
        videoPath: localVideoPath,
        config,
        outputFormat,
        outputResolution,
        useTransitions,
        renderProfile,
        styleSettings
      }, { headers: getLicenseHeaders() });

      const { jobId, totalClips } = initResponse.data;
      addLog(`SERVER: Job started (ID: ${jobId}). Total clips: ${totalClips}`);

      // --- OPTIMISTIC UI: FORCE SELECT NEW PROJECT IMMEDIATELY ---
      const optimisticProject = {
        id: jobId,
        title: config.source_info?.video_title || 'New Project',
        timestamp: new Date().toISOString(),
        totalClips: totalClips,
        clips: [], // Will be filled by polling
        status: 'processing'
      };

      setHistoryProjects(prev => [optimisticProject, ...prev]);
      setSelectedHistoryProject(optimisticProject);

      // 1. Initialize Skeletons (Smart Placeholders)
      let initialClips = [];

      if (outputFormat === 'raw-cuts') {
        // FOR RAW CUTS: Expand based on timelines (Parts) because backend splits them
        config.video_clips.forEach(c => {
          c.timelines.forEach((t, idx) => {
            initialClips.push({
              id: `${c.clip_id}_part${idx + 1}`,
              topic: `${c.topic} (Part ${idx + 1})`,
              status: 'pending',
              highlight: 'Stream Copying (Instant)...'
            });
          });
        });
      } else {
        // FOR STANDARD: One clip per topic
        initialClips = config.video_clips.map(c => ({
          id: c.clip_id,
          topic: c.topic || `Clip ${c.clip_id}`,
          status: 'pending',
          highlight: 'Processing...'
        }));
      }

      setFinalClips(initialClips);

      // 2. Poll for Updates
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/api/job/${jobId}`);
          const jobData = statusRes.data;

          // Merge Logic: Update only the completed ones
          if (jobData.completedClips && jobData.completedClips.length > 0) {
            setFinalClips(prevClips => {
              const newClips = [...prevClips];
              jobData.completedClips.forEach(completed => {
                const idx = newClips.findIndex(p => p.id === completed.id);
                if (idx !== -1) {
                  newClips[idx] = completed; // Replace placeholder with real data
                }
              });
              return newClips;
            });

            // Auto-select first completed if none selected
            if (!selectedProjectClip && jobData.completedClips.length > 0) {
              setSelectedProjectClip(jobData.completedClips[0]);
            }
          }

          const pct = Math.min(Math.round((jobData.completedClips.length / jobData.totalClips) * 100), 100);
          setProgress(pct);
          setProcessingStatus(`Streaming Assets... ${jobData.completedClips.length}/${jobData.totalClips} Ready`);

          if (jobData.status === 'completed') {
            clearInterval(pollInterval);
            setProcessing(false);
            setProcessingStatus('Completed!');
            addLog(`SERVER: Job ${jobId} finished completely.`, 'success');
          }

          // Sync with history projects to show live updates in "My Files"
          setHistoryProjects(prev => {
            const newHistory = [...prev];
            const pIdx = newHistory.findIndex(p => p.id === jobId);
            if (pIdx !== -1) {
              newHistory[pIdx] = {
                ...newHistory[pIdx],
                clips: jobData.completedClips,
                status: jobData.status
              };
            }
            return newHistory;
          });

          // Keep selectedHistoryProject in sync so the main content area updates
          setSelectedHistoryProject(prev => {
            if (prev && prev.id === jobId) {
              return { ...prev, clips: jobData.completedClips, status: jobData.status };
            }
            return prev;
          });
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 3000);

      // Return early to let the polling handle the "processing = false" state
      return;
    } catch (err) {
      addLog(`ERROR: Processing crash - ${err.message}`, 'error');
      setProcessingStatus('Operation Failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = async () => {
    setPipelineError(null);
    const targetUrl = youtubeUrl;

    try {
      let pAnalysis = pipelineAnalysis;
      let pMetadata = pipelineMetadata;

      // Retry Step 1 (Download) — if download failed
      if (pipelineStatus.step1 !== 'success') {
        setPipelineStatus(prev => ({ ...prev, step1: 'loading' }));
        setDownloadFallbackEnabled(false);
        try {
          let dlQualityRetry = 'quality_720';
          if (pipelineQuality === 'best') dlQualityRetry = 'highest';
          else if (pipelineQuality === '4k') dlQualityRetry = 'highest';
          else if (pipelineQuality === '1440') dlQualityRetry = 'highest';
          else if (pipelineQuality === '1080') dlQualityRetry = 'quality_1080';
          else if (pipelineQuality === '720') dlQualityRetry = 'quality_720';
          else if (pipelineQuality === '480') dlQualityRetry = 'quality_480';
          const startRes = await axios.post(`${API_BASE}/api/youtube/download`, { url: targetUrl, format_id: dlQualityRetry, preferredLang: pipelineVideoLang, cookieBrowser });
          const { downloadId } = startRes.data;

          const downloadData = await new Promise((resolve, reject) => {
            let lastLog = '';
            const interval = setInterval(async () => {
              try {
                const statusRes = await axios.get(`${API_BASE}/api/youtube/download/${downloadId}`);
                const { status, logs, path, filename, title } = statusRes.data;
                if (logs && logs.length > 0) {
                  const currentLog = logs[logs.length - 1];
                  if (currentLog !== lastLog) { addLog(`DL-SERVER: ${currentLog}`); lastLog = currentLog; }
                }
                if (status === 'completed') { clearInterval(interval); resolve({ path, filename, title }); }
                else if (status === 'failed') { clearInterval(interval); reject(new Error("Download retry failed.")); }
              } catch (e) { }
            }, 1500);
          });

          setPipelineMetadata(downloadData);
          setDownloadResult(downloadData);
          setPipelineStatus(prev => ({ ...prev, step1: 'success' }));
          pMetadata = downloadData;
        } catch (err) {
          setPipelineStatus(prev => ({ ...prev, step1: 'error' }));
          setDownloadFallbackEnabled(true);
          throw err;
        }
      }

      // Retry Step 2 (Analyze) — if analysis failed
      if (pipelineStatus.step2 !== 'success' && pMetadata) {
        setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
        try {
          const res = await axios.post(`${API_BASE}/api/analyze`, { url: targetUrl, language: pipelineVideoLang, instruction: pipelineInstruction || undefined }, { headers: { 'x-gemini-key': apiKey, ...getLicenseHeaders() } });
          setPipelineAnalysis(res.data);
          setPastedJson(JSON.stringify(res.data, null, 2));
          setPipelineStatus(prev => ({ ...prev, step2: 'success' }));
          pAnalysis = res.data;
        } catch (err) {
          setPipelineStatus(prev => ({ ...prev, step2: 'error' }));
          throw err;
        }
      }

      // Resume Step 3 (Clipping)
      if (pMetadata && pAnalysis) {
        // Fix: Don't auto-advance. Always give user a chance to verify settings/format on retry.
        // Auto-Proceed on Retry too regarding user feedback
        addLog(`PIPELINE: Retry successful. Auto-starting processing with ${outputFormat} logic...`, 'info');
        triggerPipelineProcessing(targetUrl, pAnalysis, outputFormat);
      } else if (pAnalysis && !pMetadata) {
        // If we have analysis but no metadata, and Step 2 hasn't run/failed yet, we might be in the pause state
        // But handleRetry is usually for errors. If we are just waiting, we do nothing.
        if (pipelineStatus.step2 === 'idle') {
          setPipelineReadyData({ analysis: pAnalysis, url: targetUrl });
          setPipelineAwaitingFormat(true);
        }
      }

    } catch (err) {
      setPipelineError(err.message || "Retry failed");
    }
  };

  const handlePipelineFormatSelect = async (format) => {
    if (!pipelineReadyData) return;
    setPipelineAwaitingFormat(false);
    setOutputFormat(format); // Sync global state

    try {
      // SKIP DOWNLOADING if we already have the metadata (Optimized Flow)
      if (pipelineMetadata && pipelineMetadata.path) {
        addLog("PIPELINE: Using pre-downloaded footage. Starting Clipping...", 'info');
        setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' }));
        await triggerPipelineProcessing(pipelineMetadata.path, pipelineReadyData.analysis, format);
        return;
      }

      // --- STEP 2: DOWNLOAD (Legacy / Retry Path) ---
      setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
      setDownloadFallbackEnabled(false);
      addLog(`PIPELINE: Step 2 (Download) Started...`);

      const targetUrl = pipelineReadyData.url || youtubeUrl;

      // 1. Start Job (use pipelineQuality if set, otherwise fall back to outputResolution)
      let dlQuality = 'quality_1080'; // default
      if (pipelineQuality === 'best') {
        dlQuality = 'highest';
      } else if (pipelineQuality === '4k') {
        dlQuality = 'highest';
      } else if (pipelineQuality === '1440') {
        dlQuality = 'highest';
      } else if (pipelineQuality === '1080') {
        dlQuality = 'quality_1080';
      } else if (pipelineQuality === '720') {
        dlQuality = 'quality_720';
      } else if (pipelineQuality === '480') {
        dlQuality = 'quality_480';
      }
      const startRes = await axios.post(`${API_BASE}/api/youtube/download`, { url: targetUrl, format_id: dlQuality, preferredLang: pipelineVideoLang, cookieBrowser });
      const { downloadId } = startRes.data;

      // --- LEGACY BACKEND FALLBACK ---
      if (!downloadId && startRes.data.path) {
        addLog("DL: Synchronous download complete (Legacy Mode).");
        setPipelineMetadata(startRes.data);
        setDownloadResult(startRes.data);
        setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' }));
        await triggerPipelineProcessing(startRes.data.path, pipelineReadyData.analysis, format);
        return;
      }

      addLog(`DL: Job ${downloadId} started. Queued.`);

      // 2. Poll Status (with timeout UX messages)
      const downloadData = await new Promise((resolve, reject) => {
        if (!downloadId) return reject(new Error("Invalid Download ID (Backend may need restart)"));

        let lastLog = '';
        const pollStart = Date.now();
        let shownStillWorking = false;
        let shownTakingLonger = false;
        const interval = setInterval(async () => {
          try {
            // Timeout UX messages
            const elapsed = Date.now() - pollStart;
            if (elapsed > 180000 && !shownTakingLonger) {
              shownTakingLonger = true;
              addLog('DL: This is taking longer than usual. The server is still working...', 'warning');
            } else if (elapsed > 60000 && !shownStillWorking) {
              shownStillWorking = true;
              addLog('DL: Still working... Large videos may take a few minutes.', 'info');
            }

            const statusRes = await axios.get(`${API_BASE}/api/youtube/download/${downloadId}`);
            const { status, progress, logs, path, filename, title, hasCaptions } = statusRes.data;

            // Log updates
            if (logs && logs.length > 0) {
              const currentLog = logs[logs.length - 1];
              if (currentLog !== lastLog) {
                addLog(`DL-SERVER: ${currentLog}`);
                lastLog = currentLog;
              }
            }

            if (status === 'completed') {
              clearInterval(interval);
              resolve({ path, filename, title, hasCaptions });
            } else if (status === 'failed') {
              clearInterval(interval);
              reject(new Error("Download failed on server side."));
            }
          } catch (e) {
            // ignore network glitches
          }
        }, 1500);
      });

      setPipelineMetadata(downloadData);
      setDownloadResult(downloadData);
      setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' }));
      addLog(`PIPELINE: Step 2 (Download) Complete.`, 'success');

      // Phase 4B: Auto-trigger Whisper if no captions found
      if (downloadData.hasCaptions === false) {
        addLog('No YouTube captions found. Attempting auto-transcription...', 'warning');
        try {
          const transcribeRes = await fetch(`${API_BASE}/api/transcribe/${encodeURIComponent(downloadData.path)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ language: pipelineVideoLang || 'auto' })
          });
          if (transcribeRes.ok) {
            addLog('Auto-transcription started. Subtitles will be available after processing.', 'success');
          } else {
            addLog('Auto-transcription not available. Install Python 3.9+ for auto-transcription, or paste a transcript manually.', 'warning');
          }
        } catch {
          addLog('Auto-transcription not available. Subtitles may not be rendered for this clip.', 'warning');
        }
      }

      // --- STEP 3: CLIPPING ---
      await triggerPipelineProcessing(downloadData.path, pipelineReadyData.analysis, format);

    } catch (err) {
      console.error("Pipeline Resume Error:", err);
      const msg = err.response?.data?.error || err.message;
      setPipelineError(msg);
      addLog(`ERROR: Pipeline failed during download - ${msg}`, 'error');
      setPipelineStatus(prev => ({ ...prev, step2: 'error' }));
      setDownloadFallbackEnabled(true);
      // Reset progress state
      setProgress(0);
      setProcessing(false);
      setProcessingStatus('');
    }
  };



  const handleSetupPipeline = () => {
    if (pipelineMode === 'upload') {
      if (!pipelineUploadedAsset) return;
      setPipelineSetup(true);
      return;
    }
    if (!youtubeUrl) {
      addLog('Please enter a video URL first (YouTube, TikTok, Instagram, X)', 'error');
      return;
    }
    setSetupUrl(youtubeUrl);
    setPipelineSetup(true);
  };

  const startPipelineFromSetup = async () => {
    // Validate Gemini API Key first
    if (!validateApiKey()) return;

    try {
      // Router tile: validateApiKey() above already checked the router config, and
      // there may be no Gemini key at all — don't gate on the Gemini check.
      if (selectedModel !== 'router') {
        setGetClipsBtnText("Checking API...");
        await axios.post(`${API_BASE}/api/system/check-gemini`, { apiKey });
      }
      setGetClipsBtnText("Get AI Clips");
      setPipelineSetup(false);

      // Map clipLength to duration
      let duration = 60;
      if (clipLength === '<30s') duration = 30;
      else if (clipLength === '30s-60s') duration = 60;
      else if (clipLength === '60s-90s') duration = 90;
      else if (clipLength === '90s-3min') duration = 180;
      setAdvMaxDuration(duration);

      if (pipelineMode === 'upload' && pipelineUploadedAsset) {
        // Upload mode keeps the legacy flow. Upload-aware download-skip in
        // the Job system is v2.4.0 scope.
        handleStartPipelineFromAsset(pipelineUploadedAsset, { maxDuration: duration });
      } else if (useJobsSystem || selectedModel === 'router') {
        // PR 3d (beta): jobs-system pipeline — resumable + structured errors.
        // The router path only exists in the jobs runner (src/shim/jobs.js).
        handleStartPipelineViaJobs(setupUrl, { maxDuration: duration });
      } else {
        handleStartPipeline(setupUrl, { maxDuration: duration });
      }
    } catch (err) {
      addLog("ERROR: Gemini API Key Validation Failed. Please check your API key in Settings.", 'error');
      setGetClipsBtnText("Get AI Clips");
      setPipelineSetup(false);
      setShowApiKeyValidation(true);
    }
  };

  // PR 4d: convert legacy pipeline errors (axios responses + thrown Error
  // instances from the polling loops) into the QlipperError shape that
  // ErrorBanner renders. Heuristic-classify by errorCode (yt-dlp's parsed
  // codes like FORMAT_NOT_AVAILABLE) + status code, then attach
  // remediations[]. Falls through to a permissive {ui_message: <string>}
  // when no structure is recognized.
  const buildLegacyErrorInfo = (err, phase = 'pipeline') => {
    const data = err.response?.data || {};
    const ytdlpCode = data.errorCode || err.errorCode || null;
    const httpStatus = err.response?.status || 0;
    const rawMessage = data.error || err.message || `${phase} failed.`;
    const suggestions = data.errorSuggestions || err.errorSuggestions || [];
    const cleanMessage = (data.errorMessage || data.error || err.message || '').split('💡')[0].trim().replace(/^📺\s*/, '').replace(/^🔒\s*/, '');

    // Map yt-dlp's structured codes (from lib/ytdlp.js#parseYtdlpError) to
    // QlipperError fields. Naming intentionally mirrors lib/ytdlp.js codes.
    const codeMap = {
      // HTTP errors
      '403_FORBIDDEN':       { category: 'capability', retryable: 'manual' },
      '404_NOT_FOUND':       { category: 'config',     retryable: 'never'  },
      '410_GONE':            { category: 'capability', retryable: 'never'  },
      '429_RATE_LIMIT':      { category: 'capability', retryable: 'auto'   },
      // Video access
      VIDEO_UNAVAILABLE:     { category: 'capability', retryable: 'never'  },
      PRIVATE_VIDEO:         { category: 'config',     retryable: 'never'  },
      AGE_RESTRICTED:        { category: 'config',     retryable: 'manual' },
      GEO_RESTRICTED:        { category: 'capability', retryable: 'never'  },
      MEMBERS_ONLY:          { category: 'config',     retryable: 'never'  },
      COPYRIGHT_BLOCKED:     { category: 'capability', retryable: 'never'  },
      LOGIN_REQUIRED:        { category: 'config',     retryable: 'manual' },
      // Content type
      LIVE_STREAM:           { category: 'capability', retryable: 'manual' },
      PREMIERE:              { category: 'capability', retryable: 'manual' },
      PLAYLIST_ERROR:        { category: 'capability', retryable: 'never'  },
      // Format / extraction
      FORMAT_UNAVAILABLE:    { category: 'capability', retryable: 'manual' },
      EXTRACTION_ERROR:      { category: 'capability', retryable: 'auto'   },
      JS_RUNTIME_REQUIRED:   { category: 'config',     retryable: 'manual' },
      COOKIE_EXTRACTION_FAILED:{ category: 'config',   retryable: 'manual' },
      INVALID_URL:           { category: 'config',     retryable: 'manual' },
      // Network
      NETWORK_ERROR:         { category: 'network',    retryable: 'auto'   },
      TIMEOUT:               { category: 'network',    retryable: 'auto'   },
      SSL_ERROR:             { category: 'network',    retryable: 'manual' },
    };
    const mapped = codeMap[ytdlpCode] || {};

    let category = mapped.category;
    let retryable = mapped.retryable;
    if (!category) {
      if (httpStatus === 401 || httpStatus === 403) category = 'config';
      else if (httpStatus === 429) category = 'capability';
      else if (httpStatus >= 500) category = 'network';
      else category = 'bug';
    }
    if (!retryable) retryable = httpStatus >= 500 ? 'auto' : 'manual';

    // Convert yt-dlp's suggestions[] into ErrorBanner remediations[].
    // Suggestions like "Use 'Best Available' quality preset" → passive chip.
    // We'd love to map specific phrases to actionable buttons but for v2.3.0
    // shipping, manual chips with the suggestion text is a big upgrade over
    // dumping the whole formatted string into one paragraph.
    const remediations = (suggestions || []).map(s => ({ action: 'manual', label: String(s).replace(/^\d+\.\s*/, '') }));

    return {
      code: ytdlpCode || (httpStatus ? `HTTP_${httpStatus}` : 'UNKNOWN'),
      ui_message: cleanMessage || rawMessage,
      category,
      retryable,
      remediations,
    };
  };

  // PR 4e: ping the license server's existing /health endpoint. Pure read
  // probe, no auth, no side-effects. Distinguishes 3 user-relevant cases:
  //   - 'up' (HTTP 200 + {status:'ok'}): server alive, your 403 is local
  //   - 'down' (timeout / network error / 5xx): server is the problem
  //   - 'idle' / 'checking': pre-check or in-flight
  // Goal: when user gets "Valid license required" 403, they can hit the
  // "Test License Server" button to know whether to re-activate locally
  // or wait for Qlipper's service to recover.
  const checkLicenseServer = async () => {
    setLicenseServerStatus('checking');
    setLicenseServerLatency(null);
    const t0 = Date.now();
    try {
      // VITE_QLIPPER_LICENSE_API override for staging/QA; defaults to prod.
      const licenseHost = import.meta.env.VITE_QLIPPER_LICENSE_API || 'https://license.qlipper.id';
      const res = await axios.get(`${licenseHost}/health`, {
        timeout: 8000,
        // Don't send credentials/cookies — pure unauth probe
        withCredentials: false,
      });
      const elapsed = Date.now() - t0;
      if (res.status === 200 && res.data?.status === 'ok') {
        setLicenseServerStatus('up');
        setLicenseServerLatency(elapsed);
        addLog(`LICENSE: server reachable (${elapsed}ms)`, 'success');
      } else {
        setLicenseServerStatus('down');
        addLog(`LICENSE: server returned unexpected response (${res.status})`, 'warning');
      }
    } catch (err) {
      setLicenseServerStatus('down');
      const reason = err.code === 'ECONNABORTED' ? 'timeout' : (err.message || 'unreachable');
      addLog(`LICENSE: server unreachable — ${reason}`, 'warning');
    }
  };

  const handleStartPipeline = async (url = youtubeUrl, config = {}) => {
    if (!validateApiKey()) return;

    const targetUrl = url || youtubeUrl;
    if (!targetUrl) return;

    setIsPipelineRunning(true);
    setPipelineError(null);
    setPipelineErrorInfo(null);
    setPipelineStatus({ step1: 'loading', step2: 'idle', step3: 'idle' });
    setProgress(0);
    setProcessingStatus('Initializing Download...');
    addLog(`PIPELINE: Initializing Smart Workflow for ${targetUrl}...`);

    try {
      // --- STEP 1: DOWNLOAD & TRANSCRIPTION (The Foundation) ---
      addLog(`PIPELINE: Step 1 (Download) Started...`);
      setDownloadFallbackEnabled(false);

      // Use pipelineQuality for yt-dlp download quality
      let dlQuality2 = 'quality_720';
      if (pipelineQuality === 'best') dlQuality2 = 'highest';
      else if (pipelineQuality === '4k') dlQuality2 = 'highest';
      else if (pipelineQuality === '1440') dlQuality2 = 'highest';
      else if (pipelineQuality === '1080') dlQuality2 = 'quality_1080';
      else if (pipelineQuality === '720') dlQuality2 = 'quality_720';
      else if (pipelineQuality === '480') dlQuality2 = 'quality_480';
      const startRes = await axios.post(`${API_BASE}/api/youtube/download`, { url: targetUrl, format_id: dlQuality2, preferredLang: pipelineVideoLang, cookieBrowser });
      const { downloadId, title } = startRes.data;

      // Poll Download with timeout protection (30 minutes max)
      const downloadData = await new Promise((resolve, reject) => {
        const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes max for download
        const startTime = Date.now();

        const interval = setInterval(async () => {
          // Check for timeout
          if (Date.now() - startTime > TIMEOUT_MS) {
            clearInterval(interval);
            reject(new Error("Download timeout - took longer than 30 minutes. Try a shorter video or check your connection."));
            return;
          }

          try {
            const statusRes = await axios.get(`${API_BASE}/api/youtube/download/${downloadId}`);
            const { status, logs, path, filename } = statusRes.data;

            // Log updates (Optional: filter logs)
            if (logs && logs.length > 0) {
              const last = logs[logs.length - 1];
              if (!last.includes('downloading')) addLog(`DL: ${last}`);
            }

            // Progress update for Step 1
            if (statusRes.data && statusRes.data.progress) {
              setProgress(Math.min(statusRes.data.progress, 100));
              setProcessingStatus(`Downloading... ${statusRes.data.progress}%`);
            }

            if (status === 'completed') {
              clearInterval(interval);
              resolve({ path, filename, title });
            } else if (status === 'failed') {
              clearInterval(interval);
              // PR 4d: attach the structured error fields (errorCode, suggestions,
              // canRetry) onto the thrown Error so the outer catch can build a
              // QlipperError-shaped pipelineErrorInfo for ErrorBanner.
              const errorMsg = statusRes.data.error || "Download failed. Please try again.";
              const e = new Error(errorMsg);
              e.errorCode = statusRes.data.errorCode || null;
              e.errorSuggestions = statusRes.data.errorSuggestions || [];
              e.canRetry = statusRes.data.canRetry;
              e.response = { data: statusRes.data, status: 200 };
              reject(e);
            }
          } catch (e) {
            // Log network errors but don't fail immediately (might be temporary)
            console.warn("Download poll error:", e.message);
          }
        }, 1500);
      });

      setPipelineMetadata(downloadData);
      setDownloadResult(downloadData);
      setPipelineStatus(prev => ({ ...prev, step1: 'success', step2: 'loading' }));
      setProgress(0); // Reset for next step
      setProcessingStatus('Transcribing Audio...');
      addLog(`PIPELINE: Download Complete. Starting Transcription...`, 'success');

      // --- STEP 1.5: TRANSCRIBE ---
      // Find the asset ID matching this filename
      let assetId = null;
      let attempts = 0;
      while (!assetId && attempts < 5) {
        const assetsRes = await axios.get(`${API_BASE}/api/assets`);
        const match = assetsRes.data.find(a => a.filename === downloadData.filename);
        if (match) assetId = match.id;
        else await new Promise(r => setTimeout(r, 1000));
        attempts++;
      }

      let transcriptText = null;
      if (assetId) {
        addLog(`PIPELINE: Found Asset ID ${assetId}. Checking Transcription Status...`);
        // Pass engine preference + Gemini fallback key
        const transcribeRes = await axios.post(`${API_BASE}/api/transcribe/${assetId}`, {
          whisperEngine,
          whisperModel,
          whisperWasmFallback,
        }, {
          headers: apiKey ? {
            'x-gemini-key': apiKey,
            'x-gemini-variant': geminiVariant
          } : {}
        });

        if (transcribeRes.data.success) {
          const jobId = transcribeRes.data.jobId;
          // Poll Transcription with timeout (5 minutes max)
          await new Promise((resolve, reject) => {
            const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
            const startTime = Date.now();
            let lastLogIdx = 0;

            const tInterval = setInterval(async () => {
              // Check for timeout
              if (Date.now() - startTime > TIMEOUT_MS) {
                clearInterval(tInterval);
                addLog('WARNING: Transcription timeout - proceeding without transcript', 'warning');
                resolve(); // Don't fail, just continue without transcript
                return;
              }

              try {
                const tStatus = await axios.get(`${API_BASE}/api/transcribe/status/${jobId}`);
                const { status, logs } = tStatus.data;

                // Sync Logs
                if (logs && logs.length > lastLogIdx) {
                  const newLogs = logs.slice(lastLogIdx);
                  newLogs.forEach(log => log && addLog(`TX: ${log}`));
                  lastLogIdx = logs.length;
                }

                if (status === 'completed') {
                  clearInterval(tInterval);
                  resolve();
                } else if (status === 'failed') {
                  clearInterval(tInterval);
                  addLog('WARNING: Transcription failed - proceeding without transcript', 'warning');
                  resolve(); // Don't fail the pipeline, just continue
                }
                // Continue if 'queued' or 'processing'
              } catch (err) {
                console.error("Poll Error", err);
              }
            }, 1000);
          });

          // Fetch Transcript Content
          try {
            // Re-fetch asset to get transcript path
            const freshAssets = await axios.get(`${API_BASE}/api/assets`);
            const freshAsset = freshAssets.data.find(a => a.id === assetId);
            if (freshAsset && freshAsset.transcriptionPath) {
              const transcriptRes = await axios.get(`${API_BASE}/${freshAsset.transcriptionPath}`);
              const tData = transcriptRes.data;
              // Use timestamped format if segments available (more accurate clip boundaries)
              if (tData.segments && tData.segments.length > 0) {
                transcriptText = tData.segments.map(s => {
                  const ts = typeof s.start === 'number'
                    ? `[${new Date(s.start * 1000).toISOString().substr(11, 8)}]`
                    : `[${s.start}]`;
                  return `${ts} ${s.text}`;
                }).join('\n');
              } else {
                transcriptText = tData.text;
              }
              addLog("PIPELINE: Transcript loaded for analysis.", 'success');
            }
          } catch (err) {
            console.error("Failed to load transcript text", err);
          }
        }
      } else {
        addLog("WARNING: Could not find asset for transcription. Skipping.", 'warning');
      }

      // NOTE: Whisper word-level transcription is NOT run here.
      // It's triggered lazily only when the user enables subtitles in Clip Edit,
      // to avoid performance issues on lower-end devices.
      // The backend endpoint POST /api/youtube/transcribe handles caching,
      // so it only runs once per video and is instant on subsequent calls.

      // We handle Step 1 and 1.5 together as "Preparation"
      // Step 2 is Analysis. So we are officially ready for Step 2.

      // --- STEP 2: ANALYZE (With Context) ---
      addLog(`PIPELINE: Step 2 (Analyze) with ${selectedModel}...`);
      setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
      setProcessingStatus(`Analyzing with ${selectedModel}...`);
      setProgress(5); // Start simulation

      const analyzeInterval = setInterval(() => {
        setProgress(p => {
          if (p >= 90) return 90;
          return p + Math.floor(Math.random() * 5);
        });
      }, 800);

      // Build headers based on selected model
      const keyMap = {
        'gemini': { header: 'x-gemini-key', key: apiKey },
        'gpt-4o': { header: 'x-openai-key', key: openaiKey },
        'claude': { header: 'x-anthropic-key', key: claudeKey },
        'mistral': { header: 'x-mistral-key', key: mistralKey },
        'deepseek': { header: 'x-deepseek-key', key: deepseekKey },
        'openrouter': { header: 'x-openrouter-key', key: openrouterKey },
        'minimax': { header: 'x-minimax-key', key: minimaxKey },
        'xai': { header: 'x-xai-key', key: xaiKey }
      };
      const modelConfig = keyMap[selectedModel] || keyMap['gemini'];
      const headers = {
        [modelConfig.header]: modelConfig.key,
        ...(selectedModel === 'gemini' ? { 'x-gemini-variant': geminiVariant } : {}),
        ...(selectedModel === 'openrouter' ? { 'x-model-variant': openrouterModel } : {})
      };

      const analyzePromise = await axios.post(`${API_BASE}/api/analyze`, {
        url: targetUrl,
        transcript: transcriptText,
        language: pipelineVideoLang,
        instruction: pipelineInstruction || undefined,
        mode: 'advanced',
        model: selectedModel,
        advanced: {
          focus: 'Viral Hooks',
          vibe: 'Dynamic & Fast',
          numCuts: advNumCuts,
          maxDuration: config.maxDuration || advMaxDuration || 60
        }
      }, { headers: { ...headers, ...getLicenseHeaders() } });

      clearInterval(analyzeInterval);
      setProgress(100);

      const analysisData = analyzePromise.data;
      setPipelineAnalysis(analysisData);
      setPastedJson(JSON.stringify(analysisData, null, 2));
      setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' })); // Done Step 2, Ready for 3 (Wait)
      addLog(`PIPELINE: Analysis Complete. Found ${analysisData.video_clips.length} moments.`, 'success');

      // --- AUTO PROCEED (Format selected upfront) ---
      addLog(`PIPELINE: Auto-starting processing with ${outputFormat} logic...`, 'info');
      triggerPipelineProcessing(downloadData.path, analysisData, outputFormat);

    } catch (err) {
      console.error("Pipeline Error:", err);
      const msg = err.response?.data?.error || err.message;
      setPipelineError(msg);
      // PR 4d: also populate pipelineErrorInfo so ErrorBanner renders the
      // structured version (with category palette + remediation chips)
      // instead of the legacy plain banner.
      setPipelineErrorInfo(buildLegacyErrorInfo(err, 'pipeline'));
      addLog(`ERROR: Pipeline failed - ${msg}`, 'error');
      setPipelineStatus(prev => ({ ...prev, step1: 'error', step2: 'error' }));
      // Reset progress state to allow retry
      setProgress(0);
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  // ================================================================
  // v2.3.0: Pipeline via Job system (resumable, structured errors)
  //
  // Bridges to legacy /api/video/process for the render step. Render-internals
  // port into the Job system is v2.4.0 scope; until then, the render half
  // stays unchanged so users get full hook+subtitle+format support.
  //
  // Flow:
  //   1. POST /api/jobs with phaseNames=[download, transcribe, analyze]
  //   2. POST /api/jobs/<id>/run (fire-and-forget — backend processes async)
  //   3. Poll GET /api/jobs/<id> until terminal status
  //   4. On completed: GET /clips/<id>/analyze/clips.json for the analysis
  //   5. triggerPipelineProcessing(jobVideoUrl, analysis, format) — render
  //
  // On error: surface QlipperError ui_message; persist activeJobId so the
  // user can hit "Resume" later.
  // ================================================================
  const pollJobUntilTerminal = async (jobId, onUpdate) => {
    const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes total
    const POLL_MS = 1500;
    const start = Date.now();

    while (Date.now() - start < TIMEOUT_MS) {
      try {
        const res = await axios.get(`${API_BASE}/api/jobs/${jobId}`);
        const m = res.data;
        if (onUpdate) onUpdate(m);
        if (m.status === 'completed' || m.status === 'failed' || m.status === 'paused') {
          return m;
        }
      } catch (e) {
        // Tolerate transient network glitches; loop continues until timeout
        console.warn(`[JOBS] poll error: ${e.message}`);
      }
      await new Promise(r => setTimeout(r, POLL_MS));
    }
    throw new Error('Job polling timed out after 30 minutes.');
  };

  const handleStartPipelineViaJobs = async (url = youtubeUrl, runConfig = {}) => {
    if (!validateApiKey()) return;
    const targetUrl = url || youtubeUrl;
    if (!targetUrl) return;

    setIsPipelineRunning(true);
    setPipelineError(null);
    setPipelineErrorInfo(null);
    setPipelineStatus({ step1: 'loading', step2: 'idle', step3: 'idle' });
    setProgress(0);
    setProcessingStatus('Creating job...');
    addLog(`PIPELINE-V2: Starting jobs-system pipeline for ${targetUrl}...`);

    // Map UI quality → format_id (same mapping as legacy)
    let dlQuality = 'quality_720';
    if (pipelineQuality === 'best' || pipelineQuality === '4k' || pipelineQuality === '1440') dlQuality = 'highest';
    else if (pipelineQuality === '1080') dlQuality = 'quality_1080';
    else if (pipelineQuality === '720') dlQuality = 'quality_720';
    else if (pipelineQuality === '480') dlQuality = 'quality_480';

    // Compose model API key per the same keyMap legacy uses
    const keyMap = {
      'gemini': apiKey, 'gpt-4o': openaiKey, 'claude': claudeKey,
      'mistral': mistralKey, 'deepseek': deepseekKey, 'openrouter': openrouterKey, 'minimax': minimaxKey, 'xai': xaiKey,
    };
    const modelApiKey = keyMap[selectedModel] || apiKey;
    const modelVariantValue = selectedModel === 'gemini'
      ? geminiVariant
      : (selectedModel === 'openrouter' ? openrouterModel : null);

    try {
      // 1. Create the job
      const createRes = await axios.post(`${API_BASE}/api/jobs`, {
        phaseNames: ['download', 'transcribe', 'analyze'],
        config: {
          url: targetUrl,
          format_id: dlQuality,
          preferredLang: pipelineVideoLang,
          cookieBrowser,
          model: selectedModel,
          modelVariant: modelVariantValue,
          apiKey: modelApiKey,
          // Router tile → the shim's analyze phase calls this provider (browser-direct
          // for a local router) instead of Gemini. Undefined = original Gemini path.
          llm: selectedModel === 'router' ? routerLlm : undefined,
          mode: 'advanced',
          instruction: pipelineInstruction || undefined,
          advanced: {
            focus: 'Viral Hooks',
            vibe: 'Dynamic & Fast',
            numCuts: advNumCuts,
            maxDuration: runConfig.maxDuration || advMaxDuration || 60,
          },
          // Whisper preferences (transcribe phase reads these via the adapter)
          whisperEngine,
          whisperModel,
          whisperWasmFallback,
          allow_transcribe_skip: true, // soft-skip if no engine available
        },
      }, { headers: getLicenseHeaders() });

      const jobId = createRes.data.id;
      setActiveJobId(jobId);
      addLog(`JOB: Created ${jobId}. Phases: download → transcribe → analyze.`);

      // 2. Kick off the runner
      await axios.post(`${API_BASE}/api/jobs/${jobId}/run`);

      // 3. Poll with phase-aware UI updates
      let loggedSkip = false;
      const final = await pollJobUntilTerminal(jobId, (m) => {
        setActiveJobManifest(m);
        const phases = m.phases || [];
        const dl = phases.find(p => p.name === 'download');
        const tx = phases.find(p => p.name === 'transcribe');
        const an = phases.find(p => p.name === 'analyze');

        // PR 4 polish: surface soft-skip events. The runner marks transcribe
        // 'skipped' (not 'failed') when no engine is available AND
        // allow_transcribe_skip is set. User benefits from knowing the
        // pipeline ran without transcript so analysis quality may differ.
        if (tx?.status === 'skipped' && !loggedSkip) {
          loggedSkip = true;
          addLog('TRANSCRIBE: Skipped — no working engine. Analysis will run without transcript.', 'warning');
        }

        // Map 3 backend phases (download → transcribe → analyze) to the 3-step
        // UI (Download → Analyze → Clipping). Transcribe + analyze BOTH live
        // under "Step 2: Analyze" — step2 stays in `loading` until the analyze
        // phase actually completes. Otherwise we'd flip step2 to success the
        // moment transcribe finished, making it look like step 2 is done while
        // the model is still working.
        if (dl?.status === 'running') {
          setPipelineStatus(prev => ({ ...prev, step1: 'loading' }));
          setProcessingStatus('Downloading video...');
          if (typeof dl.progress === 'number') setProgress(dl.progress);
        } else if (dl?.status === 'completed') {
          setPipelineStatus(prev => ({ ...prev, step1: 'success' }));
          if (tx?.status === 'running') {
            setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
            setProcessingStatus('Transcribing audio...');
            setProgress(50);
          } else if (tx?.status === 'completed' || tx?.status === 'skipped') {
            // Transcribe done, but step2 ≠ success yet — analyze may still run.
            setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
            if (an?.status === 'running') {
              setProcessingStatus(`Analyzing with ${selectedModel}...`);
              setProgress(80);
            } else if (an?.status === 'completed') {
              setPipelineStatus(prev => ({ ...prev, step2: 'success' }));
              setProgress(100);
            }
          }
        }
      });

      if (final.status !== 'completed') {
        // Surface structured error from the failed phase. Manifest stores
        // last_error per phase; pull the first failed phase's error.
        const failedPhase = (final.phases || []).find(p => p.status === 'failed');
        const errInfo = failedPhase?.last_error || {};
        // Attach structured info for ErrorBanner. Plain-string fallback
        // still set in the catch below.
        setPipelineErrorInfo({
          code: errInfo.code,
          ui_message: errInfo.ui_message,
          category: errInfo.category,
          retryable: errInfo.retryable,
          remediations: errInfo.remediations || [],
        });
        const uiMsg = errInfo.ui_message || errInfo.message || 'Pipeline failed (no error detail returned).';
        const code = errInfo.code ? ` [${errInfo.code}]` : '';
        throw new Error(`${uiMsg}${code}`);
      }

      // 4. Pipeline succeeded — fetch analysis JSON
      const analyzeUrl = final.artifacts?.clips_metadata_path;
      const videoUrl = final.artifacts?.downloaded_video_path;
      if (!analyzeUrl || !videoUrl) {
        throw new Error('Job completed but artifacts are missing — manifest is corrupt.');
      }

      const analysisRes = await axios.get(`${API_BASE}${analyzeUrl}`);
      const analysisData = analysisRes.data;
      setPipelineAnalysis(analysisData);
      setPastedJson(JSON.stringify(analysisData, null, 2));
      setPipelineStatus(prev => ({ ...prev, step1: 'success', step2: 'success', step3: 'loading' }));
      addLog(`PIPELINE-V2: All 3 phases complete. ${analysisData.video_clips?.length || 0} clips found.`, 'success');

      // 5. Bridge to legacy render. videoUrl is `/clips/<id>/download/video.mp4` —
      // the backend resolveMediaPath helper now resolves /clips/ URLs to the
      // job folder on disk (PR 3d).
      setPipelineMetadata({ path: videoUrl, title: analysisData.source_info?.video_title || 'Job-system video' });
      triggerPipelineProcessing(videoUrl, analysisData, outputFormat);

      // Job is done — clear active reference (success state)
      setActiveJobId(null);
      setActiveJobManifest(null);
    } catch (err) {
      console.error('Jobs-pipeline error:', err);
      const msg = err.response?.data?.error || err.message;
      setPipelineError(msg);
      addLog(`ERROR (jobs-pipeline): ${msg}`, 'error');
      setPipelineStatus(prev => ({ ...prev, step1: 'error', step2: 'error' }));
      setProgress(0);
      setProcessing(false);
      setProcessingStatus('');
      // activeJobId is intentionally LEFT SET so the user can resume from the
      // ErrorBanner / Resume button. Cleared on next successful run, manual
      // delete, or 30-day TTL.
    }
  };

  const handleResumeJob = async () => {
    if (!activeJobId) return;
    setIsPipelineRunning(true);
    setPipelineError(null);
    setPipelineErrorInfo(null);
    setProcessingStatus('Resuming job...');
    addLog(`PIPELINE-V2: Resuming job ${activeJobId}...`);
    try {
      await axios.post(`${API_BASE}/api/jobs/${activeJobId}/resume`);
      const final = await pollJobUntilTerminal(activeJobId, (m) => setActiveJobManifest(m));

      if (final.status !== 'completed') {
        const failedPhase = (final.phases || []).find(p => p.status === 'failed');
        const errInfo = failedPhase?.last_error || {};
        setPipelineErrorInfo({
          code: errInfo.code,
          ui_message: errInfo.ui_message,
          category: errInfo.category,
          retryable: errInfo.retryable,
          remediations: errInfo.remediations || [],
        });
        throw new Error(errInfo.ui_message || errInfo.message || 'Resume failed.');
      }

      // Same bridge to legacy render as the fresh-start path
      const analyzeUrl = final.artifacts?.clips_metadata_path;
      const videoUrl = final.artifacts?.downloaded_video_path;
      const analysisRes = await axios.get(`${API_BASE}${analyzeUrl}`);
      const analysisData = analysisRes.data;
      setPipelineAnalysis(analysisData);
      setPastedJson(JSON.stringify(analysisData, null, 2));
      setPipelineStatus({ step1: 'success', step2: 'success', step3: 'loading' });
      setPipelineMetadata({ path: videoUrl, title: analysisData.source_info?.video_title || 'Resumed job' });
      triggerPipelineProcessing(videoUrl, analysisData, outputFormat);
      setActiveJobId(null);
      setActiveJobManifest(null);
    } catch (err) {
      console.error('Resume error:', err);
      const msg = err.response?.data?.error || err.message;
      setPipelineError(msg);
      addLog(`ERROR (resume): ${msg}`, 'error');
    }
  };

  const handleStartPipelineFromAsset = async (asset, config = {}) => {
    if (!validateApiKey()) return;

    setIsPipelineRunning(true);
    setPipelineError(null);
    setPipelineErrorInfo(null);
    setPipelineStatus({ step1: 'loading', step2: 'idle', step3: 'idle' });
    setProgress(0);
    setProcessingStatus('Waiting for transcription...');
    addLog(`PIPELINE: Upload mode — using "${asset.filename || asset.name}"...`);

    try {
      // --- STEP 1: Wait for transcription to complete ---
      addLog('PIPELINE: Step 1 — Waiting for transcription...');
      let transcriptText = null;
      let assetId = asset.id;

      // Poll until transcription done (5 min max)
      if (!asset.transcriptionPath) {
        await new Promise((resolve) => {
          const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
          const startTime = Date.now();
          const interval = setInterval(async () => {
            if (Date.now() - startTime > TIMEOUT_MS) {
              clearInterval(interval);
              addLog('WARNING: Transcription timeout — proceeding without transcript', 'warning');
              resolve();
              return;
            }
            try {
              const assetsRes = await axios.get(`${API_BASE}/api/assets`);
              const fresh = assetsRes.data.find(a => a.id === assetId);
              if (fresh && fresh.transcriptionPath) {
                clearInterval(interval);
                Object.assign(asset, fresh); // update in place
                resolve();
              } else if (fresh && fresh.transcriptionError) {
                // Web shim: transcription failed — proceed without a transcript rather
                // than polling for the full 2h timeout.
                clearInterval(interval);
                addLog(`WARNING: Transcription failed (${fresh.transcriptionError}) — proceeding without transcript`, 'warning');
                resolve();
              }
            } catch (e) { /* continue */ }
          }, 2000);
        });
      }

      // Load transcript text
      if (asset.transcriptionPath) {
        try {
          const transcriptRes = await axios.get(`${API_BASE}/${asset.transcriptionPath}`);
          const tData = transcriptRes.data;
          if (tData.segments && tData.segments.length > 0) {
            transcriptText = tData.segments.map(s => {
              const ts = typeof s.start === 'number'
                ? `[${new Date(s.start * 1000).toISOString().substr(11, 8)}]`
                : `[${s.start}]`;
              return `${ts} ${s.text}`;
            }).join('\n');
          } else {
            transcriptText = tData.text;
          }
          addLog('PIPELINE: Transcript loaded.', 'success');
        } catch (e) {
          addLog('WARNING: Could not load transcript — proceeding without it.', 'warning');
        }
      }

      setPipelineStatus(prev => ({ ...prev, step1: 'success', step2: 'loading' }));
      setProgress(0);
      setProcessingStatus(`Analyzing with ${selectedModel}...`);

      // --- STEP 2: ANALYZE ---
      addLog(`PIPELINE: Step 2 — Analyzing with ${selectedModel}...`);
      const analyzeInterval = setInterval(() => {
        setProgress(p => p >= 90 ? 90 : p + Math.floor(Math.random() * 5));
      }, 800);

      const keyMap = {
        'gemini': { header: 'x-gemini-key', key: apiKey },
        'gpt-4o': { header: 'x-openai-key', key: openaiKey },
        'claude': { header: 'x-anthropic-key', key: claudeKey },
        'mistral': { header: 'x-mistral-key', key: mistralKey },
        'deepseek': { header: 'x-deepseek-key', key: deepseekKey },
        'openrouter': { header: 'x-openrouter-key', key: openrouterKey },
        'minimax': { header: 'x-minimax-key', key: minimaxKey },
        'xai': { header: 'x-xai-key', key: xaiKey }
      };
      const modelConfig = keyMap[selectedModel] || keyMap['gemini'];
      const headers = {
        [modelConfig.header]: modelConfig.key,
        ...(selectedModel === 'gemini' ? { 'x-gemini-variant': geminiVariant } : {}),
        ...(selectedModel === 'openrouter' ? { 'x-model-variant': openrouterModel } : {})
      };

      const analyzeRes = await axios.post(`${API_BASE}/api/analyze`, {
        url: asset.path,
        transcript: transcriptText,
        instruction: pipelineInstruction || undefined,
        mode: 'advanced',
        model: selectedModel,
        // Router tile → the shim's /api/analyze calls this provider instead of Gemini.
        llm: selectedModel === 'router' ? routerLlm : undefined,
        language: pipelineVideoLang === 'en' ? 'en' : 'id',
        assetMetadata: { filename: asset.filename || asset.name, duration: asset.duration, title: asset.name },
        advanced: {
          focus: 'Viral Hooks',
          vibe: 'Dynamic & Fast',
          numCuts: advNumCuts,
          maxDuration: config.maxDuration || advMaxDuration || 60
        }
      }, { headers: { ...headers, ...getLicenseHeaders() } });

      clearInterval(analyzeInterval);
      setProgress(100);

      const analysisData = analyzeRes.data;
      setPipelineAnalysis(analysisData);
      setPastedJson(JSON.stringify(analysisData, null, 2));
      setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' }));
      addLog(`PIPELINE: Analysis complete. Found ${analysisData.video_clips?.length || 0} moments.`, 'success');

      // --- STEP 3: PROCESS ---
      triggerPipelineProcessing(asset.path, analysisData, outputFormat);

    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setPipelineError(msg);
      // PR 4d: structured ErrorBanner for upload-mode legacy path too
      setPipelineErrorInfo(buildLegacyErrorInfo(err, 'upload-pipeline'));
      addLog(`ERROR: Pipeline failed — ${msg}`, 'error');
      setPipelineStatus(prev => ({ ...prev, step1: 'error', step2: 'error' }));
      setProgress(0);
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const triggerPipelineProcessing = async (videoPath, analysis, formatOverride = null) => {
    setProcessing(true);
    setProgress(0);
    setFinalClips([]);
    setProcessingStatus('Initializing processing...');
    // Ensure Step 3 shows loading now that we actually started
    setPipelineStatus(prev => ({ ...prev, step3: 'loading' }));

    try {
      const config = analysis;
      addLog("PIPELINE: Sending process request...", 'info');
      const initResponse = await axios.post(`${API_BASE}/api/video/process`, {
        videoPath,
        config,
        outputFormat: formatOverride || outputFormat,
        outputResolution,
        useTransitions,
        renderProfile,
        captionText,
        captionStyle,
        burnSubtitles,
        subtitleStyle,
        styleSettings
      }, { timeout: 10000, headers: getLicenseHeaders() }); // 10s timeout to catch hangs

      addLog(`PIPELINE: Process Request Sent. Job ID: ${initResponse.data.jobId}`, 'success');
      const { jobId, totalClips } = initResponse.data;

      const optimisticProject = {
        id: jobId,
        title: config.source_info?.video_title || 'New Project',
        timestamp: new Date().toISOString(),
        totalClips: totalClips,
        clips: [],
        status: 'processing'
      };

      setHistoryProjects(prev => [optimisticProject, ...prev]);
      setSelectedHistoryProject(optimisticProject);

      let initialClips = config.video_clips.map(c => ({
        id: c.clip_id,
        topic: c.topic || `Clip ${c.clip_id}`,
        status: 'pending',
        highlight: 'Processing...'
      }));
      setFinalClips(initialClips);

      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_BASE}/api/job/${jobId}`);
          const jobData = statusRes.data;

          if (jobData.completedClips && jobData.completedClips.length > 0) {
            setFinalClips(prevClips => {
              const newClips = [...prevClips];
              jobData.completedClips.forEach(completed => {
                const idx = newClips.findIndex(p => p.id === completed.id);
                if (idx !== -1) newClips[idx] = completed;
              });
              return newClips;
            });
          }

          const pct = Math.round((jobData.completedClips.length / jobData.totalClips) * 100);
          setProgress(Math.min(pct, 100));
          setProcessingStatus(`Clipping ${jobData.completedClips.length}/${jobData.totalClips} videos...`);

          if (jobData.status === 'completed') {
            clearInterval(pollInterval);
            setProcessing(false);
            setPipelineStatus(prev => ({ ...prev, step3: 'success' }));
            addLog(`PIPELINE: All steps completed successfully!`, 'success');
            fetchHistory(true); // Refresh My Files (background, no loading flash)
            fetchAssets();  // Refresh Asset Library
          }

          setHistoryProjects(prev => {
            const newHistory = [...prev];
            const pIdx = newHistory.findIndex(p => p.id === jobId);
            if (pIdx !== -1) {
              newHistory[pIdx] = { ...newHistory[pIdx], clips: jobData.completedClips, status: jobData.status };
            }
            return newHistory;
          });

          // Keep selectedHistoryProject in sync so the main content area updates
          setSelectedHistoryProject(prev => {
            if (prev && prev.id === jobId) {
              return { ...prev, clips: jobData.completedClips, status: jobData.status };
            }
            return prev;
          });
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 3000);

    } catch (err) {
      addLog(`ERROR: Pipeline step 3 failed - ${err.message}`, 'error');
      setPipelineError(err.response?.data?.error || err.message);
      // PR 4d: structured ErrorBanner for render-step legacy failures.
      setPipelineErrorInfo(buildLegacyErrorInfo(err, 'render'));
      setPipelineStatus(prev => ({ ...prev, step3: 'error' }));
      setProcessing(false);
      setProgress(0);
      setProcessingStatus('');
    }
  };


  // Styles
  const cardClass = "dark:bg-dark-400/50 bg-white/70 backdrop-blur-xl border dark:border-dark-300 border-gold-100 rounded-[2rem] p-8 shadow-2xl transition-all";
  const labelClass = "text-[11px] dark:text-cream-400 text-cream-400 font-bold uppercase tracking-widest mb-3 block";
  const inputClass = "w-full dark:bg-dark-600 bg-white border dark:border-dark-300 border-cream-300 rounded-2xl px-5 py-4 text-sm dark:text-white text-dark-200 placeholder-cream-400 focus:ring-2 focus:ring-gold-500/50 outline-none transition-all shadow-inner relative z-10 focus:z-20";
  const btnPrimary = "bg-gold-600 hover:bg-gold-500 text-white rounded-2xl px-6 py-4 font-bold shadow-gold-lg/20 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50";

  return (
    <div className="min-h-screen dark:bg-dark-500 bg-cream-100 dark:text-white text-dark-200 font-sans overflow-hidden flex flex-col relative transition-colors duration-500">
      {/* Atmospheric Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Sky / Base */}
        <div className="absolute inset-0 dark:bg-dark-500 bg-white transition-colors duration-700" />

        {/* Fog Elements */}
        <div className="absolute inset-0 opacity-40 dark:opacity-30">
          <div className="absolute top-1/2 left-[-10%] w-[60%] h-[40%] rounded-full blur-[120px] dark:bg-gold-500/20 bg-gold-200/40 animate-drift" />
          <div className="absolute top-[40%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[100px] dark:bg-amber-500/20 bg-gold-200/30 animate-drift-slow" />
          <div className="absolute top-[60%] left-[20%] w-[40%] h-[30%] rounded-full blur-[80px] dark:bg-gold-400/10 bg-gold-100/20 animate-drift-reverse" />
        </div>

        {/* Water Surface */}
        <div className="absolute bottom-0 left-0 w-full h-[45%] z-10 overflow-hidden">
          {/* Water Base Gradient */}
          <div className="absolute inset-0 dark:bg-gradient-to-t dark:from-gold-950/40 dark:to-transparent bg-gradient-to-t from-gold-100/50 to-transparent" />

          {/* Water Reflections / Ripples */}
          <div className="absolute inset-0 opacity-20 dark:opacity-40">
            <div
              className="absolute bottom-0 left-0 w-[200%] h-full opacity-10 animate-water-move"
              style={{ backgroundImage: `url(${API_BASE}/api/proxy-image?url=${encodeURIComponent('https://www.transparenttextures.com/patterns/carbon-fibre.png')})` }}
            />
          </div>

          {/* Fog on Water */}
          <div className="absolute inset-x-0 bottom-0 h-full overflow-hidden blur-2xl opacity-60">
            <div className="absolute bottom-0 left-0 w-[150%] h-[60%] dark:bg-gold-500/10 bg-white/40 animate-fog-move" />
            <div className="absolute bottom-10 right-0 w-[150%] h-[40%] dark:bg-amber-500/10 bg-gold-50/30 animate-fog-move-reverse" />
          </div>

          {/* Horizon Line Blend */}
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b dark:from-[#020617] from-white to-transparent transition-colors duration-700" />
        </div>

        {/* Vignette */}
        <div className="absolute inset-0 dark:shadow-[inset_0_0_200px_rgba(0,0,0,0.8)] shadow-[inset_0_0_200px_rgba(255,255,255,0.4)] pointer-events-none" />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(50px, -30px) scale(1.1); }
        }
        @keyframes drift-slow {
          0%, 100% { transform: translate(0, 0) scale(1.1); }
          50% { transform: translate(-40px, 20px) scale(1); }
        }
        @keyframes drift-reverse {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-60px, -40px); }
        }
        @keyframes water-move {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes fog-move {
          0% { transform: translateX(-20%) translateY(20px); opacity: 0.3; }
          50% { transform: translateX(0%) translateY(0px); opacity: 0.6; }
          100% { transform: translateX(-20%) translateY(20px); opacity: 0.3; }
        }
        @keyframes fog-move-reverse {
          0% { transform: translateX(20%) translateY(0px); opacity: 0.5; }
          50% { transform: translateX(0%) translateY(20px); opacity: 0.2; }
          100% { transform: translateX(20%) translateY(0px); opacity: 0.5; }
        }
        .animate-drift { animation: drift 15s ease-in-out infinite; }
        .animate-drift-slow { animation: drift-slow 22s ease-in-out infinite; }
        .animate-drift-reverse { animation: drift-reverse 18s ease-in-out infinite; }
        .animate-water-move { animation: water-move 60s linear infinite; }
        .animate-fog-move { animation: fog-move 12s ease-in-out infinite; }
        .animate-fog-move-reverse { animation: fog-move-reverse 15s ease-in-out infinite; }
      `}} />

      {/* Update-available banner (non-blocking, dismissible per version) */}
      {updateInfo && (
        <div className="relative z-50 w-full bg-gold-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3">
          <span>
            Qlipper v{updateInfo.latest} tersedia{updateInfo.notes ? ` — ${updateInfo.notes}` : ''}
          </span>
          <a
            href={updateInfo.url || 'https://member.qlipper.id/download'}
            target="_blank"
            rel="noreferrer"
            className="underline font-semibold hover:opacity-80"
          >
            Download update
          </a>
          <button
            onClick={() => {
              localStorage.setItem('qlipper_update_dismissed', updateInfo.latest);
              setUpdateInfo(null);
            }}
            className="absolute right-4 font-bold hover:opacity-70"
            aria-label="Dismiss update notice"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <AnimatePresence>
        {(activeTab !== 'dashboard' || !isPipelineRunning) && (
          <motion.header
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="grid grid-cols-3 items-center py-6 px-12 relative z-50 w-full"
          >
            <div className="flex items-center gap-3 justify-start">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab('dashboard')}>
                <div className="w-10 h-10 flex-shrink-0 rounded-xl border border-cream-200 dark:border-dark-100 shadow-card-light dark:shadow-card-dark overflow-hidden relative group">
                  <img src={darkMode ? logoDark : logoLight} alt="Qlipper Logo" className="w-full h-full object-contain relative z-10 p-0.5" />
                </div>
                <div className="hidden sm:flex flex-col">
                  <h1 className="text-xl font-bold tracking-tight dark:text-white text-dark-200 leading-none">Qlipper <span className="text-[11px] bg-gold-500 text-white px-1.5 py-0.5 rounded-md align-middle ml-1">v{__APP_VERSION_SHORT__}</span></h1>
                </div>
              </div>
            </div>

            {/* Center navigation — visible when not on dashboard */}
            <div className="flex justify-center">
              <AnimatePresence>
                {activeTab !== 'dashboard' && (
                  <motion.nav
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="flex items-center gap-1 p-1 bg-cream-100/80 dark:bg-dark-400/80 backdrop-blur-md rounded-2xl border border-cream-300 dark:border-dark-200"
                  >
                    {[
                      { id: 'dashboard', name: 'HOME', icon: <Home size={14} />, group: 'home' },
                      { id: 'tool-a', name: 'DOWNLOAD', icon: <Download size={14} />, group: 'pipeline' },
                      { id: 'tool-b', name: 'AI ANALYSIS', icon: <Zap size={14} />, group: 'pipeline' },
                      { id: 'tool-c', name: 'CLIPPER', icon: <Scissors size={14} />, group: 'pipeline' },
                      { id: 'tool-d', name: 'SUBTITLE', icon: <Type size={14} />, group: 'pipeline' },
                      { id: 'tool-f', name: 'WATERMARK', icon: <Eraser size={14} />, group: 'tools' },
                      { id: 'tool-g', name: 'SILENCE', icon: <VolumeX size={14} />, group: 'tools' },
                      { id: 'tool-h', name: 'B-ROLL', icon: <ImageIcon size={14} />, group: 'tools' },
                      { id: 'projects', name: 'MY FILES', icon: <Briefcase size={14} />, group: 'files' },
                      { id: 'assets', name: 'ASSETS', icon: <ImageIcon size={14} />, group: 'files' },
                    ].map((tab, idx, arr) => (
                      <React.Fragment key={tab.id}>
                        {idx > 0 && arr[idx - 1].group !== tab.group && (
                          <div className="w-px h-5 bg-cream-300 dark:bg-dark-200 mx-1 hidden md:block" aria-hidden="true" />
                        )}
                        <motion.button
                          layoutId={`tab-${tab.id}`}
                          onClick={() => setActiveTab(tab.id)}
                          className={`px-3 py-2 rounded-xl text-[11px] font-bold uppercase transition-all flex items-center gap-1.5 ${
                            activeTab === tab.id
                              ? 'bg-gold-500 text-white shadow-lg'
                              : 'text-cream-500 dark:text-cream-400 hover:text-dark-200 dark:hover:text-white hover:bg-cream-200/50 dark:hover:bg-dark-300/50'
                          }`}
                        >
                          {tab.icon}
                          <span className={`whitespace-nowrap ${activeTab === tab.id ? '' : 'hidden md:inline'}`}>
                            {tab.name}
                          </span>
                        </motion.button>
                      </React.Fragment>
                    ))}
                  </motion.nav>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <div className="flex items-center gap-2">
                <button onClick={() => setDarkMode(!darkMode)} className="p-2.5 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-100 hover:scale-105 transition-all shadow-card-light dark:shadow-card-dark" title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {darkMode ? <Moon size={16} className="text-gold-400" /> : <Sun size={16} className="text-gold-500" />}
                </button>

                <button
                  onClick={() => setActiveTab('faq')}
                  className={`p-2.5 rounded-xl border transition-all shadow-card-light dark:shadow-card-dark hover:scale-105 ${activeTab === 'faq' ? 'bg-gold-500 border-gold-400 text-white' : 'bg-cream-100 dark:bg-dark-300 border-cream-300 dark:border-dark-100 text-cream-500 dark:text-cream-400'}`}
                >
                  <HelpCircle size={16} />
                </button>

                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_BASE}/api/system/network-address`);
                      setNetworkUrl(res.data.primaryUrl);
                    } catch { setNetworkUrl(`http://localhost:${window.location.port || 3001}`); }
                    setShowQrModal(true);
                  }}
                  className="p-2.5 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-100 hover:scale-105 transition-all shadow-card-light dark:shadow-card-dark"
                  title="Open on phone"
                >
                  <Smartphone size={16} className="text-cream-500 dark:text-cream-400" />
                </button>

                <button onClick={() => setShowSettings(true)} className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-100 hover:border-gold-500/50 hover:scale-105 transition-all shadow-card-light dark:shadow-card-dark group" title="Settings">
                  <Settings size={16} className="text-cream-500 dark:text-cream-400 group-hover:text-gold-500 transition-colors" />
                  <span className="text-[11px] font-bold text-cream-500 dark:text-cream-400 group-hover:text-gold-500 transition-colors hidden sm:inline">Settings</span>
                </button>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Main Container */}
      <main className="flex-1 relative z-10 w-full max-w-7xl mx-auto px-8 pb-32">
        <AnimatePresence mode="wait">

          {/* DASHBOARD (SUPA STYLE) */}
          {activeTab === 'dashboard' && (
            <motion.div
              key="dash" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.5, ease: "circOut" }}
              className={`flex flex-col items-center justify-center transition-all duration-700 ${isPipelineRunning ? 'h-[80vh]' : 'pt-20'}`}
            >
              <AnimatePresence>
                {!isPipelineRunning && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="text-center mb-12"
                  >
                    <h2 className="text-5xl font-extrabold tracking-tight mb-2">Hi Qlippers, Mau bikin video apa hari ini?</h2>
                    <p className="dark:text-cream-400 text-cream-500 font-medium">Universal Pipeline for AI-Driven Video Content.</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Master Command Bar */}
              <div className="w-full max-w-3xl relative group mb-4">
                <div className="absolute inset-0 bg-gold-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity rounded-[2rem]" />
                <div className="relative flex flex-col bg-cream-100 dark:bg-dark-300 backdrop-blur-2xl border border-cream-300 dark:border-dark-50 rounded-[2rem] shadow-card-light dark:shadow-card-dark overflow-hidden transition-all duration-300">

                  {/* Live Thumbnail */}

                  {/* Input Row */}
                  <div className="flex items-center w-full px-2 py-1">
                    {/* Mode badge inside input */}
                    {pipelineMode === 'upload' && pipelineUploadedAsset ? (
                      <span className={`ml-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-bold whitespace-nowrap flex-shrink-0 ${pipelineUploadedAsset.fromLibrary ? 'bg-amber-500/10 border-amber-500/30 text-amber-500' : 'bg-gold-500/10 border-gold-500/30 text-gold-500'}`}>
                        {pipelineUploadedAsset.fromLibrary ? <Layers size={12} /> : <FolderOpen size={12} />} {(pipelineUploadedAsset.name || pipelineUploadedAsset.filename || '')?.length > 20 ? (pipelineUploadedAsset.name || pipelineUploadedAsset.filename || '').slice(0, 20) + '…' : (pipelineUploadedAsset.name || pipelineUploadedAsset.filename || '')}
                      </span>
                    ) : pipelineMode === 'upload' ? (
                      <span className="ml-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gold-500/10 border border-gold-500/30 text-gold-500 text-[11px] font-bold whitespace-nowrap flex-shrink-0">
                        <FolderOpen size={12} /> Local File
                      </span>
                    ) : (() => {
                      const url = youtubeUrl.trim();
                      if (/youtube\.com|youtu\.be/i.test(url)) return (
                        <span className="ml-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-[11px] font-bold whitespace-nowrap flex-shrink-0">
                          <img src={brandIcon('play-button', darkMode)} className="w-3 h-3 object-contain" alt="" /> YouTube
                        </span>
                      );
                      return null;
                    })()}
                    <input
                      type="text"
                      value={pipelineMode === 'upload' ? '' : youtubeUrl}
                      onChange={(e) => { if (pipelineMode !== 'upload') setYoutubeUrl(e.target.value); }}
                      placeholder={pipelineMode === 'upload' ? 'Local file selected — click Generate to proceed…' : 'Paste YouTube link…'}
                      className="flex-1 bg-transparent border-none outline-none text-sm md:text-base text-dark-200 dark:text-cream-100 placeholder-cream-400 dark:placeholder-cream-400 py-4 px-4 min-w-0"
                      onKeyDown={(e) => e.key === 'Enter' && pipelineMode !== 'upload' && handleSetupPipeline()}
                      readOnly={pipelineMode === 'upload'}
                    />
                    <div className="flex items-center gap-2 pr-2">
                      {/* Hidden file input — triggered when Generate clicked in upload mode */}
                      <input
                        id="cmd-file-input"
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          // Show transcription progress modal immediately
                          setShowTranscribingModal(true);
                          setTranscribingProgress({ status: 'uploading', message: `Uploading "${file.name}"…` });

                          const formData = new FormData();
                          formData.append('asset', file);
                          formData.append('name', file.name);
                          try {
                            // Step 1: Upload
                            const res = await axios.post(`${API_BASE}/api/assets/upload?type=asset`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                              timeout: 300000
                            });
                            const asset = res.data;
                            fetchAssets();
                            setPipelineMode('upload');
                            setPipelineUploadedAsset(asset);

                            // Step 2: Start transcription
                            setTranscribingProgress({ status: 'transcribing', message: 'Starting transcription…' });
                            addLog(`UPLOAD: Transcribing "${file.name}"...`, 'info');
                            let transcribeJobId = null;
                            try {
                              const transcribeRes = await axios.post(`${API_BASE}/api/transcribe/${asset.id}`, { whisperEngine, whisperModel, whisperWasmFallback }, {
                                headers: apiKey ? { 'x-gemini-key': apiKey, 'x-gemini-variant': geminiVariant } : {}
                              });
                              transcribeJobId = transcribeRes.data?.jobId || null;
                            } catch { /* continue polling assets as fallback */ }

                            // Step 3: Poll until done — show real progress from job status
                            setTranscribingProgress({ status: 'waiting', message: 'Waiting for transcript…' });
                            await new Promise((resolve, reject) => {
                              const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2h — match backend whisper wall-clock; older PCs need slack, backend will resolve well before this
                              const start = Date.now();
                              const interval = setInterval(async () => {
                                if (Date.now() - start > TIMEOUT_MS) { clearInterval(interval); resolve(); return; }
                                try {
                                  // Poll job status for real progress messages
                                  if (transcribeJobId) {
                                    const statusRes = await axios.get(`${API_BASE}/api/transcribe/status/${transcribeJobId}`).catch(() => null);
                                    if (statusRes?.data) {
                                      const { logs = [], progress = 0 } = statusRes.data;
                                      const lastLog = logs.filter(l => !l.startsWith('[INFO]')).slice(-1)[0] || logs.slice(-1)[0];
                                      const displayMsg = lastLog
                                        ? lastLog.replace(/^\[.*?\]\s*/, '')
                                        : 'Processing…';
                                      const pct = typeof progress === 'number' && progress > 0 ? ` (${progress}%)` : '';
                                      // Show elapsed time so a long transcription visibly "moves"
                                      // even when the backend can't report a percentage (beta
                                      // feedback: jobs sat silently for an hour).
                                      const elapsedMin = Math.floor((Date.now() - start) / 60000);
                                      const elapsed = elapsedMin >= 1 ? ` · ${elapsedMin}m elapsed` : '';
                                      setTranscribingProgress({ status: 'waiting', message: displayMsg + pct + elapsed });

                                      if (statusRes.data.status === 'failed') {
                                        // Web: fail visibly instead of "complete" + a pipeline that
                                        // then waits hours for a transcript that will never exist.
                                        clearInterval(interval);
                                        reject(new Error(statusRes.data.error || 'Transkrip gagal'));
                                        return;
                                      }
                                      if (statusRes.data.status === 'completed') {
                                        clearInterval(interval);
                                        resolve();
                                        return;
                                      }
                                    }
                                  }
                                  // Also check asset completion as fallback / confirmation
                                  const r = await axios.get(`${API_BASE}/api/assets`);
                                  const fresh = r.data.find(a => a.id === asset.id);
                                  if (fresh?.transcriptionPath) {
                                    Object.assign(asset, fresh);
                                    clearInterval(interval);
                                    resolve();
                                  }
                                } catch { /* continue */ }
                              }, 2000);
                            });

                            setTranscribingProgress({ status: 'done', message: 'Transcription complete!' });
                            addLog(`UPLOAD: Transcription done. Opening pipeline config...`, 'success');
                            fetchAssets();

                            // Short pause so user sees "done", then open pipeline config
                            await new Promise(r => setTimeout(r, 800));
                            setShowTranscribingModal(false);
                            setPipelineSetup(true);
                          } catch (err) {
                            addLog(`ERROR: Upload failed - ${err.response?.data?.error || err.message}`, 'error');
                            setTranscribingProgress({ status: 'error', message: `Failed: ${err.response?.data?.error || err.message}` });
                            setTimeout(() => { setShowTranscribingModal(false); setPipelineMode('youtube'); }, 2000);
                          }
                          e.target.value = '';
                        }}
                      />
                      <button
                        onClick={() => {
                          if (pipelineMode === 'upload') {
                            if (pipelineUploadedAsset) handleSetupPipeline();
                            else document.getElementById('cmd-file-input').click();
                          } else {
                            handleSetupPipeline();
                          }
                        }}
                        disabled={processing}
                        className={`px-5 py-2.5 rounded-xl font-bold text-sm shadow-gold transition-all flex items-center gap-2 whitespace-nowrap disabled:opacity-50 ${processing ? 'bg-gold-500/80 cursor-wait text-white' : 'bg-gold-500 hover:bg-gold-400 text-white'}`}
                      >
                        {processing ? <Loader2 className="animate-spin" size={16} /> : <img src={brandIcon('sparkle', darkMode)} className="w-4 h-4 object-contain brightness-[100] invert" alt="" />}
                        <span>
                          {processing ? 'Running…' : isPipelineRunning ? 'New Project' : pipelineMode === 'upload' ? (pipelineUploadedAsset ? 'Generate' : 'Choose File') : 'Download & Analyze'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* ============================================================
                      GUARDRAIL: Live Thumbnail During Pipeline Processing
                      DO NOT MODIFY without testing thumbnail display!
                      Uses YouTubeThumbnail component with multiple fallbacks.
                      Last verified working: 2026-01-28
                      ============================================================ */}
                  <AnimatePresence>
                    {isPipelineRunning && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="w-full px-4 pt-0 pb-4"
                      >
                        {pipelineMode === 'upload' && pipelineUploadedAsset ? (
                          /* Local file preview */
                          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden shadow-lg border border-cream-300 dark:border-dark-300 relative bg-dark-500">
                            <video
                              src={`${API_BASE}/uploads/${pipelineUploadedAsset.filename || pipelineUploadedAsset.id}`}
                              className="w-full h-full object-cover opacity-60"
                              muted autoPlay={false}
                            />
                            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex flex-col items-center justify-center text-center p-4">
                              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-xl animate-pulse mb-2">
                                <Activity className="text-gold-400" size={24} />
                              </div>
                              <h3 className="text-white font-bold text-lg drop-shadow-md line-clamp-1 max-w-[80%]">
                                {pipelineUploadedAsset.name || 'Processing Local File…'}
                              </h3>
                            </div>
                          </div>
                        ) : (
                          <YouTubeThumbnail
                            videoUrl={youtubeUrl}
                            fallbackThumbnail={pipelineMetadata?.thumbnail || metadata?.thumbnail}
                            title={pipelineMetadata?.title || metadata?.title}
                            className="w-full aspect-[21/9] rounded-2xl overflow-hidden shadow-lg border border-cream-300 dark:border-dark-300"
                            overlay={
                              <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex flex-col items-center justify-center text-center p-4">
                                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-xl animate-pulse mb-2">
                                  <Activity className="text-gold-400" size={24} />
                                </div>
                                <h3 className="text-white font-bold text-lg drop-shadow-md line-clamp-1 max-w-[80%]">
                                  {pipelineMetadata?.title || metadata?.title || 'Analyzing Video Stream...'}
                                </h3>
                              </div>
                            }
                          />
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {/* END GUARDRAIL: Live Thumbnail */}

                  {/* Pipeline Progress UI */}
                  <AnimatePresence>
                    {isPipelineRunning && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1, display: 'block' }}
                        exit={{ height: 0, opacity: 0 }}
                        className="w-full px-6 pb-6 pt-4 border-t dark:border-dark-300/50 mt-2 space-y-6"
                      >
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { id: 1, name: 'Download', icon: <Download size={16} />, status: pipelineStatus.step1 },
                            { id: 2, name: 'Analyze', icon: <Zap size={16} />, status: pipelineStatus.step2 },
                            { id: 3, name: 'Clipping', icon: <Scissors size={16} />, status: pipelineStatus.step3 }
                          ].map((step, i) => (
                            <div key={step.id} className="flex flex-col items-center gap-2">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${step.status === 'success' ? 'bg-gold-500 border-gold-400 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]' :
                                step.status === 'loading' ? 'bg-gold-500/10 border-gold-500 text-gold-500 shadow-[0_0_15px_rgba(99,102,241,0.2)]' :
                                  step.status === 'error' ? 'bg-red-500 border-red-400 text-white' :
                                    'bg-dark-300/30 border-dark-200 text-cream-500'
                                }`}>
                                {step.status === 'success' ? <CheckCircle2 size={18} /> :
                                  step.status === 'loading' ? <Loader2 size={18} className="animate-spin" /> :
                                    step.status === 'error' ? <AlertCircle size={18} /> :
                                      step.icon}
                              </div>
                              <span className={`text-[11px] font-bold uppercase tracking-tight ${step.status === 'success' ? 'text-gold-500' :
                                step.status === 'loading' ? 'text-gold-400' :
                                  step.status === 'error' ? 'text-red-500' :
                                    'text-cream-500'
                                }`}>Step {step.id}: {step.name}</span>
                            </div>
                          ))}
                        </div>

                        {pipelineError && (
                          <div className="flex flex-col gap-3">
                            {/* PR 4a: prefer structured ErrorBanner when QlipperError-shaped
                                info is available; fall back to plain banner for legacy errors. */}
                            {pipelineErrorInfo ? (
                              <ErrorBanner
                                errorInfo={pipelineErrorInfo}
                                onResume={useJobsSystem && activeJobId ? handleResumeJob : null}
                                onRetry={handleRetry}
                                onAction={(rem) => {
                                  // Remediation action dispatcher. Keeps the surface tiny:
                                  // most remediations are 'manual' (passive). The few that
                                  // need a UI side-effect map here.
                                  if (rem.action === 'open-settings') setShowSettings(true);
                                  else addLog(`Suggestion: ${rem.label}`, 'info');
                                }}
                                isResumable={useJobsSystem && !!activeJobId}
                                isRunning={isPipelineRunning}
                              />
                            ) : (
                              <div className="flex items-center justify-between p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500">
                                <div className="flex items-center gap-3">
                                  <AlertCircle size={14} />
                                  <p className="text-[11px] font-medium leading-tight">{pipelineError}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {useJobsSystem && activeJobId && (
                                    <button
                                      onClick={handleResumeJob}
                                      disabled={isPipelineRunning}
                                      className="px-3 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg shadow-gold-500/20"
                                      title="Continue this job from the last completed phase"
                                    >
                                      <RotateCcw size={12} /> Resume
                                    </button>
                                  )}
                                  <button onClick={handleRetry} className="px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg shadow-red-500/20">
                                    <RotateCcw size={12} /> Retry
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* DOWNLOAD FALLBACK UI */}
                            {downloadFallbackEnabled && pipelineStatus.step2 === 'error' && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="bg-dark-300/50 rounded-xl p-4 border border-dark-200 space-y-4"
                              >
                                <div className="flex items-center gap-2 text-amber-400">
                                  <Info size={16} />
                                  <h4 className="text-xs font-bold uppercase">Download Issue Detected</h4>
                                </div>
                                <p className="text-[11px] text-cream-400">
                                  The server is struggling to download this video (possibly Geo-restricted or age-gated).
                                  <br />You can try these alternatives:
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {/* Phase 3.5: User Selection / Format Picker (REMOVED - MOVED TO START) */}
                                  {pipelineAwaitingFormat && (
                                    <div className="hidden"></div>
                                  )}
                                  <div className="p-3 rounded-lg bg-gold-500/10 border border-gold-500/20">
                                    <h5 className="text-[11px] font-bold text-gold-400 mb-2 uppercase">Option A: Save Manually</h5>
                                    <p className="text-[11px] text-cream-500 mb-2">Download the video yourself using an external tool or browser extension.</p>
                                    <button
                                      onClick={() => window.open(youtubeUrl, '_blank')}
                                      className="w-full py-2 rounded bg-gold-600 text-white text-[11px] font-bold uppercase hover:bg-gold-500"
                                    >
                                      Open Video
                                    </button>
                                  </div>

                                  {/* Option B: Upload File */}
                                  <div className="p-3 rounded-lg bg-gold-500/10 border border-gold-500/20 relative">
                                    <h5 className="text-[11px] font-bold text-gold-500 mb-2 uppercase">Option B: Provide File</h5>
                                    <p className="text-[11px] text-cream-500 mb-2">Already have the .mp4? Upload it directly to skip Step 2.</p>

                                    <input
                                      type="file"
                                      accept="video/mp4"
                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;

                                        addLog(`FALLBACK: Uploading ${file.name}...`);
                                        const formData = new FormData();
                                        formData.append('video', file);

                                        try {
                                          setPipelineStatus(prev => ({ ...prev, step2: 'loading' }));
                                          setPipelineError(null);

                                          const upRes = await axios.post(`${API_BASE}/api/upload`, formData, {
                                            headers: { 'Content-Type': 'multipart/form-data' }
                                          });

                                          const uploadPath = upRes.data.path;
                                          setPipelineMetadata({ path: uploadPath, title: file.name });
                                          setPipelineStatus(prev => ({ ...prev, step2: 'success', step3: 'loading' }));
                                          addLog(`FALLBACK: Upload success. Resuming Pipeline...`, 'success');

                                          // Resume Pipeline
                                          await triggerPipelineProcessing(uploadPath, pipelineAnalysis, outputFormat);

                                        } catch (err) {
                                          addLog(`FALLBACK: Upload failed - ${err.message}`, 'error');
                                          setPipelineStatus(prev => ({ ...prev, step2: 'error' }));
                                          setPipelineError("Upload failed: " + err.message);
                                        }
                                      }}
                                    />
                                    <div className="w-full py-2 rounded bg-gold-600 text-white text-[11px] font-bold uppercase hover:bg-gold-500 flex items-center justify-center gap-2">
                                      <Upload size={14} /> Select File
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </div>
                        )}



                        <div className="flex items-center justify-between pt-2">
                          <div className="flex-1 mr-4">
                            <div className="flex justify-between text-[11px] font-bold uppercase text-cream-500 mb-1.5 px-1">
                              <span>Global Progress</span>
                              <span>{(() => {
                                let pct = 0;
                                if (pipelineStatus.step3 === 'success') pct = 100;
                                else if (pipelineStatus.step3 === 'loading') pct = 60 + (progress * 0.4);
                                else if (pipelineStatus.step2 === 'loading' || pipelineStatus.step2 === 'success') pct = 30 + (progress * 0.3);
                                else if (pipelineStatus.step1 === 'loading' || pipelineStatus.step1 === 'success') pct = (progress * 0.3);
                                return Math.round(pct);
                              })()}%</span>
                            </div>
                            <div className="h-1.5 bg-dark-300 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full bg-gradient-to-r from-gold-500 to-amber-500"
                                initial={{ width: 0 }}
                                animate={{
                                  width: `${(() => {
                                    let pct = 0;
                                    if (pipelineStatus.step3 === 'success') pct = 100;
                                    else if (pipelineStatus.step3 === 'loading') pct = 60 + (progress * 0.4);
                                    else if (pipelineStatus.step2 === 'loading' || pipelineStatus.step2 === 'success') pct = 30 + (progress * 0.3);
                                    else if (pipelineStatus.step1 === 'loading' || pipelineStatus.step1 === 'success') pct = (progress * 0.3);
                                    return Math.round(pct);
                                  })()}%`
                                }}
                                transition={{ duration: 0.5 }}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {pipelineStatus.step3 === 'loading' && (
                              <button
                                onClick={() => setActiveTab('projects')}
                                className="px-4 py-2 rounded-xl bg-gold-500/10 hover:bg-gold-500/20 text-gold-400 text-[11px] font-bold uppercase tracking-wide border border-gold-500/20 transition-all flex items-center gap-2"
                              >
                                View in Files <ArrowRight size={12} />
                              </button>
                            )}
                            {pipelineStatus.step3 === 'success' && (
                              <div className="flex items-center gap-2">
                                <span className="hidden sm:flex items-center gap-1 text-xs font-bold text-emerald-500">
                                  <CheckCircle2 size={14}/> Selesai!
                                </span>
                                <button
                                  onClick={() => { setIsPipelineRunning(false); setActiveTab('dashboard'); }}
                                  className="px-4 py-2 rounded-xl bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-200 font-bold text-xs flex items-center gap-1.5 transition-all"
                                >
                                  <Home size={12} /> Home
                                </button>
                                <button
                                  onClick={() => { setIsPipelineRunning(false); setActiveTab('projects'); }}
                                  className="px-4 py-2 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-xs flex items-center gap-1.5 transition-all shadow-gold"
                                >
                                  Lihat Hasil Klip <ArrowRight size={12} />
                                </button>
                              </div>
                            )}
                            {pipelineError && (
                              <button
                                onClick={() => setIsPipelineRunning(false)}
                                className="p-2 rounded-xl bg-cream-200 dark:bg-dark-300 text-cream-500 dark:text-cream-400 hover:text-dark-200 dark:hover:text-white transition-all"
                                title="Dismiss"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Platform chips — below search bar, act as mode selector */}
              <div className={`flex items-center gap-2 mt-3 mb-8 flex-wrap ${isPipelineRunning ? 'opacity-40 pointer-events-none' : ''}`}>
                {/* YouTube chip */}
                <button
                  disabled={isPipelineRunning}
                  onClick={() => { setPipelineMode('youtube'); setPipelineUploadedAsset(null); setShowAssetPicker(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-bold transition-all ${pipelineMode === 'youtube' ? 'bg-red-500/15 border-red-500/40 text-red-500' : 'bg-cream-200 dark:bg-dark-200 border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400 hover:border-red-400/40 hover:text-red-400'}`}
                >
                  <img src={brandIcon('play-button', darkMode)} className="w-3 h-3 object-contain" alt="" /> YouTube
                </button>
                {/* Local File chip — selects mode, Generate button then picks file */}
                <button
                  disabled={isPipelineRunning}
                  onClick={() => { setPipelineMode('upload'); setPipelineUploadedAsset(null); setShowAssetPicker(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-bold transition-all ${pipelineMode === 'upload' && !pipelineUploadedAsset?.fromLibrary ? 'bg-gold-500/15 border-gold-500/40 text-gold-500' : 'bg-cream-200 dark:bg-dark-200 border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400 hover:border-gold-400/40 hover:text-gold-500'}`}
                >
                  <FolderOpen size={12} /> Local File
                </button>
                {/* Asset Library chip — pick from already-uploaded assets */}
                <div className="relative">
                  <button
                    disabled={isPipelineRunning}
                    onClick={() => { setShowAssetPicker(p => !p); }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-bold transition-all ${pipelineUploadedAsset?.fromLibrary ? 'bg-amber-500/15 border-amber-500/40 text-amber-500' : showAssetPicker ? 'bg-amber-500/10 border-amber-500/30 text-amber-500' : 'bg-cream-200 dark:bg-dark-200 border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400 hover:border-amber-400/40 hover:text-amber-500'}`}
                  >
                    <ImageIcon size={10} /> Asset Library {assets.filter(a => a.type === 'video').length > 0 && <span className="opacity-60">({assets.filter(a => a.type === 'video').length})</span>}
                  </button>
                  {showAssetPicker && (
                    <div className="absolute top-full left-0 mt-1.5 z-50 w-64 bg-cream-50 dark:bg-dark-400 border border-cream-200 dark:border-dark-200 rounded-xl shadow-2xl overflow-hidden">
                      {assets.filter(a => a.type === 'video').length === 0 ? (
                        <div className="px-4 py-6 text-center text-[11px] text-cream-400 dark:text-cream-400">
                          No video assets yet.<br />Upload a file first.
                        </div>
                      ) : (
                        <div className="max-h-52 overflow-y-auto py-1">
                          {assets.filter(a => a.type === 'video').map(asset => (
                            <button
                              key={asset.id}
                              onClick={() => { setPipelineMode('upload'); setPipelineUploadedAsset({ ...asset, fromLibrary: true }); setShowAssetPicker(false); }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-amber-500/10 transition-all text-left group"
                            >
                              <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                                <ImageIcon size={12} className="text-amber-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-semibold text-dark-200 dark:text-cream-100 truncate">{asset.name || asset.filename}</div>
                                <div className="text-[11px] text-cream-400 dark:text-cream-400">{asset.hasSubtitle ? '✓ Transcribed' : 'Not transcribed'}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cream-200 dark:bg-dark-200 border border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400 text-[11px] font-bold cursor-not-allowed opacity-30">
                  <Play size={10} /> Twitch <span className="text-[11px] opacity-60 ml-0.5">soon</span>
                </span>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cream-200 dark:bg-dark-200 border border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400 text-[11px] font-bold cursor-not-allowed opacity-30">
                  <Mic size={10} /> TikTok <span className="text-[11px] opacity-60 ml-0.5">soon</span>
                </span>
              </div>

              {/* Quick Tiles */}
              <AnimatePresence>
                {!isPipelineRunning && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full max-w-4xl"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-6 mb-16 w-full">
                      {[
                        { id: 'tool-a', name: 'Download', img: '/brand/retro/tile-download.jpg' },
                        { id: 'tool-b', name: 'AI Analysis', img: '/brand/retro/tile-ai.jpg' },
                        { id: 'tool-c', name: 'Clipper', img: '/brand/retro/tile-clipper.jpg' },
                        { id: 'projects', name: 'My Files', img: '/brand/retro/tile-files.jpg' },
                        { id: 'tool-d', name: 'Subtitle', img: '/brand/retro/tile-subtitle.jpg' },
                        { id: 'assets', name: 'Assets', img: '/brand/retro/tile-assets.jpg' },
                        { id: 'tool-f', name: 'Watermark', img: '/brand/retro/tile-watermark.jpg' },
                        { id: 'tool-g', name: 'Silence', img: '/brand/retro/tile-silence.jpg' },
                        { id: 'tool-h', name: 'B-Roll', img: '/brand/retro/tile-broll.jpg' },
                      ].map(tile => (
                        <motion.button
                          key={tile.name}
                          layoutId={`tab-${tile.id}`}
                          onClick={() => setActiveTab(tile.id)}
                          className="flex flex-col items-center gap-2 md:gap-3 dark:bg-dark-400/40 bg-cream-100 p-2.5 md:p-3 rounded-2xl md:rounded-[1.75rem] border dark:border-dark-300 border-cream-300 hover:shadow-2xl hover:border-gold-500/60 transition-all group overflow-hidden"
                        >
                          <div className="w-full aspect-square rounded-xl md:rounded-2xl overflow-hidden ring-1 ring-cream-300/70 dark:ring-dark-100 group-hover:ring-gold-500/50 transition-all">
                            <img src={tile.img} alt={tile.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          </div>
                          <span className="font-retro text-[11px] md:text-sm font-bold uppercase tracking-tight md:tracking-wide text-dark-200 dark:text-cream-200 text-center pb-1">{tile.name}</span>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>




              {/* Qlipper Daily */}

              <AnimatePresence>
                {!isPipelineRunning && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full mt-24"
                  >
                    <div className="flex items-center justify-between mb-8 ml-2">
                      <div className="flex items-center gap-2">
                        <Activity size={16} className="text-amber-500" />
                        <h3 className="text-sm font-bold uppercase tracking-widest text-cream-500">Qlipper Daily</h3>
                      </div>
                      <a href="https://medium.com/@qlipper.ai" target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold text-cream-400 hover:text-white transition-colors">VIEW ALL</a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {feedItems.length > 0 ? (
                        feedItems.slice(0, 3).map((item, idx) => (
                          <ArticleCard key={idx} item={item} idx={idx} />
                        ))
                      ) : (
                        Array(3).fill(0).map((_, i) => (
                          <div key={i} className="h-[300px] dark:bg-dark-400/20 bg-cream-100 rounded-[2rem] border dark:border-dark-300/50 animate-pulse" />
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* YOUTUBE DOWNLOAD */}
          {activeTab === 'tool-a' && (
            <motion.div
              key="a" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8"
            >
              <div className="space-y-8">
                <section className={cardClass}>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <h2 className="text-xl font-bold tracking-tight">YouTube Download</h2>
                  </div>
                  <div className="space-y-6">
                    <input type="text" value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="YouTube URL..." className={inputClass} />
                    <button onClick={() => fetchMetadata()} disabled={loadingMetadata} className={`${btnPrimary} w-full`}>
                      {loadingMetadata ? <Loader2 className="animate-spin" /> : 'Fetch Metadata'}
                    </button>
                  </div>
                </section>

                <AnimatePresence>
                  {metadata && (
                    <motion.section initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={cardClass}>
                      <div className="flex items-center gap-3 mb-6"><div className="w-3 h-3 rounded-full bg-gold-500" /><h2 className="text-lg font-bold">Download Options</h2></div>
                      <div className="space-y-6">
                        {/* Save Mode Toggle */}
                        <div className="flex items-center justify-between bg-gradient-to-r from-gold-500/10 to-amber-500/10 p-4 rounded-2xl border border-gold-500/20">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gold-500/20 flex items-center justify-center">
                              <Briefcase className="w-5 h-5 text-gold-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gold-500">Save to Asset Library</p>
                              <p className="text-xs text-cream-500">Auto-transcribe & use with AI Analysis</p>
                            </div>
                          </div>
                          <div
                            className={`w-12 h-7 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${toolASaveToAssets ? 'bg-gold-500' : 'bg-gray-600'}`}
                            onClick={() => setToolASaveToAssets(!toolASaveToAssets)}
                          >
                            <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${toolASaveToAssets ? 'translate-x-5' : ''}`}></div>
                          </div>
                        </div>

                        <select value={selectedFormat} onChange={(e) => setSelectedFormat(e.target.value)} className={inputClass}>
                          {metadata.formats.map(f => (
                            <option key={f.format_id} value={f.format_id}>
                              {f.resolution} — {f.note || f.ext}
                            </option>
                          ))}
                        </select>

                        <button onClick={handleDownload} disabled={downloading || toolATranscribing} className={`${btnPrimary} w-full relative overflow-hidden`}>
                          {downloading || toolATranscribing ? (
                            <>
                              {toolASaveToAssets && <div className="absolute inset-0 bg-gold-700/50" style={{ width: `${toolAProgress}%`, transition: 'width 0.3s ease' }} />}
                              <span className="relative z-10 flex items-center gap-2">
                                <Loader2 className="animate-spin" size={18} />
                                {toolATranscribing ? 'Transcribing...' : `Downloading... ${toolAProgress}%`}
                              </span>
                            </>
                          ) : (
                            <>
                              <Download size={18} />
                              <span>{toolASaveToAssets ? 'Download & Transcribe' : 'Download to Device'}</span>
                            </>
                          )}
                        </button>

                        {/* Progress Status */}
                        {(downloading || toolATranscribing) && toolASaveToAssets && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 rounded-xl bg-gold-500/10 border border-gold-500/20"
                          >
                            <div className="flex items-center gap-2 text-xs font-bold text-gold-400">
                              <Loader2 className="animate-spin" size={14} />
                              {toolAStatus}
                            </div>
                            <div className="h-2 bg-dark-400 rounded-full overflow-hidden mt-3">
                              <motion.div className="h-full bg-gold-500" animate={{ width: `${toolAProgress}%` }} />
                            </div>
                          </motion.div>
                        )}

                        {/* Success State */}
                        {downloadResult && !downloading && !toolATranscribing && toolASaveToAssets && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-bold text-emerald-400">Ready for AI Analysis!</p>
                                <p className="text-xs text-cream-500 mt-1">Video saved & transcribed in Asset Library</p>
                              </div>
                              <button
                                onClick={() => setActiveTab('tool-b')}
                                className="px-4 py-2 bg-emerald-500 text-white text-xs font-bold rounded-xl hover:bg-emerald-400 transition-all flex items-center gap-2"
                              >
                                <Zap size={14} /> Go to AI Analysis
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.section>
                  )}
                </AnimatePresence>
              </div>

              <div className={`${cardClass} flex flex-col items-center justify-center min-h-[500px]`}>
                {metadata ? (
                  <div className="w-full space-y-6">
                    <div className="aspect-video bg-black rounded-[2rem] overflow-hidden border dark:border-dark-300 shadow-2xl relative">
                      {/* YouTube IFrame API Player Container */}
                      <div ref={ytPlayerContainerRef} className="w-full h-full absolute inset-0"></div>

                      {!getYoutubeVideoId(youtubeUrl) && !metadata?.thumbnail && (
                        <div className="w-full h-full flex items-center justify-center text-cream-500">Invalid YouTube URL</div>
                      )}
                    </div>
                    <div className="px-4">
                      <h3 className="text-xl font-bold mb-2">{metadata.title}</h3>
                      <div className="flex items-center gap-4 text-xs font-bold dark:text-cream-400">
                        <span>{metadata.channel}</span>
                        <span className="w-1 h-1 rounded-full bg-dark-200" />
                        <span>{metadata.duration}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="opacity-20 flex flex-col items-center"><Search size={80} className="mb-6" /><p className="font-mono text-sm tracking-widest uppercase text-cream-500">Awaiting Extraction Stream</p></div>
                )}
              </div>
            </motion.div>
          )}

          {/* TOOL B: ANALYZE */}
          {activeTab === 'tool-b' && (
            <motion.div
              key="b" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8"
            >
              <section className={cardClass}>
                <div className="flex items-center gap-3 mb-8"><div className="w-3 h-3 rounded-full bg-gold-500" /><h2 className="text-xl font-bold tracking-tight">AI Viral Moment Lab</h2></div>
                <div className="space-y-6">
                  {/* Source Mode Toggle */}
                  <div>
                    <label className={labelClass}>Video Source</label>
                    <div className="flex bg-cream-100 dark:bg-dark-600 p-1 rounded-2xl gap-1">
                      <button
                        onClick={() => setToolBSourceMode('url')}
                        className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all flex items-center justify-center gap-2 ${toolBSourceMode === 'url' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}
                      >
                        <Link2 size={14} /> YouTube URL
                      </button>
                      <button
                        onClick={() => setToolBSourceMode('asset')}
                        className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all flex items-center justify-center gap-2 ${toolBSourceMode === 'asset' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}
                      >
                        <Briefcase size={14} /> Asset Library
                      </button>
                    </div>
                  </div>

                  {/* URL Input (when URL mode) */}
                  {toolBSourceMode === 'url' && (
                    <div>
                      <label className={labelClass}>YouTube URL</label>
                      <input type="text" value={toolBUrl || youtubeUrl} onChange={(e) => setToolBUrl(e.target.value)} placeholder="YouTube URL..." className={inputClass} />
                      {/* Auto-fetched metadata preview */}
                      {toolBLoadingMeta && (
                        <div className="mt-2 p-3 rounded-xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 border-cream-200 animate-pulse">
                          <div className="flex items-center gap-3">
                            <div className="w-20 h-11 bg-dark-200 rounded-lg flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <div className="h-3 bg-dark-200 rounded w-3/4" />
                              <div className="h-2 bg-dark-200 rounded w-1/2" />
                            </div>
                          </div>
                        </div>
                      )}
                      {toolBMetadata && !toolBLoadingMeta && (
                        <div className="mt-2 p-3 rounded-xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 border-cream-200">
                          <div className="flex items-center gap-3">
                            {toolBMetadata.thumbnail && (
                              <img src={toolBMetadata.thumbnail} alt="" className="w-20 h-11 object-cover rounded-lg flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold truncate">{toolBMetadata.title}</p>
                              <div className="flex items-center gap-2 text-[11px] text-cream-500">
                                {toolBMetadata.channel && <span>{toolBMetadata.channel}</span>}
                                {toolBMetadata.duration && <span>• {toolBMetadata.duration}</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Asset Selector (when Asset mode) */}
                  {toolBSourceMode === 'asset' && (
                    <div>
                      <label className={labelClass}>Select Asset</label>
                      {assets.filter(a => a.type === 'video').length === 0 ? (
                        <div className="p-6 text-center border-2 border-dashed dark:border-dark-300 rounded-2xl">
                          <Briefcase className="w-8 h-8 mx-auto mb-2 text-cream-500" />
                          <p className="text-xs text-cream-500 mb-2">No video assets found.</p>
                          <button onClick={() => setActiveTab('tool-a')} className="text-gold-500 font-bold text-xs hover:underline">Download from YouTube first</button>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                          {assets.filter(a => a.type === 'video').map(asset => (
                            <div
                              key={asset.id}
                              onClick={() => setToolBSelectedAsset(asset)}
                              className={`p-3 rounded-xl border flex items-center gap-3 cursor-pointer transition-all ${toolBSelectedAsset?.id === asset.id
                                ? 'bg-gold-500/10 border-gold-500'
                                : 'dark:bg-dark-400 bg-cream-100 border-transparent hover:border-dark-100'
                                }`}
                            >
                              <div className="w-16 h-9 bg-black rounded-lg overflow-hidden flex-shrink-0">
                                <video src={`${API_BASE}/${asset.path}#t=1`} className="w-full h-full object-cover pointer-events-none" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold truncate">{asset.title || asset.filename}</p>
                                <div className="flex items-center gap-2 text-[11px] text-cream-500">
                                  {asset.duration && <span>{asset.duration}</span>}
                                  {asset.transcriptionPath ? (
                                    <span className="text-emerald-500 flex items-center gap-1"><Check size={10} /> Transcribed</span>
                                  ) : (
                                    <span className="text-amber-500">No transcript</span>
                                  )}
                                </div>
                              </div>
                              {toolBSelectedAsset?.id === asset.id && (
                                <div className="w-5 h-5 bg-gold-500 rounded-full flex items-center justify-center">
                                  <Check size={12} className="text-white" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Model Picker */}
                  <div>
                    <label className={labelClass}>AI Model</label>
                    {(() => {
                      const modelMeta = { 'gemini': { name: 'Gemini', icon: '✨' }, 'gpt-4o': { name: 'GPT-5.4', icon: '🧠' }, 'claude': { name: 'Claude', icon: '🎭' }, 'mistral': { name: 'Mistral', icon: '🌊' }, 'deepseek': { name: 'DeepSeek', icon: '🐋' }, 'xai': { name: 'Grok', icon: '⚡' }, 'router': { name: 'Router / API lain', icon: '🔀' } };
                      const active = modelMeta[selectedModel];
                      const hasKey = selectedModel === 'router' ? !routerMissing : selectedModel === 'gemini' ? !!apiKey : selectedModel === 'gpt-4o' ? !!openaiKey : selectedModel === 'claude' ? !!claudeKey : selectedModel === 'mistral' ? !!mistralKey : selectedModel === 'minimax' ? !!minimaxKey : selectedModel === 'xai' ? !!xaiKey : !!deepseekKey;
                      return (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-50">
                          <div className="flex items-center gap-2">
                            <span>{active?.icon || '✨'}</span>
                            <span className="text-sm font-bold text-gold-500">{active?.name || 'Gemini'}</span>
                          </div>
                          {!hasKey ? (
                            <button onClick={() => setShowSettings(true)} className="flex items-center gap-1 text-[11px] text-amber-500 font-bold hover:underline">
                              <AlertCircle size={11} /> Set key
                            </button>
                          ) : (
                            <button onClick={() => setShowSettings(true)} className="text-[11px] text-gold-500 hover:text-gold-400 font-bold">Change →</button>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex bg-cream-100 dark:bg-dark-600 p-1 rounded-2xl gap-1">
                    <button onClick={() => setAnalyzeMode('auto')} className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all ${analyzeMode === 'auto' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}>Auto Mode</button>
                    <button onClick={() => setAnalyzeMode('advanced')} className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all ${analyzeMode === 'advanced' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}>Advanced Mode</button>
                  </div>

                  {analyzeMode === 'advanced' ? (
                    <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="col-span-1">
                        <label className={labelClass}>Cuts per Clip</label>
                        <select value={advNumCuts} onChange={(e) => setAdvNumCuts(Number(e.target.value))} className={inputClass}>
                          {[1, 2, 3, 4, 5, 6, 10].map(n => <option key={n} value={n}>{n} Cuts</option>)}
                        </select>
                      </div>
                      <div className="col-span-1">
                        <label className={labelClass}>Max Cut Duration</label>
                        <select value={advMaxDuration} onChange={(e) => setAdvMaxDuration(Number(e.target.value))} className={inputClass}>
                          {[5, 10, 15, 30, 60].map(n => <option key={n} value={n}>{n} Seconds</option>)}
                        </select>
                      </div>
                      <div className="col-span-1">
                        <label className={labelClass}>Content Focus</label>
                        <select value={advFocus} onChange={(e) => setAdvFocus(e.target.value)} className={inputClass}>
                          {['Viral Hooks', 'Educational Tips', 'Funny Moments', 'Storytelling', 'Action Highlights'].map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <div className="col-span-1">
                        <label className={labelClass}>Vibe & Tone</label>
                        <select value={advVibe} onChange={(e) => setAdvVibe(e.target.value)} className={inputClass}>
                          {['Dynamic & Fast', 'Minimalist', 'Emotional', 'Professional', 'High Energy'].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className={labelClass}>Extraction Logic</label>
                      <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} className={`${inputClass} min-h-[160px] resize-none`} placeholder="E.g. Focus on viral hooks and funny moments..." />
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button id="btn-analyze" onClick={handleAnalyze} disabled={analyzing} className={`${btnPrimary} flex-1`}>
                      {analyzing ? <Loader2 className="animate-spin" /> : <><Sparkles size={18} /> AI Analysis</>}
                    </button>
                    <button
                      onClick={async () => {
                        const isAssetMode = toolBSourceMode === 'asset';
                        const targetUrl = toolBUrl || youtubeUrl;
                        const hasUrl = targetUrl && /youtube\.com|youtu\.be/i.test(targetUrl);

                        // Confirm for asset mode — user needs to upload video/VTT themselves in Gemini
                        if (isAssetMode || !hasUrl) {
                          if (!confirm('You will need to upload your video file or VTT transcript manually in Gemini after pasting the prompt. Continue?')) return;
                        }

                        const sourceLine = hasUrl
                          ? `Analisis video YouTube berikut ${targetUrl} dan ekstrak momen-momen terbaik untuk dijadikan klip pendek (Reels/TikTok/Shorts).`
                          : `Analisis video yang saya upload di chat ini dan ekstrak momen-momen terbaik untuk dijadikan klip pendek (Reels/TikTok/Shorts). PENTING: Upload file video atau file VTT transkrip kamu di chat Gemini ini sebelum melanjutkan.`;

                        const prompt = `Role: Kamu adalah seorang Ahli Strategi Konten Media Sosial dan Editor Video Viral yang spesialis dalam mengubah konten durasi panjang (long-form) menjadi konten pendek (short-form) yang sangat relatable dan berpotensi viral.

Task: ${sourceLine}

Steps to Follow:
1. Deep Analysis: Baca transkrip video secara utuh. Cari poin-poin yang mengandung emotional hooks, relatability, actionable advice, dan unique terminology.
2. Clip Selection: Pilih 5 klip terbaik. Setiap klip diperbolehkan melakukan jump-cut (menggabungkan beberapa segmen waktu yang berbeda) agar pesannya padat dan tidak membosankan.
3. Duration Calculation: Hitung durasi setiap segmen dan total durasi untuk setiap klip.
4. Format Output: Berikan hasil akhir dalam format JSON yang rapi.

JSON Structure Requirement:
{
  "source_info": {
    "video_title": "Judul asli video",
    "youtube_url": "${hasUrl ? targetUrl : ''}",
    "original_video_duration": "Total durasi video asli",
    "channel_name": "Nama Channel"
  },
  "video_clips": [
    {
      "clip_id": 1,
      "topic": "Judul topik yang menarik",
      "highlight": "Penjelasan singkat mengapa momen ini viral",
      "total_duration_seconds": 30,
      "total_duration_formatted": "00:30",
      "timelines": [
        { "start": "mm:ss", "end": "mm:ss", "duration": 30 }
      ]
    }
  ]
}

Constraint: Pastikan setiap klip memiliki alur: HOOK -> PROBLEM -> SOLUTION/VALUE. Jangan memotong kalimat di tengah kata.`;

                        try {
                          await navigator.clipboard.writeText(prompt);
                          addLog('SYSTEM: Prompt copied to clipboard! Just paste (Cmd+V) in Gemini.', 'success');

                          setGeminiCopyDone(true);
                          setTimeout(() => {
                            window.open(`https://gemini.google.com/app`, '_blank');
                            setTimeout(() => setGeminiCopyDone(false), 1000);
                          }, 500);
                        } catch (err) {
                          addLog('ERROR: Could not copy prompt automatically.', 'error');
                          window.open(`https://gemini.google.com/app`, '_blank');
                        }
                      }}
                      className={`p-4 rounded-2xl text-white transition-all flex items-center justify-center gap-2 group relative ${geminiCopyDone ? 'bg-emerald-600' : 'bg-dark-300 hover:bg-dark-200'}`}
                      title="Copy Prompt & Open Gemini"
                    >
                      <Copy size={18} />
                      <span className="text-[11px] font-bold uppercase hidden md:inline">{geminiCopyDone ? 'Prompt Copied!' : 'Copy & Open Gemini'}</span>
                    </button>
                  </div>
                </div>
              </section>

              <div className={`${cardClass} flex flex-col p-6`}>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-gold-500">Analysis Results</h3>
                  <div className="flex gap-2">
                    <button disabled={!analysisResult} className={`text-[11px] font-bold transition-all ${analysisResult ? 'text-gold-400 hover:text-gold-500' : 'text-cream-400 opacity-40 cursor-not-allowed'}`} onClick={() => analysisResult && navigator.clipboard.writeText(JSON.stringify(analysisResult, null, 2))}>COPY DATA</button>
                  </div>
                </div>
                <div className="flex-1 dark:bg-dark-600 bg-cream-100 rounded-3xl border dark:border-dark-300 p-6 overflow-hidden">
                  {analysisResult ? (
                    <pre className="font-mono text-xs dark:text-gold-500/80 text-gold-700/80 overflow-auto h-full custom-scrollbar">{JSON.stringify(analysisResult, null, 2)}</pre>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center opacity-10"><Zap size={100} /><p className="font-mono mt-4 text-[11px]">LENS_SCAN_INACTIVE</p></div>
                  )}
                </div>

                {/* Send to Clipper Button */}
                {analysisResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4"
                  >
                    <button
                      onClick={() => {
                        // Ensure the Clipper has a video source:
                        // - Asset mode: toolBSelectedAsset is already set, also mirror to videoFile
                        // - URL mode: if we have downloadResult from the pipeline, use that
                        if (toolBSourceMode === 'asset' && toolBSelectedAsset) {
                          setToolCSelectedAsset(toolBSelectedAsset);
                          setVideoFile(toolBSelectedAsset);
                        } else if (downloadResult?.path) {
                          setVideoFile({ ...downloadResult, name: downloadResult.title || downloadResult.filename });
                        }
                        addLog('SYSTEM: Analysis sent to Clipper! Redirecting...', 'success');
                        setActiveTab('tool-c');
                      }}
                      className={`${btnPrimary} w-full py-4 bg-emerald-600 hover:bg-emerald-500`}
                    >
                      <Scissors size={18} />
                      <span>Send to Clipper & Process</span>
                      <ArrowRight size={16} />
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* ASSETS LIBRARY */}
          {activeTab === 'assets' && (
            <motion.div
              key="assets" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col gap-8 pt-8 h-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight mb-2">Asset Library</h2>
                  <p className="text-sm dark:text-cream-400 text-cream-500">Manage global assets (Intros, Outros, Overlays) for your clips.</p>
                </div>
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleAssetUpload} className="hidden" accept="video/*,image/*" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploadingAsset} className={`${btnPrimary} shadow-gold-500/20 bg-gold-600 hover:bg-gold-500`}>
                    {uploadingAsset ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                    <span>Upload Asset</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 p-1">
                {loadingAssets && Array(5).fill(0).map((_, i) => (
                  <div key={i} className="aspect-square bg-white dark:bg-dark-400 rounded-3xl animate-pulse" />
                ))}

                {!loadingAssets && assets.length === 0 && (
                  <div className="col-span-full h-64 flex flex-col items-center justify-center opacity-30">
                    <ImageIcon size={64} className="mb-4" />
                    <p>No assets uploaded yet</p>
                  </div>
                )}

                {assets.map(asset => (
                  <motion.div
                    layout
                    key={asset.id}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onClick={() => setSelectedAsset(asset)}
                    className="group relative bg-cream-100 dark:bg-dark-400 rounded-3xl border dark:border-dark-300 overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer flex flex-col"
                  >
                    {/* Media Preview (Aspect Ratio Managed via Class) */}
                    <div className="w-full aspect-video bg-black relative">
                      {asset.type === 'video' ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <video src={`${API_BASE}/${asset.path}#t=1`} className="w-full h-full object-cover pointer-events-none" />
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                          <Play className="text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all transform scale-50 group-hover:scale-100" size={48} />
                        </div>
                      ) : (
                        <img src={`${API_BASE}/${asset.path}`} className="w-full h-full object-cover" />
                      )}
                      {/* Labels */}
                      <div className="absolute bottom-2 left-2 flex gap-1">
                        {asset.hasAudio && <div className="bg-black/50 backdrop-blur-md text-white px-1.5 py-0.5 rounded text-[11px] font-bold">AUDIO</div>}
                        {asset.hasSubtitle && <div className="bg-gold-500 text-white px-1.5 py-0.5 rounded text-[11px] font-bold">CC</div>}
                      </div>
                    </div>

                    {/* Simple Footer */}
                    <div className="p-3 bg-white dark:bg-dark-600 flex justify-between items-center">
                      <h4 className="font-bold text-xs truncate max-w-[80%] dark:text-cream-200">{asset.name}</h4>
                      <div className="w-6 h-6 rounded-full bg-cream-100 dark:bg-dark-400 flex items-center justify-center text-cream-400">
                        {asset.type === 'video' ? <FileVideo size={12} /> : <ImageIcon size={12} />}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* ASSET DETAIL POPUP MODAL */}
              <AnimatePresence>
                {selectedAsset && (
                  <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setSelectedAsset(null)}
                      className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
                    />
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0, y: 20 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0.9, opacity: 0, y: 20 }}
                      className="relative w-full max-w-5xl bg-dark-400 border border-dark-300 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh]"
                      onClick={e => e.stopPropagation()}
                    >
                      {/* LEFT: MEDIA PLAYER (60%) */}
                      <div className="w-full md:w-[65%] bg-black flex items-center justify-center relative group">
                        {selectedAsset.type === 'video' ? (
                          <video
                            src={`${API_BASE}/${selectedAsset.path}`}
                            controls
                            autoPlay
                            className="w-full h-full object-contain max-h-[60vh] md:max-h-full"
                          />
                        ) : (
                          <img src={`${API_BASE}/${selectedAsset.path}`} className="w-full h-full object-contain" />
                        )}

                        {/* Top Left Back Button (Mobile) */}
                        <button onClick={() => setSelectedAsset(null)} className="absolute top-4 left-4 p-2 bg-black/50 text-white rounded-full md:hidden">
                          <ChevronLeft size={20} />
                        </button>
                      </div>

                      {/* RIGHT: DETAILS PANEL */}
                      <div className="w-full md:w-[35%] p-6 md:p-8 flex flex-col bg-dark-400/50 border-l border-dark-300">
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <h2 className="text-lg font-bold text-white line-clamp-2 leading-tight mb-2">{selectedAsset.name}</h2>
                            <p className="text-xs text-cream-500 uppercase tracking-widest font-mono">{selectedAsset.type} • {new Date(selectedAsset.timestamp || Date.now()).toLocaleDateString()}</p>
                          </div>
                          <button onClick={() => setSelectedAsset(null)} className="p-2 hover:bg-dark-300 rounded-xl text-cream-400 hover:text-white transition-all">
                            <X size={24} />
                          </button>
                        </div>

                        <div className="flex-1 space-y-4 overflow-y-auto pr-2">
                          {/* PROPERTIES */}
                          <div className="space-y-3">
                            <div className="flex justify-between text-xs py-2 border-b border-dark-300/50">
                              <span className="text-cream-500">File Type</span>
                              <span className="text-cream-300 font-mono">{selectedAsset.filename?.split('.').pop() || 'Unknown'}</span>
                            </div>
                            <div className="flex justify-between text-xs py-2 border-b border-dark-300/50">
                              <span className="text-cream-500">Location</span>
                              <span className="text-cream-300 font-mono truncate max-w-[150px]">/assets</span>
                            </div>
                          </div>

                          {/* TRANSCRIPTION STATUS */}
                          {selectedAsset.type === 'video' && (
                            <div className="p-4 rounded-2xl bg-dark-300/50 border border-dark-200/50 mt-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-xs font-bold uppercase tracking-widest text-cream-400">Audio Intelligence</h4>
                                {selectedAsset.hasSubtitle && (
                                  <span className="text-[11px] text-gold-500 font-bold">READY</span>
                                )}
                              </div>

                              {selectedAsset.hasSubtitle ? (
                                <div className="space-y-3">
                                  {/* Transcription Complete Badge + Engine */}
                                  <div className="flex items-center gap-3 p-3 bg-gold-500/10 rounded-xl border border-gold-500/20">
                                    <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center text-gold-500">
                                      <CheckCircle2 size={16} />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-bold text-gold-400">Transcription Complete</p>
                                      <p className="text-[11px] text-cream-500">Open editor to view, edit, or re-transcribe</p>
                                    </div>
                                  </div>

                                  {/* Download Button */}
                                  <a
                                    href={`${API_BASE}/${selectedAsset.transcriptionPath}`}
                                    download
                                    className="w-full py-2.5 bg-gold-600 hover:bg-gold-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all"
                                  >
                                    <Download size={14} /> Download Transcript
                                  </a>

                                  {/* Open Subtitle Editor Button */}
                                  <button
                                    onClick={toggleTranscriptPanel}
                                    disabled={loadingTranscript}
                                    className="w-full py-2.5 bg-dark-200 hover:bg-dark-100 text-cream-200 rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all"
                                  >
                                    {loadingTranscript ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : showTranscriptPanel ? (
                                      <ChevronUp size={14} />
                                    ) : (
                                      <FileText size={14} />
                                    )}
                                    {showTranscriptPanel ? 'Close Subtitle Editor' : 'Open Subtitle Editor'}
                                  </button>

                                  {/* Use as Source for AI Analysis CTA */}
                                  <button
                                    onClick={() => {
                                      setToolBSourceMode('asset');
                                      setToolBSelectedAsset(selectedAsset);
                                      setSelectedAsset(null);
                                      setActiveTab('tool-b');
                                      addLog(`SYSTEM: Asset "${selectedAsset.name}" loaded for AI Analysis`, 'success');
                                    }}
                                    className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all"
                                  >
                                    <Zap size={14} /> Use as Source for AI Analysis
                                  </button>

                                  {/* Tool chain — send to Watermark/Silence/B-Roll */}
                                  {selectedAsset.type === 'video' && (
                                    <div className="grid grid-cols-3 gap-2">
                                      <button
                                        onClick={() => {
                                          const asset = selectedAsset;
                                          setSelectedAsset(null);
                                          setActiveTab('tool-f');
                                          handleWmExtractFrame({ type: 'asset', path: asset.path, name: asset.name || asset.filename });
                                        }}
                                        className="py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-[11px] font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                                        title="Remove Watermark"
                                      ><Eraser size={12} /> Watermark</button>
                                      <button
                                        onClick={() => {
                                          const asset = selectedAsset;
                                          setSelectedAsset(null);
                                          setActiveTab('tool-g');
                                          setSilSource({ type: 'asset', path: asset.path, name: asset.name || asset.filename });
                                        }}
                                        className="py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-[11px] font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                                        title="Remove Silence"
                                      ><VolumeX size={12} /> Silence</button>
                                      <button
                                        onClick={() => {
                                          const asset = selectedAsset;
                                          setSelectedAsset(null);
                                          setActiveTab('tool-h');
                                          setBrollSource({ type: 'asset', path: asset.path, name: asset.name || asset.filename, duration: asset.duration, hasSubtitle: asset.hasSubtitle });
                                        }}
                                        className="py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-[11px] font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                                        title="Insert B-Roll"
                                      ><ImageIcon size={12} /> B-Roll</button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-dark-200/50 flex items-center justify-center text-cream-500">
                                    <Mic size={16} />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-bold text-cream-400">No Transcript</p>
                                    <p className="text-[11px] text-cream-500">Run transcription to generate subs.</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* EDITABLE TRANSCRIPT PANEL — full width below asset info */}
                          {selectedAsset.type === 'video' && selectedAsset.hasSubtitle && showTranscriptPanel && (
                            <div className="mt-4 rounded-2xl bg-dark-400/70 border border-gold-500/30 overflow-hidden" style={{ maxHeight: '65vh' }}>
                              <EditableTranscript
                                segments={assetTranscriptData?.segments || []}
                                onSave={(segments) => saveTranscriptForAsset(selectedAsset.id, segments)}
                                isSaving={savingTranscript}
                                whisperEngine={whisperEngine}
                                setWhisperEngine={setWhisperEngine}
                                onRetranscribe={() => handleStartTranscribe(selectedAsset)}
                                transcriptMethod={assetTranscriptData?.method}
                              />
                            </div>
                          )}
                        </div>

                        {/* ACTIONS FOOTER */}
                        <div className="mt-8 space-y-3 pt-6 border-t border-dark-300">
                          {/* Transcribe Action */}
                          {selectedAsset.type === 'video' && !selectedAsset.hasSubtitle && (
                            <div className="space-y-3">
                              {transcribingAssets[selectedAsset.id] ? (
                                <div className="p-4 rounded-xl bg-gold-500/10 border border-gold-500/20">
                                  <div className="flex items-center gap-3 mb-2">
                                    <Loader2 className="animate-spin text-gold-500" size={16} />
                                    <span className="text-xs font-bold text-gold-400 uppercase tracking-widest">Processing Audio...</span>
                                  </div>
                                  <div className="h-32 overflow-y-auto bg-slate-950/50 rounded-lg p-3 font-mono text-[11px] text-cream-400 space-y-1 custom-scrollbar">
                                    {transcribingAssets[selectedAsset.id].logs.map((log, i) => (
                                      <div key={i}>{log}</div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleStartTranscribe(selectedAsset)}
                                  className="w-full py-3 bg-gold-600 hover:bg-gold-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all shadow-gold-lg/20"
                                >
                                  <Mic size={16} /> Transcribe Audio
                                </button>
                              )}
                            </div>
                          )}

                          <div className="flex gap-3">
                            <button onClick={() => requestDeleteAsset(selectedAsset.id)} className="flex-1 py-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all border border-red-500/20 hover:border-red-500">
                              <Trash2 size={16} /> Delete
                            </button>
                            <a href={`${API_BASE}/${selectedAsset.path}`} download className="flex-1 py-3 bg-dark-300 hover:bg-dark-200 text-white rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all">
                              <Download size={16} /> Download
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          )}


          {/* PROJECT LIBRARY PAGE (Vizard Style) */}
          {activeTab === 'projects' && (
            <motion.div
              key="projects" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col lg:flex-row gap-4 md:gap-6 mobile-project-layout pt-4 pb-20 md:pb-4"
            >
              {/* Sidebar List (Projects) */}
              <aside className="w-full lg:w-80 flex flex-col gap-4 mobile-sidebar">
                <div className={`${cardClass} flex-1 overflow-hidden flex flex-col p-4`}>
                  <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-cream-400">Project History</h3>
                    {(processing || loadingHistory) && <Loader2 className="animate-spin text-amber-500" size={14} />}
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-2">
                    {historyProjects.length === 0 && !processing && !loadingHistory && (
                      <div className="h-full flex flex-col items-center justify-center opacity-20 text-center px-6">
                        <Layers size={48} className="mb-4" />
                        <p className="text-[11px] font-bold uppercase tracking-widest">No projects found</p>
                      </div>
                    )}

                    {historyProjects.map((project, idx) => (
                      <div
                        key={project.id}
                        onClick={() => {
                          setSelectedHistoryProject(project);
                          setCaptionEnhanceStatus('idle'); // Reset caption status for new project
                          if (project.clips && project.clips.length > 0) setSelectedProjectClip(project.clips[0]);
                          else setSelectedProjectClip(null);
                        }}
                        className={`w-full text-left p-3 rounded-2xl border transition-all flex items-start gap-3 group cursor-pointer relative ${selectedHistoryProject?.id === project.id ? 'bg-gold-600 border-gold-500' : 'dark:bg-dark-600/50 bg-white border-transparent hover:border-dark-200'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-xs ${selectedHistoryProject?.id === project.id ? 'bg-white/20' : project.status === 'processing' ? 'bg-gold-500/20 text-gold-500 animate-pulse' : 'bg-cream-100 dark:bg-dark-400 text-cream-500'}`}>
                          {project.status === 'processing' ? <Loader2 className="animate-spin" size={14} /> : historyProjects.length - idx}
                        </div>
                        <div className="flex-1 min-w-0 pr-6">
                          <div className={`text-xs font-bold truncate mb-1 ${selectedHistoryProject?.id === project.id ? 'text-white' : 'dark:text-cream-100 text-dark-200'}`}>
                            {project.promptConfig && project.promptConfig.video_clips && project.promptConfig.video_clips.length > 0
                              ? (project.promptConfig.video_clips[0].topic || "Untitled Project")
                              : "Untitled Project"}
                          </div>
                          <div className={`text-[11px] flex items-center gap-1 ${selectedHistoryProject?.id === project.id ? 'text-gold-100' : 'text-cream-300'}`}>
                            {project.status === 'processing' ? (
                              <span className="text-amber-500 animate-pulse font-bold uppercase">
                                Processing ({project.clips?.length || 0}/{project.promptConfig?.video_clips?.length || '?'})...
                              </span>
                            ) : (
                              <>
                                <Clock size={10} className="text-gold-500" />
                                {new Date(project.timestamp).toLocaleDateString()}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Delete Button (Hover Only) */}
                        <button
                          onClick={(e) => handleDeleteProject(e, project.id, project.promptConfig?.source_info?.video_title || project.promptConfig?.video_title || 'Untitled Project')}
                          className={`absolute right-2 top-2 p-1.5 rounded-lg transition-all opacity-0 group-hover:opacity-100 z-10 ${selectedHistoryProject?.id === project.id ? 'bg-gold-500 text-white hover:bg-red-500' : 'bg-dark-300 text-cream-500 hover:bg-red-500 hover:text-white'}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Main Workspace (Vizard-style Feed) */}
              <main className="flex-1 flex flex-col gap-6 overflow-hidden">
                <div className="flex-1 overflow-y-auto custom-scrollbar px-2 space-y-6 pb-20">
                  {selectedHistoryProject ? (
                    <>
                      {/* SOURCE VIDEO CARD */}
                      {/* SOURCE VIDEO CARD (Expanded) */}
                      {selectedHistoryProject.sourceVideo && (
                        <div className="bg-dark-400 rounded-[2rem] border border-dark-300 p-2 shadow-2xl overflow-hidden group mb-8">
                          {/* HEADER */}
                          <div className="px-6 py-4 flex items-center justify-between border-b border-dark-300/50 mb-2">
                            <div>
                              <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-3">
                                <div className="bg-gold-600 rounded-lg p-1.5"><FileVideo size={16} className="text-white" /></div>
                                {selectedHistoryProject.promptConfig?.source_info?.video_title || selectedHistoryProject.promptConfig?.video_title || "Original Source Video"}
                              </h3>
                              <p className="text-xs text-cream-500 font-medium pl-11 flex items-center gap-2">
                                <span className="text-gold-400 uppercase tracking-widest">Master File</span>
                                <span>•</span>
                                <span>{selectedHistoryProject.promptConfig?.source_info?.channel_name || "YouTube"}</span>
                              </p>
                            </div>
                            <a
                              href={selectedHistoryProject.sourceVideo.startsWith('http') ? selectedHistoryProject.sourceVideo : `${API_BASE}/${selectedHistoryProject.sourceVideo}`}
                              download
                              className="px-5 py-2.5 bg-gold-600 hover:bg-gold-500 text-white rounded-xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-gold-lg/20 transition-all hover:scale-105 active:scale-95"
                            >
                              <Download size={14} /> Download Original
                            </a>
                          </div>

                          {/* MAIN PLAYER AREA */}
                          <div className="w-full aspect-video bg-black relative rounded-3xl overflow-hidden shadow-inner border border-dark-300">
                            <FeedClipPlayer
                              url={`${selectedHistoryProject.sourceVideo.startsWith('http') ? selectedHistoryProject.sourceVideo : `${API_BASE}/${selectedHistoryProject.sourceVideo}`}`}
                            />
                          </div>
                        </div>
                      )}

                      {selectedHistoryProject.clips.map((clip, idx) => {
                        const isReprocessing = reprocessingClips[idx];
                        const editState = clipEditState[idx] || {};
                        const currentHookText = editState.hookText ?? clip.customHook ?? '';
                        const currentFormat = editState.format ?? clip.outputFormat ?? 'stacked-blur';

                        return (
                          <div key={`${clip.url}-${idx}`} className={`bg-white dark:bg-dark-400 rounded-2xl border dark:border-dark-300 p-4 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-6 group ${isReprocessing ? 'opacity-60' : ''}`}>
                            {/* LEFT: Video Thumbnail - aspect ratio adapts to clip format */}
                            <div className={`w-full relative flex-shrink-0 cursor-pointer mx-auto md:mx-0 ${currentFormat === 'raw-cuts' ? 'md:w-64 aspect-video' :
                              currentFormat === 'square-blur' || currentFormat === 'square-zoom' ? 'md:w-48 aspect-square' :
                                'md:w-48 aspect-[9/16] max-h-[320px] md:max-h-none'
                              }`}>
                              {isReprocessing ? (
                                <div className="w-full h-full bg-dark-300 rounded-xl flex flex-col items-center justify-center gap-3">
                                  <Loader2 size={32} className="animate-spin text-gold-500" />
                                  <span className="text-xs font-bold text-gold-400 uppercase tracking-widest">Re-rendering...</span>
                                </div>
                              ) : (
                                <FeedClipPlayer
                                  url={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}?v=${clip.reprocessedAt || 'v1'}#t=0.1`}
                                />
                              )}
                            </div>

                            {/* CENTER: Metadata */}
                            <div className="flex-1 flex flex-col gap-4 min-w-0">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="text-xl font-bold leading-tight mb-1">#{idx + 1} {clip.customTitle || clip.topic}</h3>
                                  <div className="flex items-center gap-3 text-xs font-medium text-cream-500">
                                    <span className="flex items-center gap-1"><Clock size={12} /> {(() => {
                                      const dur = clip.duration || clip.total_duration_seconds;
                                      if (!dur) return '--:--';
                                      const mins = Math.floor(dur / 60);
                                      const secs = Math.floor(dur % 60);
                                      return `${mins}:${secs.toString().padStart(2, '0')}`;
                                    })()}</span>
                                    <span>•</span>
                                    <span className="uppercase tracking-wider px-2 py-0.5 rounded bg-cream-100 dark:bg-dark-300">{
                                      currentFormat === 'raw-cuts' ? 'Original' :
                                        currentFormat === 'stacked-blur' ? '9:16 Stacked' :
                                          currentFormat === 'center-crop' ? '9:16 Zoomed' :
                                            currentFormat === 'square-blur' ? '1:1 Square' :
                                              currentFormat === 'square-zoom' ? '1:1 Zoomed' :
                                                '9:16 Portrait'
                                    }</span>
                                    {clip.language && (
                                      <span className="uppercase tracking-wider px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold">{clip.language}</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {clip.description && (
                                <p className="text-sm text-cream-500 dark:text-cream-400 leading-relaxed">{clip.description}</p>
                              )}

                              <div className="bg-cream-100 dark:bg-dark-300/50 p-4 rounded-xl border dark:border-dark-200/50 text-sm leading-relaxed">
                                <span className="text-[11px] font-bold uppercase tracking-widest text-gold-500 mb-1 block">Highlight Reason</span>
                                "{clip.highlight}"
                              </div>

                              {/* Hashtag Suggestions */}
                              {clip.hashtags && (
                                <details className="bg-cream-100 dark:bg-dark-300/50 rounded-xl border dark:border-dark-200/50 overflow-hidden">
                                  <summary className="px-4 py-3 cursor-pointer text-[11px] font-bold uppercase tracking-widest text-gold-500 hover:bg-cream-100 dark:hover:bg-dark-200/50 transition-colors flex items-center justify-between">
                                    <span>Hashtag Suggestions</span>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const allTags = [
                                          ...(clip.hashtags.mega || []),
                                          ...(clip.hashtags.macro || []),
                                          ...(clip.hashtags.micro || []),
                                          ...(clip.hashtags.nano || [])
                                        ].join(' ');
                                        navigator.clipboard.writeText(allTags);
                                        addLog('Hashtags copied to clipboard!', 'success');
                                      }}
                                      className="px-2 py-1 bg-gold-500/10 hover:bg-gold-500/20 text-gold-600 dark:text-gold-400 rounded text-[11px] font-bold transition-all"
                                    >
                                      Copy All
                                    </button>
                                  </summary>
                                  <div className="px-4 pb-3 space-y-2">
                                    {[
                                      { key: 'mega', label: 'Mega (1M+)', color: 'text-red-500 bg-red-50 dark:bg-red-900/20' },
                                      { key: 'macro', label: 'Macro (100K+)', color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20' },
                                      { key: 'micro', label: 'Micro (10K+)', color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' },
                                      { key: 'nano', label: 'Nano (<10K)', color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' },
                                    ].map(tier => clip.hashtags[tier.key]?.length > 0 && (
                                      <div key={tier.key} className="flex items-start gap-2">
                                        <span className={`text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tier.color} whitespace-nowrap`}>{tier.label}</span>
                                        <div className="flex flex-wrap gap-1">
                                          {clip.hashtags[tier.key].map((tag, i) => (
                                            <span key={i} className="text-xs text-cream-500 dark:text-cream-400">{tag}</span>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}

                              {/* SINGLE EDIT BUTTON - Opens Full Edit Modal */}
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  onClick={() => setClipEditModal({ isOpen: true, type: 'full', clipIdx: idx })}
                                  className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all bg-cream-100 dark:bg-dark-300 hover:bg-cream-200 dark:hover:bg-dark-200 text-dark-200 dark:text-cream-200"
                                  title="Edit Video"
                                >
                                  <Settings size={14} /> Edit Video
                                </button>

                                {/* NEW: Change Video Style Button */}
                                <button
                                  onClick={() => setClipEditModal({ isOpen: true, type: 'style', clipIdx: idx })}
                                  disabled={reprocessingClips[idx]}
                                  className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all bg-purple-600 hover:bg-purple-500 text-white shadow-lg disabled:opacity-50"
                                >
                                  <Palette size={14} /> Change Style
                                </button>

                                {/* Multi-Format Export Button */}
                                <button
                                  onClick={() => {
                                    setMultiFormatSelections([]);
                                    setClipEditModal({ isOpen: true, type: 'multi-format', clipIdx: idx });
                                  }}
                                  disabled={reprocessingClips[idx]}
                                  className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg disabled:opacity-50"
                                >
                                  <Copy size={14} /> Multi-Format
                                </button>
                              </div>

                              {/* Multi-render progress indicator */}
                              {multiRenderProgress?.clipIdx === idx && (
                                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                                  <Loader2 size={14} className="animate-spin text-indigo-500" />
                                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                                    Rendering {multiRenderProgress.current}/{multiRenderProgress.total} formats...
                                  </span>
                                </div>
                              )}


                              <div className="mt-auto pt-4 border-t dark:border-dark-300 flex items-center gap-2 flex-wrap">
                                <button onClick={() => handleOpenFile(clip.url)} className="px-4 py-2 bg-cream-100 dark:bg-dark-300 hover:bg-cream-200 dark:hover:bg-dark-200 text-dark-200 dark:text-cream-200 rounded-lg text-xs font-bold flex items-center gap-2 transition-all">
                                  <FolderOpen size={14} /> Show in Folder
                                </button>
                                <select
                                  value={clipEditState[idx]?.containerFormat || 'mp4'}
                                  onChange={(e) => setClipEditState(prev => ({ ...prev, [idx]: { ...prev[idx], containerFormat: e.target.value } }))}
                                  className="px-2 py-2 bg-cream-100 dark:bg-dark-300 border border-cream-200 dark:border-dark-200 text-dark-200 dark:text-cream-200 rounded-lg text-xs font-bold cursor-pointer"
                                  title="Output container format"
                                >
                                  <option value="mp4">MP4</option>
                                  <option value="mkv">MKV</option>
                                  <option value="mov">MOV</option>
                                  <option value="webm">WebM</option>
                                  <option value="avi">AVI</option>
                                </select>
                                <button
                                  onClick={async () => {
                                    const fmt = clipEditState[idx]?.containerFormat || 'mp4';
                                    if (fmt === 'mp4') {
                                      handleSaveToFiles(clip.url, clip.customTitle || clip.topic);
                                    } else {
                                      try {
                                        addLog(`Remuxing to ${fmt.toUpperCase()}...`, 'info');
                                        const res = await axios.post(`${API_BASE}/api/video/remux`, { clipUrl: clip.url, containerFormat: fmt }, { headers: getLicenseHeaders() });
                                        const link = document.createElement('a');
                                        link.href = `${API_BASE}/api/system/download-file?filePath=${encodeURIComponent(res.data.url)}`;
                                        link.download = res.data.filename;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        addLog(`Download started: ${res.data.filename}`, 'success');
                                      } catch (err) {
                                        addLog(`Remux failed: ${err.response?.data?.error || err.message}`, 'error');
                                      }
                                    }
                                  }}
                                  className="px-4 py-2 bg-gold-600 hover:bg-gold-500 text-white rounded-lg text-xs font-bold flex items-center gap-2 transition-all shadow-sm"
                                >
                                  <Download size={14} /> Export
                                </button>
                              </div>

                              {/* Multi-format variant links */}
                              {(() => {
                                const key = `${selectedHistoryProject.id}_${idx}`;
                                const variants = multiFormatResults[key];
                                if (!variants || variants.length === 0) return null;
                                return (
                                  <div className="mt-2 pt-2 border-t dark:border-dark-300">
                                    <p className="text-[11px] text-cream-400 font-bold uppercase tracking-wider mb-1">Rendered Formats</p>
                                    <div className="flex flex-wrap gap-1">
                                      {variants.map((v, vi) => (
                                        <button
                                          key={vi}
                                          onClick={() => handleSaveToFiles(v.url, `${clip.customTitle || clip.topic}_${v.format}`)}
                                          className="px-2 py-1 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded-lg text-[11px] font-bold ring-1 ring-indigo-500/30 transition-all flex items-center gap-1"
                                        >
                                          <Download size={10} /> {v.format}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* RIGHT: Action Stack */}
                            <div className="w-full md:w-12 flex md:flex-col items-center justify-center gap-2 md:border-l dark:border-dark-300 pl-0 md:pl-2">
                              <button
                                onClick={() => {
                                  const newTitle = prompt('Enter new title:', clip.customTitle || clip.topic);
                                  if (newTitle && newTitle !== (clip.customTitle || clip.topic)) {
                                    updateClipMetadata(selectedHistoryProject.id, idx, { customTitle: newTitle });
                                  }
                                }}
                                className="p-2 rounded-xl hover:bg-cream-100 dark:hover:bg-dark-300 text-cream-400 hover:text-gold-500 transition-all"
                                title="Edit Title"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!confirm(`Delete this clip?\n\n"${clip.customTitle || clip.topic}"\n\nThis cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`${API_BASE}/api/history/${selectedHistoryProject.id}/clips/${idx}`);
                                    addLog(`Clip deleted: ${clip.topic}`, 'success');
                                    fetchHistory();
                                    if (selectedProjectClip?.id === clip.id) setSelectedProjectClip(null);
                                  } catch (err) {
                                    addLog(`Delete failed: ${err.response?.data?.error || err.message}`, 'error');
                                  }
                                }}
                                className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-rose-900/20 text-cream-400 hover:text-red-500 transition-all"
                                title="Delete Clip"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center opacity-10 text-center min-h-[400px]">
                      <Briefcase size={80} className="mb-4" />
                      <p className="font-mono text-sm tracking-widest uppercase">Select a project to view feed</p>
                    </div>
                  )}
                </div>


                {/* Progress Status Bar (Visible only when processing) */}
                {processing && (
                  <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="h-20 dark:bg-gold-950/20 bg-gold-50 border border-gold-500/20 rounded-[2rem] flex items-center px-8 gap-6 shadow-xl">
                    <Loader2 className="animate-spin text-gold-500" />
                    <div className="flex-1 space-y-2">
                      <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-gold-400">
                        <span>Current Phase: {processingStatus}</span>
                        <span>Total Job: {progress}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-200 dark:bg-dark-400 rounded-full overflow-hidden">
                        <div className="h-full bg-gold-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </main>
            </motion.div>
          )}

          {/* TOOL C: CLIPPER */}
          {
            activeTab === 'tool-c' && (
              <motion.div
                key="c" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="flex justify-center pt-8"
              >
                <div className="w-full max-w-2xl space-y-8">
                  <section className={cardClass}>
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <h2 className="text-xl font-bold tracking-tight">Assemble Clips</h2>
                    </div>
                    <div className="space-y-6">
                      {/* Source Mode Toggle */}
                      <div>
                        <label className={labelClass}>Video Source</label>
                        <div className="flex bg-cream-100 dark:bg-dark-600 p-1 rounded-2xl gap-1">
                          <button
                            onClick={() => setToolCSourceMode('asset')}
                            className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all flex items-center justify-center gap-2 ${toolCSourceMode === 'asset' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}
                          >
                            <Briefcase size={14} /> Asset Library
                          </button>
                          <button
                            onClick={() => setToolCSourceMode('upload')}
                            className={`flex-1 py-3 px-4 rounded-xl text-[11px] font-bold uppercase transition-all flex items-center justify-center gap-2 ${toolCSourceMode === 'upload' ? 'bg-gold-600 text-white' : 'dark:text-cream-400 text-cream-400 hover:dark:bg-dark-400 hover:bg-white'}`}
                          >
                            <Upload size={14} /> Upload File
                          </button>
                        </div>
                      </div>

                      {/* Asset Selector (when Asset mode) */}
                      {toolCSourceMode === 'asset' && (
                        <div>
                          <label className={labelClass}>Select Asset</label>
                          {assets.filter(a => a.type === 'video').length === 0 ? (
                            <div className="p-6 text-center border-2 border-dashed dark:border-dark-300 rounded-2xl">
                              <Briefcase className="w-8 h-8 mx-auto mb-2 text-cream-500" />
                              <p className="text-xs text-cream-500 mb-2">No video assets found.</p>
                              <button onClick={() => setActiveTab('tool-a')} className="text-gold-500 font-bold text-xs hover:underline">Download from YouTube first</button>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-[180px] overflow-y-auto custom-scrollbar pr-2">
                              {assets.filter(a => a.type === 'video').map(asset => (
                                <div
                                  key={asset.id}
                                  onClick={() => setToolCSelectedAsset(asset)}
                                  className={`p-3 rounded-xl border flex items-center gap-3 cursor-pointer transition-all ${toolCSelectedAsset?.id === asset.id
                                    ? 'bg-gold-500/10 border-gold-500'
                                    : 'dark:bg-dark-400 bg-cream-100 border-transparent hover:border-dark-100'
                                    }`}
                                >
                                  <div className="w-16 h-9 bg-black rounded-lg overflow-hidden flex-shrink-0">
                                    <video src={`${API_BASE}/${asset.path}#t=1`} className="w-full h-full object-cover pointer-events-none" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold truncate">{asset.title || asset.filename}</p>
                                    <p className="text-[11px] text-cream-500">{asset.duration || 'Video'}</p>
                                  </div>
                                  {toolCSelectedAsset?.id === asset.id && (
                                    <div className="w-5 h-5 bg-gold-500 rounded-full flex items-center justify-center">
                                      <Check size={12} className="text-white" />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Show linked asset from Tool B */}
                          {videoFile && videoFile.path && !toolCSelectedAsset && (
                            <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
                              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                <Check size={12} className="text-white" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-emerald-400">Linked from AI Analysis</p>
                                <p className="text-[11px] text-cream-500">{videoFile.filename || videoFile.name}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* File Upload (when Upload mode) */}
                      {toolCSourceMode === 'upload' && (
                        <div>
                          <label className={labelClass}>Upload Video</label>
                          <input type="file" id="lv-f" className="hidden" onChange={(e) => setVideoFile(e.target.files[0])} accept="video/*" />
                          <label htmlFor="lv-f" className={`${inputClass} cursor-pointer flex justify-between`}>
                            <span className="truncate">{videoFile && videoFile instanceof File ? videoFile.name : 'Choose .mp4'}</span>
                            <FileVideo size={16} className="text-gold-500" />
                          </label>
                        </div>
                      )}

                      <div>
                        <FormatPicker
                          selectedFormat={outputFormat}
                          onFormatChange={setOutputFormat}
                          selectedResolution={outputResolution}
                          onResolutionChange={setOutputResolution}
                          compact={false}
                          faceDetectionEnabled={styleSettings.liveFaceDetection || false}
                          faceDetectionMode={styleSettings.faceDetectionMode || 'sekali'}
                          trackingStyle={styleSettings.trackingStyle || 'smooth'}
                          onFaceDetectionChange={(changes) => setStyleSettings(prev => ({
                            ...prev,
                            ...(changes.enabled !== undefined && { liveFaceDetection: changes.enabled }),
                            ...(changes.mode && { faceDetectionMode: changes.mode }),
                            ...(changes.trackingStyle && { trackingStyle: changes.trackingStyle }),
                          }))}
                        />
                      </div>

                      {/* Style Settings Button - ENABLED */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-gold-500/10 to-amber-500/10 p-4 rounded-2xl border border-gold-500/20">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gold-500/20 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-gold-400" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gold-500">Style Settings</p>
                            <p className="text-xs text-cream-500">Hook overlay & format options</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setClipEditModal({ isOpen: true, type: 'full', clipIdx: 'preset' })}
                          className="px-4 py-2 bg-gold-600 hover:bg-gold-500 text-white font-bold text-sm rounded-lg transition-all flex items-center gap-2"
                        >
                          <Palette className="w-4 h-4" />
                          Customize
                        </button>
                      </div>

                      {/* Analysis Selector - Pick from Past Analyses */}
                      <div>
                        <label className={labelClass}>Select Analysis</label>
                        {savedAnalyses.length === 0 ? (
                          <div className="p-4 text-center border-2 border-dashed dark:border-dark-300 rounded-2xl">
                            <Zap className="w-8 h-8 mx-auto mb-2 text-cream-500" />
                            <p className="text-xs text-cream-500 mb-2">No saved analyses yet</p>
                            <button
                              onClick={() => setActiveTab('tool-b')}
                              className="text-gold-500 font-bold text-xs hover:underline"
                            >
                              Go to AI Analysis to create one
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                            {savedAnalyses.map(analysis => (
                              <div
                                key={analysis.id}
                                onClick={() => {
                                  setSelectedAnalysis(analysis);
                                  setPastedJson(JSON.stringify(analysis.data, null, 2));
                                  // If analysis has linked asset, try to select it
                                  if (analysis.assetId) {
                                    const linkedAsset = assets.find(a => a.id === analysis.assetId);
                                    if (linkedAsset) {
                                      setToolCSelectedAsset(linkedAsset);
                                      setVideoFile(linkedAsset);
                                    }
                                  }
                                  addLog(`Loaded analysis: ${analysis.title}`, 'success');
                                }}
                                className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedAnalysis?.id === analysis.id
                                  ? 'bg-gold-500/10 border-gold-500'
                                  : 'dark:bg-dark-400 bg-cream-100 border-transparent hover:border-dark-100'
                                  }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold truncate">{analysis.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-[11px] text-cream-500">
                                        {analysis.clipCount} clips
                                      </span>
                                      <span className="w-1 h-1 rounded-full bg-dark-100" />
                                      <span className="text-[11px] text-cream-500">
                                        {analysis.model}
                                      </span>
                                      <span className="w-1 h-1 rounded-full bg-dark-100" />
                                      <span className="text-[11px] text-cream-500">
                                        {new Date(analysis.timestamp).toLocaleDateString()}
                                      </span>
                                    </div>
                                    {analysis.assetFilename && (
                                      <p className="text-[11px] text-emerald-500 mt-1 flex items-center gap-1">
                                        <Check size={10} /> {analysis.assetFilename}
                                      </p>
                                    )}
                                  </div>
                                  {selectedAnalysis?.id === analysis.id && (
                                    <div className="w-5 h-5 bg-gold-500 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                                      <Check size={12} className="text-white" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Clear History Button */}
                        {savedAnalyses.length > 0 && (
                          <button
                            onClick={() => {
                              if (window.confirm('Clear all saved analyses?')) {
                                setSavedAnalyses([]);
                                localStorage.removeItem('qlipper_saved_analyses');
                                setSelectedAnalysis(null);
                                addLog('Cleared analysis history', 'info');
                              }
                            }}
                            className="text-[11px] text-cream-500 hover:text-red-400 mt-2 flex items-center gap-1"
                          >
                            <Trash2 size={10} /> Clear History
                          </button>
                        )}
                      </div>

                      {/* Manual JSON Input (collapsible) */}
                      <div>
                        <div className="flex justify-between mb-3">
                          <label className={labelClass}>
                            {selectedAnalysis ? 'JSON Preview' : 'Manual JSON Input'}
                          </label>
                          <button
                            onClick={() => setUseTransitions(!useTransitions)}
                            className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${useTransitions ? 'border-gold-500 text-gold-500' : 'border-cream-300 dark:border-dark-200 text-cream-500 dark:text-cream-400'}`}
                          >
                            FX: {useTransitions ? 'FADE' : 'OFF'}
                          </button>
                        </div>
                        <textarea
                          value={pastedJson}
                          onChange={(e) => {
                            setPastedJson(e.target.value);
                            setSelectedAnalysis(null); // Clear selection when manually editing
                          }}
                          className={`${inputClass} min-h-[120px] font-mono text-[11px]`}
                          placeholder="Select an analysis above or paste JSON manually..."
                        />
                      </div>
                    </div>

                    {/* PR 4g: Render Engine Profile picker (replaces turbo toggle) */}
                    <div className="bg-gold-500/10 p-3 rounded-2xl border border-gold-500/20 my-6">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gold-400 mb-2">Render Engine</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'hw',          label: 'Hardware',   sub: 'Fastest · GPU encoder', disabled: !hwEncoderAvailable },
                          { id: 'cpu-fast',    label: 'CPU Fast',   sub: 'Fallback · libx264 ultrafast', disabled: false },
                          { id: 'cpu-quality', label: 'CPU Quality',sub: 'Slowest · cleanest dark scenes', disabled: false },
                        ].map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => !opt.disabled && setRenderProfile(opt.id)}
                            disabled={opt.disabled}
                            className={`p-2 rounded-xl border text-left transition-all ${
                              renderProfile === opt.id
                                ? 'border-gold-500 bg-gold-500/20'
                                : 'border-cream-300 dark:border-dark-200 hover:border-gold-300'
                            } ${opt.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <p className={`text-[11px] font-bold ${renderProfile === opt.id ? 'text-gold-400' : 'text-cream-500'}`}>{opt.label}</p>
                            <p className="text-[9px] text-cream-500 mt-0.5">{opt.sub}</p>
                            {opt.disabled && <p className="text-[9px] text-amber-500 mt-0.5">No GPU encoder detected</p>}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button onClick={handleProcessClips} disabled={processing || !pastedJson} className={`${btnPrimary} w-full py-5 text-lg relative overflow-hidden`}>
                      {processing ? (
                        <>
                          <div className="absolute inset-0 bg-gold-700/50" style={{ width: `${progress}%`, transition: 'width 0.3s ease' }} />
                          <span className="relative z-10 flex items-center gap-2">
                            <Loader2 className="animate-spin" size={24} />
                            Baking Clips... {progress}%
                          </span>
                        </>
                      ) : (
                        <>
                          <Scissors size={20} />
                          Assemble Artifacts
                        </>
                      )}
                    </button>

                    <AnimatePresence>
                      {processing && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="mt-6 p-5 rounded-2xl bg-gold-500/10 border border-gold-500/20 space-y-3"
                        >
                          <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-widest">
                            <span className="text-gold-400 animate-pulse">{processingStatus}</span>
                            <span className="text-gold-400">{progress}%</span>
                          </div>
                          <div className="h-2 bg-dark-400 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gold-500"
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                            />
                          </div>
                          <p className="text-[11px] text-cream-500 italic text-center"> Redirecting to My Files for live monitoring...</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                </div>
              </motion.div>
            )
          }

          {/* TOOL D: TRANSCRIBER */}
          {activeTab === 'tool-d' && (
            <motion.div
              key="d" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="flex justify-center pt-8"
            >
              <div className="w-full max-w-4xl space-y-8">
                <section className={cardClass}>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-3 h-3 rounded-full bg-gold-400" />
                    <h2 className="text-xl font-bold tracking-tight">AI Subtitle Editor</h2>
                  </div>

                  {/* TOP: Asset selector — horizontal scrollable row */}
                  <div className="space-y-3 mb-6">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Select Source Video</h3>
                    {assets.filter(a => a.type === 'video').length === 0 ? (
                      <div className="p-6 text-center border-2 border-dashed dark:border-dark-300 rounded-2xl">
                        <p className="text-xs text-cream-500">No video assets found.</p>
                        <button onClick={() => setActiveTab('assets')} className="text-gold-500 font-bold text-xs mt-2 hover:underline">Go to Library →</button>
                      </div>
                    ) : (
                      <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                        {assets.filter(a => a.type === 'video').map(asset => (
                          <div
                            key={asset.id}
                            onClick={() => setVideoFile(asset)}
                            className={`flex-shrink-0 w-48 p-3 rounded-xl border flex items-center gap-3 cursor-pointer transition-all ${videoFile?.id === asset.id ? 'bg-gold-400/10 border-gold-400' : 'dark:bg-dark-400 bg-cream-100 border-transparent hover:border-dark-200'}`}
                          >
                            <div className="w-12 h-8 bg-black rounded-lg overflow-hidden flex-shrink-0">
                              <video src={`${API_BASE}/${asset.path}#t=1`} className="w-full h-full object-cover pointer-events-none" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`text-[11px] font-bold truncate ${videoFile?.id === asset.id ? 'text-gold-400' : 'dark:text-cream-200'}`}>{asset.name}</h4>
                              <div className="flex items-center gap-1 mt-1">
                                {asset.hasAudio && <span className="text-[11px] bg-gold-500 text-white px-1 rounded">AUDIO</span>}
                                {asset.hasSubtitle && <span className="text-[11px] bg-gold-500 text-white px-1 rounded">CC</span>}
                              </div>
                            </div>
                            {videoFile?.id === asset.id && <CheckCircle2 size={14} className="text-gold-400 flex-shrink-0" />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* BOTTOM: Full-width editor */}
                  {!videoFile ? (
                    <div className="flex flex-col items-center justify-center text-center opacity-30 py-16">
                      <Mic size={48} className="mb-4" />
                      <p className="text-sm font-bold">Select a video above to start</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Status bar */}
                      <div className="flex items-center justify-between px-4 py-3 rounded-xl dark:bg-dark-400 bg-cream-100">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-cream-400 truncate max-w-xs">{videoFile.name}</span>
                          <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${videoFile.hasSubtitle ? 'bg-gold-500/20 text-gold-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {videoFile.hasSubtitle ? 'Transcribed' : 'Not Transcribed'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {videoFile.hasSubtitle && (
                            <a href={`${API_BASE}/${videoFile.transcriptionPath}`} download
                              className="px-3 py-1.5 bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all">
                              <Download size={12}/> Download
                            </a>
                          )}
                          {/* Re-transcribe */}
                          <div className="flex items-center gap-2">
                            <select
                              value={whisperEngine}
                              onChange={e => setWhisperEngine(e.target.value)}
                              className="px-2 py-1.5 rounded-lg bg-white dark:bg-dark-400 border border-cream-300 dark:border-dark-200 text-xs text-dark-200 dark:text-cream-300 outline-none"
                            >
                              <option value="whisper-cpp">Whisper.cpp</option>
                              <option value="whisper-wasm">Whisper WASM</option>
                              <option value="gemini">Gemini AI</option>
                            </select>
                            {(() => {
                              const isTranscribing = videoFile?.id && transcribingAssets[videoFile.id]?.status === 'processing';
                              return (
                                <button
                                  disabled={isTranscribing}
                                  onClick={() => handleStartTranscribe(videoFile)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${isTranscribing ? 'bg-dark-200 text-cream-500 cursor-wait' : videoFile.hasSubtitle ? 'bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-300' : 'bg-gold-600 hover:bg-gold-500 text-white'}`}
                                >
                                  {isTranscribing ? <Loader2 size={12} className="animate-spin"/> : <Mic size={12}/>}
                                  {isTranscribing ? 'Transcribing...' : videoFile.hasSubtitle ? 'Re-transcribe' : 'Transcribe'}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Full-width editable transcript */}
                      {videoFile.hasSubtitle && loadingTranscript && (
                        <div className="p-12 text-center rounded-2xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300">
                          <Loader2 size={32} className="mx-auto mb-3 text-gold-500 animate-spin"/>
                          <p className="text-sm font-bold text-dark-200 dark:text-cream-200 mb-1">Loading subtitle data...</p>
                          <p className="text-xs text-cream-500">Parsing transcript for the editor.</p>
                        </div>
                      )}

                      {videoFile.hasSubtitle && !loadingTranscript && (
                        <div className="rounded-2xl bg-white dark:bg-dark-400/70 border border-cream-300 dark:border-gold-500/30 overflow-hidden" style={{ minHeight: '60vh' }}>
                          <EditableTranscript
                            segments={assetTranscriptData?.segments || cachedTranscripts[videoFile.id]?.segments || []}
                            onSave={(segments) => saveTranscriptForAsset(videoFile.id, segments)}
                            isSaving={savingTranscript}
                            whisperEngine={whisperEngine}
                            setWhisperEngine={setWhisperEngine}
                            onRetranscribe={() => handleStartTranscribe(videoFile)}
                            transcriptMethod={assetTranscriptData?.method || cachedTranscripts[videoFile.id]?.method}
                          />
                        </div>
                      )}

                      {(() => {
                        const isTranscribing = videoFile?.id && transcribingAssets[videoFile.id]?.status === 'processing';
                        return !videoFile.hasSubtitle && !isTranscribing && (
                          <div className="p-8 text-center rounded-2xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300">
                            <Mic size={32} className="mx-auto mb-3 text-cream-500"/>
                            <p className="text-sm font-bold text-cream-400 mb-1">No transcript yet</p>
                            <p className="text-xs text-cream-500">Select an engine above and click Transcribe to generate subtitles.</p>
                          </div>
                        );
                      })()}

                      {(() => {
                        const isTranscribing = videoFile?.id && transcribingAssets[videoFile.id]?.status === 'processing';
                        const logs = transcribingAssets[videoFile.id]?.logs || [];
                        return isTranscribing && (
                          <div className="p-6 rounded-2xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 space-y-3">
                            <div className="flex items-center gap-3">
                              <Loader2 size={20} className="text-gold-500 animate-spin"/>
                              <p className="text-sm font-bold text-dark-200 dark:text-cream-200">Transcribing...</p>
                            </div>
                            {logs.length > 0 && (
                              <div className="bg-black/30 rounded-lg p-3 max-h-40 overflow-y-auto font-mono text-[11px] text-cream-400 space-y-0.5">
                                {logs.slice(-10).map((log, li) => <div key={li}>{log}</div>)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </section>
              </div>
            </motion.div>
          )
          }

          {/* TOOL E: ADD LOGO/STICKER */}
          {activeTab === 'tool-e' && (
            <motion.div
              key="e" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="flex justify-center pt-8"
            >
              <div className="w-full max-w-4xl space-y-8">
                <section className={cardClass}>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-3 h-3 rounded-full bg-gold-400" />
                    <h2 className="text-xl font-bold tracking-tight">Add Logo / Sticker</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* LEFT: Clip Selector */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-cream-500 mb-2">Select Clip from My Files</h3>
                      <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                        {(historyProjects || []).filter(p => p.clips?.length > 0).map(proj => (
                          <div key={proj.id} className="space-y-2">
                            <p className="text-[11px] font-bold text-cream-500 uppercase tracking-widest">{proj.title?.slice(0, 40) || proj.id}</p>
                            {proj.clips.map((clip, idx) => (
                              <button
                                key={`${proj.id}-${idx}`}
                                onClick={() => {
                                  // Select this clip for logo editing
                                  setSelectedHistoryProject(proj);
                                  setClipEditModal({ isOpen: true, type: 'logo', clipIdx: idx });
                                  fetchLogos();
                                }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl transition-all bg-cream-100 dark:bg-dark-400 hover:bg-gold-500/10 hover:border-gold-500/30 border border-transparent"
                              >
                                <div className="w-16 h-10 bg-black rounded-lg overflow-hidden flex-shrink-0">
                                  {clip.url && (
                                    <video
                                      src={`${API_BASE}/${clip.url}#t=1`}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                                <div className="text-left flex-1 min-w-0">
                                  <p className="text-xs font-bold dark:text-white truncate">{clip.topic || `Clip #${idx + 1}`}</p>
                                  <p className="text-[11px] text-cream-500">{clip.startTime}s – {clip.endTime}s</p>
                                </div>
                                <Stamp size={14} className="text-gold-500 flex-shrink-0" />
                              </button>
                            ))}
                          </div>
                        ))}

                        {(historyProjects || []).filter(p => p.clips?.length > 0).length === 0 && (
                          <div className="p-6 text-center border-2 border-dashed dark:border-dark-300 rounded-xl">
                            <Stamp className="w-8 h-8 mx-auto mb-3 text-cream-500" />
                            <p className="text-xs text-cream-500 mb-2">No clips found</p>
                            <p className="text-[11px] text-cream-400">Process a video first using the Pipeline or Clipper tool</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT: Logo Library & Upload */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-cream-500 mb-2">Logo Library</h3>

                      {/* Upload */}
                      <div className="flex gap-2">
                        <input
                          type="file"
                          id="tool-e-logo-upload"
                          className="hidden"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('logo', file);
                            formData.append('name', file.name.replace(/\.[^.]+$/, ''));
                            try {
                              await axios.post(`${API_BASE}/api/logos/upload?type=logo`, formData);
                              fetchLogos();
                            } catch (err) {
                              addLog('Logo upload failed: ' + err.message, 'error');
                            }
                            e.target.value = '';
                          }}
                        />
                        <label
                          htmlFor="tool-e-logo-upload"
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all bg-gold-500/10 border-2 border-dashed border-gold-500/30 hover:border-gold-500 text-gold-500 text-xs font-bold"
                        >
                          <Upload size={14} /> Upload Logo
                        </label>
                      </div>

                      {/* Logo Grid */}
                      {logos.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {logos.map(logo => (
                            <div
                              key={logo.id}
                              className="relative group rounded-xl border-2 border-transparent hover:border-gold-500/30 bg-cream-100 dark:bg-dark-400 p-3 text-center"
                            >
                              <img
                                src={`${API_BASE}/${logo.path}`}
                                alt={logo.name}
                                className="w-full h-16 object-contain mb-2"
                              />
                              <p className="text-[11px] text-cream-500 truncate">{logo.name}</p>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${API_BASE}/api/logos/${logo.id}`);
                                    fetchLogos();
                                  } catch (err) {
                                    addLog('Delete failed: ' + err.message, 'error');
                                  }
                                }}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={10} className="text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {logos.length === 0 && (
                        <div className="p-6 text-center border-2 border-dashed dark:border-dark-300 rounded-xl">
                          <Upload className="w-6 h-6 mx-auto mb-2 text-cream-500" />
                          <p className="text-[11px] text-cream-500">Upload your first logo to get started</p>
                        </div>
                      )}

                      <p className="text-[11px] text-cream-400 leading-relaxed">
                        <strong className="text-gold-500">How it works:</strong> Upload your logo, then select a clip from the left panel.
                        The Edit modal will open with the Logo tab where you can drag to position, adjust scale and opacity, then apply.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </motion.div>
          )}

          {/* WATERMARK REMOVER (Experimental) */}
          {activeTab === 'tool-f' && (
            <motion.div
              key="watermark" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}
              className="max-w-5xl mx-auto py-8"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-2xl bg-gold-500/10 text-gold-500">
                  <Eraser size={28} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Remove Watermark</h2>
                  <p className="text-sm text-cream-500 dark:text-cream-400">Select a video, mark the watermark area, and remove it.</p>
                </div>
                <span className="ml-auto px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-[11px] font-bold uppercase">Experimental</span>
              </div>

              <div className={`${cardClass}`}>

                {/* PHASE 1: Source Selection */}
                {!wmSource && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Choose a video source</h3>

                    {/* Assets */}
                    <div className="space-y-3">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From Asset Library</label>
                      {assets.filter(a => a.type === 'video').length === 0 ? (
                        <div className="p-6 text-center border-2 border-dashed border-cream-300 dark:border-dark-200 rounded-2xl">
                          <ImageIcon size={32} className="mx-auto mb-2 text-cream-400" />
                          <p className="text-sm text-cream-500 dark:text-cream-400">No video assets. Upload one in Asset Library first.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {assets.filter(a => a.type === 'video').map(asset => (
                            <button
                              key={asset.id}
                              onClick={() => handleWmExtractFrame({ type: 'asset', path: asset.path, name: asset.name || asset.filename })}
                              className="rounded-xl border border-cream-300 dark:border-dark-200 hover:border-gold-500 transition-all text-left group bg-white dark:bg-dark-300 overflow-hidden"
                            >
                              <div className="aspect-video bg-black relative">
                                <video src={`${API_BASE}/${asset.path}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />
                              </div>
                              <div className="p-2.5">
                                <p className="text-xs font-bold truncate text-dark-200 dark:text-cream-100 group-hover:text-gold-500 transition-colors">{asset.name || asset.filename}</p>
                                <p className="text-[11px] text-cream-500 dark:text-cream-400">{asset.duration ? `${Math.round(asset.duration)}s` : 'Video'}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* My Files clips */}
                    {historyProjects.filter(p => p.clips?.length > 0).length > 0 && (
                      <div className="space-y-3">
                        <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From My Files</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {historyProjects.filter(p => p.clips?.length > 0).flatMap(project =>
                            project.clips.filter(c => c.url).map(clip => (
                              <button
                                key={`${project.id}-${clip.id}`}
                                onClick={() => handleWmExtractFrame({ type: 'clip', path: clip.url, name: clip.topic || clip.id })}
                                className="rounded-xl border border-cream-300 dark:border-dark-200 hover:border-gold-500 transition-all text-left group bg-white dark:bg-dark-300 overflow-hidden"
                              >
                                <div className="aspect-video bg-black relative">
                                  <video src={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                  <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />
                                  <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">Clip</span>
                                </div>
                                <div className="p-2.5">
                                  <p className="text-xs font-bold truncate text-dark-200 dark:text-cream-100 group-hover:text-gold-500 transition-colors">{clip.topic || clip.id}</p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* PHASE 2: Region Drawing */}
                {wmSource && !wmResult && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Draw the watermark area</h3>
                        <p className="text-xs text-cream-500 dark:text-cream-400 mt-1">Click and drag on the frame to select the region to remove.</p>
                      </div>
                      <button onClick={handleWmReset} className="px-4 py-2 rounded-xl bg-cream-200 dark:bg-dark-200 text-dark-200 dark:text-cream-200 text-xs font-bold hover:bg-cream-300 dark:hover:bg-dark-100 transition-all">
                        Change Source
                      </button>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <FileVideo size={14} className="text-gold-500" />
                      <span className="text-xs font-bold text-dark-200 dark:text-cream-100">{wmSource.name}</span>
                      {wmVideoDims && <span className="text-[11px] text-cream-500 dark:text-cream-400">({wmVideoDims.width}x{wmVideoDims.height})</span>}
                    </div>

                    {/* Frame preview with drawable overlay */}
                    {wmFrameUrl ? (
                      <div className="relative inline-block w-full max-w-3xl mx-auto">
                        <div
                          className="relative select-none cursor-crosshair rounded-xl overflow-hidden border-2 border-cream-300 dark:border-dark-200 shadow-lg"
                          onMouseDown={(e) => {
                            if (wmProcessing) return;
                            const rect = wmImgRef.current.getBoundingClientRect();
                            const xPct = ((e.clientX - rect.left) / rect.width) * 100;
                            const yPct = ((e.clientY - rect.top) / rect.height) * 100;
                            setWmDrawing(true);
                            setWmDrawStart({ x: xPct, y: yPct });
                            setWmRegion({ x: xPct, y: yPct, w: 0, h: 0 });
                          }}
                          onMouseMove={(e) => {
                            if (!wmDrawing || !wmDrawStart) return;
                            const rect = wmImgRef.current.getBoundingClientRect();
                            const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                            const yPct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                            setWmRegion({
                              x: Math.min(wmDrawStart.x, xPct),
                              y: Math.min(wmDrawStart.y, yPct),
                              w: Math.abs(xPct - wmDrawStart.x),
                              h: Math.abs(yPct - wmDrawStart.y)
                            });
                          }}
                          onMouseUp={() => setWmDrawing(false)}
                          onMouseLeave={() => setWmDrawing(false)}
                        >
                          <img
                            ref={wmImgRef}
                            src={wmFrameUrl}
                            alt="Video frame"
                            className="w-full h-auto block pointer-events-none"
                            draggable={false}
                          />
                          {/* Drawn rectangle overlay */}
                          {wmRegion && wmRegion.w > 0 && wmRegion.h > 0 && (
                            <div
                              className="absolute border-2 border-dashed border-red-500 bg-red-500/20 pointer-events-none"
                              style={{
                                left: `${wmRegion.x}%`,
                                top: `${wmRegion.y}%`,
                                width: `${wmRegion.w}%`,
                                height: `${wmRegion.h}%`
                              }}
                            >
                              <span className="absolute -top-5 left-0 text-[11px] font-bold text-red-500 bg-white dark:bg-dark-400 px-1.5 py-0.5 rounded shadow-sm">
                                Watermark Area
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Timeline Scrubber */}
                        {wmDuration > 0 && (
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-3">
                              <Clock size={12} className="text-cream-500 flex-shrink-0" />
                              <input
                                type="range"
                                min="0"
                                max={Math.floor(wmDuration)}
                                step="0.5"
                                value={wmTimestamp}
                                onChange={(e) => {
                                  const t = parseFloat(e.target.value);
                                  setWmTimestamp(t);
                                  // Debounce frame extraction
                                  if (wmScrubbing) return;
                                  setWmScrubbing(true);
                                  setTimeout(() => {
                                    handleWmExtractFrame(wmSource, t);
                                    setWmScrubbing(false);
                                  }, 300);
                                }}
                                className="flex-1 accent-gold-500"
                              />
                              <span className="text-[11px] font-mono text-cream-500 w-20 text-right">
                                {Math.floor(wmTimestamp / 60)}:{String(Math.floor(wmTimestamp % 60)).padStart(2, '0')} / {Math.floor(wmDuration / 60)}:{String(Math.floor(wmDuration % 60)).padStart(2, '0')}
                              </span>
                            </div>
                            <p className="text-[10px] text-cream-500 dark:text-cream-400 pl-7">Scrub to find the watermark position in the video</p>
                          </div>
                        )}

                        {/* Region info */}
                        {wmRegion && wmRegion.w > 1 && (
                          <div className="mt-3 flex items-center justify-between">
                            <p className="text-[11px] text-cream-500 dark:text-cream-400 font-mono">
                              Region: {Math.round(wmRegion.x)}%, {Math.round(wmRegion.y)}% — {Math.round(wmRegion.w)}% x {Math.round(wmRegion.h)}%
                            </p>
                            <button onClick={() => setWmRegion(null)} className="text-[11px] text-red-500 hover:text-red-400 font-bold">
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-gold-500" />
                        <span className="ml-3 text-sm text-cream-500 dark:text-cream-400">Extracting frame...</span>
                      </div>
                    )}

                    {wmError && (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                        <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-500">{wmError}</p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleWmRemove}
                        disabled={!wmRegion || wmRegion.w < 1 || wmProcessing}
                        className="flex-1 py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {wmProcessing ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <><Eraser size={16} /> Remove Watermark</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* PHASE 3: Result */}
                {wmResult && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle2 size={22} className="text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Watermark Removed!</h3>
                        <p className="text-xs text-cream-500 dark:text-cream-400">Saved to Asset Library as "{wmResult.outputFilename}"</p>
                      </div>
                    </div>

                    {/* Result video player */}
                    <div className="rounded-2xl overflow-hidden border border-cream-300 dark:border-dark-200 shadow-lg">
                      <video
                        src={`${API_BASE}${wmResult.outputUrl}`}
                        controls
                        className="w-full max-h-[50vh]"
                      />
                    </div>

                    {/* Next step buttons — chain into other tools */}
                    <div className="space-y-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 mb-2">Continue with this result</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <button
                            onClick={() => { setActiveTab('tool-g'); setSilSource({ type: 'asset', path: wmResult.outputUrl.replace(/^\//, ''), name: wmResult.outputFilename }); handleWmReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><VolumeX size={12} /> Silence</button>
                          <button
                            onClick={() => { setActiveTab('tool-h'); setBrollSource({ type: 'asset', path: wmResult.outputUrl.replace(/^\//, ''), name: wmResult.outputFilename }); handleWmReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><ImageIcon size={12} /> B-Roll</button>
                          <button
                            onClick={() => { setActiveTab('tool-b'); setToolBSourceMode('asset'); setToolBSelectedAsset({ path: wmResult.outputUrl.replace(/^\//, ''), name: wmResult.outputFilename }); handleWmReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><Zap size={12} /> AI Analysis</button>
                          <button
                            onClick={() => setActiveTab('assets')}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><FolderOpen size={12} /> Assets</button>
                        </div>
                      </div>
                      <button
                        onClick={handleWmReset}
                        className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold"
                      >
                        <Eraser size={16} /> Remove Another Watermark
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </motion.div>
          )}

          {/* SILENCE REMOVER (Experimental) */}
          {activeTab === 'tool-g' && (
            <motion.div
              key="silence" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}
              className="max-w-5xl mx-auto py-8"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-500">
                  <VolumeX size={28} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Remove Silence</h2>
                  <p className="text-sm text-cream-500 dark:text-cream-400">Automatically trim silent sections from your video.</p>
                </div>
                <span className="ml-auto px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-[11px] font-bold uppercase">Experimental</span>
              </div>

              <div className={`${cardClass}`}>

                {/* PHASE 1: Source Selection + Settings */}
                {!silResult && (
                  <div className="space-y-6">
                    {!silSource ? (
                      <>
                        <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Choose a video source</h3>

                        {/* Assets */}
                        <div className="space-y-3">
                          <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From Asset Library</label>
                          {assets.filter(a => a.type === 'video').length === 0 ? (
                            <div className="p-6 text-center border-2 border-dashed border-cream-300 dark:border-dark-200 rounded-2xl">
                              <ImageIcon size={32} className="mx-auto mb-2 text-cream-400" />
                              <p className="text-sm text-cream-500 dark:text-cream-400">No video assets. Upload one in Asset Library first.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {assets.filter(a => a.type === 'video').map(asset => (
                                <button
                                  key={asset.id}
                                  onClick={() => setSilSource({ type: 'asset', path: asset.path, name: asset.name || asset.filename })}
                                  className="rounded-xl border border-cream-300 dark:border-dark-200 hover:border-gold-500 transition-all text-left group bg-white dark:bg-dark-300 overflow-hidden"
                                >
                                  <div className="aspect-video bg-black relative">
                                    <video src={`${API_BASE}/${asset.path}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                    <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                    <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />
                                  </div>
                                  <div className="p-2.5">
                                    <p className="text-xs font-bold truncate text-dark-200 dark:text-cream-100 group-hover:text-gold-500 transition-colors">{asset.name || asset.filename}</p>
                                    <p className="text-[11px] text-cream-500 dark:text-cream-400">{asset.duration ? `${Math.round(asset.duration)}s` : 'Video'}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* My Files clips */}
                        {historyProjects.filter(p => p.clips?.length > 0).length > 0 && (
                          <div className="space-y-3">
                            <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From My Files</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {historyProjects.filter(p => p.clips?.length > 0).flatMap(project =>
                                project.clips.filter(c => c.url).map(clip => (
                                  <button
                                    key={`${project.id}-${clip.id}`}
                                    onClick={() => setSilSource({ type: 'clip', path: clip.url, name: clip.topic || clip.id })}
                                    className="rounded-xl border border-cream-300 dark:border-dark-200 hover:border-gold-500 transition-all text-left group bg-white dark:bg-dark-300 overflow-hidden"
                                  >
                                    <div className="aspect-video bg-black relative">
                                      <video src={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                      <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />
                                      <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">Clip</span>
                                    </div>
                                    <div className="p-2.5">
                                      <p className="text-xs font-bold truncate text-dark-200 dark:text-cream-100 group-hover:text-gold-500 transition-colors">{clip.topic || clip.id}</p>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Settings + Process */
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileVideo size={16} className="text-gold-500" />
                            <span className="text-sm font-bold text-dark-200 dark:text-cream-100">{silSource.name}</span>
                          </div>
                          <button onClick={handleSilReset} className="px-4 py-2 rounded-xl bg-cream-200 dark:bg-dark-200 text-dark-200 dark:text-cream-200 text-xs font-bold hover:bg-cream-300 dark:hover:bg-dark-100 transition-all">
                            Change Source
                          </button>
                        </div>

                        {/* Quick Mode Presets */}
                        <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Quick Mode</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { id: 'auto', label: 'Auto', desc: 'Smart detection', threshold: -30, duration: 0.5, icon: '🎯' },
                              { id: 'aggressive', label: 'No Sound Only', desc: 'Remove total silence', threshold: -50, duration: 0.3, icon: '🔇' },
                              { id: 'tight', label: 'Tight Cut', desc: 'Podcast style', threshold: -20, duration: 0.2, icon: '✂️' },
                            ].map(mode => (
                              <button
                                key={mode.id}
                                onClick={() => { setSilThreshold(mode.threshold); setSilMinDuration(mode.duration); }}
                                className={`p-3 rounded-xl border-2 text-center transition-all ${silThreshold === mode.threshold && silMinDuration === mode.duration ? 'border-gold-500 bg-gold-500/10' : 'border-cream-300 dark:border-dark-200 hover:border-gold-300'}`}
                              >
                                <span className="text-lg">{mode.icon}</span>
                                <p className={`text-xs font-bold mt-1 ${silThreshold === mode.threshold && silMinDuration === mode.duration ? 'text-gold-500' : 'text-dark-200 dark:text-cream-100'}`}>{mode.label}</p>
                                <p className="text-[10px] text-cream-500 dark:text-cream-400">{mode.desc}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Manual Settings */}
                        <div className="bg-cream-100 dark:bg-dark-300/30 rounded-xl p-5 space-y-4 border border-cream-300 dark:border-dark-200/50">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">Manual Settings</h4>

                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <label className="text-xs font-bold text-dark-200 dark:text-cream-400">Silence Threshold</label>
                                <span className="text-xs text-cream-500 font-mono">{silThreshold} dB</span>
                              </div>
                              <input type="range" min="-60" max="-10" value={silThreshold} onChange={e => setSilThreshold(parseInt(e.target.value))} className="w-full accent-gold-500" />
                              <p className="text-[11px] text-cream-500 dark:text-cream-400">Lower = more aggressive (removes quieter sounds too).</p>
                            </div>

                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <label className="text-xs font-bold text-dark-200 dark:text-cream-400">Min Silence Duration</label>
                                <span className="text-xs text-cream-500 font-mono">{silMinDuration}s</span>
                              </div>
                              <input type="range" min="0.1" max="3" step="0.1" value={silMinDuration} onChange={e => setSilMinDuration(parseFloat(e.target.value))} className="w-full accent-gold-500" />
                              <p className="text-[11px] text-cream-500 dark:text-cream-400">Only remove silences longer than this.</p>
                            </div>
                          </div>

                          {/* Save Mode */}
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-dark-200 dark:text-cream-400">Save Mode</label>
                            <div className="flex gap-2">
                              {[
                                { id: 'duplicate', label: 'Save as New File', desc: 'Keep original, create new asset' },
                                { id: 'replace', label: 'Replace Original', desc: 'Overwrite the source file' }
                              ].map(m => (
                                <button
                                  key={m.id}
                                  onClick={() => setSilMode(m.id)}
                                  className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${silMode === m.id ? 'border-gold-500 bg-gold-500/10' : 'border-cream-300 dark:border-dark-200 hover:border-gold-300'}`}
                                >
                                  <p className={`text-xs font-bold ${silMode === m.id ? 'text-gold-500' : 'text-dark-200 dark:text-cream-100'}`}>{m.label}</p>
                                  <p className="text-[11px] text-cream-500 dark:text-cream-400 mt-0.5">{m.desc}</p>
                                </button>
                              ))}
                            </div>
                          </div>

                          {silMode === 'replace' && (
                            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                              <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-amber-600 dark:text-amber-400">This will permanently overwrite the original file. Make sure you have a backup.</p>
                            </div>
                          )}
                        </div>

                        {silError && (
                          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                            <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-500">{silError}</p>
                          </div>
                        )}

                        <button
                          onClick={handleSilRemove}
                          disabled={silProcessing}
                          className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {silProcessing ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <><VolumeX size={16} /> Remove Silence</>}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* PHASE 2: Result */}
                {silResult && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle2 size={22} className="text-emerald-500" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Silence Removed!</h3>
                        <p className="text-xs text-cream-500 dark:text-cream-400">
                          {silResult.replaced ? 'Original file has been replaced.' : `Saved to Asset Library as "${silResult.outputFilename}"`}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-cream-300 dark:border-dark-200 shadow-lg">
                      <video
                        src={`${API_BASE}${silResult.outputUrl}`}
                        controls
                        className="w-full max-h-[50vh]"
                      />
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 mb-2">Continue with this result</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <button
                            onClick={() => { setActiveTab('tool-f'); handleWmExtractFrame({ type: 'asset', path: silResult.outputUrl.replace(/^\//, ''), name: silResult.outputFilename }); handleSilReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><Eraser size={12} /> Watermark</button>
                          <button
                            onClick={() => { setActiveTab('tool-h'); setBrollSource({ type: 'asset', path: silResult.outputUrl.replace(/^\//, ''), name: silResult.outputFilename }); handleSilReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><ImageIcon size={12} /> B-Roll</button>
                          <button
                            onClick={() => { setActiveTab('tool-b'); setToolBSourceMode('asset'); setToolBSelectedAsset({ path: silResult.outputUrl.replace(/^\//, ''), name: silResult.outputFilename }); handleSilReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><Zap size={12} /> AI Analysis</button>
                          <button
                            onClick={() => setActiveTab('assets')}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><FolderOpen size={12} /> Assets</button>
                        </div>
                      </div>
                      <button onClick={handleSilReset} className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold">
                        <VolumeX size={16} /> Process Another
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </motion.div>
          )}

          {/* B-ROLL INSERTION (Experimental) */}
          {activeTab === 'tool-h' && (
            <motion.div
              key="broll" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}
              className="max-w-5xl mx-auto py-8"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-2xl bg-gold-400/10 text-gold-400">
                  <ImageIcon size={28} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Insert B-Roll</h2>
                  <p className="text-sm text-cream-500 dark:text-cream-400">AI finds visual moments and inserts stock images or custom cards.</p>
                </div>
                <span className="ml-auto px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-[11px] font-bold uppercase">Experimental</span>
              </div>

              <div className={`${cardClass}`}>

                {/* PHASE 1: Source Selection */}
                {!brollSource && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">Choose a video with transcript</h3>
                    <div className="space-y-3">
                      <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From Asset Library</label>
                      {assets.filter(a => a.type === 'video').length === 0 ? (
                        <div className="p-6 text-center border-2 border-dashed border-cream-300 dark:border-dark-200 rounded-2xl">
                          <ImageIcon size={32} className="mx-auto mb-2 text-cream-400" />
                          <p className="text-sm text-cream-500 dark:text-cream-400">No video assets. Upload one first.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {assets.filter(a => a.type === 'video').sort((a, b) => (b.hasSubtitle ? 1 : 0) - (a.hasSubtitle ? 1 : 0)).map(asset => (
                            <button key={asset.id} disabled={!asset.hasSubtitle} onClick={() => asset.hasSubtitle && setBrollSource({ type: 'asset', path: asset.path, name: asset.name || asset.filename, duration: asset.duration, hasSubtitle: asset.hasSubtitle })}
                              className={`rounded-xl border transition-all text-left group overflow-hidden ${asset.hasSubtitle ? 'border-cream-300 dark:border-dark-200 hover:border-gold-500 bg-white dark:bg-dark-300 cursor-pointer' : 'border-transparent bg-white/50 dark:bg-dark-300/30 opacity-40 cursor-not-allowed'}`}>
                              <div className="aspect-video bg-black relative">
                                <video src={`${API_BASE}/${asset.path}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                {asset.hasSubtitle && <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />}
                                {asset.hasSubtitle && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-gold-500 text-white">CC</span>}
                                {!asset.hasSubtitle && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/80 text-white">No CC</span>}
                              </div>
                              <div className="p-2.5">
                                <p className={`text-xs font-bold truncate ${asset.hasSubtitle ? 'text-dark-200 dark:text-cream-100 group-hover:text-gold-500' : 'text-cream-400'} transition-colors`}>{asset.name || asset.filename}</p>
                                <p className="text-[11px] text-cream-500 dark:text-cream-400">{asset.hasSubtitle ? 'Transcribed' : 'Transcribe in Subtitle tab first'}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {historyProjects.filter(p => p.clips?.length > 0).length > 0 && (
                      <div className="space-y-3">
                        <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">From My Files</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {historyProjects.filter(p => p.clips?.length > 0).flatMap(project =>
                            project.clips.filter(c => c.url).map(clip => (
                              <button key={`${project.id}-${clip.id}`} onClick={() => setBrollSource({ type: 'clip', path: clip.url, name: clip.topic || clip.id, duration: clip.duration })}
                                className="rounded-xl border border-cream-300 dark:border-dark-200 hover:border-gold-500 transition-all text-left group bg-white dark:bg-dark-300 overflow-hidden">
                                <div className="aspect-video bg-black relative">
                                  <video src={`${clip.url.startsWith('http') ? clip.url : `${API_BASE}/${clip.url}`}#t=1`} muted playsInline className="w-full h-full object-cover pointer-events-none" />
                                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                                  <Play className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-all" size={32} />
                                  <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500 text-white">Clip</span>
                                </div>
                                <div className="p-2.5">
                                  <p className="text-xs font-bold truncate text-dark-200 dark:text-cream-100 group-hover:text-gold-500 transition-colors">{clip.topic || clip.id}</p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* PHASE 2: Analysis + Image Assignment */}
                {brollSource && !brollResult && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileVideo size={16} className="text-gold-500" />
                        <span className="text-sm font-bold text-dark-200 dark:text-cream-100">{brollSource.name}</span>
                      </div>
                      <button onClick={handleBrollReset} className="px-4 py-2 rounded-xl bg-cream-200 dark:bg-dark-200 text-dark-200 dark:text-cream-200 text-xs font-bold hover:bg-cream-300 dark:hover:bg-dark-100 transition-all">
                        Change Source
                      </button>
                    </div>

                    {/* Analyze Button */}
                    {brollMoments.length === 0 && !brollAnalyzing && (
                      <div className="text-center py-8">
                        <p className="text-sm text-cream-500 dark:text-cream-400 mb-4">AI will analyze the transcript and find the best moments for visual B-roll.</p>
                        <button onClick={handleBrollAnalyze} disabled={brollAnalyzing}
                          className="px-8 py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold mx-auto">
                          <Sparkles size={16} /> Analyze for B-Roll
                        </button>
                      </div>
                    )}

                    {brollAnalyzing && (
                      <div className="text-center py-12">
                        <Loader2 size={32} className="animate-spin text-gold-500 mx-auto mb-3" />
                        <p className="text-sm text-cream-500 dark:text-cream-400">AI is analyzing the transcript for visual opportunities...</p>
                      </div>
                    )}

                    {/* B-Roll Moment Cards */}
                    {brollItems.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400">
                            {brollItems.length} B-Roll Moments Found
                          </h4>
                          <span className="text-[11px] text-cream-500 dark:text-cream-400">Click images to change selection</span>
                        </div>

                        {brollItems.map((item, idx) => (
                          <div key={idx} className="bg-cream-100 dark:bg-dark-300/30 rounded-xl p-4 border border-cream-300 dark:border-dark-200/50 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-1 bg-gold-500/10 text-gold-500 rounded-lg text-[11px] font-bold font-mono">{item.start} → {item.end}</span>
                                <span className="text-xs font-bold text-dark-200 dark:text-cream-100">"{item.keyword}"</span>
                              </div>
                              <button onClick={() => { setBrollItems(prev => prev.filter((_, i) => i !== idx)); setBrollImageSearch(prev => { const n = { ...prev }; delete n[idx]; return n; }); }}
                                className="p-1.5 rounded-lg hover:bg-red-500/10 text-cream-400 hover:text-red-500 transition-all"><Trash2 size={14} /></button>
                            </div>

                            <p className="text-[11px] text-cream-500 dark:text-cream-400">{item.description}</p>

                            {/* Image options */}
                            <div className="space-y-2">
                              {/* Source selector */}
                              <div className="flex gap-1 bg-cream-200 dark:bg-dark-400/50 p-1 rounded-lg">
                                {[{ id: 'pexels', label: 'Pexels' }, { id: 'asset', label: 'My Assets' }, { id: 'card', label: 'AI Card' }].map(src => (
                                  <button key={src.id} onClick={() => setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], source: src.id }; return n; })}
                                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-bold transition-all ${item.source === src.id ? 'bg-white dark:bg-dark-300 text-gold-500 shadow-sm' : 'text-cream-500 hover:text-dark-200 dark:hover:text-cream-200'}`}>
                                    {src.label}
                                  </button>
                                ))}
                              </div>

                              {/* Pexels images */}
                              {item.source === 'pexels' && brollImageSearch[idx] && (
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {brollImageSearch[idx].map(img => (
                                    <button key={img.id} onClick={() => setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], imageUrl: img.url, thumbnail: img.thumbnail, imagePath: null }; return n; })}
                                      className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${item.imageUrl === img.url ? 'border-gold-500' : 'border-transparent hover:border-cream-400'}`}>
                                      <img src={img.thumbnail} alt={img.alt} className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                  {brollImageSearch[idx].length === 0 && <p className="text-[11px] text-cream-500 py-2">No images found. Try "My Assets" or "AI Card".</p>}
                                </div>
                              )}
                              {item.source === 'pexels' && !brollImageSearch[idx] && (
                                <div className="flex items-center gap-2 py-2"><Loader2 size={14} className="animate-spin text-gold-500" /><span className="text-[11px] text-cream-500">Searching...</span></div>
                              )}

                              {/* Asset picker */}
                              {item.source === 'asset' && (
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {assets.filter(a => a.type === 'image' || a.type === 'video').map(a => (
                                    <button key={a.id} onClick={() => setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], imagePath: a.path, imageUrl: null, thumbnail: null }; return n; })}
                                      className={`flex-shrink-0 px-3 py-2 rounded-lg border-2 text-xs font-bold transition-all ${item.imagePath === a.path ? 'border-gold-500 bg-gold-500/10 text-gold-500' : 'border-cream-300 dark:border-dark-200 text-cream-500'}`}>
                                      {a.name || a.filename}
                                    </button>
                                  ))}
                                  {assets.filter(a => a.type === 'image').length === 0 && <p className="text-[11px] text-cream-500 py-2">No image assets. Upload images to Asset Library.</p>}
                                </div>
                              )}

                              {/* AI Card generator */}
                              {item.source === 'card' && (
                                <div className="flex gap-2">
                                  <input type="text" value={item.card_text || ''} onChange={e => setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], card_text: e.target.value }; return n; })}
                                    placeholder="Text for the card (stat, quote, key point)..."
                                    className="flex-1 px-3 py-2 bg-white dark:bg-dark-300 border border-cream-300 dark:border-dark-200 rounded-lg text-xs text-dark-200 dark:text-white placeholder-cream-400 focus:border-gold-500 focus:outline-none" />
                                  <button onClick={async () => {
                                    try {
                                      const r = await axios.post(`${API_BASE}/api/video/generate-broll-card`, { text: item.card_text || item.keyword }, { headers: getLicenseHeaders() });
                                      setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], imagePath: r.data.path, imageUrl: null }; return n; });
                                      addLog(`B-ROLL: Card generated for "${item.keyword}"`, 'success');
                                    } catch (err) { addLog(`B-ROLL: Card failed — ${err.message}`, 'error'); }
                                  }} className="px-4 py-2 rounded-lg bg-gold-500 hover:bg-gold-400 text-white text-xs font-bold transition-all">
                                    Generate
                                  </button>
                                </div>
                              )}

                              {/* Selected image preview */}
                              {(item.thumbnail || item.imagePath) && (
                                <div className="flex items-center gap-3 p-2 bg-cream-200/50 dark:bg-dark-400/30 rounded-lg">
                                  {item.thumbnail && <img src={item.thumbnail} alt="" className="w-16 h-10 rounded object-cover" />}
                                  {item.imagePath && !item.thumbnail && <div className="w-16 h-10 rounded bg-gold-500/10 flex items-center justify-center"><Check size={14} className="text-gold-500" /></div>}
                                  <span className="text-[11px] text-cream-500 dark:text-cream-400">
                                    {item.imagePath ? `Local: ${item.imagePath.split('/').pop()}` : 'Pexels stock image selected'}
                                  </span>
                                </div>
                              )}

                              {/* Effect selector */}
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-cream-500">Effect:</span>
                                {['kenburns', 'static'].map(eff => (
                                  <button key={eff} onClick={() => setBrollItems(prev => { const n = [...prev]; n[idx] = { ...n[idx], effect: eff }; return n; })}
                                    className={`px-2 py-1 rounded text-[11px] font-bold transition-all ${item.effect === eff ? 'bg-gold-500/10 text-gold-500' : 'text-cream-500 hover:text-gold-400'}`}>
                                    {eff === 'kenburns' ? 'Ken Burns' : 'Static'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}

                        {brollError && (
                          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                            <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-500">{brollError}</p>
                          </div>
                        )}

                        <button onClick={handleBrollApply}
                          disabled={brollProcessing || brollItems.filter(i => i.imageUrl || i.imagePath).length === 0}
                          className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold disabled:opacity-40 disabled:cursor-not-allowed">
                          {brollProcessing ? <><Loader2 size={16} className="animate-spin" /> Compositing...</> : <><ImageIcon size={16} /> Apply B-Roll ({brollItems.filter(i => i.imageUrl || i.imagePath).length} images)</>}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* PHASE 3: Result */}
                {brollResult && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><CheckCircle2 size={22} className="text-emerald-500" /></div>
                      <div>
                        <h3 className="text-sm font-bold text-dark-200 dark:text-cream-100">B-Roll Applied!</h3>
                        <p className="text-xs text-cream-500 dark:text-cream-400">Saved to Asset Library as "{brollResult.outputFilename}"</p>
                      </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-cream-300 dark:border-dark-200 shadow-lg">
                      <video src={`${API_BASE}${brollResult.outputUrl}`} controls className="w-full max-h-[50vh]" />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 mb-2">Continue with this result</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <button
                            onClick={() => { setActiveTab('tool-f'); handleWmExtractFrame({ type: 'asset', path: brollResult.outputUrl.replace(/^\//, ''), name: brollResult.outputFilename }); handleBrollReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><Eraser size={12} /> Watermark</button>
                          <button
                            onClick={() => { setActiveTab('tool-g'); setSilSource({ type: 'asset', path: brollResult.outputUrl.replace(/^\//, ''), name: brollResult.outputFilename }); handleBrollReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><VolumeX size={12} /> Silence</button>
                          <button
                            onClick={() => { setActiveTab('tool-b'); setToolBSourceMode('asset'); setToolBSelectedAsset({ path: brollResult.outputUrl.replace(/^\//, ''), name: brollResult.outputFilename }); handleBrollReset(); }}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><Zap size={12} /> AI Analysis</button>
                          <button
                            onClick={() => setActiveTab('assets')}
                            className="px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-200 hover:border-gold-500 text-xs font-bold text-dark-200 dark:text-cream-200 transition-all flex items-center justify-center gap-1.5"
                          ><FolderOpen size={12} /> Assets</button>
                        </div>
                      </div>
                      <button onClick={handleBrollReset} className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-gold">
                        <ImageIcon size={16} /> Process Another
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </motion.div>
          )}

          {/* FAQ PAGE */}
          {activeTab === 'faq' && <FAQ />}
        </AnimatePresence>

        {/* API KEY VALIDATION — Non-blocking inline banner (replaces old full-screen modal) */}
        <AnimatePresence>
          {showApiKeyValidation && (
            <motion.div
              initial={{ opacity: 0, y: 40, x: 0 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: 40 }}
              className="fixed bottom-24 right-8 z-[200] w-full max-w-sm"
            >
              <div className="bg-cream-50 dark:bg-dark-400 border border-gold-500/40 rounded-2xl shadow-gold-lg overflow-hidden">
                <div className="px-5 py-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Key size={20} className="text-gold-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-dark-200 dark:text-cream-100 mb-1">API Key Required</h4>
                    <p className="text-xs text-cream-500 dark:text-cream-400 leading-relaxed">
                      {(() => {
                        const names = { 'gemini': 'Gemini', 'gpt-4o': 'OpenAI', 'claude': 'Anthropic', 'mistral': 'Mistral', 'deepseek': 'DeepSeek', 'openrouter': 'OpenRouter', 'minimax': 'MiniMax', 'xai': 'xAI Grok' };
                        if (selectedModel === 'router') return `Router belum siap: ${routerMissing || 'cek pengaturan'} Buka Settings → Lanjutan → Router / API lain.`;
                        return `Configure your ${names[selectedModel] || 'AI'} API Key in Settings to use AI features.`;
                      })()}
                    </p>
                  </div>
                  <button onClick={() => setShowApiKeyValidation(false)} className="p-1 hover:bg-cream-200 dark:hover:bg-dark-200 rounded-lg transition-colors flex-shrink-0">
                    <X size={14} className="text-cream-400 dark:text-cream-400" />
                  </button>
                </div>
                <div className="px-5 pb-4 flex gap-2">
                  <button
                    onClick={() => { setShowApiKeyValidation(false); setShowSettings(true); }}
                    className="flex-1 py-2.5 rounded-xl bg-gold-500 hover:bg-gold-400 text-white font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-gold"
                  >
                    <Settings size={13} /> Open Settings
                  </button>
                  <a
                    href="https://aistudio.google.com/app/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 rounded-xl bg-cream-200 dark:bg-dark-200 hover:bg-cream-300 dark:hover:bg-dark-100 text-dark-200 dark:text-cream-200 font-bold text-xs transition-all flex items-center gap-1.5"
                  >
                    Get Key <Globe size={12} />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Settings Modal */}
        <AnimatePresence>
          {showSettings && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSettings(false)} className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative w-full max-w-lg bg-cream-50 dark:bg-dark-500 border border-cream-300 dark:border-dark-100 rounded-[2rem] shadow-card-light dark:shadow-card-dark overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-8 border-b border-cream-200 dark:border-dark-200 flex justify-between items-center flex-shrink-0 bg-cream-100 dark:bg-dark-400">
                  <div className="flex items-center gap-3">
                    <img src={brandIcon('sparkle', darkMode)} className="w-5 h-5 object-contain" alt="" />
                    <h2 className="text-xl font-bold text-dark-200 dark:text-cream-100">Settings</h2>
                  </div>
                  <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-cream-200 dark:hover:bg-dark-300 rounded-xl transition-all text-cream-500 dark:text-cream-400"><X size={20} /></button>
                </div>
                <div className="p-8 space-y-8 overflow-y-auto flex-1 custom-scrollbar">

                  {/* AI MODEL PICKER — authoritative, single source of truth */}
                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 dark:text-cream-400 flex items-center gap-2">
                      <img src={brandIcon('sparkle', darkMode)} className="w-3.5 h-3.5 object-contain" alt="" /> AI Model
                    </label>
                    {(() => {
                      const modelTiles = [
                        { id: 'gemini',      name: 'Gemini',      icon: null, svgSrc: '/brand/providers/gemini.png',      desc: 'Fast & Free', iconBg: 'bg-white' },
                        { id: 'gpt-4o',      name: 'GPT-4o',      icon: null, svgSrc: '/brand/providers/openai.png',      desc: 'Best Quality', iconBg: 'bg-white' },
                        { id: 'claude',      name: 'Claude',      icon: null, svgSrc: '/brand/providers/anthropic.svg',   desc: 'Long Context' },
                        // QW-1: Groq removed (xAI Grok now the only Grok-like provider — no
                        // more naming-collision concern). Tile label simplified.
                        { id: 'xai',         name: 'Grok',        icon: null, svgSrc: '/brand/providers/xai.svg',         desc: 'xAI · Elon\'s AI' },
                        { id: 'openrouter',  name: 'OpenRouter',  icon: null, svgSrc: '/brand/providers/openrouter.png',  desc: 'Any Model' },
                        { id: 'mistral',     name: 'Mistral',     icon: null, svgSrc: '/brand/providers/mistral.png',       desc: 'Open Alt' },
                        { id: 'deepseek',    name: 'DeepSeek',    icon: null, svgSrc: '/brand/providers/deepseek.png',      desc: 'Reasoning' },
                        { id: 'router',      name: 'Router / API lain', icon: '🔀', svgSrc: null,                              desc: 'OmniRoute & lainnya', advanced: true },
                      ];
                      const renderModelTile = (m) => (
                        <button
                          key={m.id}
                          onClick={() => setSelectedModel(m.id)}
                          className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all text-center ${selectedModel === m.id
                            ? 'border-gold-500 bg-gold-500/10 shadow-gold'
                            : 'border-cream-300 dark:border-dark-100 hover:border-gold-300 dark:hover:border-gold-700 bg-cream-100 dark:bg-dark-300'
                          }`}
                        >
                          {m.svgSrc
                            ? <img src={m.svgSrc} className={`w-6 h-6 object-contain ${m.iconBg ? `${m.iconBg} rounded-md p-0.5` : ''}`} alt={m.name} />
                            : <span className="text-xl">{m.icon}</span>}
                          <span className={`font-bold text-xs ${selectedModel === m.id ? 'text-gold-500' : 'text-dark-200 dark:text-cream-100'}`}>{m.name}</span>
                          <span className="text-[11px] text-cream-500 dark:text-cream-400">{m.desc}</span>
                        </button>
                      );
                      return (
                        <>
                          <div className="grid grid-cols-3 gap-2">
                            {modelTiles.filter(m => !m.advanced).map(renderModelTile)}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowAdvancedModels(v => !v)}
                            aria-expanded={showAdvancedModels}
                            data-testid="ai-model-advanced-toggle"
                            className="flex items-center gap-1.5 text-xs font-bold text-cream-500 dark:text-cream-400 hover:text-gold-500 transition-colors"
                          >
                            <ChevronDown size={14} className={`transition-transform ${showAdvancedModels ? 'rotate-180' : ''}`} />
                            Lanjutan
                          </button>
                          {showAdvancedModels && (
                            <div className="grid grid-cols-3 gap-2" data-testid="ai-model-advanced">
                              {modelTiles.filter(m => m.advanced).map(renderModelTile)}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="w-full h-px bg-cream-200 dark:bg-dark-200" />

                  {/* API KEY — shows only the key input for the selected model */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className={labelClass}>
                        {selectedModel === 'gemini' ? 'Gemini API Key' :
                         selectedModel === 'gpt-4o' ? 'OpenAI API Key' :
                         selectedModel === 'claude' ? 'Anthropic API Key' :
                         selectedModel === 'mistral' ? 'Mistral API Key' :
                         selectedModel === 'deepseek' ? 'DeepSeek API Key' :
                         selectedModel === 'openrouter' ? 'OpenRouter API Key' :
                         selectedModel === 'xai' ? 'xAI Grok API Key' :
                         selectedModel === 'router' ? 'Router / API lain (OmniRoute, dll.)' : 'API Key'}
                      </label>
                    </div>
                    {/* Get API Key link — contextual per model */}
                    {(() => {
                      const keyLinks = {
                        'gemini':     { url: 'https://aistudio.google.com/apikey',              label: 'Google AI Studio (Free)' },
                        'gpt-4o':     { url: 'https://platform.openai.com/api-keys',            label: 'OpenAI Platform' },
                        'claude':     { url: 'https://console.anthropic.com/settings/keys',     label: 'Anthropic Console' },
                        'mistral':    { url: 'https://console.mistral.ai/api-keys',             label: 'Mistral Console' },
                        'deepseek':   { url: 'https://platform.deepseek.com/api_keys',          label: 'DeepSeek Platform' },
                        'openrouter': { url: 'https://openrouter.ai/keys',                      label: 'OpenRouter Dashboard' },
                        'xai':        { url: 'https://console.x.ai',                            label: 'xAI Console' },
                      };
                      const link = keyLinks[selectedModel];
                      return link ? (
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold text-gold-500 hover:text-gold-400 transition-colors">
                          <Key size={12} /> Get your API key at {link.label} <Globe size={11} />
                        </a>
                      ) : null;
                    })()}
                    {selectedModel === 'gemini' ? (
                    <>
                    {/* SX-1+SX-2: single-purpose Verify button. Auto-save handles
                        persistence (no manual localStorage writes). After verify,
                        availableGeminiModels populates → curated radio list below
                        marks each variant tile with availability badges. */}
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={apiKey}
                        onChange={(e) => { setApiKey(e.target.value); setIsKeyVerified(false); }}
                        className={inputClass}
                        placeholder="sk-..."
                      />
                      <button
                        onClick={async () => {
                          if (!apiKey) return;
                          setFetchingModels(true);
                          try {
                            const res = await axios.get(`${API_BASE}/api/gemini/models`, {
                              headers: { 'x-gemini-key': apiKey }
                            });
                            const models = res.data.models || [];
                            setAvailableGeminiModels(models);
                            setIsKeyVerified(true);
                            // First-time verify: auto-pick recommended model
                            // (subsequent verifies leave existing geminiVariant alone;
                            // also re-pick when the stored variant is deprecated/retiring)
                            if (!geminiVariant || deprecatedGeminiVariants.includes(geminiVariant)) {
                              const best = models.find(m => m.recommended && m.accessible)
                                        || models.find(m => m.accessible);
                              if (best) setGeminiVariant(best.id);
                            }
                            addLog(`SUCCESS: Verified · ${models.length} models accessible`, 'success');
                          } catch (err) {
                            setIsKeyVerified(false);
                            const errMsg = err.response?.data?.error || err.message;
                            if (errMsg.includes('API_KEY_INVALID') || err.response?.status === 400) {
                              addLog('ERROR: Invalid API Key — copy the full key from Google AI Studio', 'error');
                            } else if (err.response?.status === 429) {
                              addLog('ERROR: Rate limited — wait a moment and try again', 'error');
                            } else {
                              addLog('ERROR: Verification failed — ' + errMsg, 'error');
                            }
                          } finally {
                            setFetchingModels(false);
                          }
                        }}
                        disabled={fetchingModels || !apiKey}
                        className={`px-6 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all active:scale-95 flex items-center gap-2 ${
                          isKeyVerified
                            ? 'bg-green-600/80 text-white'
                            : 'bg-dark-100 hover:bg-dark-50 dark:bg-dark-100 dark:hover:bg-dark-50 text-cream-100'
                        } ${fetchingModels || !apiKey ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {fetchingModels
                          ? <Loader2 size={16} className="animate-spin" />
                          : isKeyVerified ? <Check size={16} /> : <Check size={16} />}
                        {fetchingModels ? 'Verifying' : isKeyVerified ? 'Verified' : 'Verify'}
                      </button>
                    </div>
                    <p className="text-[11px] dark:text-cream-400 text-cream-400">Key stored locally · used for AI viral moment analysis. Changes save automatically.</p>
                    </>) : selectedModel === 'gpt-4o' ? (
                      <ApiKeyInput id="gpt-4o" label="" value={openaiKey} onChange={setOpenaiKey} placeholder="sk-..." />
                    ) : selectedModel === 'claude' ? (
                      <ApiKeyInput id="claude" label="" value={claudeKey} onChange={setClaudeKey} placeholder="sk-ant-..." />
                    ) : selectedModel === 'mistral' ? (
                      <><div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <AlertCircle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">This AI model may not work properly due to model limitations. Consider using Gemini or GPT-5.4 first.</p>
                      </div>
                      <ApiKeyInput id="mistral" label="" value={mistralKey} onChange={setMistralKey} placeholder="..." /></>
                    ) : selectedModel === 'deepseek' ? (
                      <><div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <AlertCircle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">This AI model may not work properly due to model limitations. Consider using Gemini or GPT-5.4 first.</p>
                      </div>
                      <ApiKeyInput id="deepseek" label="" value={deepseekKey} onChange={setDeepseekKey} placeholder="sk-..." /></>
                    ) : selectedModel === 'minimax' ? (
                      <ApiKeyInput id="minimax" label="" value={minimaxKey} onChange={setMinimaxKey} placeholder="sk-..." />
                    ) : selectedModel === 'xai' ? (
                      <><div className="flex items-start gap-2 p-3 rounded-xl bg-cream-200/50 dark:bg-dark-300 border border-cream-300 dark:border-dark-200">
                        <Info size={13} className="text-gold-500 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-cream-600 dark:text-cream-400">
                          Get your xAI Grok API key at <a href="https://console.x.ai" target="_blank" rel="noreferrer" className="underline text-gold-500">console.x.ai</a>. Default model: <code className="text-[10px]">grok-4-fast-non-reasoning</code> (best for JSON output).
                        </p>
                      </div>
                      <ApiKeyInput id="xai" label="" value={xaiKey} onChange={setXaiKey} placeholder="xai-..." /></>
                    ) : selectedModel === 'openrouter' ? (
                      <div className="space-y-3">
                        <ApiKeyInput id="openrouter" label="" value={openrouterKey} onChange={setOpenrouterKey} placeholder="sk-or-..." />
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold uppercase tracking-widest text-cream-500 dark:text-cream-400">Model ID</label>
                          <input
                            type="text"
                            value={openrouterModel}
                            onChange={e => setOpenrouterModel(e.target.value)}
                            className={inputClass}
                            placeholder="google/gemma-3-27b-it"
                          />
                          <p className="text-[11px] text-cream-500 dark:text-cream-400">
                            Find models at{' '}
                            <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="text-gold-500 hover:underline">openrouter.ai/models</a>
                            {' '}— Results quality varies by model. Large models work best.
                          </p>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                          <AlertCircle size={13} className="text-blue-400 mt-0.5 flex-shrink-0" />
                          <p className="text-[11px] text-blue-400">If results are malformed, switch to a larger model. Gemini models on OpenRouter (e.g. google/gemma-3-27b-it) work well.</p>
                        </div>
                      </div>
                    ) : selectedModel === 'router' ? (
                      <LiteLlmSettings
                        key={routerProvider}
                        presets={PRESETS}
                        providerId={routerProvider}
                        onProviderChange={onRouterProviderChange}
                        cfg={routerLlm}
                        onCfgChange={onRouterCfgChange}
                        geminiKey={apiKey}
                        onGeminiKeyChange={setApiKey}
                        freeEnabled={false}
                        busy={false}
                      />
                    ) : null}
                  </div>

                  {/* GEMINI MODEL PICKER - Only visible after verification + Gemini selected */}
                  {selectedModel === 'gemini' && isKeyVerified && (
                    <div className="space-y-4 pt-4 border-t dark:border-dark-300 border-slate-100 animate-in fade-in slide-in-from-top-4 duration-300">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <label className={labelClass}>Gemini Model Variant</label>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        {(availableGeminiModels.length > 0 ? availableGeminiModels.map(m => ({
                          id: m.id,
                          label: m.displayName,
                          badge: m.recommended ? '★ Recommended' : m.tier === 'paid' ? 'Paid' : 'Free',
                          badgeColor: m.recommended ? 'text-gold-400 bg-gold-500/10' : m.tier === 'paid' ? 'text-cream-400 bg-dark-200/50' : 'text-emerald-400 bg-emerald-500/10',
                          disabled: !m.accessible,
                        })) : [
                          { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', badge: '★ Recommended', badgeColor: 'text-gold-400 bg-gold-500/10' },
                          { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', badge: 'Free', badgeColor: 'text-emerald-400 bg-emerald-500/10' },
                          { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', badge: 'Paid', badgeColor: 'text-cream-400 bg-dark-200/50' },
                          { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', badge: 'Retiring Okt 2026', badgeColor: 'text-amber-400 bg-amber-500/10' },
                          { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', badge: 'Paid · retiring Okt 2026', badgeColor: 'text-cream-400 bg-dark-200/50' },
                          { id: 'gemma-4-31b-it', label: 'Gemma 4 31B', badge: 'Free', badgeColor: 'text-emerald-400 bg-emerald-500/10' },
                          { id: 'gemma-3-27b-it', label: 'Gemma 3 27B', badge: 'Free', badgeColor: 'text-emerald-400 bg-emerald-500/10' },
                        ]).map(model => (
                          <button
                            key={model.id}
                            disabled={model.disabled}
                            onClick={() => { setGeminiVariant(model.id); addLog(`SYSTEM: Gemini model set to ${model.id}`, 'success'); }}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all text-left ${
                              geminiVariant === model.id
                                ? 'dark:bg-gold-500/10 bg-gold-50 border-gold-500/40 text-gold-400'
                                : model.disabled
                                ? 'dark:bg-dark-400/30 bg-cream-100 border-transparent text-cream-500 cursor-not-allowed opacity-50'
                                : 'dark:bg-dark-400/50 bg-cream-100 border-transparent dark:hover:border-dark-200 hover:border-cream-300 dark:text-cream-200 text-slate-700'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${geminiVariant === model.id ? 'border-gold-500 bg-gold-500' : 'dark:border-dark-100 border-cream-300'}`}>
                                {geminiVariant === model.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                              </div>
                              <span className="text-sm font-medium">{model.label}</span>
                            </div>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${model.badgeColor}`}>{model.badge}</span>
                          </button>
                        ))}
                        {availableGeminiModels.length > 0 && (
                          <p className="text-[11px] dark:text-cream-400 text-cream-400 pt-1">{availableGeminiModels.length} models available using your verified key.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SYSTEM STATUS CHECK */}
                  <div className="space-y-4 pt-4 border-t dark:border-dark-300 border-slate-100">
                    <div className="flex justify-between items-center">
                      <label className={labelClass}>YouTube Downloader (yt-dlp)</label>
                    </div>

                    <div className="p-4 rounded-xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 border-cream-200 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-500">
                            <Play size={14} />
                          </div>
                          <div>
                            <h4 className="font-bold text-xs text-dark-200 dark:text-cream-200">yt-dlp Engine</h4>
                            <p className={sysStatusInfo.className}>{sysStatusInfo.text}</p>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            setSysCheckLoading(true);
                            try {
                              const res = await axios.get(`${API_BASE}/api/system/ytdlp-status`);
                              if (res.data.available === true) {
                                addLog(`SYSTEM: yt-dlp is active (v${res.data.version})`, 'success');
                                setSysStatusInfo({ text: `v${res.data.version} (${res.data.source})`, className: 'text-[11px] font-bold text-emerald-500' });
                              } else {
                                throw new Error(res.data.error || "Not available");
                              }
                            } catch (err) {
                              addLog('ERROR: System Check Failed - ' + err.message, 'error');
                              setSysStatusInfo({ text: "Error: " + (err.response?.data?.error || err.message), className: 'text-[11px] font-bold text-red-500' });
                            }
                            setSysCheckLoading(false);
                          }}
                          disabled={sysCheckLoading}
                          className="px-3 py-1.5 text-[11px] font-bold bg-dark-200 hover:bg-dark-100 text-white rounded-lg transition-all disabled:opacity-50"
                        >
                          {sysCheckLoading ? 'Checking...' : 'Check'}
                        </button>
                      </div>

                      {/* Update yt-dlp Button */}
                      <div className="pt-3 border-t dark:border-dark-300 border-cream-200 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-bold text-cream-500">Getting 403 errors?</p>
                            <p className="text-[11px] text-cream-400">Update yt-dlp to fix YouTube download issues</p>
                          </div>
                          <select
                            value={ytdlpChannel}
                            onChange={(e) => setYtdlpChannel(e.target.value)}
                            disabled={ytdlpUpdateState === 'updating'}
                            title="stable: well-tested, slower YouTube fixes&#10;nightly: latest extractor fixes (recommended for YouTube errors)"
                            className="px-2 py-1.5 text-[11px] font-bold bg-dark-300 hover:bg-dark-200 text-cream-300 rounded-lg transition-all disabled:opacity-50 cursor-pointer border border-dark-100"
                          >
                            <option value="stable">Stable</option>
                            <option value="nightly">Nightly</option>
                          </select>
                          <button
                            onClick={async () => {
                              setYtdlpUpdateState('updating');
                              addLog(`SYSTEM: Downloading latest yt-dlp (${ytdlpChannel})...`, 'info');
                              const pollId = setInterval(async () => {
                                try {
                                  const p = await axios.get(`${API_BASE}/api/system/download-progress/ytdlp`);
                                  if (p.data.status === 'downloading') setYtdlpProgress(p.data);
                                } catch {}
                              }, 1000);
                              try {
                                const res = await axios.post(`${API_BASE}/api/system/ytdlp-update`, { channel: ytdlpChannel }, { timeout: 120000 });
                                if (res.data.success) {
                                  addLog(`SUCCESS: ${res.data.message}`, 'success');
                                  setYtdlpUpdateState('updated');
                                  setSysStatusInfo({ text: `v${res.data.newVersion} (updated)`, className: 'text-[11px] font-bold text-emerald-500' });
                                } else {
                                  throw new Error(res.data.error || 'Update failed');
                                }
                              } catch (err) {
                                const errMsg = err.response?.data?.error || err.message;
                                addLog(`ERROR: yt-dlp update failed - ${errMsg}`, 'error');
                                setYtdlpUpdateState('failed');
                              }
                              clearInterval(pollId);
                              setYtdlpProgress(null);
                              setTimeout(() => setYtdlpUpdateState('idle'), 3000);
                            }}
                            disabled={ytdlpUpdateState === 'updating'}
                            className="px-3 py-1.5 text-[11px] font-bold bg-gold-600 hover:bg-gold-500 text-white rounded-lg transition-all flex items-center gap-1 disabled:opacity-50"
                          >
                            <RotateCcw size={12} /> {ytdlpUpdateState === 'updating' ? 'Updating...' : ytdlpUpdateState === 'updated' ? 'Updated!' : ytdlpUpdateState === 'failed' ? 'Failed' : 'Update yt-dlp'}
                          </button>
                        </div>
                        {ytdlpUpdateState === 'updating' && ytdlpProgress && (
                          <div className="space-y-1">
                            <div className="w-full h-1.5 bg-dark-200 rounded-full overflow-hidden">
                              <div className="h-full bg-gold-500 rounded-full transition-all duration-300" style={{ width: `${ytdlpProgress.percent}%` }} />
                            </div>
                            <p className="text-[10px] text-cream-500">{ytdlpProgress.message}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 403 Troubleshooting Tips */}
                    <details className="group">
                      <summary className="cursor-pointer text-[11px] font-bold text-cream-500 hover:text-cream-400 flex items-center gap-1">
                        <HelpCircle size={12} /> 403 Error Troubleshooting Guide
                      </summary>
                      <div className="mt-3 p-4 rounded-xl dark:bg-dark-600 bg-cream-100 text-[11px] space-y-3">
                        <div>
                          <p className="font-bold text-cream-400 mb-1">Common Causes:</p>
                          <ul className="text-cream-500 space-y-1 list-disc list-inside">
                            <li><strong>Outdated yt-dlp</strong> - Click "Update yt-dlp" above</li>
                            <li><strong>YouTube API changes</strong> - Updates needed every 2-4 weeks</li>
                            <li><strong>Rate limiting</strong> - Wait a few minutes and try again</li>
                            <li><strong>Region restrictions</strong> - Video may be geo-blocked</li>
                            <li><strong>Age-restricted</strong> - See YouTube Authentication below</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-bold text-cream-400 mb-1">Quick Fixes:</p>
                          <ol className="text-cream-500 space-y-1 list-decimal list-inside">
                            <li>Update yt-dlp using the button above</li>
                            <li>Try a different YouTube video</li>
                            <li>Wait 5-10 minutes if rate limited</li>
                            <li>Restart the Qlipper application</li>
                            <li>Enable YouTube Authentication for age-restricted videos</li>
                          </ol>
                        </div>
                        <p className="text-cream-500 italic">
                          Note: YouTube frequently updates their systems. If v1.2 worked but newer versions don't,
                          the bundled yt-dlp likely needs updating. Use the "Update yt-dlp" button.
                        </p>
                      </div>
                    </details>
                  </div>

                  {/* YouTube Authentication (Cookie Browser) */}
                  <div className="space-y-4 pt-4 border-t dark:border-dark-300 border-slate-100">
                    <div className="flex justify-between items-center">
                      <label className={labelClass}>YouTube Authentication</label>
                    </div>

                    <div className="p-4 rounded-xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 border-cream-200 space-y-4">
                      <div className="space-y-2">
                        <p className="text-[11px] text-cream-500 leading-relaxed">
                          Enable this to access age-restricted and members-only videos by using your browser's YouTube login session. 
                          This helps bypass automated access restrictions while keeping your downloads seamless.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-bold uppercase tracking-widest text-cream-500">Browser Selection</label>
                        <select
                          value={cookieBrowser}
                          onChange={(e) => {
                            setCookieBrowser(e.target.value);
                            if (e.target.value !== 'none') {
                              addLog(`SYSTEM: YouTube authentication enabled via ${e.target.value}`, 'success');
                            } else {
                              addLog('SYSTEM: YouTube authentication disabled', 'info');
                            }
                          }}
                          className={inputClass}
                        >
                          <option value="none">None (Default)</option>
                          <option value="edge">Edge (Recommended on Windows)</option>
                          <option value="chrome">Chrome</option>
                          <option value="firefox">Firefox</option>
                          <option value="safari">Safari (macOS only)</option>
                        </select>
                        <p className="text-[11px] text-cream-400">
                          {cookieBrowser === 'none' 
                            ? 'Authentication disabled. Age-restricted videos may fail to download.' 
                            : `Using ${cookieBrowser.charAt(0).toUpperCase() + cookieBrowser.slice(1)} cookies. Make sure you're logged into YouTube in ${cookieBrowser.charAt(0).toUpperCase() + cookieBrowser.slice(1)}.`
                          }
                        </p>
                      </div>

                      {cookieBrowser !== 'none' && (
                        <div className="p-3 rounded-lg dark:bg-amber-500/10 bg-amber-50 border dark:border-amber-500/20 border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                            <div className="text-[11px] text-amber-600 dark:text-amber-400 space-y-1">
                              <p className="font-bold">Privacy Notice</p>
                              <p>This feature uses your YouTube login session from {cookieBrowser.charAt(0).toUpperCase() + cookieBrowser.slice(1)}. Your credentials stay secure in your browser and are never sent to our servers.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <details className="group">
                        <summary className="cursor-pointer text-[11px] font-bold text-cream-500 hover:text-cream-400 flex items-center gap-1">
                          <HelpCircle size={12} /> When to enable this?
                        </summary>
                        <div className="mt-2 p-3 rounded-lg dark:bg-dark-600 bg-cream-100 text-[11px] text-cream-500 space-y-2">
                          <p><strong>Enable if you're downloading:</strong></p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Age-restricted videos (18+)</li>
                            <li>Members-only content (if you have membership)</li>
                            <li>Videos that show "Login Required" error</li>
                          </ul>
                          <p className="pt-2"><strong>Keep disabled if:</strong></p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Downloading public videos (faster, no authentication overhead)</li>
                            <li>You don't have the selected browser installed</li>
                            <li>You're not logged into YouTube in that browser</li>
                          </ul>
                        </div>
                      </details>
                    </div>
                  </div>
                  {/* TRANSCRIPTION ENGINE (Whisper) */}
                  <div className="space-y-4 pt-4 border-t dark:border-dark-300 border-slate-100">
                    <div className="flex justify-between items-center">
                      <label className={labelClass}>Transcription Engine</label>
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API_BASE}/api/system/whisper-version`);
                            setWhisperStatus(res.data);
                          } catch { setWhisperStatus({ installed: false, availableModels: [] }); }
                        }}
                        className="text-[11px] text-gold-500 hover:text-gold-400 font-bold"
                      >
                        Check Status
                      </button>
                    </div>

                    <div className="p-4 rounded-xl dark:bg-dark-400 bg-cream-100 border dark:border-dark-300 border-cream-200 space-y-4">
                      {/* Status */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[11px] font-bold text-cream-500">Whisper Status</p>
                          <p className={`text-[11px] font-bold ${whisperStatus?.installed ? 'text-emerald-500' : 'text-cream-400'}`}>
                            {whisperStatus ? (whisperStatus.installed ? `Installed (${whisperStatus.availableModels?.map(m => m.name).join(', ') || 'no models'})` : 'Not installed') : 'Click "Check Status" to verify'}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            setWhisperUpdateState('updating');
                            addLog('SYSTEM: Downloading whisper model...', 'info');
                            const pollId = setInterval(async () => {
                              try {
                                const p = await axios.get(`${API_BASE}/api/system/download-progress/whisper-model`);
                                if (p.data.status === 'downloading') setWhisperProgress(p.data);
                              } catch {}
                            }, 1000);
                            try {
                              const res = await axios.post(`${API_BASE}/api/system/whisper-update`, { action: 'download-model', model: whisperModel }, { timeout: 300000 });
                              if (res.data.success) {
                                addLog(`SUCCESS: Model "${whisperModel}" downloaded (${res.data.sizeMB}MB)`, 'success');
                                setWhisperUpdateState('updated');
                                const status = await axios.get(`${API_BASE}/api/system/whisper-version`);
                                setWhisperStatus(status.data);
                              } else { throw new Error(res.data.message || 'Download failed'); }
                            } catch (err) {
                              addLog(`ERROR: Model download failed - ${err.response?.data?.error || err.message}`, 'error');
                              setWhisperUpdateState('failed');
                            }
                            clearInterval(pollId);
                            setWhisperProgress(null);
                            setTimeout(() => setWhisperUpdateState('idle'), 3000);
                          }}
                          disabled={whisperUpdateState === 'updating'}
                          className="px-3 py-1.5 text-[11px] font-bold bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-all flex items-center gap-1 disabled:opacity-50"
                        >
                          <RotateCcw size={12} /> {whisperUpdateState === 'updating' ? 'Downloading...' : whisperUpdateState === 'updated' ? 'Done!' : 'Download Model'}
                        </button>
                      </div>
                      {whisperUpdateState === 'updating' && whisperProgress && (
                        <div className="space-y-1 mt-2">
                          <div className="w-full h-1.5 bg-dark-200 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-500 rounded-full transition-all duration-300" style={{ width: `${whisperProgress.percent}%` }} />
                          </div>
                          <p className="text-[10px] text-cream-500">{whisperProgress.message}</p>
                        </div>
                      )}

                      {/* Engine selector */}
                      <div className="space-y-2">
                        <p className="text-[11px] font-bold text-cream-500">Engine</p>
                        <select value={whisperEngine} onChange={(e) => setWhisperEngine(e.target.value)} className={inputClass}>
                          <option value="whisper-cpp">Whisper.cpp (Built-in, fastest)</option>
                          <option value="whisper-wasm">Whisper WASM (No install needed)</option>
                        </select>
                        <p className="text-[11px] text-cream-400">
                          {whisperEngine === 'whisper-cpp'
                            ? 'Uses the bundled whisper-cli binary. Fastest option.'
                            : 'Runs entirely in browser/WASM — no binary download needed. Slower but works everywhere, no antivirus issues.'}
                        </p>
                      </div>

                      {/* WASM Fallback checkbox — only show when not already using WASM */}
                      {whisperEngine !== 'whisper-wasm' && (
                        <label className="flex items-center gap-3 p-3 rounded-xl dark:bg-dark-400/30 bg-cream-100 border dark:border-dark-300 border-slate-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={whisperWasmFallback}
                            onChange={(e) => setWhisperWasmFallback(e.target.checked)}
                            className="w-4 h-4 rounded accent-amber-500"
                          />
                          <div>
                            <p className="text-xs font-semibold dark:text-white text-slate-800">Auto-fallback to WASM if binary fails</p>
                            <p className="text-[11px] dark:text-cream-400 text-cream-500">If whisper-cli is blocked by antivirus or missing, automatically use the WASM engine instead. Slower but guaranteed to work.</p>
                          </div>
                        </label>
                      )}

                      {/* Model selector */}
                      <div className="space-y-2">
                        <p className="text-[11px] font-bold text-cream-500">Model</p>
                        <select value={whisperModel} onChange={(e) => setWhisperModel(e.target.value)} className={inputClass}>
                          <option value="tiny">Tiny (75MB, fastest, basic quality)</option>
                          <option value="base">Base (142MB, balanced)</option>
                          <option value="small">Small (466MB, better accuracy)</option>
                          <option value="medium">Medium (1.5GB, high accuracy)</option>
                        </select>
                      </div>

                      {/* Manual binary replacement (for AV-quarantine edge cases) */}
                      <div className="space-y-2 pt-3 mt-3 border-t dark:border-dark-300 border-slate-100">
                        <p className="text-[11px] font-bold text-cream-500">Manual binary replacement</p>
                        <p className="text-[11px] dark:text-cream-400 text-cream-500">
                          If your antivirus keeps blocking the bundled Whisper or yt-dlp binaries, download the
                          official releases below and drop them into the binaries folder.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <a
                            href="https://github.com/ggml-org/whisper.cpp/releases"
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 hover:bg-cream-200 dark:hover:bg-dark-200 text-xs font-semibold transition-all"
                            title="Opens https://github.com/ggml-org/whisper.cpp/releases — download whisper-bin-x64.zip (Windows) or build from source (macOS)"
                          >
                            <Download size={14} /> Whisper.cpp releases
                          </a>
                          <a
                            href="https://github.com/yt-dlp/yt-dlp/releases"
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-cream-100 dark:bg-dark-300 hover:bg-cream-200 dark:hover:bg-dark-200 text-xs font-semibold transition-all"
                            title="Opens https://github.com/yt-dlp/yt-dlp/releases — grab yt-dlp.exe (Windows) or yt-dlp_macos (macOS)"
                          >
                            <Download size={14} /> yt-dlp releases
                          </a>
                          <button
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API_BASE}/api/system/open-binaries-folder`);
                                addLog(`Opened binaries folder: ${res.data.path}`, 'success');
                              } catch (err) {
                                addLog(`Failed to open binaries folder: ${err.response?.data?.error || err.message}`, 'error');
                              }
                            }}
                            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-semibold transition-all"
                          >
                            <FolderOpen size={14} /> Open binaries folder
                          </button>
                        </div>
                        <p className="text-[10px] dark:text-cream-500 text-cream-400 leading-relaxed">
                          <strong>Windows:</strong> replace whisper-cli.exe, whisper.dll, ggml.dll, ggml-base.dll, ggml-cpu.dll, yt-dlp.exe.{' '}
                          <strong>macOS:</strong> replace whisper-cli, yt-dlp — then run{' '}
                          <code className="px-1 rounded bg-cream-200 dark:bg-dark-200">xattr -cr</code> and{' '}
                          <code className="px-1 rounded bg-cream-200 dark:bg-dark-200">chmod +x</code> on each to clear the quarantine bit.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* JOBS-SYSTEM PIPELINE TOGGLE (PR 3d beta) */}
                  <div className="flex items-center justify-between p-4 dark:bg-dark-400/40 bg-cream-100 border dark:border-dark-300 border-slate-100 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <Activity size={18} className="text-gold-500" />
                      <div>
                        <h4 className="font-bold text-sm">Resumable Pipeline (Beta)</h4>
                        <p className="text-[11px] dark:text-cream-400 text-cream-400">
                          Use the new job system for download → transcribe → analyze. If a step fails, you can resume from where it left off. Render still uses the legacy renderer.
                        </p>
                      </div>
                    </div>
                    <button onClick={() => {
                      setUseJobsSystem(!useJobsSystem);
                      addLog(`SYSTEM: Resumable pipeline ${!useJobsSystem ? 'enabled' : 'disabled'}`, 'info');
                    }} className={`w-12 h-6 rounded-full transition-all relative flex-shrink-0 ${useJobsSystem ? 'bg-gold-600' : 'bg-dark-200'}`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${useJobsSystem ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  {/* PR 4e: LICENSE SERVER STATUS CHECK */}
                  <div className="flex items-center justify-between p-4 dark:bg-dark-400/40 bg-cream-100 border dark:border-dark-300 border-slate-100 rounded-2xl">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Activity size={18} className={`flex-shrink-0 ${
                        licenseServerStatus === 'up'   ? 'text-green-500'  :
                        licenseServerStatus === 'down' ? 'text-red-500'    :
                        licenseServerStatus === 'checking' ? 'text-gold-500 animate-pulse' :
                        'text-gold-500'
                      }`} />
                      <div className="min-w-0">
                        <h4 className="font-bold text-sm">License Server</h4>
                        <p className="text-[11px] dark:text-cream-400 text-cream-400 truncate">
                          {licenseServerStatus === 'idle'     && 'Test if Qlipper\'s license server is reachable.'}
                          {licenseServerStatus === 'checking' && 'Pinging license.qlipper.id...'}
                          {licenseServerStatus === 'up'       && `✓ Server is up (${licenseServerLatency}ms). If you got a license error, it's a local issue — re-activate your license.`}
                          {licenseServerStatus === 'down'     && '✗ Server unreachable. License errors right now are server-side — try again in a few minutes.'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={checkLicenseServer}
                      disabled={licenseServerStatus === 'checking'}
                      className="px-3 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-all flex-shrink-0"
                    >
                      <Activity size={11} className={licenseServerStatus === 'checking' ? 'animate-spin' : ''} />
                      {licenseServerStatus === 'checking' ? 'Testing...' : 'Test'}
                    </button>
                  </div>

                  {/* ERROR REPORTING OPT-IN */}
                  <div className="flex items-center justify-between p-4 dark:bg-dark-400/40 bg-cream-100 border dark:border-dark-300 border-slate-100 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <Activity size={18} className="text-gold-500" />
                      <div>
                        <h4 className="font-bold text-sm">Help Improve Qlipper</h4>
                        <p className="text-[11px] dark:text-cream-400 text-cream-400">Send anonymous error reports to help us fix bugs faster</p>
                      </div>
                    </div>
                    <button onClick={() => {
                      setErrorReporting(!errorReporting);
                      addLog(`SYSTEM: Error reporting ${!errorReporting ? 'enabled' : 'disabled'}`, 'info');
                    }} className={`w-12 h-6 rounded-full transition-all relative ${errorReporting ? 'bg-gold-600' : 'bg-dark-200'}`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${errorReporting ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 dark:bg-dark-400/40 bg-cream-100 border dark:border-dark-300 border-slate-100 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <Terminal size={18} className="text-gold-500" />
                      <div><h4 className="font-bold text-sm">System Log Stream</h4><p className="text-[11px] dark:text-cream-400 text-cream-400">Show terminal output at bottom</p></div>
                    </div>
                    <button onClick={() => setShowConsole(!showConsole)} className={`w-12 h-6 rounded-full transition-all relative ${showConsole ? 'bg-gold-600' : 'bg-dark-200'}`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${showConsole ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                  <div className="bg-cream-100 dark:bg-dark-400 p-5 rounded-2xl border border-cream-200 dark:border-dark-200 italic text-[11px] text-cream-500 dark:text-cream-400">
                    * All settings stored locally in your browser. Nothing is sent to our servers.
                  </div>
                </div>

                {/* SX-1: simple Close footer (auto-save handles persistence — no
                    explicit Save button needed). All `localStorage.setItem` calls
                    that used to live here are now redundant; the App-level
                    useEffect (line ~2716) covers every persisted field. */}
                <div className="flex-shrink-0 p-6 border-t border-cream-200 dark:border-dark-200 bg-cream-50 dark:bg-dark-500 flex items-center justify-between gap-4">
                  <span className="text-[11px] text-cream-500 dark:text-cream-400 flex items-center gap-1.5">
                    <Check size={11} className="text-green-500" />
                    Changes save automatically
                  </span>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-6 py-3 rounded-xl bg-cream-200 hover:bg-cream-300 dark:bg-dark-300 dark:hover:bg-dark-200 text-dark-200 dark:text-cream-100 font-bold text-sm transition-all flex items-center gap-2 active:scale-95"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence >

        {/* DELETE CONFIRMATION MODAL */}
        <AnimatePresence>
          {deleteConfirmation.isOpen && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
                onClick={() => setDeleteConfirmation({ isOpen: false, assetId: null })}
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="relative bg-dark-400 border border-dark-300 p-8 rounded-3xl shadow-2xl max-w-sm w-full text-center space-y-6"
              >
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto text-red-500 mb-4">
                  <Trash2 size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Delete Asset?</h3>
                  <p className="text-xs text-cream-400">This action cannot be undone. The file will be permanently removed from your library.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmation({ isOpen: false, assetId: null })}
                    className="flex-1 py-3 rounded-xl bg-dark-300 hover:bg-dark-200 text-white font-bold text-xs uppercase tracking-widest transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeDeleteAsset}
                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-500/20"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        {/* PROJECT DELETE CONFIRMATION MODAL */}
        <AnimatePresence>
          {projectToDelete && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
                onClick={() => setProjectToDelete(null)}
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="relative bg-dark-400 border border-dark-300 p-8 rounded-3xl shadow-2xl max-w-sm w-full text-center space-y-6"
              >
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto text-red-500 mb-4">
                  <Trash2 size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Delete Project?</h3>
                  <p className="text-xs text-cream-400">
                    Are you sure you want to delete <span className="text-white font-bold">"{projectToDelete.title}"</span>?
                    <br />This action cannot be undone.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setProjectToDelete(null)}
                    className="flex-1 py-3 rounded-xl bg-dark-300 hover:bg-dark-200 text-white font-bold text-xs uppercase tracking-widest transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteProject}
                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-500/20"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        {/* PIPELINE SETUP MODAL */}
        <AnimatePresence>
          {pipelineSetup && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 pt-16">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPipelineSetup(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" />
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative w-full max-w-5xl dark:bg-dark-400 bg-white border dark:border-dark-300 border-cream-200 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh] mt-4">

                {/* Modal Header */}
                <div className="p-8 border-b dark:border-dark-300 border-slate-100 flex justify-between items-center bg-cream-100/50 dark:bg-dark-400/50">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Pipeline Configuration</h2>
                    <p className="text-sm text-cream-500">
                      {pipelineMode === 'upload'
                        ? `Upload mode — "${pipelineUploadedAsset?.name || 'Uploaded file'}"`
                        : 'Customize how AI extracts and formats your clips.'}
                    </p>
                  </div>
                  <button onClick={() => setPipelineSetup(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-dark-300 rounded-xl transition-all">
                    <X size={24} />
                  </button>
                </div>

                <div className="p-8 overflow-y-auto custom-scrollbar space-y-8 bg-cream-100/30 dark:bg-dark-600/30">

                  {/* 0. AI Model — read-only, managed in Settings */}
                  {(() => {
                    const modelMeta = {
                      'gemini':   { name: 'Gemini', icon: '✨', desc: 'Fast & Free' },
                      'gpt-4o':   { name: 'GPT-5.4', icon: '🧠', desc: 'Best Quality' },
                      'claude':   { name: 'Claude', icon: '🎭', desc: 'Long Context' },
                      'mistral':  { name: 'Mistral', icon: '🌊', desc: 'Open Alt' },
                      'deepseek': { name: 'DeepSeek', icon: '🐋', desc: 'Reasoning' },
                      'xai':      { name: 'Grok', icon: '⚡', desc: 'xAI · Elon\'s AI' },
                      'router':   { name: 'Router / API lain', icon: '🔀', desc: 'OmniRoute & lainnya' },
                    };
                    const active = modelMeta[selectedModel];
                    const hasKey = selectedModel === 'router' ? !routerMissing : selectedModel === 'gemini' ? !!apiKey : (selectedModel === 'gpt-4o' ? !!openaiKey : selectedModel === 'claude' ? !!claudeKey : selectedModel === 'mistral' ? !!mistralKey : selectedModel === 'minimax' ? !!minimaxKey : selectedModel === 'xai' ? !!xaiKey : !!deepseekKey);
                    return (
                      <div className="flex items-center justify-between p-4 rounded-2xl bg-cream-100 dark:bg-dark-300 border border-cream-300 dark:border-dark-50">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{active?.icon || '✨'}</span>
                          <div>
                            <p className="text-xs font-bold text-dark-200 dark:text-cream-100">AI Model</p>
                            <p className="text-sm font-bold text-gold-500">{active?.name || 'Gemini'} <span className="text-[11px] font-normal text-cream-500 dark:text-cream-400">{active?.desc}</span></p>
                          </div>
                        </div>
                        {!hasKey ? (
                          <button onClick={() => { setPipelineSetup(false); setShowSettings(true); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-500 text-[11px] font-bold hover:bg-amber-500/20 transition-all">
                            <AlertCircle size={12} /> Set API Key in Settings
                          </button>
                        ) : (
                          <button onClick={() => { setPipelineSetup(false); setShowSettings(true); }} className="text-[11px] text-gold-500 hover:text-gold-400 font-bold transition-colors">
                            Change in Settings →
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  <div className="w-full h-px bg-cream-200 dark:bg-dark-100" />

                  {/* 1. Video Format Picker (Aspect Ratio + Style + Resolution) */}
                  {/* Face-detection controls live on styleSettings — the backend
                      pipeline reads liveFaceDetection/faceDetectionMode/trackingStyle
                      from there. Without these props the Zoom (Face) controls were
                      missing from the front pipeline flow (beta feedback, v2.3). */}
                  <FormatPicker
                    selectedFormat={outputFormat}
                    onFormatChange={setOutputFormat}
                    selectedResolution={outputResolution}
                    onResolutionChange={setOutputResolution}
                    compact={false}
                    faceDetectionEnabled={styleSettings.liveFaceDetection || false}
                    faceDetectionMode={styleSettings.faceDetectionMode || 'sekali'}
                    trackingStyle={styleSettings.trackingStyle || 'smooth'}
                    onFaceDetectionChange={(changes) => setStyleSettings(prev => ({
                      ...prev,
                      ...(changes.enabled !== undefined && { liveFaceDetection: changes.enabled }),
                      ...(changes.mode && { faceDetectionMode: changes.mode }),
                      ...(changes.trackingStyle && { trackingStyle: changes.trackingStyle }),
                    }))}
                  />

                  <div className="w-full h-px bg-slate-200 dark:bg-dark-300" />

                  {/* 2. Download Quality — hidden for upload mode */}
                  {pipelineMode === 'upload' && (
                    <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
                      <p className="text-xs text-emerald-300">File already uploaded — no download needed. Transcription is running in the background.</p>
                    </div>
                  )}
                  {pipelineMode !== 'upload' && <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-2">
                      <Download size={14} /> Download Quality
                      {qualityProbing && <Loader2 className="animate-spin text-gold-400" size={12} />}
                      {availableQualities && <span className="text-[11px] text-gold-400 font-medium ml-1">(max {availableQualities.maxHeight}p)</span>}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      {[
                        { id: 'best', label: 'Best', desc: 'Best A/V', minHeight: 0, icon: true },
                        { id: '4k', label: '4K', desc: 'Ultra HD', minHeight: 2160 },
                        { id: '1440', label: '1440p', desc: 'QHD', minHeight: 1440 },
                        { id: '1080', label: '1080p', desc: 'Full HD', minHeight: 1080 },
                        { id: '720', label: '720p', desc: 'HD', minHeight: 720 },
                        { id: '480', label: '480p', desc: 'Fast DL', minHeight: 480 }
                      ].filter(q => !availableQualities || availableQualities.maxHeight >= q.minHeight)
                        .map(q => (
                          <button
                            key={q.id}
                            onClick={() => setPipelineQuality(q.id)}
                            className={`p-3 rounded-xl border flex items-center justify-between px-4 transition-all ${pipelineQuality === q.id ? 'border-gold-500 bg-gold-500/10 text-gold-500' : 'border-cream-200 dark:border-dark-300 hover:border-gold-300 dark:hover:border-gold-700'}`}
                          >
                            <div className="text-left flex items-center gap-1.5">
                              {q.icon && <Sparkles size={12} className={pipelineQuality === q.id ? 'text-gold-500' : 'text-cream-400'} />}
                              <div>
                                <div className="font-bold text-xs">{q.label}</div>
                                <div className="text-[11px] opacity-60 uppercase">{q.desc}</div>
                              </div>
                            </div>
                            {pipelineQuality === q.id && <CheckCircle2 size={16} />}
                          </button>
                        ))}
                    </div>
                  </div>}

                  <div className="w-full h-px bg-slate-200 dark:bg-dark-300" />

                  {/* 2.5. AI Prompt Selector */}
                  <div className="space-y-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-2">
                      <Sparkles size={14} /> AI Analysis Prompt
                    </label>
                    <select
                      value={selectedPromptIdx}
                      onChange={(e) => {
                        const idx = parseInt(e.target.value);
                        setSelectedPromptIdx(idx);
                        if (idx === 0) setPipelineInstruction('');
                        else if (idx === -1) setPipelineInstruction('');
                        else if (promptPresets[idx - 1]) setPipelineInstruction(promptPresets[idx - 1].prompt || '');
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-cream-200 dark:border-dark-200 bg-white dark:bg-dark-400 text-sm"
                    >
                      <option value={0}>Default (Viral Highlights)</option>
                      {promptPresets.map((p, i) => (
                        <option key={p.id || i} value={i + 1}>{p.name || `Preset ${i + 1}`}</option>
                      ))}
                      <option value={-1}>Custom...</option>
                    </select>
                    {selectedPromptIdx === -1 && (
                      <textarea
                        value={pipelineInstruction}
                        onChange={(e) => setPipelineInstruction(e.target.value)}
                        placeholder="Enter custom instruction for AI analysis..."
                        className="w-full px-3 py-2 rounded-lg border border-cream-200 dark:border-dark-200 bg-white dark:bg-dark-400 text-sm min-h-[60px] resize-y"
                        rows={2}
                      />
                    )}
                  </div>

                  <div className="w-full h-px bg-slate-200 dark:bg-dark-300" />

                  {/* 3. Clip Duration */}
                  {/* Number of Clips */}
                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-2">
                      <Scissors size={14} /> Number of Clips
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[
                        { id: 3, label: '3 clips', desc: 'Focused' },
                        { id: 5, label: '5 clips', desc: 'Balanced' },
                        { id: 7, label: '7 clips', desc: 'More variety' },
                        { id: 10, label: '10 clips', desc: 'Maximum' },
                        { id: 15, label: '15 clips', desc: 'Full sweep' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setAdvNumCuts(opt.id)}
                          className={`p-3 rounded-xl border flex items-center justify-between px-4 transition-all ${advNumCuts === opt.id ? 'border-gold-500 bg-gold-500/10 text-gold-500' : 'border-cream-200 dark:border-dark-300 hover:border-gold-300 dark:hover:border-gold-700'}`}
                        >
                          <div className="text-left">
                            <div className="font-bold text-xs">{opt.label}</div>
                            <div className="text-[11px] opacity-60 uppercase">{opt.desc}</div>
                          </div>
                          {advNumCuts === opt.id && <CheckCircle2 size={16} />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="w-full h-px bg-slate-200 dark:bg-dark-300" />

                  {/* Target Clip Duration */}
                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-dark-200 dark:text-cream-400 flex items-center gap-2">
                      <Clock size={14} /> Target Clip Duration
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: '<30s', label: 'Under 30s', desc: 'Fast Paced' },
                        { id: '30s-60s', label: '30s - 60s', desc: 'Standard Viral' },
                        { id: '60s-90s', label: '60s - 90s', desc: 'Storytelling' },
                        { id: '90s-3min', label: '90s - 3min', desc: 'Long Form' }
                      ].map(len => (
                        <button
                          key={len.id}
                          onClick={() => setClipLength(len.id)}
                          className={`p-3 rounded-xl border flex items-center justify-between px-4 transition-all ${clipLength === len.id ? 'border-gold-500 bg-gold-500/10 text-gold-500' : 'border-cream-200 dark:border-dark-300 hover:border-gold-300 dark:hover:border-gold-700'}`}
                        >
                          <div className="text-left">
                            <div className="font-bold text-xs">{len.label}</div>
                            <div className="text-[11px] opacity-60 uppercase">{len.desc}</div>
                          </div>
                          {clipLength === len.id && <CheckCircle2 size={16} />}
                        </button>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t dark:border-dark-300 bg-white dark:bg-dark-400 flex justify-end gap-4">
                  <button onClick={() => setPipelineSetup(false)} className="px-6 py-4 rounded-xl font-bold text-cream-500 hover:text-dark-200 dark:hover:text-cream-200 transition-colors">Cancel</button>
                  <button
                    onClick={() => setClipEditModal({ isOpen: true, type: 'full', clipIdx: 'preset' })}
                    className="px-6 py-4 rounded-xl border-2 border-gold-500/50 text-gold-500 font-bold hover:bg-gold-500/10 transition-all flex items-center gap-2"
                    title="Configure video style settings"
                  >
                    <span>✨</span> Style Settings
                  </button>
                  <button onClick={startPipelineFromSetup} className="px-8 py-4 rounded-xl bg-gold-600 hover:bg-gold-500 text-white font-bold shadow-gold-lg/20 flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all">
                    <Sparkles size={18} /> {getClipsBtnText}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </main>

      {/* FIXED CONSOLE */}
      <ApiKeyPrompt
        isOpen={showApiKeyPrompt}
        onClose={() => setShowApiKeyPrompt(false)}
        onSave={(key) => {
          setApiKey(key);
          setShowApiKeyPrompt(false);
          addLog("SYSTEM: API Key Saved & Verified", "success");
        }}
      />

      {/* TRANSCRIPTION PROGRESS MODAL — shown during upload → transcribe flow */}
      <AnimatePresence>
        {showTranscribingModal && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="w-full max-w-sm bg-cream-50 dark:bg-dark-400 rounded-3xl border border-cream-200 dark:border-dark-200 shadow-2xl p-8 text-center space-y-6"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto ${transcribingProgress.status === 'done' ? 'bg-emerald-500/10 text-emerald-500' : transcribingProgress.status === 'error' ? 'bg-red-500/10 text-red-500' : 'bg-gold-500/10 text-gold-500'}`}>
                {transcribingProgress.status === 'done'
                  ? <CheckCircle2 size={32} />
                  : transcribingProgress.status === 'error'
                  ? <AlertCircle size={32} />
                  : <img src={brandIcon('film-reel', darkMode)} className="w-8 h-8 object-contain" alt="" />}
              </div>

              {/* Title */}
              <div>
                <h3 className="text-lg font-bold text-dark-200 dark:text-cream-100 mb-1">
                  {transcribingProgress.status === 'done' ? 'Ready!' : transcribingProgress.status === 'error' ? 'Something went wrong' : 'Preparing your file…'}
                </h3>
                <p className="text-sm text-cream-500 dark:text-cream-400">{transcribingProgress.message}</p>
              </div>

              {/* Steps */}
              <div className="flex items-center justify-center gap-3">
                {[
                  { key: 'uploading', label: 'Upload' },
                  { key: 'transcribing', label: 'Transcribe' },
                  { key: 'done', label: 'Ready' },
                ].map((step, i, arr) => {
                  const statuses = ['uploading', 'transcribing', 'waiting', 'done'];
                  const currentIdx = statuses.indexOf(transcribingProgress.status);
                  const stepIdx = statuses.indexOf(step.key);
                  const isDone = currentIdx > stepIdx || transcribingProgress.status === 'done';
                  const isActive = step.key === 'transcribing' && (transcribingProgress.status === 'transcribing' || transcribingProgress.status === 'waiting');
                  const isUpload = step.key === 'uploading' && transcribingProgress.status === 'uploading';
                  return (
                    <React.Fragment key={step.key}>
                      <div className="flex flex-col items-center gap-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${isDone ? 'bg-gold-500 border-gold-400 text-white' : (isActive || isUpload) ? 'border-gold-500 bg-gold-500/10 text-gold-500' : 'border-cream-300 dark:border-dark-100 text-cream-400 dark:text-cream-400'}`}>
                          {isDone ? <CheckCircle2 size={14} /> : (isActive || isUpload) ? <Loader2 size={14} className="animate-spin" /> : <span className="text-[11px] font-bold">{i + 1}</span>}
                        </div>
                        <span className="text-[11px] font-bold text-cream-500 dark:text-cream-400 uppercase">{step.label}</span>
                      </div>
                      {i < arr.length - 1 && <div className={`h-px w-8 ${isDone ? 'bg-gold-500' : 'bg-cream-300 dark:bg-dark-100'}`} />}
                    </React.Fragment>
                  );
                })}
              </div>

              {transcribingProgress.status === 'error' && (
                <button onClick={() => { setShowTranscribingModal(false); setPipelineMode('youtube'); }}
                  className="w-full py-2.5 rounded-xl bg-cream-200 dark:bg-dark-300 text-dark-200 dark:text-cream-100 font-bold text-xs transition-all">
                  Dismiss
                </button>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* QR CODE MODAL — Mobile Access */}
      {showQrModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowQrModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xs sm:max-w-sm bg-cream-50 dark:bg-dark-400 rounded-2xl border border-cream-200 dark:border-dark-200 shadow-2xl overflow-hidden overflow-y-auto max-h-[90vh] p-5 sm:p-8 text-center space-y-4 sm:space-y-6"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto text-cyan-500">
              <Smartphone size={28} />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-dark-200 dark:text-cream-100 mb-1">Open on Phone</h3>
              <p className="text-xs text-cream-500 dark:text-cream-400">Scan this QR code with your phone camera to access Qlipper on your mobile browser.</p>
            </div>
            {networkUrl && (
              <div className="flex flex-col items-center gap-3 sm:gap-4">
                <div className="p-3 sm:p-4 bg-white rounded-2xl">
                  <QRCodeSVG value={networkUrl} size={160} level="M" className="w-[120px] h-[120px] sm:w-[160px] sm:h-[160px]" />
                </div>
                <div className="px-4 py-2 bg-cream-100 dark:bg-dark-400 rounded-xl">
                  <p className="text-xs font-mono text-cream-500 dark:text-cream-400 select-all">{networkUrl}</p>
                </div>
                <p className="text-[11px] text-cream-400 dark:text-cream-400">Make sure your phone is on the same WiFi network</p>
                <div className="w-full flex items-start gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-left">
                  <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400 leading-relaxed">
                    <span className="font-bold">Shared license.</span> License aktif di PC berlaku untuk semua device di WiFi yang sama. Tidak perlu beli lisensi tambahan.
                  </p>
                </div>
              </div>
            )}
            <button onClick={() => setShowQrModal(false)} className="w-full py-3 rounded-xl bg-dark-300 dark:bg-dark-200 hover:bg-dark-200 dark:hover:bg-dark-100 text-cream-100 font-bold text-xs uppercase tracking-widest transition-all">
              Close
            </button>
          </motion.div>
        </div>
      )}

      {/* CLIP EDIT MODAL */}
      <ClipEditModal
        isOpen={clipEditModal.isOpen}
        type={clipEditModal.type}
        clipIdx={clipEditModal.clipIdx}
        clip={selectedHistoryProject?.clips?.[clipEditModal.clipIdx]}
        projectId={selectedHistoryProject?.id}
        editState={clipEditState[clipEditModal.clipIdx]}
        videoThumbnail={pipelineMetadata?.thumbnail || metadata?.thumbnail || null}
        setEditState={setClipEditState}
        captionStatus={captionStatus}
        onClose={() => setClipEditModal({ isOpen: false, type: null, clipIdx: null })}
        onApply={(format, hookText, subtitleText) => {
          const idx = clipEditModal.clipIdx;

          // Preset mode: save config to styleSettings (no reprocess)
          if (idx === 'preset') {
            const presetEdits = clipEditState['preset'] || {};
            const mergedDefaults = {
              ...styleSettings,
              // Hook settings from edit state
              ...(presetEdits.hookText !== undefined && { hookHeadline: { ...styleSettings.hookHeadline, text: presetEdits.hookText } }),
              ...(presetEdits.hookTextColor && { hookHeadline: { ...styleSettings.hookHeadline, color: presetEdits.hookTextColor } }),
              ...(presetEdits.hookFontFamily && { hookFontFamily: presetEdits.hookFontFamily }),
              ...(presetEdits.hookFontSize && { hookFontSize: presetEdits.hookFontSize }),
              ...(presetEdits.hookVerticalPosition !== undefined && { hookVerticalPosition: presetEdits.hookVerticalPosition }),
              ...(presetEdits.hookAlignment && { hookAlignment: presetEdits.hookAlignment }),
              // Subtitle settings
              ...(presetEdits.subtitleEnabled !== undefined && { subtitleEnabled: presetEdits.subtitleEnabled }),
              ...(presetEdits.subtitleTextColor && { subtitleTextColor: presetEdits.subtitleTextColor }),
              ...(presetEdits.subtitleHighlightColor && { subtitleHighlightColor: presetEdits.subtitleHighlightColor }),
              ...(presetEdits.subtitleFontFamily && { subtitleFontFamily: presetEdits.subtitleFontFamily }),
              ...(presetEdits.subtitleFontSize && { subtitleFontSize: presetEdits.subtitleFontSize }),
              ...(presetEdits.subtitlePosition !== undefined && { subtitlePosition: presetEdits.subtitlePosition }),
              // Video style / format
              ...(presetEdits.format && { outputFormat: presetEdits.format }),
              ...(presetEdits.resolution && { resolution: presetEdits.resolution }),
              // Face detection — backend reads these off styleSettings, so the
              // Style Defaults modal must carry them through to the pipeline.
              ...(presetEdits.liveFaceDetection !== undefined && { liveFaceDetection: presetEdits.liveFaceDetection }),
              ...(presetEdits.faceDetectionMode && { faceDetectionMode: presetEdits.faceDetectionMode }),
              ...(presetEdits.trackingStyle && { trackingStyle: presetEdits.trackingStyle }),
              // Default Logo Settings
              ...(logoOverlay['preset'] && {
                defaultLogoPath: logoOverlay['preset'].serverPath,
                defaultLogoX: logoOverlay['preset'].x,
                defaultLogoY: logoOverlay['preset'].y,
                defaultLogoScale: logoOverlay['preset'].scale,
                defaultLogoOpacity: logoOverlay['preset'].opacity
              })
            };
            setStyleSettings(mergedDefaults);
            persistStyleDefaults(mergedDefaults);
            setClipEditModal({ isOpen: false, type: null, clipIdx: null });
            return;
          }

          const projectId = selectedHistoryProject?.id;
          if (projectId === undefined || idx === null) return;

          // Save metadata first
          updateClipMetadata(projectId, idx, {
            customHook: hookText,
            customSubtitle: subtitleText
          });

          // Trigger reprocess with new format
          reprocessClip(projectId, idx, format);

          // Close modal
          setClipEditModal({ isOpen: false, type: null, clipIdx: null });
        }}
        generateAutoHook={generateAutoHook}
        isReprocessing={reprocessingClips[clipEditModal.clipIdx]}
        captionEnhanceStatus={captionEnhanceStatus}
        enhanceCaptions={enhanceCaptions}
        logos={logos}
        logoOverlay={logoOverlay}
        setLogoOverlay={setLogoOverlay}
        fetchLogos={fetchLogos}
        styleSettings={styleSettings}
        setStyleSettings={setStyleSettings}
        multiFormatSelections={multiFormatSelections}
        onMultiFormatToggle={(formatId) => {
          setMultiFormatSelections(prev =>
            prev.includes(formatId)
              ? prev.filter(f => f !== formatId)
              : [...prev, formatId]
          );
        }}
        onMultiFormatApply={(resolution) => {
          const idx = clipEditModal.clipIdx;
          const projectId = selectedHistoryProject?.id;
          if (projectId === undefined || idx === null || multiFormatSelections.length === 0) return;
          setClipEditModal({ isOpen: false, type: null, clipIdx: null });
          reprocessClipMultiFormat(projectId, idx, multiFormatSelections, resolution);
        }}
        videoTitle={selectedHistoryProject?.title || metadata?.title || ''}
      />
      {
        showConsole && (
          <footer className={`fixed bottom-0 left-0 w-full dark:bg-dark-500/90 bg-white/90 backdrop-blur-md border-t dark:border-dark-300 border-cream-200 z-[60] flex flex-col shadow-2xl transition-all duration-300 ${isConsoleMinimized ? 'h-10' : 'h-[180px]'}`}>
            <div className="h-10 dark:bg-dark-400/40 bg-cream-100/50 border-b dark:border-dark-300 border-cream-200 flex items-center justify-between px-8 cursor-pointer" onClick={() => setIsConsoleMinimized(!isConsoleMinimized)}>
              <div className="flex items-center gap-3"><Terminal size={12} className="text-gold-500" /><span className="text-[11px] font-bold uppercase tracking-widest text-cream-500">Live Web-Worker Stream</span></div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => setIsConsoleMinimized(!isConsoleMinimized)}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-dark-300 rounded text-cream-500 transition-colors"
                  title={isConsoleMinimized ? "Expand" : "Minimize"}
                >
                  {isConsoleMinimized ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                <div className="w-px h-3 bg-slate-300 dark:bg-dark-200 mx-1" />
                <button
                  onClick={() => {
                    const logText = logs.map(l => `[${l.type.toUpperCase()}] ${l.msg}`).join('\n');
                    navigator.clipboard.writeText(logText);
                    addLog('SYSTEM: Logs copied to clipboard.', 'success');
                  }}
                  className="px-2 py-1 hover:bg-slate-200 dark:hover:bg-dark-300 rounded text-[11px] font-bold text-cream-500"
                >
                  COPY
                </button>
                <button
                  onClick={() => {
                    const logText = logs.map(l => `[${l.type.toUpperCase()}] ${l.msg}`).join('\n');
                    const blob = new Blob([logText], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `qlipper_logs_${Date.now()}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                    addLog('SYSTEM: Logs exported to file.', 'success');
                  }}
                  className="px-2 py-1 hover:bg-slate-200 dark:hover:bg-dark-300 rounded text-[11px] font-bold text-cream-500"
                >
                  EXPORT
                </button>
                <div className="w-px h-3 bg-slate-300 dark:bg-dark-200 mx-1" />
                <div className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" /><span className="text-[11px] font-mono text-gold-500 uppercase">System Ready</span>
                <div className="w-px h-3 bg-slate-300 dark:bg-dark-200 mx-1" />
                <button
                  onClick={() => setShowConsole(false)}
                  className="p-1 hover:bg-red-500/20 rounded text-cream-400 dark:text-cream-400 hover:text-red-400 transition-colors"
                  title="Hide Log Stream"
                >
                  <X size={13} />
                </button>
              </div>
            </div>
            {!isConsoleMinimized && (
              <div className="flex-1 overflow-y-auto p-4 font-mono text-[11px] space-y-1 custom-scrollbar">
                {logs.map((log, i) => (
                  <div key={i} className="flex gap-4 hover:dark:bg-dark-400/40 p-0.5 rounded px-4">
                    <span className="text-cream-500 min-w-[70px]">{log.time}</span>
                    <span className={`${log.type === 'error' ? 'text-red-500' : log.type === 'success' ? 'text-gold-500' : 'dark:text-cream-400 text-cream-500'}`}>{log.msg}</span>
                  </div>
                ))}
                <div ref={consoleEndRef} />
              </div>
            )}
          </footer>
        )
      }

      {/* Floating Log Stream Toggle — visible when console is hidden */}
      {!showConsole && (
        <button
          onClick={() => { setShowConsole(true); setIsConsoleMinimized(false); }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-2 px-4 py-2 bg-cream-100/90 dark:bg-dark-400/90 backdrop-blur-md border border-cream-300 dark:border-dark-200 rounded-full shadow-card-light dark:shadow-card-dark hover:border-gold-500/50 transition-all group cursor-pointer"
          title="Open Live Web-Worker Stream"
        >
          <Terminal size={13} className="text-gold-500" />
          <span className="text-[11px] font-bold text-cream-500 dark:text-cream-400 group-hover:text-gold-500 transition-colors">Log Stream</span>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" />
        </button>
      )}

      {/* Global Toast Error - Enhanced for multi-line errors */}
      <AnimatePresence>
        {error && (() => { const errorStr = String(error); return (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-24 right-12 z-[200] max-w-md"
          >
            <div className="bg-dark-400 border border-red-500/30 text-white rounded-2xl shadow-3xl overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3 bg-red-500/20 flex items-center justify-between border-b border-red-500/20">
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} className="text-red-400" />
                  <span className="font-bold text-sm text-red-300">
                    {errorStr.includes('\n') ? errorStr.split('\n')[0] : 'Error'}
                  </span>
                </div>
                <button onClick={() => setError(null)} className="p-1.5 hover:bg-white/10 rounded-lg transition-all">
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4 max-h-80 overflow-y-auto">
                {errorStr.includes('\n') ? (
                  <div className="space-y-3 text-sm">
                    {errorStr.split('\n\n').map((paragraph, i) => {
                      // Check if this is a suggestions section
                      if (paragraph.includes('💡 What to try:')) {
                        const lines = paragraph.split('\n');
                        return (
                          <div key={i} className="mt-2">
                            <p className="text-yellow-400 font-semibold text-xs mb-2">{lines[0]}</p>
                            <ul className="space-y-1.5">
                              {lines.slice(1).map((line, j) => (
                                <li key={j} className="text-cream-300 text-xs flex items-start gap-2">
                                  <span className="text-yellow-500 mt-0.5">•</span>
                                  <span>{line.replace(/^\d+\.\s*/, '')}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      }
                      // Skip if it's the title (already shown in header)
                      if (i === 0 && paragraph.match(/^[🚫⏱️🔍🗑️🔒🔞🌍💎©️🔴🎬📋🌐⏰🔐🔗📹⚙️🔑❌]/)) {
                        const lines = paragraph.split('\n');
                        return lines.length > 1 ? (
                          <p key={i} className="text-cream-300">{lines.slice(1).join('\n')}</p>
                        ) : null;
                      }
                      return (
                        <p key={i} className="text-cream-300">{paragraph}</p>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-cream-300 text-sm">{errorStr}</p>
                )}
              </div>

              {/* Footer with copy + dismiss */}
              <div className="px-5 py-3 bg-dark-300/50 border-t border-dark-200 flex justify-between">
                <button
                  onClick={() => {
                    const report = JSON.stringify({
                      app: 'Qlipper AI',
                      version: typeof __APP_VERSION_SHORT__ !== 'undefined' ? __APP_VERSION_SHORT__ : 'unknown',
                      platform: navigator.platform,
                      timestamp: new Date().toISOString(),
                      error: error,
                    }, null, 2);
                    navigator.clipboard.writeText(report);
                    addLog('SYSTEM: Error report copied to clipboard', 'success');
                  }}
                  className="px-4 py-1.5 text-xs font-bold text-cream-500 hover:text-cream-300 hover:bg-dark-200 rounded-lg transition-all flex items-center gap-1.5"
                >
                  <Copy size={12} /> Salin Laporan
                </button>
                <button
                  onClick={() => setError(null)}
                  className="px-4 py-1.5 text-xs font-bold text-cream-400 hover:text-white hover:bg-dark-200 rounded-lg transition-all"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </motion.div>
        ); })()}
      </AnimatePresence>

      {/* Style Settings Modal */}
      <StyleSettingsModal
        isOpen={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        onSave={(settings) => { setStyleSettings(settings); persistStyleDefaults(settings); }}
        videoThumbnail={pipelineStatus?.thumbnail || null}
        videoTitle={metadata?.title || selectedHistoryProject?.title || ''}
        styleSettings={styleSettings}
        setStyleSettings={setStyleSettings}
      />

      {/* Welcome + Onboarding Carousel Modal */}
      <AnimatePresence>
        {showThankYouModal && (() => {
          const SLIDES = [
            {
              emoji: '🎉',
              gradient: 'from-gold-500 to-amber-500',
              tag: 'Welcome',
              title: 'Thank You for Your Purchase!',
              desc: 'Qlipper is a one-click viral clip factory. Paste a video link (or upload your own), and AI does the rest — download, transcribe, find the best moments, and export ready-to-post clips.',
              demo: (
                <div className="mt-2 flex items-center justify-center gap-2">
                  {[['🔗','Link'],['⬇️','Download'],['🤖','AI'],['✂️','Clips'],['📱','Post']].map(([e,l],i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 rounded-2xl bg-gold-500/20 flex items-center justify-center text-xl">{e}</div>
                      <span className="text-[11px] text-cream-500 dark:text-cream-400">{l}</span>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              emoji: '🔗',
              gradient: 'from-blue-500 to-cyan-500',
              tag: 'Step 1 — URL',
              title: 'Paste a Link, Hit Generate',
              desc: 'Paste any YouTube link. Qlipper downloads at your chosen quality (480p–4K), runs Whisper transcription to get accurate word-level timestamps, then sends the transcript to AI for clip detection.',
              demo: (
                <div className="mt-2 space-y-2">
                  <div className="rounded-xl bg-dark-400 border border-dark-200 px-4 py-3 flex items-center gap-3">
                    <div className="flex-1 text-xs text-cream-400 truncate">youtube.com/watch?v=...</div>
                    <div className="px-3 py-1.5 bg-gold-600 rounded-lg text-xs font-bold text-white flex items-center gap-1"><Sparkles size={12}/> Generate</div>
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-[11px] font-bold">▶ YouTube</span>
                    <span className="text-[11px] text-cream-500">480p · 720p · 1080p · 4K</span>
                  </div>
                </div>
              ),
            },
            {
              emoji: '📁',
              gradient: 'from-emerald-500 to-teal-500',
              tag: 'Step 1 — Upload',
              title: 'Already Have the Video? Upload It',
              desc: 'Click Upload next to the URL bar. Your file goes straight into the pipeline — no download needed. Transcription starts automatically, then the same AI clip config modal opens.',
              demo: (
                <div className="mt-2 space-y-2">
                  <div className="rounded-xl bg-dark-400 border border-dashed border-dark-100 px-4 py-4 flex flex-col items-center gap-2">
                    <Upload size={20} className="text-cream-500"/>
                    <span className="text-xs text-cream-400">Drop video here or click Upload</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-cream-500">
                    <CheckCircle2 size={12} className="text-emerald-400"/>
                    Transcription starts immediately in the background
                  </div>
                </div>
              ),
            },
            {
              emoji: '⚙️',
              gradient: 'from-violet-500 to-purple-500',
              tag: 'Step 2 — Configure',
              title: 'Choose Format, Duration & Prompt',
              desc: 'Before AI runs, you set the output format (9:16 for TikTok/Reels, 1:1 for Feed, 16:9 for YouTube), target clip duration, AI model, and an optional custom prompt to guide what moments to find.',
              demo: (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    {[{l:'9:16',s:'TikTok',a:true},{l:'1:1',s:'Feed',a:false},{l:'16:9',s:'YouTube',a:false}].map(f => (
                      <div key={f.l} className={`flex-1 py-2 rounded-xl border text-center transition-all ${f.a ? 'border-gold-500 bg-gold-500/10' : 'border-dark-200 bg-dark-400'}`}>
                        <div className={`text-xs font-bold ${f.a ? 'text-gold-400' : 'text-cream-400'}`}>{f.l}</div>
                        <div className="text-[11px] text-cream-500">{f.s}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-dark-400 border border-dark-200 px-3 py-2 text-xs text-cream-400 italic">"Find moments where speaker makes a controversial claim..."</div>
                </div>
              ),
            },
            {
              emoji: '✂️',
              gradient: 'from-amber-500 to-orange-500',
              tag: 'Step 3 — Clips',
              title: 'AI Finds & Renders Your Clips',
              desc: 'Qlipper renders each detected moment as a standalone MP4 — with your hook text overlay, karaoke subtitles burned in, and cropped to your chosen aspect ratio. Files land in My Files ready to download.',
              demo: (
                <div className="mt-2 space-y-1.5">
                  {[
                    { topic: 'The moment he breaks character', dur: '0:42', status: 'Done' },
                    { topic: 'Plot twist nobody saw coming', dur: '1:15', status: 'Done' },
                    { topic: 'Crowd reaction compilation', dur: '0:28', status: 'Rendering...' },
                  ].map((c,i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl bg-dark-400 border border-dark-300">
                      <span className="text-xs text-cream-300 truncate flex-1">{c.topic}</span>
                      <span className="text-[11px] text-cream-500 mx-2">{c.dur}</span>
                      <span className={`text-[11px] font-bold ${c.status === 'Done' ? 'text-emerald-400' : 'text-gold-400'}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              emoji: '✏️',
              gradient: 'from-rose-500 to-pink-500',
              tag: 'Subtitle Editor',
              title: 'Edit Subtitles Before Exporting',
              desc: 'In the Asset Library, open any transcribed video in the Subtitle Editor. Fix words, correct timing, then re-export. The edited transcript is what gets burned into your final clip subtitles.',
              demo: (
                <div className="mt-2 rounded-xl bg-dark-400 border border-dark-200 overflow-hidden">
                  <div className="px-3 py-2 border-b border-dark-300 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-cream-400 uppercase tracking-widest">Subtitle Editor</span>
                    <span className="text-[11px] text-cyan-400 font-bold">whisper-cpp</span>
                  </div>
                  {[
                    { t: '00:00:04', txt: 'So the thing nobody tells you about—' },
                    { t: '00:00:07', txt: 'building in public is that it\'s...' },
                  ].map((s,i) => (
                    <div key={i} className="flex gap-3 px-3 py-2 border-b border-dark-300/50">
                      <span className="text-[11px] font-mono text-gold-500/70">{s.t}</span>
                      <span className="text-[11px] text-cream-300">{s.txt}</span>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              emoji: '🌟',
              gradient: 'from-gold-500 to-yellow-400',
              tag: 'Let\'s Go',
              title: 'You\'re All Set!',
              desc: 'Follow @qlipper.ai on Instagram for tips, templates, and updates. Now go clip something worth posting.',
              demo: (
                <a
                  href="https://instagram.com/qlipper.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-2xl font-bold text-sm shadow-lg transition-all hover:scale-105 w-full"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  Follow @qlipper.ai
                </a>
              ),
            },
          ];

          const slide = SLIDES[onboardingSlide];
          const isLast = onboardingSlide === SLIDES.length - 1;

          return (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 20 }}
                className="relative w-full max-w-md bg-cream-50 dark:bg-dark-400 border border-cream-300 dark:border-dark-300 rounded-3xl shadow-2xl overflow-hidden"
              >
                {/* Progress bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-cream-200 dark:bg-dark-300">
                  <motion.div
                    className={`h-full bg-gradient-to-r ${slide.gradient}`}
                    animate={{ width: `${((onboardingSlide + 1) / SLIDES.length) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>

                {/* Close */}
                <button
                  onClick={() => setShowThankYouModal(false)}
                  className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-cream-200 dark:hover:bg-dark-300/80 text-cream-400 dark:text-cream-400 hover:text-dark-200 dark:hover:text-white transition-all z-10"
                >
                  <X size={16} />
                </button>

                {/* Slide content */}
                <div className="pt-8 px-8 pb-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={onboardingSlide}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.25 }}
                      className="space-y-4"
                    >
                      {/* Tag + emoji */}
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full bg-gradient-to-r ${slide.gradient} text-white`}>{slide.tag}</span>
                        <span className="text-2xl">{slide.emoji}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-dark-200 dark:text-white leading-tight">{slide.title}</h3>

                      {/* Description */}
                      <p className="text-sm text-cream-500 dark:text-cream-300 leading-relaxed">{slide.desc}</p>

                      {/* Demo area */}
                      {slide.demo}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Footer */}
                <div className="px-8 pb-6 flex items-center justify-between">
                  {/* Dots */}
                  <div className="flex gap-1.5">
                    {SLIDES.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setOnboardingSlide(i)}
                        className={`rounded-full transition-all ${i === onboardingSlide ? `w-4 h-2 bg-gradient-to-r ${slide.gradient}` : 'w-2 h-2 bg-cream-300 dark:bg-cream-600 hover:bg-cream-400 dark:hover:bg-cream-500'}`}
                      />
                    ))}
                  </div>

                  {/* Nav buttons */}
                  <div className="flex items-center gap-2">
                    {onboardingSlide > 0 && (
                      <button
                        onClick={() => setOnboardingSlide(s => s - 1)}
                        className="px-4 py-2 rounded-xl bg-cream-200 dark:bg-dark-300 hover:bg-cream-300 dark:hover:bg-dark-200 text-dark-200 dark:text-cream-200 font-bold text-xs transition-all"
                      >
                        Back
                      </button>
                    )}
                    {isLast ? (
                      <button
                        onClick={() => {
                          if (neverShowAgain) localStorage.setItem('qlipper_thank_you_dismissed', 'true');
                          setShowThankYouModal(false);
                        }}
                        className={`px-5 py-2 rounded-xl bg-gradient-to-r ${slide.gradient} text-white font-bold text-xs transition-all hover:scale-105 flex items-center gap-1.5`}
                      >
                        <Sparkles size={12}/> Start Clipping
                      </button>
                    ) : (
                      <button
                        onClick={() => setOnboardingSlide(s => s + 1)}
                        className={`px-5 py-2 rounded-xl bg-gradient-to-r ${slide.gradient} text-white font-bold text-xs transition-all hover:scale-105 flex items-center gap-1.5`}
                      >
                        Next <ArrowRight size={12}/>
                      </button>
                    )}
                  </div>
                </div>

                {/* Don't show again + skip — bottom */}
                <div className="px-8 pb-5 flex items-center justify-between border-t border-cream-200 dark:border-dark-300/50 pt-3">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className="relative w-4 h-4">
                      <input type="checkbox" checked={neverShowAgain} onChange={e => setNeverShowAgain(e.target.checked)}
                        className="w-4 h-4 rounded border border-cream-300 dark:border-cream-600 bg-cream-100 dark:bg-dark-300 checked:bg-gold-500 checked:border-gold-500 cursor-pointer appearance-none transition-all" />
                      {neverShowAgain && <Check size={11} className="absolute top-0.5 left-0.5 text-white pointer-events-none" />}
                    </div>
                    <span className="text-[11px] text-cream-500 dark:text-cream-400 group-hover:text-dark-200 dark:group-hover:text-cream-200">Don't show again</span>
                  </label>
                  <button
                    onClick={() => {
                      if (neverShowAgain) localStorage.setItem('qlipper_thank_you_dismissed', 'true');
                      setShowThankYouModal(false);
                    }}
                    className="text-[11px] text-cream-500 dark:text-cream-400 hover:text-dark-200 dark:hover:text-cream-200 transition-colors"
                  >
                    Skip tour
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

    </div >
  );
};

// ============================================================
// LICENSE GATE WRAPPER
// Wraps QlipperAppInner with the license verification gate.
// useLicense is called here (top-level) to avoid hooks-after-returns.
// ============================================================
const QlipperApp = () => {
  const license = useLicense();

  // Show loading screen while verifying on startup
  if (false) { /* web: nginx-gated */
    return <LicenseLoadingScreen />;
  }

  // Block access if not authenticated OR license not verified
  if (false) { /* web: nginx-gated */
    return <LicenseLogin license={license} />;
  }

  // License valid — render the full app
  return <QlipperAppInner />;
};
// ============================================================
// END LICENSE GATE WRAPPER
// ============================================================

export default QlipperApp;
